import { useState, useEffect, useRef } from "react";

// ============================================================
// REUSABLE ADDRESS FIELDS COMPONENT
// ============================================================
function AddressFields({ value, onChange, style }) {
  // Parse existing value into parts
  const parts = value ? value.split(",").map(s => s.trim()) : [];
  const [street, setStreet] = useState(parts[0] || "");
  const [city, setCity] = useState(parts[1] || "");
  const [state, setState] = useState(parts[2]?.replace(/\s*\d+/, "").trim() || "FL");
  const [zip, setZip] = useState(parts[2]?.match(/\d+/)?.[0] || "");

  function update(s, c, st, z) {
    const full = [s, c, `${st} ${z}`.trim()].filter(Boolean).join(", ");
    onChange(full);
  }

  const FL_CITIES = ["West Palm Beach","Boca Raton","Delray Beach","Boynton Beach","Lake Worth","Palm Beach Gardens","Jupiter","Riviera Beach","Greenacres","Wellington","Royal Palm Beach","Palm Springs","Lantana","Manalapan","Palm Beach","Tequesta","Juno Beach","North Palm Beach","Lake Park","Mangonia Park"];

  return (
    <div style={style}>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 10, letterSpacing: 2, color: "#666", fontWeight: 700, display: "block", marginBottom: 6 }}>STREET ADDRESS</label>
        <input style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, padding: "12px 14px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box" }}
          placeholder="123 Main St, Apt 4B"
          value={street}
          onChange={e => { setStreet(e.target.value); update(e.target.value, city, state, zip); }} />
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
        <div style={{ flex: 2 }}>
          <label style={{ fontSize: 10, letterSpacing: 2, color: "#666", fontWeight: 700, display: "block", marginBottom: 6 }}>CITY</label>
          <input style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, padding: "12px 14px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box" }}
            placeholder="West Palm Beach"
            value={city}
            onChange={e => { setCity(e.target.value); update(street, e.target.value, state, zip); }}
            list="fl-cities" />
          <datalist id="fl-cities">
            {FL_CITIES.map(c => <option key={c} value={c} />)}
          </datalist>
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 10, letterSpacing: 2, color: "#666", fontWeight: 700, display: "block", marginBottom: 6 }}>STATE</label>
          <select style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, padding: "12px 14px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box" }}
            value={state}
            onChange={e => { setState(e.target.value); update(street, city, e.target.value, zip); }}>
            {["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label style={{ fontSize: 10, letterSpacing: 2, color: "#666", fontWeight: 700, display: "block", marginBottom: 6 }}>ZIP CODE</label>
        <input style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, padding: "12px 14px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box" }}
          placeholder="33401"
          value={zip}
          maxLength={5}
          onChange={e => { setZip(e.target.value.replace(/\D/,"")); update(street, city, state, e.target.value.replace(/\D/,"")); }} />
      </div>
    </div>
  );
}


const FIREBASE_CONFIG = {
  apiKey: "AIzaSyCR6MxMISOrMOQ5LgFOyjsXjz2OiZXU0wQ",
  authDomain: "superfast-tires.firebaseapp.com",
  databaseURL: "https://superfast-tires-default-rtdb.firebaseio.com",
  projectId: "superfast-tires",
  storageBucket: "superfast-tires.firebasestorage.app",
};

// ============================================================
// ADMIN CREDENTIALS (only you)
// ============================================================
const ADMIN_EMAIL = "superfastmobiletires@gmail.com";
const ADMIN_PASSWORD = "Green2021$$##@@";

// ============================================================
// FIREBASE DB HELPERS
// ============================================================
const DB_URL = `${FIREBASE_CONFIG.databaseURL}`;

// ── Device / permission helpers ──
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;

// Ask for location and actually WAIT for the user's answer
function requestLocation() {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve({ ok: false, reason: "unsupported" });
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ ok: true, coords: pos.coords }),
      err => resolve({
        ok: false,
        reason: err.code === 1 ? "denied" : err.code === 2 ? "unavailable" : "timeout",
        message: err.message,
      }),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  });
}

// ── SCHEDULING ──
function minDateTime() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Total a customer/partner owes on a quote, honoring the no-tax flag
function quoteTotal(job) {
  if (!job?.quotedPrice) return 0;
  const mult = job.quoteTaxable === false ? 1 : 1.07;
  return parseFloat((job.quotedPrice * mult).toFixed(2));
}

function formatSchedule(dateStr, timeStr) {
  if (!dateStr) return null;
  try {
    const dt = new Date(`${dateStr}T${timeStr || "09:00"}`);
    return dt.toLocaleString("en-US", {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return `${dateStr} ${timeStr || ""}`.trim(); }
}

function SchedulePicker({ mode, setMode, date, setDate, time, setTime, accent = "#E63329" }) {
  return (
    <div style={c.fieldGroup}>
      <label style={c.label}>When do you need it?</label>
      <div style={{ display: "flex", gap: 8, marginBottom: mode === "later" ? 12 : 0 }}>
        {[["now", "⚡ ASAP — Right Now"], ["later", "📅 Schedule for Later"]].map(([m, lbl]) => (
          <button key={m} onClick={() => setMode(m)}
            style={{ flex: 1, background: mode === m ? accent : "#161616",
              border: `1px solid ${mode === m ? accent : "#2a2a2a"}`,
              color: mode === m ? (accent === "#22c55e" ? "#000" : "#fff") : "#888",
              padding: "13px", borderRadius: 9, fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
            {lbl}
          </button>
        ))}
      </div>

      {mode === "now" && (
        <div style={{ background: "#161616", border: `1px solid ${accent}`, borderRadius: 8, padding: "10px 12px", fontSize: 12, color: accent, fontWeight: 700, marginTop: 10 }}>
          ⚡ ASAP — we'll dispatch the closest tech right away
        </div>
      )}

      {mode === "later" && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ ...c.label, fontSize: 9 }}>DATE</label>
              <input type="date" style={c.input} min={minDateTime()}
                value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ ...c.label, fontSize: 9 }}>TIME</label>
              <input type="time" style={c.input}
                value={time} onChange={e => setTime(e.target.value)} />
            </div>
          </div>
          {date && (
            <div style={{ background: "#161616", border: `1px solid ${accent}`, borderRadius: 8, padding: "10px 12px", fontSize: 12.5, color: accent, fontWeight: 700 }}>
              📅 Scheduled for {formatSchedule(date, time)}
            </div>
          )}
          <div style={{ fontSize: 11, color: "#555", marginTop: 7, lineHeight: 1.5 }}>
            We'll dispatch a tech to arrive around this time. You'll get a call if anything changes.
          </div>
        </>
      )}
    </div>
  );
}

// ── REGIONAL FLYERS — matched to where the provider works ──
const FLYERS = {
  atlanta: {
    id: "atlanta",
    src: "/flyer-atlanta.jpg",
    label: "Metro Atlanta",
    blurb: "Fulton · Gwinnett · Cobb · DeKalb · Clayton · Cherokee · Henry",
  },
  tampa: {
    id: "tampa",
    src: "/flyer-tampa.jpg",
    label: "Tampa & SW Florida",
    blurb: "Hillsborough · Pinellas · Pasco · Polk · Manatee · Sarasota · Lee",
  },
  southflorida: {
    id: "southflorida",
    src: "/flyer-southflorida.jpg",
    label: "South Florida",
    blurb: "Palm Beach · Broward · Miami-Dade · Treasure Coast",
  },
  newyork: {
    id: "newyork",
    src: "/flyer-newyork.jpg",
    label: "New York City",
    blurb: "Manhattan · Brooklyn · Queens · The Bronx · Staten Island",
  },
  newjersey: {
    id: "newjersey",
    src: "/flyer-newjersey.jpg",
    label: "New Jersey",
    blurb: "Bergen · Essex · Hudson · Union · Middlesex · Passaic · Monmouth",
  },
};

// West-coast / central Florida cities → Tampa flyer
const TAMPA_CITIES = [
  "tampa","st. petersburg","st petersburg","saint petersburg","clearwater","brandon","lakeland",
  "sarasota","bradenton","fort myers","ft myers","cape coral","naples","venice","port charlotte",
  "punta gorda","north port","ocala","spring hill","brooksville","new port richey","palm harbor",
  "largo","pinellas park","dunedin","tarpon springs","plant city","riverview","wesley chapel",
  "lutz","valrico","winter haven","bartow","estero","bonita springs","englewood","sebring",
  "gainesville","the villages","leesburg","clermont","kissimmee","orlando","winter park","sanford",
];

// East / south Florida → South Florida flyer
const SOFLO_CITIES = [
  "west palm beach","palm beach","boca raton","delray beach","boynton beach","lake worth",
  "wellington","jupiter","greenacres","royal palm beach","riviera beach","lantana","palm springs",
  "north palm beach","tequesta","juno beach","lake park","belle glade","loxahatchee",
  "fort lauderdale","ft lauderdale","hollywood","pompano beach","miami","hialeah","coral springs",
  "pembroke pines","miramar","davie","plantation","sunrise","coral gables","deerfield beach",
  "homestead","port st. lucie","port st lucie","stuart","fort pierce","ft pierce","vero beach",
  "jensen beach","palm city","margate","coconut creek","tamarac","weston","aventura","doral",
];

const NYC_CITIES = [
  "new york","new york city","nyc","manhattan","brooklyn","queens","bronx","the bronx",
  "staten island","astoria","flushing","jamaica","long island city","harlem","yonkers",
  "far rockaway","forest hills","bay ridge","williamsburg","bushwick","flatbush",
];

const NJ_CITIES = [
  "newark","jersey city","paterson","elizabeth","edison","woodbridge","lakewood","toms river",
  "hamilton","trenton","clifton","camden","brick","cherry hill","passaic","union city",
  "bayonne","east orange","vineland","new brunswick","hoboken","perth amboy","west new york",
  "plainfield","hackensack","sayreville","kearny","linden","atlantic city","fort lee",
  "north bergen","irvington","bloomfield","montclair","belleville","union","westfield",
  "parsippany","howell","freehold","piscataway","old bridge","gloucester","egg harbor",
];

// Pick a flyer from a free-text address like "123 Main St, Brooklyn, NY 11201"
function flyerForAddress(addr = "") {
  const a = String(addr).toLowerCase();
  const stMatch = / \b(ny|nj|ga|fl|ct|pa)\b/.exec(a);
  const st = stMatch ? stMatch[1].toUpperCase() : "";
  if (st === "NY") return FLYERS.newyork;
  if (st === "NJ") return FLYERS.newjersey;
  if (st === "GA") return FLYERS.atlanta;
  if (NYC_CITIES.some(x => a.includes(x))) return FLYERS.newyork;
  if (NJ_CITIES.some(x => a.includes(x))) return FLYERS.newjersey;
  if (a.includes("atlanta") || st === "GA") return FLYERS.atlanta;
  if (TAMPA_CITIES.some(x => a.includes(x))) return FLYERS.tampa;
  return FLYERS.southflorida;
}

function flyerForProvider(profile = {}) {
  // Manual override always wins
  if (profile.flyerRegion && FLYERS[profile.flyerRegion]) return FLYERS[profile.flyerRegion];

  const st = (profile.state || "").toUpperCase();
  const city = (profile.city || "").trim().toLowerCase();

  if (st === "GA") return FLYERS.atlanta;
  if (st === "NY") return FLYERS.newyork;
  if (st === "NJ") return FLYERS.newjersey;
  if (st === "CT" || st === "PA") {
    // Tri-state commuters — nearest market by city
    if (city && NJ_CITIES.some(x => city.includes(x))) return FLYERS.newjersey;
    return FLYERS.newyork;
  }

  if (st === "FL" || !st) {
    if (city) {
      if (TAMPA_CITIES.some(x => city.includes(x))) return FLYERS.tampa;
      if (SOFLO_CITIES.some(x => city.includes(x))) return FLYERS.southflorida;
    }
    return FLYERS.southflorida;
  }

  // Any other state — match on city, else default
  if (city) {
    if (NYC_CITIES.some(x => city.includes(x))) return FLYERS.newyork;
    if (NJ_CITIES.some(x => city.includes(x))) return FLYERS.newjersey;
    if (TAMPA_CITIES.some(x => city.includes(x))) return FLYERS.tampa;
  }
  return FLYERS.southflorida;
}

// ── Stable form field (defined at module level so React never remounts it) ──
function Field({ label, value, onChange, hint, ...rest }) {
  return (
    <div style={c.fieldGroup}>
      <label style={c.label}>{label}</label>
      <input
        style={c.input}
        value={value}
        onChange={onChange}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        {...rest}
      />
      {hint && <div style={{ fontSize: 11, color: "#555", marginTop: 5 }}>{hint}</div>}
    </div>
  );
}

// ── LIVE INTERACTIVE MAP (Google Maps JS API) ──
let __mapsPromise = null;
function loadGoogleMaps() {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (__mapsPromise) return __mapsPromise;
  __mapsPromise = new Promise((resolve, reject) => {
    const sc = document.createElement("script");
    sc.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=marker`;
    sc.async = true;
    sc.onload = () => resolve(window.google.maps);
    sc.onerror = () => reject(new Error("Maps failed to load"));
    document.head.appendChild(sc);
  });
  return __mapsPromise;
}

function LiveMap({ coords, height = "42vh" }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let dead = false;
    loadGoogleMaps().then(maps => {
      if (dead || !ref.current) return;
      const center = { lat: coords.lat, lng: coords.lng };
      mapRef.current = new maps.Map(ref.current, {
        center,
        zoom: 14,
        disableDefaultUI: true,
        gestureHandling: "greedy",
        clickableIcons: false,
        styles: [
          { elementType: "geometry", stylers: [{ color: "#1d2c4d" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#8ec3b9" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#1a3646" }] },
          { featureType: "poi", stylers: [{ visibility: "off" }] },
          { featureType: "transit", stylers: [{ visibility: "off" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#304a7d" }] },
          { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2c6675" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e1626" }] },
        ],
      });
      markerRef.current = new maps.Marker({
        position: center,
        map: mapRef.current,
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: "#22c55e",
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 3,
        },
      });
    }).catch(() => setFailed(true));
    return () => { dead = true; };
  }, []);

  // Follow the user as their position updates
  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;
    const pos = { lat: coords.lat, lng: coords.lng };
    markerRef.current.setPosition(pos);
    mapRef.current.panTo(pos);
  }, [coords.lat, coords.lng]);

  if (failed) {
    return (
      <div style={{ position: "absolute", inset: 0, background: "#0d1117",
        backgroundImage: `url(https://maps.googleapis.com/maps/api/staticmap?center=${coords.lat},${coords.lng}&zoom=14&size=640x640&scale=2&key=${GOOGLE_MAPS_KEY})`,
        backgroundSize: "cover", backgroundPosition: "center" }} />
    );
  }
  return <div ref={ref} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />;
}

// ============================================================
// SECURE DATABASE ACCESS
// Nothing talks to Firebase directly. Every call goes through the
// Cloudflare Worker, which verifies who you are and returns only
// the records you're allowed to see.
// ============================================================
let __authToken = null;
let __refreshToken = null;

function setAuthToken(t, refresh) {
  __authToken = t || null;
  if (refresh) __refreshToken = refresh;
}

function sessionKeys() { return ["sfrs_customer", "sfrs_provider", "sfrs_partner", "sfrs_fleet", "sfrs_admin"]; }

function getAuthToken() {
  if (__authToken) return __authToken;
  try {
    for (const k of sessionKeys()) {
      const raw = localStorage.getItem(k);
      if (raw) {
        const u = JSON.parse(raw);
        if (u?.token) {
          __authToken = u.token;
          __refreshToken = u.refreshToken || __refreshToken;
          return u.token;
        }
      }
    }
  } catch {}
  return null;
}

function getRefreshToken() {
  if (__refreshToken) return __refreshToken;
  try {
    for (const k of sessionKeys()) {
      const raw = localStorage.getItem(k);
      if (raw) {
        const u = JSON.parse(raw);
        if (u?.refreshToken) { __refreshToken = u.refreshToken; return u.refreshToken; }
      }
    }
  } catch {}
  return null;
}

// Firebase ID tokens expire after 1 hour — swap the refresh token for a fresh one
let __refreshing = null;
async function refreshAuthToken() {
  const rt = getRefreshToken();
  if (!rt) return null;
  if (__refreshing) return __refreshing;
  __refreshing = (async () => {
    try {
      const res = await fetch(`https://securetoken.googleapis.com/v1/token?key=${FIREBASE_CONFIG.apiKey}`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: `grant_type=refresh_token&refresh_token=${encodeURIComponent(rt)}`,
      });
      const d = await res.json();
      if (d.error || !d.id_token) return null;
      __authToken = d.id_token;
      __refreshToken = d.refresh_token || rt;
      // Persist to whichever session is active
      for (const k of sessionKeys()) {
        const raw = localStorage.getItem(k);
        if (raw) {
          const u = JSON.parse(raw);
          u.token = __authToken;
          u.refreshToken = __refreshToken;
          localStorage.setItem(k, JSON.stringify(u));
        }
      }
      return __authToken;
    } catch { return null; }
    finally { setTimeout(() => { __refreshing = null; }, 0); }
  })();
  return __refreshing;
}

// Refresh the token well before its 1-hour expiry
if (typeof window !== "undefined") {
  setInterval(() => { if (getRefreshToken()) refreshAuthToken(); }, 45 * 60 * 1000);
  // And once shortly after load, in case the stored token is already stale
  setTimeout(() => { if (getRefreshToken()) refreshAuthToken(); }, 1500);
}

// If the secure proxy is unavailable, fall back to direct Firebase so the app
// keeps working. Set DB_PROXY_REQUIRED = true once the Worker is confirmed and
// you've locked your Firebase rules.
const DB_PROXY_REQUIRED = false;
let __proxyDown = false;
let __reloginPrompted = false;

async function dbDirect(op, path, data) {
  const url = `${DB_URL}/${path}.json`;
  if (op === "get") {
    const r = await fetch(url);
    return { ok: true, data: await r.json() };
  }
  if (op === "push") {
    const r = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
    const d = await r.json();
    return { ok: true, key: d.name };
  }
  if (op === "set" || op === "patch") {
    await fetch(url, { method: op === "set" ? "PUT" : "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
    return { ok: true };
  }
  if (op === "delete") {
    await fetch(url, { method: "DELETE" });
    return { ok: true };
  }
  return { error: "Unknown op" };
}

async function dbCall(op, path, data, opts = {}, _retried) {
  // Proxy already known to be down this session — don't keep waiting on it
  if (__proxyDown && !DB_PROXY_REQUIRED) return dbDirect(op, path, data);

  let res, out;
  try {
    res = await fetch(`${WORKER_URL}/db`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        op, path, data,
        idToken: opts.guest ? null : getAuthToken(),
        guest: !!opts.guest,
      }),
    });
    out = await res.json().catch(() => ({ error: "Bad response from proxy" }));
  } catch (e) {
    out = { error: "Proxy unreachable: " + e.message };
  }

  // Expired / rejected token — refresh once and retry
  const looksAuthy = res?.status === 403 || res?.status === 401 ||
    /not authorized|expired|sign in again|token/i.test(out?.error || "");
  if (out?.error && !opts.guest && !_retried && looksAuthy) {
    const fresh = await refreshAuthToken();
    if (fresh) return dbCall(op, path, data, opts, true);

    // No refresh token stored — this session predates the fix. Force a clean re-login.
    if (!getRefreshToken()) {
      console.warn("[db] No refresh token on this session. Signing out so you can log back in.");
      try {
        for (const k of sessionKeys()) localStorage.removeItem(k);
      } catch {}
      __authToken = null;
      if (!__reloginPrompted) {
        __reloginPrompted = true;
        alert("Your session expired. Please sign in again.");
        setTimeout(() => window.location.reload(), 300);
      }
      return { error: "Session expired." };
    }
  }

  if (out?.error) {
    console.warn("[db]", op, path, "→", out.error, out.detail || "");
    if (!DB_PROXY_REQUIRED) {
      __proxyDown = true;
      console.warn("[db] Falling back to direct Firebase. Fix the Worker, then set DB_PROXY_REQUIRED = true.");
      return dbDirect(op, path, data);
    }
  }
  return out;
}

async function dbGet(path, opts = {}) {
  try {
    const r = await dbCall("get", path);

    // Surface real failures instead of silently looking like "no data"
    if (r?.error) {
      console.warn("[dbGet] failed:", path, r.error);
      if (opts.throwOnError) throw new Error(r.error);
      return path.includes("users/") ? null : [];
    }

    const data = r?.data;
    if (!data) return path.includes("users/") ? null : [];

    // Single record lookup
    if (path.startsWith("users/") && path.split("/").length === 2) return data;

    if (typeof data === "object" && !Array.isArray(data)) {
      return Object.entries(data).map(([key, val]) => ({ ...val, _key: key }));
    }
    return data;
  } catch (e) {
    if (opts.throwOnError) throw e;
    return [];
  }
}

// Read a user profile right after sign-in, retrying while the token settles
// ============================================================
// ACCOUNT CREATION — single path used by every signup flow.
//
// Fixes three bugs that were breaking signups:
//  1. The new user's token was never attached before writing their profile,
//     so the worker saw an anonymous request and refused it.
//  2. A failed profile write left an orphaned Firebase auth account, so the
//     next attempt returned EMAIL_EXISTS — "you already have an account"
//     even though they'd never completed one.
//  3. Failures were silent, so nobody knew the profile hadn't saved.
//
// Returns { uid, token, refreshToken, profile } or { error }.
// ============================================================
function notifyAdminSignupFailure(profileData, mail, why) {
  const who = profileData.business || `${profileData.firstName || ""} ${profileData.lastName || ""}`.trim() || "Unknown";
  fetch(`${WORKER_URL}/notify`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      title: "🚨 SIGNUP FAILED — action needed",
      message: `${who} (${profileData.role}) couldn't complete signup.\n📧 ${mail}\n📞 ${profileData.phone || "no phone"}\n\nReason: ${why}\n\n➡️ Call them — they're stuck and probably won't try again.`,
    }),
  }).catch(() => {});
}

async function createAccount(email, password, profileData) {
  const mail = String(email).trim();
  let auth = await fbSignUp(mail, password);

  // ---- Recover an orphaned account from a previously failed signup ----
  if (auth.error && String(auth.error).includes("EMAIL_EXISTS")) {
    console.warn("[signup] auth account already exists — checking for an orphan");
    const signIn = await fbSignIn(mail, password);

    if (signIn.error) {
      // Real duplicate: the email is taken and this password doesn't match it
      return { error: "An account with this email already exists. Try signing in, or use \"Forgot password\" if you don't remember it." };
    }

    setAuthToken(signIn.token, signIn.refreshToken);
    const existing = await loadProfileAfterLogin(signIn.uid, 3);

    if (existing.profile && existing.profile.role) {
      return { error: `You already have an account (${existing.profile.role}). Please sign in instead.` };
    }

    // Auth exists but no usable profile — finish what the last attempt started
    console.warn("[signup] orphan found, completing the profile");
    auth = { uid: signIn.uid, token: signIn.token, refreshToken: signIn.refreshToken, email: signIn.email };
  } else if (auth.error) {
    return { error: fbFormatError(auth.error) };
  }

  // ---- CRITICAL: attach the new token before writing anything ----
  setAuthToken(auth.token, auth.refreshToken);

  // Give the token a moment to be accepted by Google's lookup endpoint
  await new Promise(r => setTimeout(r, 300));

  // ---- Write the profile, retrying on auth hiccups ----
  let wrote = null;
  for (let i = 0; i < 4; i++) {
    wrote = await dbSet(`users/${auth.uid}`, profileData);
    if (!wrote?.error) break;
    console.warn(`[signup] profile write attempt ${i + 1} failed:`, wrote.error);
    await refreshAuthToken();
    await new Promise(r => setTimeout(r, 500));
  }
  if (wrote?.error) {
    notifyAdminSignupFailure(profileData, mail, "profile write failed: " + wrote.error);
    return { error: `We couldn't save your account details (${wrote.error}). Your login was created — sign in and you'll be prompted to finish setup.`, uid: auth.uid };
  }

  // ---- Confirm it actually landed ----
  const check = await loadProfileAfterLogin(auth.uid, 4);
  if (!check.profile || check.profile.role !== profileData.role) {
    notifyAdminSignupFailure(profileData, mail, "verification failed: " + (check.error || "unknown"));
    return { error: `Your account was created but the details didn't save (${check.error || "unknown"}). Sign in and you'll be prompted to finish setup.`, uid: auth.uid };
  }

  return { uid: auth.uid, token: auth.token, refreshToken: auth.refreshToken, profile: check.profile };
}

// Returns { profile } on success, or { error, reason } so the caller can say
// something useful instead of a generic "try again".
async function loadProfileAfterLogin(uid, tries = 5) {
  let lastError = null;
  for (let i = 0; i < tries; i++) {
    try {
      const r = await dbCall("get", `users/${uid}`);

      if (r?.error) {
        lastError = r.error;
        console.warn(`[login] attempt ${i + 1} rejected:`, r.error, r.detail || "");
        // Auth problems are worth retrying after a token refresh
        if (/expired|authoriz|token|sign in/i.test(r.error)) {
          await refreshAuthToken();
          await new Promise(res => setTimeout(res, 500));
          continue;
        }
        // Anything else won't fix itself
        return { error: r.error, reason: "rejected" };
      }

      const data = r?.data;
      if (data && typeof data === "object") return { profile: data };

      // Read succeeded but there's no record — no point retrying
      console.warn(`[login] no user record found for ${uid}`);
      return { error: "No account record found.", reason: "missing" };

    } catch (e) {
      lastError = e.message;
      console.warn(`[login] attempt ${i + 1} threw:`, e.message);
      await refreshAuthToken();
      await new Promise(res => setTimeout(res, 500));
    }
  }
  return { error: lastError || "Could not reach the server.", reason: "network" };
}

async function dbSet(path, data) {
  try {
    const r = await dbCall("set", path, data);
    if (r?.error) {
      console.error("[dbSet] failed:", path, r.error);
      return { error: r.error };
    }
    return { ok: true };
  } catch (e) {
    console.error("[dbSet] failed:", path, e.message);
    return { error: e.message };
  }
}

async function dbPatch(path, updates) {
  try { await dbCall("patch", path, updates); } catch {}
}

async function dbPush(path, data, opts = {}) {
  try {
    const r = await dbCall("push", path, data, opts);
    return r?.key || null;
  } catch { return null; }
}

async function dbDelete(path) {
  try { await dbCall("delete", path); } catch {}
}

// ============================================================
// FIREBASE AUTH HELPERS
// ============================================================
const AUTH_URL = `https://identitytoolkit.googleapis.com/v1/accounts`;

async function fbSignUp(email, password) {
  try {
    const res = await fetch(`${AUTH_URL}:signUp?key=${FIREBASE_CONFIG.apiKey}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, returnSecureToken: true }),
    });
    const data = await res.json();
    if (data.error) return { error: data.error.message };
    return { uid: data.localId, token: data.idToken, refreshToken: data.refreshToken, email: data.email };
  } catch { return { error: "Network error." }; }
}

async function fbSignIn(email, password) {
  try {
    const res = await fetch(`${AUTH_URL}:signInWithPassword?key=${FIREBASE_CONFIG.apiKey}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, returnSecureToken: true }),
    });
    const data = await res.json();
    if (data.error) return { error: data.error.message };
    return { uid: data.localId, token: data.idToken, refreshToken: data.refreshToken, email: data.email };
  } catch { return { error: "Network error." }; }
}

async function fbSendVerification(token) {
  try {
    await fetch(`${AUTH_URL}:sendOobCode?key=${FIREBASE_CONFIG.apiKey}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ requestType: "VERIFY_EMAIL", idToken: token }),
    });
  } catch {}
}

async function fbCheckVerified(token) {
  try {
    const res = await fetch(`${AUTH_URL}:lookup?key=${FIREBASE_CONFIG.apiKey}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken: token }),
    });
    const data = await res.json();
    return data.users?.[0]?.emailVerified === true;
  } catch { return false; }
}

async function fbResetPassword(email) {
  try {
    const res = await fetch(`${AUTH_URL}:sendOobCode?key=${FIREBASE_CONFIG.apiKey}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ requestType: "PASSWORD_RESET", email }),
    });
    const data = await res.json();
    if (data.error) return { error: data.error.message };
    return { success: true };
  } catch { return { error: "Network error." }; }
}

function fbFormatError(msg) {
  if (!msg) return "Something went wrong.";
  if (msg.includes("EMAIL_EXISTS")) return "An account with this email already exists.";
  if (msg.includes("INVALID_PASSWORD") || msg.includes("INVALID_LOGIN_CREDENTIALS")) return "Incorrect email or password.";
  if (msg.includes("EMAIL_NOT_FOUND")) return "No account found with this email.";
  if (msg.includes("WEAK_PASSWORD")) return "Password must be at least 6 characters.";
  if (msg.includes("INVALID_EMAIL")) return "Please enter a valid email address.";
  if (msg.includes("TOO_MANY_ATTEMPTS")) return "Too many attempts. Please try again later.";
  return msg;
}

// ── STRIPE CONFIG ──
const STRIPE_PK = "pk_live_51RbLk5CHXBgvdCrXulfaEFS7K3M08IVVDzt5UeKsLp3YTmBKDnTjCiEqlaMbWLS3UC5m54DHwiWOnTHDnxuOeurT00kEyy1Coh";
const WORKER_URL = "https://superfast-stripe.superfastmobiletires.workers.dev";
const GOOGLE_MAPS_KEY = "AIzaSyBjd_0MKI6gB3wD_i0DRHDReJv_FN9ID_w";

// Sponsor inquiries email to superfastmobiletires@gmail.com
// Get this at formspree.io — create a form pointed at that address, paste the endpoint below.
const SPONSOR_EMAIL = "superfastmobiletires@gmail.com";

// $10/month membership — Stripe Payment Link
const MEMBERSHIP_LINK = "https://buy.stripe.com/eVqfZgbDpdTw1ew3ySaZi07";
const SPONSOR_FORM_ENDPOINT = "https://formspree.io/f/xaewyqnr";

const US_STATES = [
  {code:"AL",name:"Alabama"},{code:"AK",name:"Alaska"},{code:"AZ",name:"Arizona"},{code:"AR",name:"Arkansas"},
  {code:"CA",name:"California"},{code:"CO",name:"Colorado"},{code:"CT",name:"Connecticut"},{code:"DE",name:"Delaware"},
  {code:"DC",name:"District of Columbia"},{code:"FL",name:"Florida"},{code:"GA",name:"Georgia"},{code:"HI",name:"Hawaii"},
  {code:"ID",name:"Idaho"},{code:"IL",name:"Illinois"},{code:"IN",name:"Indiana"},{code:"IA",name:"Iowa"},
  {code:"KS",name:"Kansas"},{code:"KY",name:"Kentucky"},{code:"LA",name:"Louisiana"},{code:"ME",name:"Maine"},
  {code:"MD",name:"Maryland"},{code:"MA",name:"Massachusetts"},{code:"MI",name:"Michigan"},{code:"MN",name:"Minnesota"},
  {code:"MS",name:"Mississippi"},{code:"MO",name:"Missouri"},{code:"MT",name:"Montana"},{code:"NE",name:"Nebraska"},
  {code:"NV",name:"Nevada"},{code:"NH",name:"New Hampshire"},{code:"NJ",name:"New Jersey"},{code:"NM",name:"New Mexico"},
  {code:"NY",name:"New York"},{code:"NC",name:"North Carolina"},{code:"ND",name:"North Dakota"},{code:"OH",name:"Ohio"},
  {code:"OK",name:"Oklahoma"},{code:"OR",name:"Oregon"},{code:"PA",name:"Pennsylvania"},{code:"RI",name:"Rhode Island"},
  {code:"SC",name:"South Carolina"},{code:"SD",name:"South Dakota"},{code:"TN",name:"Tennessee"},{code:"TX",name:"Texas"},
  {code:"UT",name:"Utah"},{code:"VT",name:"Vermont"},{code:"VA",name:"Virginia"},{code:"WA",name:"Washington"},
  {code:"WV",name:"West Virginia"},{code:"WI",name:"Wisconsin"},{code:"WY",name:"Wyoming"},
];
const STATE_NAME = Object.fromEntries(US_STATES.map(s => [s.code, s.name]));

// Non-member emergency rate — no account required
const GUEST_RATE = 89;

async function loadStripe() {
  if (window.Stripe) return window.Stripe(STRIPE_PK);
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/";
    script.onload = () => resolve(window.Stripe(STRIPE_PK));
    document.head.appendChild(script);
  });
}


const SERVICES = [
  { id: "tire",    icon: "🛞", name: "Tire Change",     desc: "Plug, spare, or replacement — we handle it all.",  price: null, eta: "25-40 min", fields: ["tire_option"] },
  { id: "fuel",    icon: "⛽", name: "Fuel Delivery",   desc: "2 gallons delivered — $60 flat, any hour.", price: 60,  eta: "15-30 min", fields: ["fuel_type"] },
  { id: "jump",    icon: "🔋", name: "Jump Start",      desc: "Dead battery? $60 flat, any hour.",         price: 60,  eta: "20-35 min", fields: ["battery_upgrade"] },
  { id: "lockout", icon: "🔑", name: "Lockout Service", desc: "Locked out? $60 flat, any hour.",           price: 60,  eta: "25-45 min", fields: [] },
  { id: "tow",      icon: "🚛", name: "Tow Truck",       desc: "$25 dispatch fee. Driver quotes tow price on arrival.", price: 25,  eta: "30-60 min", fields: [] },
  { id: "mechanic",   icon: "🔧", name: "Mobile Mechanic",      desc: "$20 booking fee. Mechanic quotes repair on site.",    price: 20,  eta: "Scheduled",  fields: ["mechanic_service"] },
  { id: "wheellock",  icon: "🔐", name: "Wheel Lock Removal",   desc: "Lost your wheel lock key? $60 flat, any hour.",  price: 60,  eta: "25-45 min",  fields: [] },
  { id: "battery_replace", icon: "🔋", name: "Battery Replacement", desc: "New battery brought out & installed. Call for a quote.", price: null, eta: "30-60 min", fields: ["quote_only"] },
  { id: "rim",        icon: "⚙️", name: "Rim Damage Repair",   desc: "Bent, cracked or curbed rim? Call for a quote.",   price: null, eta: "Scheduled",  fields: ["quote_only"] },
];

const TIRE_SIZES = ["175/65R14","185/65R15","195/65R15","205/55R16","205/60R16","215/55R17","225/45R17","225/50R17","225/55R17","235/45R17","235/55R17","245/45R18","245/50R18","255/45R18","265/50R19","275/40R20","285/40R22","305/35R22","Other"];
const CAR_YEARS = Array.from({ length: 35 }, (_, i) => (2025 - i).toString());
const CAR_MAKES = ["Acura","Audi","BMW","Buick","Cadillac","Chevrolet","Chrysler","Dodge","Ford","GMC","Honda","Hyundai","Infiniti","Jeep","Kia","Lexus","Lincoln","Mazda","Mercedes-Benz","Mitsubishi","Nissan","Ram","Subaru","Tesla","Toyota","Volkswagen","Volvo","Other"];

const STATUS_META = {
  new:           { label: "New",          color: "#E63329", bg: "#2a0a0a" },
  assigned:      { label: "Assigned",     color: "#f59e0b", bg: "#1f1500" },
  en_route:      { label: "En Route",     color: "#3b82f6", bg: "#0a1525" },
  on_scene:      { label: "On Scene",     color: "#a855f7", bg: "#1a0a2a" },
  tow_requested: { label: "Tow Needed",   color: "#E63329", bg: "#2a0a0a" },
  returned:      { label: "Returned",     color: "#f59e0b", bg: "#1f1500" },
  cancelled:     { label: "Cancelled",    color: "#666",    bg: "#1a1a1a" },
  completed:     { label: "Done",         color: "#22c55e", bg: "#0a1f0a" },
};

const SERVICE_ICONS = { "Rim Damage Repair": "⚙️", "Tire Change": "🛞", "Jump Start": "🔋", "Fuel Delivery": "⛽", "Lockout Service": "🔑", "Tow Truck": "🚛", "Mobile Mechanic": "🔧", "Wheel Lock Removal": "🔐", "Battery Replacement": "🔋" };
const SERVICE_LABELS = { tire: "🛞 Tire", jump: "🔋 Jump", fuel: "⛽ Fuel", lockout: "🔑 Lockout", tow: "🚛 Tow", mechanic: "🔧 Mechanic", wheellock: "🔐 Wheel Lock", battery_replace: "🔋 Battery", rim: "⚙️ Rim Repair" };

function timeAgo(ts) {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function genId() { return "JOB-" + Math.floor(Math.random() * 90000 + 10000); }

// ============================================================
// ROOT — Routes based on URL path
// ============================================================
export default function App() {
  const path = window.location.pathname;

  if (path.startsWith("/admin")) return <AdminPortal />;
  if (path.startsWith("/provider")) return <ProviderPortal />;
  if (path.startsWith("/partner") || path.startsWith("/business")) return <PartnerPortal />;
  if (path.startsWith("/fleet")) return <FleetPortal />;
  return <CustomerPortal />;
}

// ============================================================
// ██████╗ ██╗   ██╗███████╗████████╗ ██████╗ ███╗   ███╗███████╗██████╗
// ██╔════╝██║   ██║██╔════╝╚══██╔══╝██╔═══██╗████╗ ████║██╔════╝██╔══██╗
// ██║     ██║   ██║███████╗   ██║   ██║   ██║██╔████╔██║█████╗  ██████╔╝
// ██║     ██║   ██║╚════██║   ██║   ██║   ██║██║╚██╔╝██║██╔══╝  ██╔══██╗
// ╚██████╗╚██████╔╝███████║   ██║   ╚██████╔╝██║ ╚═╝ ██║███████╗██║  ██║
// ============================================================
// ============================================================
// GUEST BOOKING — $89 FLAT, NO ACCOUNT REQUIRED
// ============================================================
function GuestBooking({ onBack, onJoin, prefill }) {
  const [screen, setScreen] = useState("service"); // service | details | pay | done
  const [svc, setSvc] = useState(null);
  const [form, setForm] = useState({
    name: prefill?.name || "",
    phone: prefill?.phone || "",
    address: prefill?.address || "",
    note: "",
  });
  const [payStep, setPayStep] = useState("idle");
  const [error, setError] = useState("");
  const [jobId, setJobId] = useState(null);
  const [gSchedMode, setGSchedMode] = useState("now");
  const [gSchedDate, setGSchedDate] = useState("");
  const [gSchedTime, setGSchedTime] = useState("");
  const cardElRef = useRef(null);
  const cardRef = useRef(null);
  const stripeRef = useRef(null);

  const GUEST_SERVICES = [
    { id: "tire",      icon: "🛞", name: "Flat Tire — Spare Mount" },
    { id: "plug",      icon: "🔩", name: "Tire Plug" },
    { id: "jump",      icon: "🔋", name: "Jump Start" },
    { id: "lockout",   icon: "🔑", name: "Lockout Service" },
    { id: "fuel",      icon: "⛽", name: "Fuel Delivery" },
    { id: "wheellock", icon: "🔐", name: "Wheel Lock Removal" },
  ];

  const tax = parseFloat((GUEST_RATE * 0.07).toFixed(2));
  const total = parseFloat((GUEST_RATE + tax).toFixed(2));
  const canProceed = form.name.trim() && form.phone.trim() && form.address.trim() && !(gSchedMode === "later" && !gSchedDate);

  async function startPayment() {
    setError("");
    setPayStep("loading");
    try {
      const stripe = await loadStripe();
      stripeRef.current = stripe;
      const id = "GUEST-" + genId();
      setJobId(id);
      const res = await fetch(`${WORKER_URL}/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: total, jobId: id, customerName: form.name, service: `${svc.name} (Non-Member)` }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setPayStep("card");
      setTimeout(() => {
        const elements = stripe.elements();
        const card = elements.create("card", {
          style: { base: { color: "#fff", fontSize: "16px", "::placeholder": { color: "#666" } }, invalid: { color: "#ff6b6b" } },
        });
        card.mount(cardElRef.current);
        cardRef.current = { card, clientSecret: data.clientSecret, customerId: data.customerId };
      }, 100);
    } catch (err) {
      setError(err.message);
      setPayStep("idle");
    }
  }

  async function confirmPayment() {
    if (!stripeRef.current || !cardRef.current) return;
    setPayStep("verifying");
    try {
      const { error: err, paymentIntent } = await stripeRef.current.confirmCardPayment(
        cardRef.current.clientSecret,
        { payment_method: { card: cardRef.current.card, billing_details: { name: form.name } } }
      );
      if (err) { setError(err.message); setPayStep("card"); return; }
      if (paymentIntent.status !== "succeeded") { setError("Payment not completed."); setPayStep("card"); return; }

      await dbPush("jobs", {
        id: jobId,
        service: svc.name,
        customer: `GUEST — ${form.name.trim()}`,
        realName: form.name.trim(),
        phone: form.phone,
        address: form.address,
        note: form.note,
        price: total,
        paid: true,
        paymentIntentId: paymentIntent.id,
        paidAt: Date.now(),
        stripeCustomerId: cardRef.current?.customerId || null,
        savedPaymentMethod: paymentIntent.payment_method || null,
        scheduled: gSchedMode === "later" && !!gSchedDate,
        scheduledDate: gSchedMode === "later" ? gSchedDate : null,
        scheduledTime: gSchedMode === "later" ? (gSchedTime || "09:00") : null,
        scheduledLabel: gSchedMode === "later" && gSchedDate ? formatSchedule(gSchedDate, gSchedTime) : null,
        guest: true,
        nonMember: true,
        status: "new",
        tech: null, techName: null,
        createdAt: Date.now(),
      }, { guest: true });

      await fetch(`${WORKER_URL}/notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: "🔥 NEW GUEST JOB — $89 Non-Member",
          message: `⚡ GUEST — ${form.name}\n\n${svc.name}\n📞 ${form.phone}\n📍 ${form.address}\n${gSchedMode === "later" && gSchedDate ? `📅 SCHEDULED: ${formatSchedule(gSchedDate, gSchedTime)}\n` : "⚡ ASAP\n"}💰 $${total} PAID\nJob ${jobId}\n\n⚠️ Non-member, no account — call them and dispatch manually.`,
        }),
      }).catch(() => {});

      setPayStep("done");
      setScreen("done");
    } catch (e) {
      setError(e.message);
      setPayStep("card");
    }
  }

  return (
    <div style={c.root}>
      <header style={c.header}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div><div style={c.logoTop}>SUPER FAST</div><div style={c.logoBot}>ROADSIDE</div></div>
        </div>
        <button style={c.back} onClick={screen === "details" ? () => setScreen("service") : onBack}>← Back</button>
      </header>

      <div style={c.page}>
        {screen === "done" ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 60, marginBottom: 14 }}>✅</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#22c55e", marginBottom: 8 }}>You're All Set!</div>
            <div style={{ fontSize: 14, color: "#888", lineHeight: 1.6, marginBottom: 20 }}>
              We received your request and a tech is being dispatched now. You'll get a call at{" "}
              <strong style={{ color: "#fff" }}>{form.phone}</strong> shortly.
            </div>
            <div style={c.trackCard}>
              {[["🆔","Job ID", jobId], ["🔧","Service", svc.name], ["📍","Location", form.address], ["💳","Paid", `$${total.toFixed(2)}`]].map(([ic,l,v]) => (
                <div key={l}>
                  <div style={c.trackRow}>
                    <span style={{ fontSize: 18 }}>{ic}</span>
                    <div><div style={c.trackLabel}>{l}</div><div style={{ fontSize: 13, fontWeight: 600 }}>{v}</div></div>
                  </div>
                  <div style={c.divider} />
                </div>
              ))}
            </div>
            <div style={{ background: "#1a0a0a", border: "1px solid #E63329", borderRadius: 12, padding: 18, marginTop: 20, textAlign: "left" }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: "#fff", marginBottom: 6 }}>💡 You just paid $89.</div>
              <div style={{ fontSize: 13, color: "#aaa", lineHeight: 1.6, marginBottom: 14 }}>
                Members pay <strong style={{ color: "#22c55e" }}>$60</strong> for this exact service — and never see a night surcharge. That's $29 saved every single call, for $10/month.
              </div>
              <button style={{ ...c.btnPrimary, marginBottom: 0 }} onClick={onJoin}>
                Join for $10/month →
              </button>
            </div>
            <button style={{ ...c.btnSecondary, marginTop: 12 }} onClick={onBack}>Done</button>
            <div style={{ fontSize: 13, color: "#555", textAlign: "center", marginTop: 16 }}>
              Questions? <a href="tel:5612558506" style={{ color: "#E63329" }}>📞 (561) 255-8506</a>
            </div>
          </div>
        ) : screen === "service" ? (
          <>
            <div style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 6 }}>⚡ NON-MEMBER EMERGENCY RATE</div>
              <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", marginBottom: 4 }}>$89 flat rate</div>
              <div style={{ fontSize: 12.5, color: "#aaa", lineHeight: 1.55 }}>
                No account. No membership. Pick your service, pay once, we dispatch a tech to you.
              </div>
            </div>

            <div style={c.sectionLabel}>WHAT DO YOU NEED?</div>
            {GUEST_SERVICES.map(g => (
              <button key={g.id}
                style={{ ...c.spareBtn, ...(svc?.id === g.id ? c.spareBtnActiveRed : {}), justifyContent: "space-between", marginBottom: 9 }}
                onClick={() => setSvc(g)}>
                <span>{g.icon} {g.name}</span>
                <span style={{ fontWeight: 900 }}>$89</span>
              </button>
            ))}

            <div style={{ fontSize: 11, color: "#555", lineHeight: 1.6, margin: "14px 0 18px" }}>
              Need a tow, mobile mechanic, used tire or battery replacement? Those are quoted individually —{" "}
              <a href="tel:5612558506" style={{ color: "#E63329", fontWeight: 700 }}>call (561) 255-8506</a>.
            </div>

            <button style={{ ...c.btnPrimary, opacity: svc ? 1 : 0.4 }} disabled={!svc}
              onClick={() => setScreen("details")}>
              {svc ? `Continue — ${svc.name}` : "Select a service above"}
            </button>

            <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: 14, marginTop: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#22c55e", marginBottom: 4 }}>💰 Save $29 on this call</div>
              <div style={{ fontSize: 12, color: "#4caf50", lineHeight: 1.5, marginBottom: 10 }}>
                Members pay $60 instead of $89 — and never pay a night surcharge. Membership is $10/month, cancel any time.
              </div>
              <button style={{ width: "100%", background: "none", border: "1px solid #22c55e", color: "#22c55e", padding: "10px", borderRadius: 8, fontWeight: 800, fontSize: 13, cursor: "pointer" }}
                onClick={onJoin}>
                Join & pay $60 instead →
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: "12px 14px", marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{svc.icon} {svc.name}</span>
              <span style={{ fontSize: 16, fontWeight: 900, color: "#f59e0b" }}>$89</span>
            </div>

            {payStep === "idle" && (
              <>
                <SchedulePicker
                  mode={gSchedMode} setMode={setGSchedMode}
                  date={gSchedDate} setDate={setGSchedDate}
                  time={gSchedTime} setTime={setGSchedTime}
                  accent="#f59e0b"
                />

                <div style={c.sectionLabel}>YOUR DETAILS</div>
                <div style={c.fieldGroup}>
                  <label style={c.label}>Full Name</label>
                  <input style={c.input} placeholder="John Smith" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>
                <div style={c.fieldGroup}>
                  <label style={c.label}>Phone Number</label>
                  <input style={c.input} type="tel" placeholder="(561) 000-0000" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                  <div style={{ fontSize: 11, color: "#555", marginTop: 5 }}>Your tech will call this number on the way.</div>
                </div>
                <div style={c.fieldGroup}>
                  <label style={c.label}>Where Are You?</label>
                  <AddressFields value={form.address} onChange={val => setForm({ ...form, address: val })} />
                </div>
                <div style={c.fieldGroup}>
                  <label style={c.label}>Anything We Should Know? (optional)</label>
                  <input style={c.input} placeholder="e.g. rear passenger tire, in the Publix lot" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
                </div>
              </>
            )}

            <div style={c.confirmCard}>
              <div style={{ ...c.confirmRow, fontSize: 13 }}>
                <span style={{ color: "#666" }}>{svc.name} — Non-Member Rate</span>
                <span>${GUEST_RATE}.00</span>
              </div>
              <div style={{ ...c.confirmRow, fontSize: 13 }}>
                <span style={{ color: "#666" }}>Tax (7%)</span>
                <span>${tax.toFixed(2)}</span>
              </div>
              <div style={c.divider} />
              <div style={{ ...c.confirmRow, fontSize: 17, fontWeight: 900 }}>
                <span>Total</span>
                <span style={{ color: "#22c55e" }}>${total.toFixed(2)}</span>
              </div>
            </div>

            {error && <div style={c.gpsError}>⚠️ {error}</div>}

            {payStep === "card" ? (
              <>
                <div style={{ fontSize: 10, letterSpacing: 3, color: "#666", fontWeight: 700, marginBottom: 9 }}>PAYMENT INFO</div>
                <div ref={cardElRef} style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 8, padding: "14px 16px", marginBottom: 14 }} />
                <button style={c.btnPrimary} onClick={confirmPayment}>
                  🔒 Pay ${total.toFixed(2)} & Dispatch →
                </button>
                <button style={c.btnSecondary} onClick={() => setPayStep("idle")}>← Cancel</button>
              </>
            ) : payStep === "verifying" ? (
              <div style={{ textAlign: "center", padding: 24, color: "#22c55e" }}>
                <div style={{ fontSize: 34, marginBottom: 8 }}>🔒</div>
                Verifying payment...
              </div>
            ) : payStep === "loading" ? (
              <div style={{ textAlign: "center", padding: 24, color: "#666" }}>
                <div style={{ fontSize: 34, marginBottom: 8 }}>💳</div>
                Loading secure payment...
              </div>
            ) : (
              <>
                <button style={{ ...c.btnPrimary, opacity: canProceed ? 1 : 0.4 }} disabled={!canProceed} onClick={startPayment}>
                  {canProceed ? `Enter Payment — $${total.toFixed(2)} →` : "Fill in your details above"}
                </button>
                <div style={c.payNotice}>🔒 Secure payment by Stripe. Tech dispatched immediately after payment.</div>
              </>
            )}
          </>
        )}
      </div>
      <div style={c.footer}>Based in South Florida · Serving nationwide</div>
    </div>
  );
}

// ============================================================
// SPONSORSHIP — LOCAL BUSINESS ADVERTISING
// ============================================================
function SponsorPage({ onBack }) {
  const [form, setForm] = useState({ business: "", contact: "", email: "", phone: "", city: "", type: "", tier: "", notes: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const BUSINESS_TYPES = [
    "Restaurants & diners","Fast food & takeout","Coffee shops & cafes","Convenience stores",
    "Grocery stores","Food trucks","Tire shops","Auto body shops","Car washes",
    "Tint & window shops","Auto glass","Mechanics & garages","Gas stations",
    "Insurance agents","Urgent care","Pharmacies","Nail & hair salons","Barber shops",
    "Liquor stores","Smoke & vape shops","Hotels & motels","Parking garages",
    "Churches & community orgs","Lawyers & bail bonds","Towing companies",
    "Gyms & fitness","Laundromats","Cell phone repair","Other",
  ];

  async function submit() {
    if (!form.business || !form.contact || !form.phone || !form.city || !form.tier) {
      return alert("Please fill in business name, contact, phone, city, and pick a plan.");
    }
    setBusy(true);

    // 1. Save the lead so it's never lost
    await dbPush("sponsorLeads", { ...form, createdAt: Date.now(), status: "new" }, { guest: !getAuthToken() });

    // 2. Email it to superfastmobiletires@gmail.com
    if (SPONSOR_FORM_ENDPOINT && !SPONSOR_FORM_ENDPOINT.includes("YOUR_FORM_ID")) {
      await fetch(SPONSOR_FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: `New Sponsor Inquiry — ${form.business} (${form.tier})`,
          _replyto: form.email || "",
          "Business Name": form.business,
          "Business Type": form.type || "Not specified",
          "City": form.city,
          "Contact Person": form.contact,
          "Phone": form.phone,
          "Email": form.email || "Not provided",
          "Plan Interested In": form.tier,
          "Notes": form.notes || "None",
        }),
      }).catch(() => {});
    }

    // 3. Buzz the phone
    await fetch(`${WORKER_URL}/notify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: "🏪 New Sponsor Inquiry — " + form.tier,
        message: `${form.business}\n${form.type || "Type not given"}\n📍 ${form.city}\n\nContact: ${form.contact}\n📞 ${form.phone}\n📧 ${form.email || "No email"}\n\nPlan: ${form.tier}${form.notes ? `\n\nNotes: ${form.notes}` : ""}`,
      }),
    }).catch(() => {});

    setBusy(false);
    setSent(true);
  }

  return (
    <div style={c.root}>
      <header style={c.header}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div><div style={c.logoTop}>SUPER FAST</div><div style={c.logoBot}>SPONSORS</div></div>
        </div>
        <button style={c.back} onClick={onBack}>← Back</button>
      </header>

      <div style={c.page}>
        {sent ? (
          <div style={{ textAlign: "center", padding: "50px 0" }}>
            <div style={{ fontSize: 56, marginBottom: 14 }}>🎉</div>
            <div style={{ fontSize: 21, fontWeight: 900, color: "#22c55e", marginBottom: 8 }}>Thanks — we got it!</div>
            <div style={{ fontSize: 13.5, color: "#888", lineHeight: 1.6, marginBottom: 22 }}>
              We'll reach out to <strong style={{ color: "#fff" }}>{form.phone}</strong> within one business day to get your pin live.
            </div>
            <a href="tel:5612558506" style={{ display: "block", background: "#E63329", color: "#fff", borderRadius: 10, padding: "14px", fontWeight: 800, fontSize: 14, textDecoration: "none", marginBottom: 10 }}>
              📞 Or call us now — (561) 255-8506
            </a>
            <button style={c.btnSecondary} onClick={onBack}>Back to app</button>
          </div>
        ) : (
          <>
            <div style={{ background: "linear-gradient(135deg,#1a1400 0%,#0f0f0f 100%)", border: "1px solid #f59e0b", borderRadius: 14, padding: 20, marginBottom: 20 }}>
              <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#f59e0b", fontWeight: 800, marginBottom: 10 }}>✨ LOCAL PARTNER PROGRAM</div>
              <div style={{ fontSize: 25, fontWeight: 900, color: "#fff", lineHeight: 1.1, marginBottom: 12 }}>
                Reach Real People.<br />Right Now. Right<br />Where They're Stuck.
              </div>
              <div style={{ fontSize: 13, color: "#999", lineHeight: 1.65 }}>
                Super Fast puts your business on the live map — in front of drivers who are physically near you, phone in hand, with nowhere to go for 30 minutes to 3 hours. That's an advertising moment that doesn't exist anywhere else.
              </div>
            </div>

            {/* Why */}
            <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#f59e0b", fontWeight: 800, marginBottom: 12 }}>WHY IT WORKS</div>
            {[
              ["📍", "Hyper-local", "Your pin only shows to users near your business. Not citywide noise — real proximity."],
              ["🕐", "Captive & idle", "Stranded drivers are on their phone, waiting. Nowhere to go. Nothing to scroll past."],
              ["⚡", "Open hours match", "Your pin is active during your business hours, reaching people when you're actually open."],
              ["👥", "High intent", "Someone stuck a block from your restaurant hasn't eaten in hours. That's your customer."],
            ].map(([ic, t, d]) => (
              <div key={t} style={{ background: "#161616", border: "1px solid #222", borderRadius: 11, padding: 15, marginBottom: 9 }}>
                <div style={{ fontSize: 20, marginBottom: 7 }}>{ic}</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", marginBottom: 4 }}>{t}</div>
                <div style={{ fontSize: 12, color: "#888", lineHeight: 1.55 }}>{d}</div>
              </div>
            ))}

            {/* Pricing */}
            <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#f59e0b", fontWeight: 800, margin: "24px 0 6px" }}>PRICING</div>
            <div style={{ fontSize: 12.5, color: "#666", marginBottom: 14 }}>Two tiers. Flat rate. No bidding.</div>

            {[
              { name: "STARTER", price: "$19", per: "/month", note: "Month-to-month · cancel any time", popular: false,
                feats: [
                  "Branded sponsored map pin (your logo as the pin icon)",
                  "City-only targeting — your pin shows in your city, not the next one over",
                  "Visible during your business hours",
                  "Business profile page with info, photos, and links",
                  "Month-to-month — cancel any time",
                ]},
              { name: "PREMIUM", price: "$2,000", per: "/year", note: "≈ $167/month", popular: true,
                feats: [
                  "Everything in Starter, plus:",
                  "Full SEO landing page written for your services",
                  "1 co-branded social media post per month",
                  "Map banner ad — \u201cAdvertised by: [Your Logo]\u201d every session",
                  "Maximum brand presence on every map view",
                  "Priority pin placement",
                ]},
            ].map(tier => (
              <div key={tier.name} style={{ position: "relative", background: "#161616", border: `2px solid ${tier.popular ? "#f59e0b" : "#222"}`, borderRadius: 14, padding: 20, marginBottom: 14, marginTop: tier.popular ? 16 : 0 }}>
                {tier.popular && (
                  <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: "#f59e0b", color: "#000", fontSize: 10, fontWeight: 900, letterSpacing: 1.5, padding: "4px 14px", borderRadius: 12, whiteSpace: "nowrap" }}>
                    ⭐ MOST POPULAR
                  </div>
                )}
                <div style={{ fontSize: 11, letterSpacing: 2, color: tier.popular ? "#f59e0b" : "#888", fontWeight: 800, marginBottom: 8, marginTop: tier.popular ? 4 : 0 }}>{tier.name}</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
                  <span style={{ fontSize: 34, fontWeight: 900, color: "#fff" }}>{tier.price}</span>
                  <span style={{ fontSize: 14, color: "#888" }}>{tier.per}</span>
                </div>
                <div style={{ fontSize: 11.5, color: "#666", marginBottom: 14 }}>{tier.note}</div>
                {tier.feats.map(f => (
                  <div key={f} style={{ display: "flex", gap: 9, marginBottom: 8, alignItems: "flex-start" }}>
                    <span style={{ color: "#22c55e", fontSize: 13, flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.5 }}>{f}</span>
                  </div>
                ))}
                <button style={{ width: "100%", background: tier.popular ? "#f59e0b" : "none", border: tier.popular ? "none" : "1px solid #f59e0b", color: tier.popular ? "#000" : "#f59e0b", padding: "13px", borderRadius: 9, fontWeight: 900, fontSize: 13.5, cursor: "pointer", marginTop: 10 }}
                  onClick={() => { setForm(f => ({ ...f, tier: tier.name === "STARTER" ? "Starter — $19/mo" : "Premium — $2,000/yr" })); document.getElementById("sponsor-form")?.scrollIntoView({ behavior: "smooth" }); }}>
                  {tier.name === "STARTER" ? "Start with Starter" : "Go Premium"}
                </button>
              </div>
            ))}

            {/* Who */}
            <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#f59e0b", fontWeight: 800, margin: "24px 0 12px" }}>WHO SHOULD ADVERTISE</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {BUSINESS_TYPES.slice(0, -1).map(t => (
                <span key={t} style={{ fontSize: 11.5, background: "#161616", border: "1px solid #2a2a2a", color: "#999", padding: "6px 11px", borderRadius: 13 }}>{t}</span>
              ))}
            </div>
            <div style={{ fontSize: 12, color: "#666", lineHeight: 1.6, marginBottom: 26 }}>
              If your business is within blocks of where someone might get stranded — and you want that person to know you exist — you belong on this map.
            </div>

            {/* Form */}
            <div id="sponsor-form" style={{ background: "#161616", border: "1px solid #f59e0b", borderRadius: 14, padding: 18 }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: "#fff", marginBottom: 4 }}>Get on the map</div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>Tell us about your business and we'll call you within one business day.</div>

              <div style={c.fieldGroup}><label style={c.label}>Business Name</label>
                <input style={c.input} placeholder="e.g. Tony's Pizza" value={form.business} onChange={e => setForm({ ...form, business: e.target.value })} /></div>
              <div style={c.fieldGroup}><label style={c.label}>Business Type</label>
                <select style={c.input} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                  <option value="">Select...</option>
                  {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select></div>
              <div style={c.fieldGroup}><label style={c.label}>City</label>
                <input style={c.input} placeholder="e.g. West Palm Beach, FL" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
              <div style={c.fieldGroup}><label style={c.label}>Your Name</label>
                <input style={c.input} placeholder="Contact person" value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} /></div>
              <div style={c.fieldGroup}><label style={c.label}>Phone</label>
                <input style={c.input} type="tel" placeholder="(561) 000-0000" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
              <div style={c.fieldGroup}><label style={c.label}>Email (optional)</label>
                <input style={c.input} type="email" placeholder="you@business.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div style={c.fieldGroup}><label style={c.label}>Plan You Want</label>
                <select style={c.input} value={form.tier} onChange={e => setForm({ ...form, tier: e.target.value })}>
                  <option value="">Select a plan...</option>
                  <option>Starter — $19/mo</option>
                  <option>Premium — $2,000/yr</option>
                  <option>Not sure — call me</option>
                </select></div>

              <div style={c.fieldGroup}><label style={c.label}>Anything Else? (optional)</label>
                <textarea style={{ ...c.input, minHeight: 90, resize: "vertical" }}
                  placeholder="Tell us about your business, your hours, questions about the plans, or anything you want us to know."
                  value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>

              <button style={{ ...c.btnPrimary, background: "#f59e0b", color: "#000" }} onClick={submit} disabled={busy}>
                {busy ? "Sending..." : "Send My Info →"}
              </button>
              <div style={{ fontSize: 11.5, color: "#666", textAlign: "center", marginTop: 12 }}>
                Prefer to talk? <a href="tel:5612558506" style={{ color: "#f59e0b", fontWeight: 700, textDecoration: "none" }}>Call (561) 255-8506</a>
              </div>
            </div>
          </>
        )}
      </div>
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC</div>
    </div>
  );
}

// ============================================================
// LEGAL — TERMS OF SERVICE & PRIVACY POLICY
// ============================================================
function LegalPage({ doc, onBack, audience }) {
  const TERMS = [
    ["1. Who We Are", [
      "Super Fast Mobile Tires LLC (\u201cSuper Fast,\u201d \u201cwe,\u201d \u201cus\u201d) is a Florida limited liability company based in Palm Beach County, Florida, operating a platform that connects customers needing roadside assistance with independent service providers.",
      "By creating an account, booking a service, or providing services through our platform, you agree to these Terms.",
    ]],
    ["2. What We Are and Are Not", [
      "Super Fast operates a dispatch platform. Service providers on this platform are independent contractors, not our employees.",
      "We do not directly perform roadside repairs. We coordinate, dispatch, and process payment.",
      "We do not guarantee arrival times. Estimates are estimates, affected by traffic, weather, and provider availability.",
    ]],
    ["3. Membership and Pricing", [
      "Membership is $10 per month, billed automatically until cancelled. Cancel any time from your account; cancellation takes effect at the end of the current billing period. We do not prorate partial months.",
      "Members pay a $60 flat rate for covered services. Non-members pay $89 per service call with no membership required.",
      "Covered flat-rate services: tire change (spare mount), tire plug, jump start, lockout, fuel delivery, and wheel lock removal.",
      "Tow dispatch, mobile mechanic, used tires, battery replacement, and rim repair are quoted separately.",
      "All prices are subject to 7% Florida sales tax where applicable.",
    ]],
    ["4. Payment", [
      "Payments are processed by Stripe. We never see or store your full card number.",
      "Service calls are charged at the time of booking. If we cannot complete the service, contact us and we will review a refund.",
      "Tips are optional, charged separately, and passed to your provider in full. We take no portion of tips.",
      "Chargebacks filed without first contacting us may result in account suspension.",
    ]],
    ["5. Cancellations", [
      "Cancel a job before a provider is dispatched for a full refund.",
      "Once a provider is en route, cancellation may forfeit the service fee, as the provider has already incurred time and fuel.",
      "If nobody is present at the location when the provider arrives, the fee is not refundable.",
    ]],
    ["6. Your Responsibilities", [
      "Provide accurate location and contact information.",
      "Be present, or arrange for someone to be present, at the vehicle.",
      "Ensure the vehicle is in a location where work can be performed safely and legally.",
      "You represent that you own the vehicle or are authorized to request service on it.",
    ]],
    ["7. Used Tires and Tire Plugs \u2014 Important", [
      "USED TIRES: Used tires may have prior wear, road damage, or reduced tread life. We make no warranty, express or implied, as to condition, longevity, or fitness. You assume all risk.",
      "TIRE PLUGS: A tire plug is a temporary repair. Plugs can fail without warning. Plugs are not appropriate for sidewall damage, large punctures, or high-speed or long-distance driving. You should have the tire professionally inspected or replaced as soon as possible.",
      "By accepting either service you release Super Fast Mobile Tires LLC, its owners, employees, and independent providers from all claims arising from tire failure, property damage, injury, or loss related to that tire.",
    ]],
    ["8. Limitation of Liability", [
      "To the fullest extent permitted by Florida law, our total liability for any claim is limited to the amount you paid for the service in question.",
      "We are not liable for indirect, incidental, or consequential damages, including lost wages, missed appointments, towing costs, or vehicle damage not directly caused by our provider's proven negligence.",
      "Providers carry their own insurance. Claims of provider negligence should be raised with us promptly so we can facilitate resolution.",
    ]],
    ["9. Photos and Records", [
      "Providers photograph vehicles before and after service. These protect both you and the provider by documenting condition.",
      "We retain these photos, your signature, and job records as business records.",
    ]],
    ["10. Account Suspension", [
      "We may suspend or terminate an account for abusive behavior toward providers or staff, fraudulent payment activity, repeated no-shows, or misuse of the platform.",
    ]],
    ["11. Disputes", [
      "These Terms are governed by the laws of the State of Florida. Any dispute shall be brought in the state or federal courts located in Palm Beach County, Florida.",
      "Before filing anything, contact us at (561) 255-8506. Most issues are resolved with a phone call.",
    ]],
    ["12. Changes", [
      "We may update these Terms. Continued use after changes means you accept them. Material changes to pricing will be communicated before they take effect.",
    ]],
  ];

  const PROVIDER_TERMS = [
    ["1. Independent Contractor Status", [
      "You are an independent contractor, not an employee of Super Fast Mobile Tires LLC.",
      "You are responsible for your own taxes, insurance, licensing, equipment, vehicle, and business expenses.",
      "You control when you work, which services you offer, and which jobs you accept. We do not set your hours.",
      "Nothing in this agreement creates an employment, partnership, or joint venture relationship.",
    ]],
    ["2. Requirements", [
      "Valid driver's license and reliable vehicle.",
      "Your own tools appropriate for the services you choose to offer.",
      "Insurance appropriate to the work you perform.",
      "Identity verification through Stripe, including government ID and tax information.",
      "You may not let anyone else use your account.",
    ]],
    ["3. Pay", [
      "$40 per completed call. $50 for calls created between 10:00 PM and 6:00 AM.",
      "Used tire jobs: $40 labor plus $60 per tire reimbursed.",
      "You keep 100% of every tip. We take nothing from tips.",
      "Pay period runs Thursday through Wednesday. Payouts are issued Friday by direct deposit through Stripe.",
      "Your bank must be verified through Stripe Connect before we can pay you.",
    ]],
    ["4. Location Tracking", [
      "While you are online, we collect your device location so customers can track your arrival and so dispatch can assign nearby jobs.",
      "Tracking stops when you go offline or complete a job.",
      "You consent to this collection as a condition of accepting jobs. You may withdraw consent by going offline or revoking permission in your device settings.",
    ]],
    ["5. Conduct", [
      "Complete the job you accepted. Do not solicit payment directly from customers or take work off the platform.",
      "Never work under a vehicle supported only by a jack.",
      "Wear safety equipment and set out flares or triangles on roadside jobs.",
      "Do not work while impaired.",
      "Do not mark a job complete that you did not perform.",
    ]],
    ["6. Removal", [
      "We may deactivate your account for violating these terms, falling below a 4.0 rating, fraudulent activity, or conduct that endangers customers.",
    ]],
    ["7. Liability", [
      "You are responsible for damage you cause through negligence in performing a service.",
      "You agree to indemnify Super Fast Mobile Tires LLC against claims arising from your work, your vehicle, or your conduct.",
    ]],
    ["8. Governing Law", [
      "This agreement is governed by Florida law, with venue in Palm Beach County, Florida.",
    ]],
  ];

  const PRIVACY = [
    ["What We Collect", [
      "Account information: name, email address, phone number, and home address.",
      "Service information: job locations, vehicle details, service history, and photos taken before and after work.",
      "Location data: your device location when you request service, and continuous location from providers while they are online.",
      "Payment information: processed and stored by Stripe. We receive only the last four digits of your card and transaction records \u2014 never full card numbers.",
      "Identity information (providers only): government ID and tax details, collected and stored by Stripe, not by us.",
    ]],
    ["Why We Collect It", [
      "To dispatch a provider to your location.",
      "To let you track your provider's arrival.",
      "To process payment and issue receipts.",
      "To document vehicle condition before and after service, protecting both parties.",
      "To contact you about your job.",
      "To verify provider identity and pay providers.",
    ]],
    ["Who We Share It With", [
      "Your assigned provider receives your name, phone number, and service location \u2014 only for the job they are performing.",
      "Stripe processes payments and provider identity verification.",
      "Google Maps provides mapping and navigation.",
      "Cloudinary stores service photos.",
      "Firebase (Google) hosts our database.",
      "We do not sell your information. We do not share it with advertisers or data brokers.",
      "We may disclose information if required by law or valid legal process.",
    ]],
    ["Location Data", [
      "Customers: we use your location only to dispatch service and only while you have an active job.",
      "Providers: location is collected while you are online so customers can track arrival. Tracking stops when you go offline.",
      "You can revoke location permission at any time in your device settings, though this will limit the service.",
    ]],
    ["How Long We Keep It", [
      "Job records, photos, and signatures are retained as business and tax records.",
      "If you delete your account, we remove your profile, contact details, message history, and photos. Anonymized transaction records may be retained where required for accounting and tax law.",
    ]],
    ["Security", [
      "All data is transmitted over encrypted HTTPS connections.",
      "Our database is not directly accessible from browsers. Every request passes through an authenticated server that verifies your identity and returns only your own records.",
      "Card, bank, and identity data are handled by Stripe, a PCI-DSS Level 1 certified processor. We never store this data.",
      "No system is perfectly secure. If we become aware of a breach affecting your data, we will notify you.",
    ]],
    ["Your Rights", [
      "You can view and update your information in your profile at any time.",
      "You can delete your account and data from your profile screen.",
      "You can request a copy of your data by contacting us.",
      "You can opt out of non-essential communications.",
    ]],
    ["Children", [
      "Our service is not directed to anyone under 18. We do not knowingly collect information from minors.",
    ]],
    ["Contact", [
      "Questions about privacy: call (561) 255-8506 or email superfastmobiletires@gmail.com.",
    ]],
  ];

  const isTerms = doc === "terms";
  const content = isTerms ? (audience === "provider" ? PROVIDER_TERMS : TERMS) : PRIVACY;

  return (
    <div style={c.root}>
      <header style={{ ...c.header, ...(audience === "provider" ? { background: "#0a1525", borderBottom: "3px solid #3b82f6" } : {}) }}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div>
            <div style={c.logoTop}>SUPER FAST</div>
            <div style={{ ...c.logoBot, ...(audience === "provider" ? { color: "#3b82f6" } : {}) }}>
              {isTerms ? "TERMS" : "PRIVACY"}
            </div>
          </div>
        </div>
        <button style={c.back} onClick={onBack}>← Back</button>
      </header>

      <div style={c.page}>
        <div style={{ fontSize: 23, fontWeight: 900, color: "#fff", marginBottom: 6 }}>
          {isTerms ? (audience === "provider" ? "Provider Agreement" : "Terms of Service") : "Privacy Policy"}
        </div>
        <div style={{ fontSize: 11.5, color: "#666", marginBottom: 22 }}>
          Super Fast Mobile Tires LLC · Last updated August 2026
        </div>

        {content.map(([heading, lines]) => (
          <div key={heading} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 14.5, fontWeight: 800, color: audience === "provider" ? "#3b82f6" : "#E63329", marginBottom: 9 }}>
              {heading}
            </div>
            {lines.map((ln, i) => (
              <div key={i} style={{ fontSize: 12.5, color: "#aaa", lineHeight: 1.75, marginBottom: 8 }}>
                {ln}
              </div>
            ))}
          </div>
        ))}

        <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 16, marginTop: 10 }}>
          <div style={{ fontSize: 12.5, color: "#888", lineHeight: 1.65 }}>
            Questions about any of this? Call or text{" "}
            <a href="tel:5612558506" style={{ color: "#E63329", fontWeight: 700, textDecoration: "none" }}>(561) 255-8506</a>
            {" "}or email{" "}
            <a href="mailto:superfastmobiletires@gmail.com" style={{ color: "#E63329", fontWeight: 700, textDecoration: "none" }}>
              superfastmobiletires@gmail.com
            </a>.
          </div>
        </div>

        <button style={{ ...c.btnSecondary, marginTop: 18 }} onClick={onBack}>← Back</button>
      </div>
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC · Palm Beach County, FL</div>
    </div>
  );
}

// ============================================================
// B2B PARTNER PORTAL — businesses that dispatch jobs to us
// ============================================================
// On-scene upsells — discounted vs booking them separately
const ADDON_PRICE = 40;
const ADDON_PROVIDER_BONUS = 20;
const ADDONS = [
  { id: "wheellock", label: "🔐 Wheel Lock Removal", why: "Customer lost their wheel lock key" },
  { id: "plug",      label: "🔩 Tire Plug",          why: "Puncture found while on scene" },
  { id: "jump",      label: "🔋 Jump Start",         why: "Battery died too" },
  { id: "fuel",      label: "⛽ Fuel Delivery",       why: "Also out of gas" },
  { id: "spare",     label: "🛞 Spare Tire Mount",   why: "Second tire needs the spare" },
  { id: "lockout",   label: "🔑 Lockout Service",    why: "Keys locked in the vehicle" },
];

const PARTNER_RATE = 70;

// ── NETWORK DISPATCH — shop-to-shop jobs we broker for a flat fee ──
const NETWORK_FEE = 20;

// ── FLEET MAINTENANCE ──
const FLEET_MIN = 400;            // monthly minimum
const FLEET_PER_VEHICLE = 32;     // per vehicle above the break-even
const FLEET_BREAKEVEN = 13;       // 13 vehicles is where per-vehicle beats the minimum
const FLEET_PROVIDER_PAY = 150;   // flat per inspection visit, any vehicle count
const FLEET_SUB_LINK = "https://buy.stripe.com/14AeVc5f1g1EcXe2uOaZi0b";

function fleetMonthly(vehicles) {
  const n = Math.max(1, parseInt(vehicles, 10) || 1);
  return Math.max(FLEET_MIN, n * FLEET_PER_VEHICLE);
}

// Everything checked on a visit
const INSPECTION_SECTIONS = [
  { id: "tires", label: "🛞 Tires", items: [
    { id: "tread_lf", label: "Tread depth — LF", type: "depth" },
    { id: "tread_rf", label: "Tread depth — RF", type: "depth" },
    { id: "tread_lr", label: "Tread depth — LR", type: "depth" },
    { id: "tread_rr", label: "Tread depth — RR", type: "depth" },
    { id: "psi_lf", label: "Pressure — LF", type: "psi" },
    { id: "psi_rf", label: "Pressure — RF", type: "psi" },
    { id: "psi_lr", label: "Pressure — LR", type: "psi" },
    { id: "psi_rr", label: "Pressure — RR", type: "psi" },
    { id: "tire_damage", label: "Sidewall / uneven wear", type: "status" },
    { id: "spare", label: "Spare present & inflated", type: "status" },
  ]},
  { id: "battery", label: "🔋 Battery", items: [
    { id: "batt_volts", label: "Voltage", type: "number" },
    { id: "batt_load", label: "Load test result", type: "status" },
    { id: "batt_terminals", label: "Terminals & cables", type: "status" },
  ]},
  { id: "fluids", label: "💧 Fluids", items: [
    { id: "oil", label: "Engine oil", type: "status" },
    { id: "coolant", label: "Coolant", type: "status" },
    { id: "brake_fluid", label: "Brake fluid", type: "status" },
    { id: "power_steering", label: "Power steering", type: "status" },
    { id: "washer", label: "Washer fluid", type: "status" },
  ]},
  { id: "lights", label: "💡 Lights & Wipers", items: [
    { id: "headlights", label: "Headlights", type: "status" },
    { id: "taillights", label: "Tail & brake lights", type: "status" },
    { id: "turn_signals", label: "Turn signals", type: "status" },
    { id: "wipers", label: "Wiper blades", type: "status" },
  ]},
  { id: "brakes", label: "🛑 Brakes (visual only)", items: [
    { id: "brake_front", label: "Front pads — visual", type: "status" },
    { id: "brake_rear", label: "Rear pads — visual", type: "status" },
    { id: "brake_noise", label: "Noise / pulling reported", type: "status" },
  ]},
];

const STATUS_OPTIONS = [
  { id: "ok",   label: "OK",        color: "#22c55e" },
  { id: "soon", label: "Due Soon",  color: "#f59e0b" },
  { id: "now",  label: "Needs Now", color: "#E63329" },
  { id: "na",   label: "N/A",       color: "#555" },
];   // what Super Fast keeps on every brokered job

const NETWORK_STATUS = {
  posted:     { label: "POSTED",        color: "#f59e0b", bg: "#1a1400", desc: "Waiting on us to source a partner" },
  quoted:     { label: "QUOTE SENT",    color: "#3b82f6", bg: "#0a1525", desc: "Price sent — waiting on payment" },
  paid:       { label: "PAID",          color: "#22c55e", bg: "#0a1f0a", desc: "Ready to dispatch to a partner" },
  dispatched: { label: "DISPATCHED",    color: "#a855f7", bg: "#150a1f", desc: "Partner assigned and on the way" },
  collected:  { label: "CASH COLLECTED",color: "#22c55e", bg: "#0a1f0a", desc: "Partner has payment in hand" },
  completed:  { label: "COMPLETED",     color: "#666",    bg: "#151515", desc: "Job finished" },
  failed:     { label: "COLLECTION FAILED", color: "#E63329", bg: "#1a0a0a", desc: "Customer wouldn't pay" },
};
const PARTNER_SUB = 20;

// Create a $20/month Stripe Payment Link and paste it here
const PARTNER_SUB_LINK = "https://buy.stripe.com/bJe4gy22P4iW5uMfhAaZi09";

// Industry partners who send us work — no monthly fee
const FREE_PARTNER_TYPES = [
  "Roadside assistance company",
  "Mobile tire service",
  "Tire shop",
  "Towing company",
];
const isFreePartner = t => FREE_PARTNER_TYPES.includes(t);

const PARTNER_SERVICES = [
  { id: "tire_spare",  label: "🛞 Tire Change — Customer Has Spare", quote: false },
  { id: "plug",        label: "🔩 Tire Plug",                        quote: false },
  { id: "jump",        label: "🔋 Jump Start",                       quote: false },
  { id: "lockout",     label: "🔑 Lockout Service",                  quote: false },
  { id: "fuel",        label: "⛽ Fuel Delivery",                     quote: false },
  { id: "wheellock",   label: "🔐 Wheel Lock Removal",               quote: false },
  { id: "tire_install",label: "🔧 Tire Install — They Supply Tire",   quote: false, perTire: 25 },
  { id: "used_tire",   label: "🛞 Used Tire (we supply)",            quote: true  },
  { id: "new_tire",    label: "✨ New Tire (we supply)",             quote: true  },
  { id: "battery",     label: "🔋 Battery Replacement",              quote: true  },
  { id: "rim",         label: "⚙️ Rim Damage Repair",                quote: true  },
  { id: "mechanic",    label: "🔧 Mobile Mechanic",                  quote: true  },
  { id: "tow",         label: "🚛 Tow Truck",                        quote: true  },
];

function PartnerSubGate({ authUser, onSignOut, onSubscribed }) {
  const profile = authUser?.profile || {};
  const [step, setStep] = useState("intro");
  const [error, setError] = useState("");
  const email = profile.email || authUser.email || "";

  function openLink() {
    setError("");
    const url = `${PARTNER_SUB_LINK}?prefilled_email=${encodeURIComponent(email)}&client_reference_id=${encodeURIComponent(authUser.uid)}`;
    window.open(url, "_blank");
    setStep("waiting");
  }

  async function verify(silent) {
    if (!silent) setStep("checking");
    try {
      const res = await fetch(`${WORKER_URL}/verify-membership`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, uid: authUser.uid }),
      });
      const d = await res.json();
      if (d.active) {
        await dbPatch(`users/${authUser.uid}`, {
          subscribed: true, subscriptionId: d.subscriptionId,
          stripeCustomerId: d.customerId, subscribedAt: Date.now(),
        });
        await fetch(`${WORKER_URL}/notify`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: "💳 Business Subscribed",
            message: `${profile.business} is now on the $${PARTNER_SUB}/month plan.\n📞 ${profile.phone}`,
          }),
        }).catch(() => {});
        setStep("done");
        setTimeout(onSubscribed, 1500);
        return;
      }
      if (!silent) { setError("No active subscription found yet. If you just paid, give it a few seconds and tap again."); setStep("waiting"); }
    } catch (e) {
      if (!silent) { setError(e.message); setStep("waiting"); }
    }
  }

  useEffect(() => { verify(true); }, []);
  useEffect(() => {
    if (step !== "waiting") return;
    const iv = setInterval(() => verify(true), 5000);
    return () => clearInterval(iv);
  }, [step]);

  return (
    <div style={c.root}>
      <header style={{ ...c.header, background: "#0f1a0f", borderBottom: "3px solid #22c55e" }}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div>
            <div style={{ ...c.logoTop, fontSize: 13 }}>{profile.business || "BUSINESS"}</div>
            <div style={{ ...c.logoBot, color: "#22c55e", fontSize: 10 }}>DISPATCH PORTAL</div>
          </div>
        </div>
        <button style={{ ...c.back, color: "#E63329", borderColor: "#E63329" }} onClick={onSignOut}>Sign Out</button>
      </header>

      <div style={c.page}>
        {step === "done" ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ fontSize: 56, marginBottom: 14 }}>🎉</div>
            <div style={{ fontSize: 21, fontWeight: 900, color: "#22c55e", marginBottom: 8 }}>You're all set!</div>
            <div style={{ fontSize: 13, color: "#888" }}>Loading your dispatch board...</div>
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center", marginTop: 10, marginBottom: 20 }}>
              <div style={{ fontSize: 42, marginBottom: 10 }}>💳</div>
              <div style={{ fontSize: 21, fontWeight: 900, color: "#fff", marginBottom: 6 }}>Activate Your Account</div>
              <div style={{ fontSize: 13, color: "#777", lineHeight: 1.6 }}>
                You're approved, {profile.contactName || "there"} — one last step before you can dispatch.
              </div>
            </div>

            <div style={{ background: "linear-gradient(135deg,#0a1f0a 0%,#0f0f0f 100%)", border: "2px solid #22c55e", borderRadius: 14, padding: 20, marginBottom: 18 }}>
              <div style={{ fontSize: 10, letterSpacing: 3, color: "#22c55e", fontWeight: 800, marginBottom: 10 }}>BUSINESS ACCOUNT</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 14 }}>
                <span style={{ fontSize: 40, fontWeight: 900, color: "#fff" }}>${PARTNER_SUB}</span>
                <span style={{ fontSize: 15, color: "#888" }}>/month</span>
              </div>
              {[
                `Dispatch jobs to our network any time`,
                `$${PARTNER_RATE} flat per service call`,
                "Priority response for your customers",
                "Track every job you've sent",
                "Cancel any time — no contract",
              ].map(x => (
                <div key={x} style={{ display: "flex", gap: 9, marginBottom: 8 }}>
                  <span style={{ color: "#22c55e", fontSize: 13 }}>✓</span>
                  <span style={{ fontSize: 12.5, color: "#ccc", lineHeight: 1.45 }}>{x}</span>
                </div>
              ))}
            </div>

            {error && <div style={c.gpsError}>⚠️ {error}</div>}

            {step === "waiting" ? (
              <>
                <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 12, padding: 16, marginBottom: 14 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: "#3b82f6", marginBottom: 6 }}>💳 Finish in the Stripe tab</div>
                  <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.6 }}>
                    Complete payment there, then come back — we'll activate your account automatically.
                  </div>
                </div>
                <button style={{ ...c.btnPrimary, background: "#22c55e", color: "#000" }} onClick={() => verify(false)}>
                  ✅ I've Paid — Activate My Account
                </button>
                <button style={c.btnSecondary} onClick={openLink}>🔄 Reopen Checkout</button>
              </>
            ) : step === "checking" ? (
              <div style={{ textAlign: "center", padding: 24, color: "#22c55e" }}>
                <div style={{ fontSize: 34, marginBottom: 8 }}>🔒</div>
                Checking your subscription...
              </div>
            ) : (
              <button style={{ ...c.btnPrimary, background: "#22c55e", color: "#000" }} onClick={openLink}>
                🔒 Subscribe — ${PARTNER_SUB}/month →
              </button>
            )}

            <div style={{ fontSize: 11, color: "#555", textAlign: "center", marginTop: 16, lineHeight: 1.6 }}>
              Service calls are billed separately at ${PARTNER_RATE} each.<br />
              Questions? <a href="tel:5612558506" style={{ color: "#22c55e" }}>(561) 255-8506</a>
            </div>
          </>
        )}
      </div>
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC</div>
    </div>
  );
}

// ============================================================
// NETWORK OVERFLOW — a shop posts a job they can't run
// ============================================================
function NetworkPost({ authUser, profile }) {
  const blank = {
    service: "", tireSize: "", tireQty: "1",
    customer: "", phone: "", address: "", city: "", state: profile.state || "", zip: "",
    vehicle: "", note: "", urgency: "asap",
  };
  const [f, setF] = useState(blank);
  const [posts, setPosts] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState("");
  const [payKey, setPayKey] = useState(null);
  const [payStep, setPayStep] = useState("idle");
  const payStripeRef = useRef(null);
  const payCardRef = useRef(null);

  const NET_SERVICES = [
    "Used Tire — we supply", "New Tire — we supply", "Tire Install — customer has tire",
    "Spare Tire Mount", "Tire Plug", "Jump Start", "Lockout", "Fuel Delivery",
    "Battery Replacement", "Wheel Lock Removal", "Tow", "Mobile Mechanic",
  ];

  async function load() {
    const all = await dbGet("networkJobs");
    if (!Array.isArray(all)) return;
    setPosts(all.filter(x => x.postedBy === authUser.uid).sort((a, b) => b.createdAt - a.createdAt));
  }
  useEffect(() => {
    load();
    const iv = setInterval(() => { if (payStep === "idle") load(); }, 12000);
    return () => clearInterval(iv);
  }, [payStep]);

  const canPost = f.service && f.customer && f.phone && f.address && f.city && f.state;

  async function post() {
    if (!canPost) return setErr("Service, customer name, phone, address, city and state are required.");
    setErr("");
    setSending(true);
    const id = "NET-" + genId();
    const full = `${f.address}, ${f.city}, ${f.state}${f.zip ? " " + f.zip : ""}`;

    await dbPush("networkJobs", {
      id,
      service: f.service,
      tireSize: f.tireSize.trim() || null,
      tireQty: f.tireQty || "1",
      customer: f.customer.trim(),
      phone: f.phone.trim(),
      address: full,
      vehicle: f.vehicle.trim() || null,
      note: f.note.trim() || null,
      urgency: f.urgency,
      netStatus: "posted",
      postedBy: authUser.uid,
      postedByBusiness: profile.business || "",
      postedByPhone: profile.phone || "",
      postedByContact: profile.contactName || "",
      createdAt: Date.now(),
    });

    await fetch(`${WORKER_URL}/notify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: `🔄 OVERFLOW JOB — ${profile.business || "Partner"}`,
        message: `${f.service}${f.tireSize ? ` (${f.tireSize} ×${f.tireQty})` : ""}\n${f.customer} · ${f.phone}\n📍 ${full}\n${f.vehicle ? `🚗 ${f.vehicle}\n` : ""}${f.urgency === "asap" ? "⚡ ASAP" : "📅 Scheduled"}\n${f.note ? `📝 ${f.note}\n` : ""}\n${id}\n\n➡️ Source a partner and send them a quote.`,
      }),
    }).catch(() => {});

    setSending(false);
    setSent(id);
    setF({ ...blank, state: profile.state || "" });
    load();
  }

  async function startPay(job) {
    setPayKey(job._key);
    setPayStep("loading");
    try {
      const stripe = await loadStripe();
      payStripeRef.current = stripe;
      const res = await fetch(`${WORKER_URL}/create-payment-intent`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: NETWORK_FEE, jobId: job.id,
          customerName: profile.business || "Business",
          service: `Dispatch fee — ${job.service}`,
          email: profile.email || null,
        }),
      });
      const d = await res.json();
      if (d.error) throw new Error(d.error);
      if (!d.clientSecret) throw new Error("No payment session returned.");
      setPayStep("card");
      let tries = 0;
      const mount = () => {
        const node = document.getElementById("net-card-mount");
        if (!node) {
          if (++tries > 60) { setPayStep("idle"); setPayKey(null); return; }
          return setTimeout(mount, 50);
        }
        node.innerHTML = "";
        const el = stripe.elements();
        const card = el.create("card", {
          style: { base: { color: "#fff", fontSize: "16px", "::placeholder": { color: "#666" } }, invalid: { color: "#ff6b6b" } },
        });
        card.mount(node);
        payCardRef.current = { card, clientSecret: d.clientSecret };
      };
      mount();
    } catch (e) {
      alert("Could not start payment: " + e.message);
      setPayStep("idle"); setPayKey(null);
    }
  }

  async function confirmPay(job) {
    if (!payStripeRef.current || !payCardRef.current?.card) return;
    setPayStep("verifying");
    try {
      const { error, paymentIntent } = await payStripeRef.current.confirmCardPayment(
        payCardRef.current.clientSecret,
        { payment_method: { card: payCardRef.current.card, billing_details: { name: profile.business || "Business" } } }
      );
      if (error) { alert(error.message); setPayStep("card"); return; }
      if (paymentIntent.status !== "succeeded") { alert("Payment not completed."); setPayStep("card"); return; }
      await dbPatch(`networkJobs/${job._key}`, {
        netStatus: "paid", paidAt: Date.now(),
        paymentIntentId: paymentIntent.id, amountPaid: NETWORK_FEE,
      });
      await fetch(`${WORKER_URL}/notify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: `✅ Dispatch Fee Paid — $${NETWORK_FEE}`,
          message: `${profile.business} paid the $${NETWORK_FEE} dispatch fee.\n${job.service}\nPartner collects $${(job.partnerPrice || 0).toFixed(2)} on arrival.\n${job.id}\n\n➡️ Dispatch it now.`,
        }),
      }).catch(() => {});
      setPayStep("idle"); setPayKey(null);
      load();
      alert("✅ Paid. We're dispatching your partner now.");
    } catch (e) {
      alert(e.message);
      setPayStep("card");
    }
  }

  if (sent) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{ fontSize: 54, marginBottom: 14 }}>🔄</div>
        <div style={{ fontSize: 21, fontWeight: 900, color: "#a855f7", marginBottom: 8 }}>Posted to the Network</div>
        <div style={{ fontSize: 13.5, color: "#aaa", lineHeight: 1.7, marginBottom: 8 }}>
          We're sourcing a partner now. You'll get a quote back here — usually within minutes.
          Nothing is charged until you accept the price.
        </div>
        <div style={{ fontSize: 13, color: "#fff", fontWeight: 800, marginBottom: 20 }}>{sent}</div>
        <button style={{ ...c.btnPrimary, background: "#a855f7" }} onClick={() => setSent(null)}>
          Post Another
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Explainer */}
      <div style={{ background: "linear-gradient(135deg,#150a1f 0%,#0f0f0f 100%)", border: "1px solid #a855f7", borderRadius: 12, padding: 16, marginBottom: 18 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, color: "#a855f7", fontWeight: 800, marginBottom: 7 }}>🔄 OVERFLOW DISPATCH</div>
        <div style={{ fontSize: 16, fontWeight: 900, color: "#fff", marginBottom: 7 }}>Can't take a job? Don't turn it down.</div>
        <div style={{ fontSize: 12.5, color: "#999", lineHeight: 1.65, marginBottom: 12 }}>
          Post it here and we'll source a partner from the network. We come back with a price, you approve it, and we dispatch. You keep the customer relationship and whatever you charge them.
        </div>
        <div style={{ background: "#0f0f0f", borderRadius: 8, padding: 11, fontSize: 12, color: "#bbb", lineHeight: 1.7 }}>
          <strong style={{ color: "#a855f7" }}>How the money works:</strong><br />
          You pay us a flat <strong style={{ color: "#22c55e" }}>${NETWORK_FEE} dispatch fee</strong> — that's it.<br />
          The partner collects their price directly from your customer on arrival.<br />
          You charge your customer whatever you want. The spread is yours.
        </div>
      </div>

      {/* My posted jobs */}
      {posts.length > 0 && (
        <>
          <div style={{ fontSize: 10, letterSpacing: 2, color: "#a855f7", fontWeight: 800, marginBottom: 10 }}>MY OVERFLOW JOBS</div>
          {posts.map(job => {
            const meta = NETWORK_STATUS[job.netStatus] || NETWORK_STATUS.posted;
            return (
              <div key={job._key} style={{ background: "#161616", border: `1px solid ${meta.color}`, borderRadius: 11, padding: 14, marginBottom: 11 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 7 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 800, color: "#fff" }}>{job.service}</div>
                    <div style={{ fontSize: 11.5, color: "#888", marginTop: 2 }}>{job.customer} · {job.phone}</div>
                  </div>
                  <span style={{ fontSize: 9.5, fontWeight: 900, padding: "3px 9px", borderRadius: 10, background: meta.bg, color: meta.color, whiteSpace: "nowrap" }}>
                    {meta.label}
                  </span>
                </div>
                <div style={{ fontSize: 11.5, color: "#777", marginBottom: 8 }}>📍 {job.address}</div>
                {job.tireSize && <div style={{ fontSize: 11.5, color: "#999", marginBottom: 8 }}>🔢 {job.tireSize} × {job.tireQty}</div>}

                {/* Quote came back — pay it */}
                {job.netStatus === "quoted" && (
                  <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 9, padding: 13, marginTop: 10 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: "#3b82f6", marginBottom: 9 }}>💵 We found you a partner</div>

                    <div style={{ background: "#0f0f0f", borderRadius: 7, padding: 12, marginBottom: 11 }}>
                      <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#666", fontWeight: 800, marginBottom: 7 }}>JOB COST</div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 3 }}>
                        <span style={{ fontSize: 13, color: "#ccc" }}>Cost to complete</span>
                        <span style={{ fontSize: 19, fontWeight: 900, color: "#f59e0b" }}>${(job.partnerPrice || 0).toFixed(2)}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#888", lineHeight: 1.5 }}>
                        Collected directly from your customer on arrival — not billed to you.
                      </div>
                    </div>

                    <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 7, padding: 12, marginBottom: 11 }}>
                      <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#22c55e", fontWeight: 800, marginBottom: 7 }}>YOU PAY US</div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span style={{ fontSize: 13, color: "#ccc" }}>Dispatch fee</span>
                        <span style={{ fontSize: 24, fontWeight: 900, color: "#22c55e" }}>${NETWORK_FEE}.00</span>
                      </div>
                    </div>

                    {job.quoteNote && (
                      <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.55, marginBottom: 11 }}>{job.quoteNote}</div>
                    )}

                    <div style={{ background: "#1a1400", border: "1px solid #f59e0b", borderRadius: 7, padding: 11, marginBottom: 11, fontSize: 11.5, color: "#f59e0b", lineHeight: 1.65 }}>
                      💰 <strong>Tell your customer:</strong> the tech collects <strong>${(job.partnerPrice || 0).toFixed(2)}</strong> on arrival — cash, Venmo or Zelle — in full before any work starts. Charge your customer whatever you like above that; the difference is yours.
                    </div>

                    {payKey === job._key && payStep === "card" ? (
                      <>
                        <div id="net-card-mount" style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 8, padding: "13px 15px", marginBottom: 10 }} />
                        <button style={{ width: "100%", background: "#22c55e", border: "none", color: "#000", padding: "13px", borderRadius: 8, fontWeight: 900, fontSize: 14, cursor: "pointer" }}
                          onClick={() => confirmPay(job)}>
                          🔒 Pay ${NETWORK_FEE}.00 & Dispatch
                        </button>
                        <button style={{ width: "100%", background: "none", border: "1px solid #333", color: "#888", padding: "10px", borderRadius: 8, fontSize: 12, cursor: "pointer", marginTop: 7 }}
                          onClick={() => { setPayStep("idle"); setPayKey(null); }}>Cancel</button>
                      </>
                    ) : payKey === job._key && payStep === "verifying" ? (
                      <div style={{ textAlign: "center", padding: 14, color: "#22c55e", fontSize: 13 }}>🔒 Processing...</div>
                    ) : payKey === job._key && payStep === "loading" ? (
                      <div style={{ textAlign: "center", padding: 14, color: "#666", fontSize: 13 }}>💳 Loading...</div>
                    ) : (
                      <button style={{ width: "100%", background: "#3b82f6", border: "none", color: "#fff", padding: "13px", borderRadius: 8, fontWeight: 900, fontSize: 14, cursor: "pointer" }}
                        onClick={() => startPay(job)}>
                        💳 Accept & Pay ${NETWORK_FEE} Dispatch Fee
                      </button>
                    )}
                  </div>
                )}

                {job.netStatus === "dispatched" && (
                  <div style={{ background: "#150a1f", border: "1px solid #a855f7", borderRadius: 8, padding: 11, marginTop: 9, fontSize: 12, color: "#c9a0f7", lineHeight: 1.6 }}>
                    🚐 {job.partnerName || "Your partner"} is on the way. They'll collect ${(job.partnerPrice || 0).toFixed(2)} on arrival.
                  </div>
                )}
                {job.netStatus === "collected" && (
                  <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 8, padding: 11, marginTop: 9, fontSize: 12, color: "#4caf50" }}>
                    ✅ Payment collected — work in progress.
                  </div>
                )}
                {job.netStatus === "failed" && (
                  <div style={{ background: "#1a0a0a", border: "1px solid #E63329", borderRadius: 8, padding: 11, marginTop: 9, fontSize: 12, color: "#ff6b6b", lineHeight: 1.6 }}>
                    ⚠️ The customer wouldn't pay on arrival. Call us at (561) 255-8506.
                  </div>
                )}

                <div style={{ fontSize: 11, color: "#555", marginTop: 9 }}>{job.id} · {timeAgo(job.createdAt)}</div>
              </div>
            );
          })}
          <div style={{ height: 22 }} />
        </>
      )}

      {/* Post a new one */}
      <div style={c.sectionLabel}>POST A JOB YOU CAN'T RUN</div>

      <div style={c.fieldGroup}>
        <label style={c.label}>What's Needed</label>
        <select style={c.input} value={f.service} onChange={e => setF(p => ({ ...p, service: e.target.value }))}>
          <option value="">Select service...</option>
          {NET_SERVICES.map(x => <option key={x}>{x}</option>)}
        </select>
      </div>

      {f.service.toLowerCase().includes("tire") && (
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 2 }}>
            <label style={c.label}>Tire Size</label>
            <input style={c.input} placeholder="205/55R16" value={f.tireSize}
              onChange={e => setF(p => ({ ...p, tireSize: e.target.value }))} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={c.label}>Qty</label>
            <select style={c.input} value={f.tireQty} onChange={e => setF(p => ({ ...p, tireQty: e.target.value }))}>
              {[1,2,3,4].map(n => <option key={n} value={String(n)}>{n}</option>)}
            </select>
          </div>
        </div>
      )}

      <Field label="Customer Name" value={f.customer} onChange={e => setF(p => ({ ...p, customer: e.target.value }))} placeholder="Who's with the vehicle" autoCapitalize="words" />
      <Field label="Customer Phone" value={f.phone} onChange={e => setF(p => ({ ...p, phone: e.target.value }))} type="tel" inputMode="tel" placeholder="(561) 000-0000" />
      <Field label="Street Address" value={f.address} onChange={e => setF(p => ({ ...p, address: e.target.value }))} placeholder="123 Main St" autoCapitalize="words" />

      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        <div style={{ flex: 2 }}>
          <label style={c.label}>City</label>
          <input style={c.input} value={f.city} onChange={e => setF(p => ({ ...p, city: e.target.value }))} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={c.label}>State</label>
          <select style={c.input} value={f.state} onChange={e => setF(p => ({ ...p, state: e.target.value }))}>
            <option value="">—</option>
            {US_STATES.map(st => <option key={st.code} value={st.code}>{st.code}</option>)}
          </select>
        </div>
      </div>

      <Field label="ZIP" value={f.zip} onChange={e => setF(p => ({ ...p, zip: e.target.value.replace(/\D/g, "") }))} inputMode="numeric" maxLength={5} />
      <Field label="Vehicle (optional)" value={f.vehicle} onChange={e => setF(p => ({ ...p, vehicle: e.target.value }))} placeholder="2019 Ford F-150, black" />

      <div style={c.fieldGroup}>
        <label style={c.label}>How Urgent?</label>
        <div style={{ display: "flex", gap: 8 }}>
          {[["asap", "⚡ ASAP"], ["scheduled", "📅 Can wait"]].map(([v, lbl]) => (
            <button key={v} onClick={() => setF(p => ({ ...p, urgency: v }))}
              style={{ flex: 1, background: f.urgency === v ? "#a855f7" : "#161616",
                border: `1px solid ${f.urgency === v ? "#a855f7" : "#2a2a2a"}`,
                color: f.urgency === v ? "#fff" : "#888",
                padding: "13px", borderRadius: 9, fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <div style={c.fieldGroup}>
        <label style={c.label}>Notes (optional)</label>
        <textarea style={{ ...c.input, minHeight: 80, resize: "vertical" }}
          placeholder="Anything the partner should know — gate code, where the car is, what you already tried..."
          value={f.note} onChange={e => setF(p => ({ ...p, note: e.target.value }))} />
      </div>

      {err && <div style={c.gpsError}>⚠️ {err}</div>}

      <button
        onClick={post}
        disabled={!canPost || sending}
        style={{ width: "100%", background: canPost ? "#a855f7" : "#1f1226", border: "none",
          color: canPost ? "#fff" : "#665", padding: "18px", borderRadius: 12, fontSize: 16.5,
          fontWeight: 900, cursor: canPost ? "pointer" : "not-allowed", letterSpacing: 0.4 }}>
        {sending ? "Posting..." : "🔄 POST TO NETWORK"}
      </button>
      <div style={{ fontSize: 11.5, color: "#666", textAlign: "center", marginTop: 9, lineHeight: 1.55 }}>
        No charge to post. We'll come back with a price and you decide.
      </div>
    </div>
  );
}

function PartnerDashboard({ authUser, onSignOut }) {
  const profile = authUser?.profile || {};
  const [tab, setTab] = useState("new");
  const [jobs, setJobs] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState("");
  const [payStep, setPayStep] = useState("idle");
  const [paidForThisJob, setPaidForThisJob] = useState(null);
  const [showFlyerQr, setShowFlyerQr] = useState(false);
  const [pSchedMode, setPSchedMode] = useState("now");
  const [pSchedDate, setPSchedDate] = useState("");
  const [pSchedTime, setPSchedTime] = useState("");
  const [quotePayStep, setQuotePayStep] = useState("idle");
  const quotePayStepRef = useRef("idle");
  const payStepRef = useRef("idle");
  const [quoteJobKey, setQuoteJobKey] = useState(null);
  const quoteCardRef = useRef(null);
  const quoteStripeRef = useRef(null);
  const payCardElRef = useRef(null);
  const payCardRef = useRef(null);
  const payStripeRef = useRef(null);

  const blank = {
    jobName: "", customer: "", phone: "", address: "", city: "", state: profile.state || "", zip: "",
    locationNote: "", vehicleYear: "", vehicleMake: "", vehicleModel: "", vehicleColor: "", plate: "",
    service: "", note: "", tireQty: 1,
  };
  const [j, setJ] = useState(blank);

  useEffect(() => { quotePayStepRef.current = quotePayStep; }, [quotePayStep]);
  useEffect(() => { payStepRef.current = payStep; }, [payStep]);

  function closeQuoteModal() {
    if (quoteCardRef.current?.card) { try { quoteCardRef.current.card.unmount(); } catch {} }
    quoteCardRef.current = null;
    setQuotePayStep("idle");
    setQuoteJobKey(null);
  }

  async function startQuotePayment(job) {
    setQuoteJobKey(job._key);
    setQuotePayStep("loading");
    try {
      const stripe = await loadStripe();
      quoteStripeRef.current = stripe;
      const total = quoteTotal(job);
      const res = await fetch(`${WORKER_URL}/create-payment-intent`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: total, jobId: job.id,
          customerName: profile.business || "Business",
          service: `Quote — ${job.service}`,
          email: profile.email || null,
        }),
      });
      const d = await res.json();
      if (d.error) throw new Error(d.error);
      if (!d.clientSecret) throw new Error("Stripe did not return a payment session.");

      setQuotePayStep("card");

      // The mount target is a single fixed node in the modal — it never re-renders
      let tries = 0;
      const mount = () => {
        const node = document.getElementById("quote-card-mount");
        if (!node) {
          if (++tries > 60) { alert("Payment form failed to load. Please try again."); closeQuoteModal(); return; }
          return setTimeout(mount, 50);
        }
        // Reuse an existing mounted card if it's still alive
        if (quoteCardRef.current?.card && quoteCardRef.current.jobKey === job._key) {
          quoteCardRef.current.clientSecret = d.clientSecret;
          quoteCardRef.current.total = total;
          return;
        }
        node.innerHTML = "";
        const elements = stripe.elements();
        const card = elements.create("card", {
          style: { base: { color: "#fff", fontSize: "16px", "::placeholder": { color: "#666" } }, invalid: { color: "#ff6b6b" } },
        });
        card.mount(node);
        card.on("ready", () => {
          if (quoteCardRef.current) quoteCardRef.current.ready = true;
        });
        quoteCardRef.current = { card, elements, clientSecret: d.clientSecret, total, jobKey: job._key, ready: false };
      };
      mount();
    } catch (e) {
      console.error("[quote-pay]", e);
      alert("Could not start payment:\n\n" + e.message);
      closeQuoteModal();
    }
  }

  async function confirmQuotePayment(job) {
    if (!quoteStripeRef.current || !quoteCardRef.current?.card) {
      alert("The card form isn't ready yet — give it a second and try again.");
      return;
    }
    setQuotePayStep("verifying");
    try {
      const ref = quoteCardRef.current;
      let result;
      try {
        result = await quoteStripeRef.current.confirmCardPayment(
          ref.clientSecret,
          { payment_method: { card: ref.card, billing_details: { name: profile.business || "Business" } } }
        );
      } catch (mountErr) {
        // Element was lost — rebuild it and ask them to tap again rather than failing outright
        console.error("[quote-pay] element lost:", mountErr);
        quoteCardRef.current = null;
        setQuotePayStep("idle");
        alert("The card form lost its connection. Tap Pay again and re-enter your card.");
        return;
      }
      const { error, paymentIntent } = result;
      if (error) { alert(error.message); setQuotePayStep("card"); return; }
      if (paymentIntent.status !== "succeeded") { alert("Payment not completed."); setQuotePayStep("card"); return; }

      const amountPaid = quoteCardRef.current.total;
      const saved = await dbCall("patch", `jobs/${job._key}`, {
        paid: true, price: job.quotedPrice,
        amountPaid, paymentIntentId: paymentIntent.id, paidAt: Date.now(),
      });
      if (saved?.error) {
        console.error("[quote-pay] charged but DB write failed:", saved.error);
        alert(`⚠️ Your card was charged $${amountPaid.toFixed(2)} but we couldn't update the job.\n\nCall (561) 255-8506. Payment ref: ${paymentIntent.id}`);
      }

      await fetch(`${WORKER_URL}/notify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: `✅ Quote Paid — $${amountPaid.toFixed(2)}`,
          message: `${profile.business} paid the quote for ${job.service}.\n${job.customer} · ${job.address}\nJob ${job.id}\n\nReady to dispatch.`,
        }),
      }).catch(() => {});

      closeQuoteModal();
      loadJobs();
      alert("✅ Paid — we're dispatching now.");
    } catch (e) {
      console.error("[quote-pay]", e);
      alert(e.message);
      setQuotePayStep("card");
    }
  }

  async function loadJobs() {
    const all = await dbGet("jobs");
    if (!Array.isArray(all)) return;
    setJobs(all.filter(x => x.partnerId === authUser.uid).sort((a, b) => b.createdAt - a.createdAt));
  }
  useEffect(() => {
    loadJobs();
    const iv = setInterval(() => {
      // Don't re-render the list while a Stripe card form is mounted —
      // React would tear the element out from under Stripe.
      if (quotePayStepRef.current === "idle" && payStepRef.current === "idle") loadJobs();
    }, 15000);
    return () => clearInterval(iv);
  }, []);

  const svc = PARTNER_SERVICES.find(x => x.id === j.service);
  const isQuote = !!svc?.quote;
  const jobSubtotal = svc?.perTire ? PARTNER_RATE + (j.tireQty || 1) * svc.perTire : PARTNER_RATE;
  const canSend = j.customer && j.phone && j.address && j.city && j.state && j.service && !(pSchedMode === "later" && !pSchedDate);

  async function dispatchJob() {
    if (!canSend) return setErr("Fill in the customer name, phone, address, city, state, and service.");
    setErr("");
    // Quote-only services skip prepayment — we call them with pricing first
    if (!isQuote && !paidForThisJob) {
      return startJobPayment();
    }
    await sendJob();
  }

  async function startJobPayment() {
    setErr("");
    setPayStep("loading");
    try {
      const stripe = await loadStripe();
      payStripeRef.current = stripe;
      const total = parseFloat((jobSubtotal * 1.07).toFixed(2));
      const res = await fetch(`${WORKER_URL}/create-payment-intent`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: total,
          jobId: "B2B-PENDING",
          customerName: profile.business || "Business",
          service: `B2B Dispatch — ${svc?.label || "service"}`,
          email: profile.email || null,
        }),
      });
      const d = await res.json();
      if (d.error) throw new Error(d.error);
      setPayStep("card");
      setTimeout(() => {
        const el = stripe.elements();
        const card = el.create("card", {
          style: { base: { color: "#fff", fontSize: "16px", "::placeholder": { color: "#666" } }, invalid: { color: "#ff6b6b" } },
        });
        card.mount(payCardElRef.current);
        payCardRef.current = { card, clientSecret: d.clientSecret, total };
      }, 100);
    } catch (e) {
      setErr(e.message);
      setPayStep("idle");
    }
  }

  async function confirmJobPayment() {
    if (!payStripeRef.current || !payCardRef.current) return;
    setPayStep("verifying");
    try {
      const { error, paymentIntent } = await payStripeRef.current.confirmCardPayment(
        payCardRef.current.clientSecret,
        { payment_method: { card: payCardRef.current.card, billing_details: { name: profile.business || "Business" } } }
      );
      if (error) { setErr(error.message); setPayStep("card"); return; }
      if (paymentIntent.status !== "succeeded") { setErr("Payment not completed."); setPayStep("card"); return; }
      setPaidForThisJob({ intentId: paymentIntent.id, amount: payCardRef.current.total });
      setPayStep("idle");
      await sendJob({ intentId: paymentIntent.id, amount: payCardRef.current.total });
    } catch (e) {
      setErr(e.message);
      setPayStep("card");
    }
  }

  async function sendJob(payment) {
    const pay = payment || paidForThisJob;
    setSending(true);
    const id = "B2B-" + genId();
    const fullAddress = `${j.address}, ${j.city}, ${j.state}${j.zip ? " " + j.zip : ""}`;
    const vehicle = [j.vehicleYear, j.vehicleMake, j.vehicleModel, j.vehicleColor && `(${j.vehicleColor})`].filter(Boolean).join(" ");

    await dbPush("jobs", {
      id,
      service: svc.label.replace(/^[^\s]+\s/, ""),
      serviceId: j.service,
      jobName: j.jobName.trim() || null,
      customer: j.customer.trim(),
      phone: j.phone.trim(),
      address: fullAddress,
      locationNote: j.locationNote.trim() || null,
      vehicle: vehicle || null,
      plate: j.plate.trim() || null,
      note: j.note.trim() || null,
      price: isQuote ? null : jobSubtotal,
      tireQty: svc?.perTire ? (j.tireQty || 1) : null,
      quoteNeeded: isQuote,
      paid: !isQuote && !!pay,
      paymentIntentId: pay?.intentId || null,
      amountPaid: pay?.amount || null,
      paidAt: pay ? Date.now() : null,
      billTo: "partner",
      partnerId: authUser.uid,
      partnerBusiness: profile.business || "",
      partnerContact: profile.contactName || "",
      partnerPhone: profile.phone || "",
      scheduled: pSchedMode === "later" && !!pSchedDate,
      scheduledDate: pSchedMode === "later" ? pSchedDate : null,
      scheduledTime: pSchedMode === "later" ? (pSchedTime || "09:00") : null,
      scheduledLabel: pSchedMode === "later" && pSchedDate ? formatSchedule(pSchedDate, pSchedTime) : null,
      status: "new",
      tech: null, techName: null,
      createdAt: Date.now(),
    });

    await fetch(`${WORKER_URL}/notify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: `🏢 B2B DISPATCH — ${profile.business || "Partner"}`,
        message: `${svc.label}\n${j.customer} · ${j.phone}\n📍 ${fullAddress}${j.locationNote ? `\n(${j.locationNote})` : ""}\n${vehicle ? `🚗 ${vehicle}\n` : ""}${j.plate ? `🔖 ${j.plate}\n` : ""}\n💰 ${isQuote ? "QUOTE NEEDED — call them" : `$${pay?.amount?.toFixed(2) || jobSubtotal} PAID ✅`}${svc?.perTire ? ` (${j.tireQty || 1} tire${(j.tireQty || 1) > 1 ? "s" : ""})` : ""}\nJob ${id}\n\nFrom: ${profile.contactName} @ ${profile.business}\n📞 ${profile.phone}`,
      }),
    }).catch(() => {});

    setSending(false);
    setPaidForThisJob(null);
    setPSchedMode("now"); setPSchedDate(""); setPSchedTime("");
    setSent({ id, isQuote });
    setJ({ ...blank, state: profile.state || "" });
    loadJobs();
  }


  return (
    <div style={c.root}>
      <header style={{ ...c.header, background: "#0f1a0f", borderBottom: "3px solid #22c55e" }}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div>
            <div style={{ ...c.logoTop, fontSize: 13 }}>{profile.business || "BUSINESS"}</div>
            <div style={{ ...c.logoBot, color: "#22c55e", fontSize: 10 }}>DISPATCH PORTAL</div>
          </div>
        </div>
        <button style={{ ...c.back, color: "#E63329", borderColor: "#E63329" }} onClick={onSignOut}>Sign Out</button>
      </header>

      <div style={au.tabs}>
        <button style={{ ...au.tab, ...(tab === "new" ? { ...au.tabActive, background: "#22c55e", color: "#000" } : {}) }} onClick={() => { setTab("new"); setSent(null); }}>
          ➕ New Job
        </button>
        <button style={{ ...au.tab, ...(tab === "history" ? { ...au.tabActive, background: "#22c55e", color: "#000" } : {}) }} onClick={() => setTab("history")}>
          📋 My Jobs ({jobs.length})
        </button>
        <button style={{ ...au.tab, ...(tab === "network" ? { ...au.tabActive, background: "#a855f7", color: "#fff" } : {}) }} onClick={() => { setTab("network"); setSent(null); }}>
          🔄 Overflow
        </button>
        <button style={{ ...au.tab, ...(tab === "qr" ? { ...au.tabActive, background: "#22c55e", color: "#000" } : {}) }} onClick={() => { setTab("qr"); setSent(null); }}>
          📱 QR Code
        </button>
      </div>

      <div style={c.page}>
        {tab === "network" ? (
          <NetworkPost authUser={authUser} profile={profile} />
        ) : tab === "qr" ? (
          <div style={{ textAlign: "center", padding: "4px 0 20px" }}>
            <div style={{ fontSize: 10, letterSpacing: 3, color: "#22c55e", fontWeight: 800, marginBottom: 6 }}>
              SHARE WITH YOUR CUSTOMERS
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 4, lineHeight: 1.2 }}>
              South Florida Flyer
            </div>
            <div style={{ fontSize: 11.5, color: "#666", marginBottom: 16 }}>
              Palm Beach · Broward · Miami-Dade · Treasure Coast
            </div>

            <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", border: "1px solid #222", marginBottom: 14 }}>
              <img src={FLYERS.southflorida.src} alt="Super Fast Roadside — South Florida"
                onClick={() => setShowFlyerQr(true)}
                style={{ width: "100%", display: "block", cursor: "pointer" }} />
              <button
                onClick={() => setShowFlyerQr(true)}
                style={{ position: "absolute", left: "50%", bottom: 14, transform: "translateX(-50%)",
                  background: "#22c55e", border: "none", color: "#000", padding: "12px 22px",
                  borderRadius: 30, fontSize: 14, fontWeight: 900, cursor: "pointer",
                  boxShadow: "0 6px 22px rgba(0,0,0,0.6)", whiteSpace: "nowrap" }}>
                📱 Tap to Enlarge QR
              </button>
            </div>

            <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 12, padding: 15, marginBottom: 11, textAlign: "left" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#22c55e", marginBottom: 7 }}>💡 When to use this</div>
              <div style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.7 }}>
                Print it for your counter or waiting area. Customers who need help outside your account — or on their own vehicle — can book straight from their phone. $60 flat for members, $89 without.
              </div>
            </div>

            <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 12, padding: 15, textAlign: "left" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#f59e0b", marginBottom: 7 }}>📵 Won't scan?</div>
              <div style={{ fontSize: 12, color: "#999", lineHeight: 1.65 }}>
                Have them open their camera and hold steady over the code. Or type <strong style={{ color: "#fff" }}>superfast-tires.web.app</strong> into any browser.
              </div>
            </div>

            <div style={{ fontSize: 11, color: "#555", marginTop: 14, lineHeight: 1.55 }}>
              Turn your screen brightness up — it scans faster.
            </div>

            {showFlyerQr && (
              <div onClick={() => setShowFlyerQr(false)}
                style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.97)", zIndex: 999,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  padding: 14, cursor: "pointer" }}>
                <div style={{ fontSize: 11, letterSpacing: 3, color: "#22c55e", fontWeight: 900, marginBottom: 10 }}>
                  POINT YOUR CAMERA AT THE CODE
                </div>
                <div style={{ background: "#fff", borderRadius: 22, padding: 20, lineHeight: 0,
                  boxShadow: "0 14px 60px rgba(34,197,94,0.35)" }}>
                  <img src="/qr-signup.png" alt="Scan to book Super Fast Roadside"
                    style={{ width: "min(78vw, 340px)", height: "min(78vw, 340px)", display: "block" }} />
                </div>
                <div style={{ fontSize: 16, fontWeight: 900, color: "#fff", marginTop: 18 }}>superfast-tires.web.app</div>
                <div style={{ fontSize: 12.5, color: "#888", marginTop: 6 }}>$10/month · $60 flat rate</div>
                <div style={{ fontSize: 11.5, color: "#555", marginTop: 14 }}>Tap anywhere to close</div>
                <button style={{ position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,0.12)",
                  border: "1px solid #555", color: "#fff", width: 40, height: 40, borderRadius: "50%",
                  fontSize: 18, cursor: "pointer" }}
                  onClick={() => setShowFlyerQr(false)}>✕</button>
              </div>
            )}
          </div>
        ) : tab === "history" ? (
          <>
            {jobs.length === 0 && <div style={{ textAlign: "center", padding: "50px 0", color: "#555" }}>No jobs dispatched yet.</div>}
            {jobs.map(job => {
              const meta = STATUS_META[job.status] || { label: job.status, color: "#888", bg: "#1a1a1a" };
              return (
                <div key={job._key} style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 14, marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 7 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>{job.service}</div>
                      <div style={{ fontSize: 11.5, color: "#888", marginTop: 2 }}>{job.customer} · {job.phone}</div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 10, background: meta.bg, color: meta.color }}>{meta.label}</span>
                  </div>
                  {job.scheduled && job.scheduledLabel && (
                    <div style={{ fontSize: 11.5, color: "#f59e0b", fontWeight: 700, marginBottom: 4 }}>📅 {job.scheduledLabel}</div>
                  )}
                  <div style={{ fontSize: 11.5, color: "#777", marginBottom: 6 }}>📍 {job.address}</div>
                  {job.techName && <div style={{ fontSize: 11.5, color: "#22c55e", marginBottom: 6 }}>🛻 Assigned to {job.techName.split(" ")[0]}</div>}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5 }}>
                    <span style={{ color: "#555" }}>{job.id} · {timeAgo(job.createdAt)}</span>
                    <span style={{ color: job.paid ? "#22c55e" : job.quotedPrice ? "#f59e0b" : job.quoteNeeded ? "#f59e0b" : "#22c55e", fontWeight: 700 }}>
                      {job.paid ? `$${(job.amountPaid || 0).toFixed(2)} paid`
                        : job.quotedPrice ? `$${quoteTotal(job).toFixed(2)} due`
                        : job.quoteNeeded ? "Awaiting quote"
                        : `$${PARTNER_RATE}`}
                    </span>
                  </div>

                  {/* Quote ready — pay it */}
                  {job.quotedPrice && !job.paid && (
                    <div style={{ background: "#1a1400", border: "1px solid #f59e0b", borderRadius: 9, padding: 13, marginTop: 11 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#f59e0b", marginBottom: 5 }}>💵 Your quote is ready</div>
                      {job.quoteNote && (
                        <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.55, marginBottom: 9 }}>{job.quoteNote}</div>
                      )}
                      <div style={{ background: "#0f0f0f", borderRadius: 7, padding: 10, marginBottom: 10, fontSize: 12 }}>
                        {job.quoteTaxable === false ? (
                          <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 5 }}>
                            <span>Flat quote — no tax</span><span>${job.quotedPrice.toFixed(2)}</span>
                          </div>
                        ) : (
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 3 }}>
                              <span>Quote</span><span>${job.quotedPrice.toFixed(2)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 5 }}>
                              <span>Tax (7%)</span><span>${(job.quotedPrice * 0.07).toFixed(2)}</span>
                            </div>
                          </>
                        )}
                        <div style={{ display: "flex", justifyContent: "space-between", color: "#fff", fontWeight: 900, fontSize: 14, borderTop: "1px solid #222", paddingTop: 6 }}>
                          <span>Total</span><span style={{ color: "#22c55e" }}>${quoteTotal(job).toFixed(2)}</span>
                        </div>
                      </div>

                      <button style={{ width: "100%", background: "#f59e0b", border: "none", color: "#000", padding: "12px", borderRadius: 8, fontWeight: 900, fontSize: 13.5, cursor: "pointer" }}
                        onClick={() => startQuotePayment(job)}>
                        💳 Pay ${quoteTotal(job).toFixed(2)} & Dispatch
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        ) : sent ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 56, marginBottom: 14 }}>✅</div>
            <div style={{ fontSize: 21, fontWeight: 900, color: "#22c55e", marginBottom: 8 }}>Job Dispatched</div>
            <div style={{ fontSize: 13.5, color: "#888", lineHeight: 1.65, marginBottom: 8 }}>
              Sent to Super Fast dispatch. We're assigning the closest provider now.
            </div>
            <div style={{ fontSize: 13, color: "#fff", fontWeight: 800, marginBottom: 20 }}>Job {sent.id}</div>
            {sent.isQuote && (
              <div style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 10, padding: 14, marginBottom: 18, fontSize: 12.5, color: "#f59e0b", lineHeight: 1.6 }}>
                📞 This service needs a quote. We'll call you at {profile.phone} with pricing before we send anyone.
              </div>
            )}
            <button style={{ ...c.btnPrimary, background: "#22c55e", color: "#000" }} onClick={() => setSent(null)}>
              ➕ Dispatch Another Job
            </button>
            <button style={c.btnSecondary} onClick={() => { setSent(null); setTab("history"); }}>View My Jobs</button>
          </div>
        ) : (
          <>
            <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: "12px 14px", marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#22c55e" }}>Your rate</div>
                <div style={{ fontSize: 10.5, color: "#4caf50", marginTop: 1 }}>Billed to {profile.business}</div>
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#fff" }}>${PARTNER_RATE}<span style={{ fontSize: 11, color: "#888" }}>/call</span></div>
            </div>

            <div style={c.sectionLabel}>JOB REFERENCE (OPTIONAL)</div>
            <Field label="Your Job / RO Number" value={j.jobName} onChange={e => setJ(p => ({ ...p, jobName: e.target.value }))} placeholder="e.g. RO-4471 or Unit 12" />

            <SchedulePicker
              mode={pSchedMode} setMode={setPSchedMode}
              date={pSchedDate} setDate={setPSchedDate}
              time={pSchedTime} setTime={setPSchedTime}
              accent="#22c55e"
            />

            <div style={c.sectionLabel}>CUSTOMER</div>
            <Field label="Customer Name" value={j.customer} onChange={e => setJ(p => ({ ...p, customer: e.target.value }))} placeholder="Who's with the vehicle" autoCapitalize="words" />
            <Field label="Phone Number" value={j.phone} onChange={e => setJ(p => ({ ...p, phone: e.target.value }))} type="tel" inputMode="tel" placeholder="(561) 000-0000" />

            <div style={c.sectionLabel}>LOCATION</div>
            <Field label="Street Address" value={j.address} onChange={e => setJ(p => ({ ...p, address: e.target.value }))} placeholder="123 Main St" autoCapitalize="words" />
            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 2 }}><label style={c.label}>City</label>
                <input style={c.input} value={j.city} onChange={e => setJ(p => ({ ...p, city: e.target.value }))} /></div>
              <div style={{ flex: 1 }}><label style={c.label}>State</label>
                <select style={c.input} value={j.state} onChange={e => setJ(p => ({ ...p, state: e.target.value }))}>
                  <option value="">—</option>
                  {US_STATES.map(st => <option key={st.code} value={st.code}>{st.code}</option>)}
                </select></div>
            </div>
            <Field label="ZIP Code" value={j.zip} onChange={e => setJ(p => ({ ...p, zip: e.target.value.replace(/\D/g, "") }))} inputMode="numeric" maxLength={5} placeholder="33401" />
            <Field label="Where Exactly? (optional)" value={j.locationNote} onChange={e => setJ(p => ({ ...p, locationNote: e.target.value }))} placeholder="e.g. north lot behind the building, spot 14" />

            <div style={c.sectionLabel}>VEHICLE</div>
            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1 }}><label style={c.label}>Year</label>
                <input style={c.input} value={j.vehicleYear} onChange={e => setJ(p => ({ ...p, vehicleYear: e.target.value }))} placeholder="2019" /></div>
              <div style={{ flex: 1.4 }}><label style={c.label}>Make</label>
                <input style={c.input} value={j.vehicleMake} onChange={e => setJ(p => ({ ...p, vehicleMake: e.target.value }))} placeholder="Ford" /></div>
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1.4 }}><label style={c.label}>Model</label>
                <input style={c.input} value={j.vehicleModel} onChange={e => setJ(p => ({ ...p, vehicleModel: e.target.value }))} placeholder="F-150" /></div>
              <div style={{ flex: 1 }}><label style={c.label}>Color</label>
                <input style={c.input} value={j.vehicleColor} onChange={e => setJ(p => ({ ...p, vehicleColor: e.target.value }))} placeholder="Black" /></div>
            </div>
            <Field label="Plate / Unit # (optional)" value={j.plate} onChange={e => setJ(p => ({ ...p, plate: e.target.value.toUpperCase() }))} placeholder="ABC1234" autoCapitalize="characters" />

            <div style={c.sectionLabel}>SERVICE NEEDED</div>
            <div style={{ marginBottom: 14 }}>
              {PARTNER_SERVICES.map(sv => {
                const on = j.service === sv.id;
                return (
                  <button key={sv.id} onClick={() => setJ(p => ({ ...p, service: sv.id }))}
                    style={{ width: "100%", textAlign: "left", background: on ? (sv.quote ? "#1a1000" : "#0a1f0a") : "#161616",
                      border: `1px solid ${on ? (sv.quote ? "#f59e0b" : "#22c55e") : "#2a2a2a"}`, borderRadius: 9,
                      padding: "12px 14px", marginBottom: 7, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13.5, color: on ? (sv.quote ? "#f59e0b" : "#22c55e") : "#aaa", fontWeight: on ? 700 : 500 }}>{sv.label}</span>
                    <span style={{ fontSize: 11.5, fontWeight: 800, color: sv.quote ? "#f59e0b" : "#22c55e" }}>
                      {sv.quote ? "QUOTE" : sv.perTire ? `$70 +$${sv.perTire}/tire` : "$70"}
                    </span>
                  </button>
                );
              })}
            </div>

            {svc?.perTire && (
              <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: 14, marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#22c55e", marginBottom: 9 }}>How many tires?</div>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  {[1, 2, 3, 4].map(n => (
                    <button key={n} onClick={() => setJ(p => ({ ...p, tireQty: n }))}
                      style={{ flex: 1, background: j.tireQty === n ? "#22c55e" : "#161616",
                        border: `1px solid ${j.tireQty === n ? "#22c55e" : "#2a2a2a"}`,
                        color: j.tireQty === n ? "#000" : "#888",
                        padding: "13px 0", borderRadius: 8, fontSize: 16, fontWeight: 900, cursor: "pointer" }}>
                      {n}
                    </button>
                  ))}
                </div>
                <div style={{ background: "#0f0f0f", borderRadius: 7, padding: 10, fontSize: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 3 }}>
                    <span>Service call</span><span>${PARTNER_RATE}.00</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 5 }}>
                    <span>{j.tireQty || 1} tire{(j.tireQty || 1) > 1 ? "s" : ""} × $25</span><span>${((j.tireQty || 1) * 25).toFixed(2)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#22c55e", fontWeight: 900, fontSize: 13.5, borderTop: "1px solid #222", paddingTop: 6 }}>
                    <span>Subtotal</span><span>${(PARTNER_RATE + (j.tireQty || 1) * 25).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {isQuote && (
              <div style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 10, padding: 13, marginBottom: 14, fontSize: 12, color: "#f59e0b", lineHeight: 1.6 }}>
                📞 This one gets quoted. Send it through and we'll call {profile.phone} with pricing before dispatching.
              </div>
            )}

            <div style={c.fieldGroup}><label style={c.label}>Notes for the Tech (optional)</label>
              <textarea style={{ ...c.input, minHeight: 80, resize: "vertical" }}
                placeholder="Tire size, gate code, which door, anything they should know..."
                value={j.note} onChange={e => setJ(p => ({ ...p, note: e.target.value }))} /></div>

            {err && <div style={c.gpsError}>⚠️ {err}</div>}

            {/* Payment before dispatch */}
            {!isQuote && canSend && payStep === "idle" && !paidForThisJob && (
              <div style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: 11, padding: 14, marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 5 }}>
                  <span style={{ color: "#888" }}>Service call</span>
                  <span style={{ color: "#ccc" }}>${PARTNER_RATE}.00</span>
                </div>
                {svc?.perTire && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 5 }}>
                    <span style={{ color: "#888" }}>{j.tireQty || 1} tire{(j.tireQty || 1) > 1 ? "s" : ""} × $25</span>
                    <span style={{ color: "#ccc" }}>${((j.tireQty || 1) * 25).toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 8 }}>
                  <span style={{ color: "#888" }}>Tax (7%)</span>
                  <span style={{ color: "#ccc" }}>${(jobSubtotal * 0.07).toFixed(2)}</span>
                </div>
                <div style={{ borderTop: "1px solid #222", paddingTop: 8, display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 900 }}>
                  <span style={{ color: "#fff" }}>Due now</span>
                  <span style={{ color: "#22c55e" }}>${(jobSubtotal * 1.07).toFixed(2)}</span>
                </div>
              </div>
            )}

            {payStep === "card" ? (
              <>
                <div style={{ fontSize: 10, letterSpacing: 3, color: "#666", fontWeight: 700, marginBottom: 9 }}>PAYMENT INFO</div>
                <div ref={payCardElRef} style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 8, padding: "14px 16px", marginBottom: 12 }} />
                <button style={{ ...c.btnPrimary, background: "#22c55e", color: "#000" }} onClick={confirmJobPayment}>
                  🔒 Pay ${(jobSubtotal * 1.07).toFixed(2)} & Dispatch →
                </button>
                <button style={c.btnSecondary} onClick={() => { setPayStep("idle"); setErr(""); }}>← Cancel</button>
              </>
            ) : payStep === "verifying" ? (
              <div style={{ textAlign: "center", padding: 24, color: "#22c55e" }}>
                <div style={{ fontSize: 34, marginBottom: 8 }}>🔒</div>
                Processing payment...
              </div>
            ) : payStep === "loading" ? (
              <div style={{ textAlign: "center", padding: 24, color: "#666" }}>
                <div style={{ fontSize: 34, marginBottom: 8 }}>💳</div>
                Loading secure payment...
              </div>
            ) : (
              <>
                <button
                  onClick={dispatchJob}
                  disabled={!canSend || sending}
                  style={{ width: "100%", background: canSend ? "#E63329" : "#2a1010", border: "none",
                    color: canSend ? "#fff" : "#664", padding: "20px", borderRadius: 13, fontSize: 18,
                    fontWeight: 900, cursor: canSend ? "pointer" : "not-allowed", letterSpacing: 0.5,
                    boxShadow: canSend ? "0 6px 24px rgba(230,51,41,0.35)" : "none", marginTop: 4 }}>
                  {sending
                    ? "Dispatching..."
                    : isQuote
                      ? "🚨 SEND FOR QUOTE"
                      : `🚨 PAY $${(jobSubtotal * 1.07).toFixed(2)} & DISPATCH`}
                </button>
                {!canSend && (
                  <div style={{ fontSize: 11.5, color: "#666", textAlign: "center", marginTop: 9 }}>
                    Customer name, phone, address, city, state, and service are required.
                  </div>
                )}
                {canSend && !isQuote && (
                  <div style={{ fontSize: 11.5, color: "#666", textAlign: "center", marginTop: 9, lineHeight: 1.5 }}>
                    🔒 Payment is taken before we dispatch. Secure checkout by Stripe.
                  </div>
                )}
                {canSend && isQuote && (
                  <div style={{ fontSize: 11.5, color: "#f59e0b", textAlign: "center", marginTop: 9, lineHeight: 1.5 }}>
                    No charge yet — we'll call you with pricing first.
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      {/* ── QUOTE PAYMENT MODAL — lives outside the list so nothing re-renders it ── */}
      {quoteJobKey && (() => {
        const job = jobs.find(x => x._key === quoteJobKey);
        if (!job) return null;
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.94)", zIndex: 950,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 18, overflowY: "auto" }}>
            <div style={{ background: "#111", border: "2px solid #f59e0b", borderRadius: 16, padding: 22, width: "100%", maxWidth: 400, margin: "auto" }}>
              <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#f59e0b", fontWeight: 800, marginBottom: 8 }}>PAY YOUR QUOTE</div>
              <div style={{ fontSize: 17, fontWeight: 900, color: "#fff", marginBottom: 4 }}>{job.service}</div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>{job.customer} · {job.id}</div>

              <div style={{ background: "#0f0f0f", borderRadius: 9, padding: 13, marginBottom: 16, fontSize: 13 }}>
                {job.quoteTaxable === false ? (
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 6 }}>
                    <span>Flat quote — no tax</span><span>${(job.quotedPrice || 0).toFixed(2)}</span>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 4 }}>
                      <span>Quote</span><span>${(job.quotedPrice || 0).toFixed(2)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 6 }}>
                      <span>Tax (7%)</span><span>${((job.quotedPrice || 0) * 0.07).toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", color: "#fff", fontWeight: 900, fontSize: 15, borderTop: "1px solid #222", paddingTop: 7 }}>
                  <span>Total</span><span style={{ color: "#22c55e" }}>${quoteTotal(job).toFixed(2)}</span>
                </div>
              </div>

              {quotePayStep === "loading" && (
                <div style={{ textAlign: "center", padding: 26, color: "#666" }}>💳 Loading secure payment...</div>
              )}
              {quotePayStep === "verifying" && (
                <div style={{ textAlign: "center", padding: 26, color: "#22c55e" }}>🔒 Processing your payment...</div>
              )}

              <div id="quote-card-mount"
                style={{ display: quotePayStep === "card" ? "block" : "none",
                  background: "#111", border: "1px solid #2a2a2a", borderRadius: 8,
                  padding: "14px 16px", marginBottom: 12, minHeight: 46 }} />

              {quotePayStep === "card" && (
                <button style={{ width: "100%", background: "#22c55e", border: "none", color: "#000", padding: "14px", borderRadius: 9, fontWeight: 900, fontSize: 15, cursor: "pointer" }}
                  onClick={() => confirmQuotePayment(job)}>
                  🔒 Pay ${quoteTotal(job).toFixed(2)}
                </button>
              )}

              {quotePayStep !== "verifying" && (
                <button style={{ width: "100%", background: "none", border: "1px solid #333", color: "#888", padding: "12px", borderRadius: 9, fontSize: 13, cursor: "pointer", marginTop: 9 }}
                  onClick={closeQuoteModal}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        );
      })()}

      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC · Business Dispatch</div>
    </div>
  );
}

// ============================================================
// FLEET MAINTENANCE PORTAL — monthly inspection accounts
// Uses the same createAccount helper as every other portal so
// tokens and sessions behave identically. Nothing shared mutably.
// ============================================================
// ============================================================
// PROVIDER — FLEET INSPECTION SHEETS
// One report per visit, one sheet per vehicle, photos per sheet.
// ============================================================
function FleetInspection({ authUser, profile }) {
  const [mode, setMode] = useState("list");     // list | edit
  const [fleets, setFleets] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [fleetId, setFleetId] = useState("");
  const [sheets, setSheets] = useState([]);
  const [active, setActive] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [draftKey, setDraftKey] = useState(null);
  const [search, setSearch] = useState("");

  const blankSheet = () => ({
    unit: "", year: "", make: "", model: "", plate: "", vin: "", mileage: "",
    results: {}, values: {}, photos: [], notes: "",
  });

  async function load() {
    const users = await dbGet("users");
    if (Array.isArray(users)) setFleets(users.filter(u => u.role === "fleet" && u.subscribed));
    const insp = await dbGet("inspections");
    if (Array.isArray(insp)) {
      setDrafts(insp.filter(x => x.techId === authUser.uid && x.status === "draft")
        .sort((a, b) => b.createdAt - a.createdAt));
    }
  }
  useEffect(() => { load(); }, []);

  const fleet = fleets.find(x => x._key === fleetId);
  const sh = sheets[active] || blankSheet();

  function updateSheet(patch) {
    setSheets(prev => prev.map((x, i) => i === active ? { ...x, ...patch } : x));
  }
  function setResult(itemId, status) {
    setSheets(prev => prev.map((x, i) => i === active
      ? { ...x, results: { ...x.results, [itemId]: status } } : x));
  }
  function setValue(itemId, val) {
    setSheets(prev => prev.map((x, i) => i === active
      ? { ...x, values: { ...x.values, [itemId]: val } } : x));
  }

  async function addPhoto() {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*"; input.capture = "environment";
    input.onchange = e => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async ev => {
        try {
          setUploading("Uploading photo...");
          const url = await uploadPhoto(ev.target.result, `superfast/fleet/${fleetId}_${Date.now()}`);
          setSheets(prev => prev.map((x, i) => i === active
            ? { ...x, photos: [...(x.photos || []), url] } : x));
          setUploading("");
        } catch (err) {
          setUploading("");
          alert("Upload failed: " + err.message);
        }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }

  async function saveDraft() {
    if (!fleetId) return alert("Pick a fleet account first.");
    setSaving(true);
    const payload = {
      fleetId,
      fleetBusiness: fleet?.business || "",
      techId: authUser.uid,
      techName: `${profile.firstName} ${profile.lastName}`,
      sheets,
      status: "draft",
      providerPay: FLEET_PROVIDER_PAY,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    if (draftKey) {
      await dbPatch(`inspections/${draftKey}`, { ...payload, createdAt: undefined });
    } else {
      const key = await dbPush("inspections", payload);
      setDraftKey(key);
    }
    setSaving(false);
    alert("💾 Saved. You can close the app and pick this up later.");
    load();
  }

  async function submitReport() {
    if (!fleetId) return alert("Pick a fleet account first.");
    if (sheets.length === 0) return alert("Add at least one vehicle sheet.");
    const incomplete = sheets.findIndex(x => !x.unit && !x.plate);
    if (incomplete >= 0) return alert(`Vehicle ${incomplete + 1} needs a unit number or plate.`);
    if (!window.confirm(`Submit ${sheets.length} vehicle sheet${sheets.length !== 1 ? "s" : ""} to ${fleet?.business}? They'll be able to see it right away.`)) return;

    setSaving(true);
    const flagged = sheets.reduce((n, x) =>
      n + Object.values(x.results || {}).filter(v => v === "soon" || v === "now").length, 0);
    const urgent = sheets.reduce((n, x) =>
      n + Object.values(x.results || {}).filter(v => v === "now").length, 0);

    const payload = {
      fleetId,
      fleetBusiness: fleet?.business || "",
      techId: authUser.uid,
      techName: `${profile.firstName} ${profile.lastName}`,
      sheets,
      vehicleCount: sheets.length,
      flaggedCount: flagged,
      urgentCount: urgent,
      status: "submitted",
      providerPay: FLEET_PROVIDER_PAY,
      submittedAt: Date.now(),
      createdAt: draftKey ? undefined : Date.now(),
      updatedAt: Date.now(),
    };

    if (draftKey) await dbPatch(`inspections/${draftKey}`, payload);
    else await dbPush("inspections", { ...payload, createdAt: Date.now() });

    await fetch(`${WORKER_URL}/notify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: `🚚 Inspection Submitted — ${fleet?.business}`,
        message: `${profile.firstName} ${profile.lastName} inspected ${sheets.length} vehicle${sheets.length !== 1 ? "s" : ""}.\n\n⚠️ ${flagged} flagged (${urgent} need attention now)\n\n💰 Provider pay: $${FLEET_PROVIDER_PAY}\n\n➡️ Every flagged item is a job you can quote.`,
      }),
    }).catch(() => {});

    setSaving(false);
    setMode("list");
    setSheets([]); setFleetId(""); setDraftKey(null); setActive(0);
    load();
    alert(`✅ Submitted. $${FLEET_PROVIDER_PAY} added to your earnings.`);
  }

  // ---------- LIST ----------
  if (mode === "list") {
    const q = search.trim().toLowerCase();
    const shown = q ? fleets.filter(x => [x.business, x.city, x.state, x.contactName]
      .some(v => (v || "").toLowerCase().includes(q))) : fleets;

    return (
      <div>
        <div style={{ background: "linear-gradient(135deg,#0a1f18 0%,#0f0f0f 100%)", border: "1px solid #14b8a6", borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: "#14b8a6", fontWeight: 800, marginBottom: 7 }}>🚚 FLEET INSPECTIONS</div>
          <div style={{ fontSize: 15, fontWeight: 900, color: "#fff", marginBottom: 6 }}>
            ${FLEET_PROVIDER_PAY} per visit
          </div>
          <div style={{ fontSize: 12.5, color: "#999", lineHeight: 1.65 }}>
            Flat ${FLEET_PROVIDER_PAY} no matter how many vehicles. Fill out a sheet for each one, take photos, submit. Save as you go — it won't lose your work.
          </div>
        </div>

        {drafts.length > 0 && (
          <>
            <div style={{ fontSize: 11, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 10 }}>📝 UNFINISHED</div>
            {drafts.map(d => (
              <button key={d._key}
                onClick={() => {
                  setFleetId(d.fleetId); setSheets(d.sheets || []); setDraftKey(d._key);
                  setActive(0); setMode("edit");
                }}
                style={{ width: "100%", textAlign: "left", background: "#1a1400", border: "1px solid #f59e0b",
                  borderRadius: 10, padding: 14, marginBottom: 9, cursor: "pointer" }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>{d.fleetBusiness}</div>
                <div style={{ fontSize: 11.5, color: "#f59e0b", marginTop: 3 }}>
                  {(d.sheets || []).length} vehicle{(d.sheets || []).length !== 1 ? "s" : ""} started · {timeAgo(d.updatedAt || d.createdAt)}
                </div>
              </button>
            ))}
            <div style={{ height: 18 }} />
          </>
        )}

        <div style={{ fontSize: 11, letterSpacing: 2, color: "#14b8a6", fontWeight: 800, marginBottom: 10 }}>START AN INSPECTION</div>

        {fleets.length > 3 && (
          <input style={{ ...c.input, marginBottom: 10 }} placeholder="🔍 Search by business or city..."
            value={search} onChange={e => setSearch(e.target.value)} />
        )}

        {fleets.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#555" }}>
            <div style={{ fontSize: 34, marginBottom: 10 }}>🚚</div>
            <div style={{ fontSize: 13 }}>No fleet accounts assigned yet.</div>
            <div style={{ fontSize: 11.5, color: "#666", marginTop: 8 }}>Dispatch will let you know when there's a visit.</div>
          </div>
        )}

        {shown.map(x => (
          <button key={x._key}
            onClick={() => { setFleetId(x._key); setSheets([blankSheet()]); setActive(0); setDraftKey(null); setMode("edit"); }}
            style={{ width: "100%", textAlign: "left", background: "#161616", border: "1px solid #222",
              borderLeft: "4px solid #14b8a6", borderRadius: 10, padding: 15, marginBottom: 9, cursor: "pointer" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#fff" }}>{x.business}</div>
            <div style={{ fontSize: 11.5, color: "#888", marginTop: 3 }}>
              {x.vehicleCount} vehicle{x.vehicleCount !== 1 ? "s" : ""} · 📍 {x.city}, {x.state}
            </div>
            <div style={{ fontSize: 11.5, color: "#666", marginTop: 3 }}>
              {x.contactName} · {x.phone}
            </div>
          </button>
        ))}
      </div>
    );
  }

  // ---------- EDIT ----------
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <button style={{ background: "none", border: "1px solid #333", color: "#aaa", padding: "8px 13px", borderRadius: 6, cursor: "pointer", fontSize: 12.5 }}
          onClick={() => { if (window.confirm("Leave this inspection? Save first if you want to keep it.")) setMode("list"); }}>
          ← Back
        </button>
        <button style={{ background: "#1a1400", border: "1px solid #f59e0b", color: "#f59e0b", padding: "8px 13px", borderRadius: 6, cursor: "pointer", fontSize: 12.5, fontWeight: 700 }}
          onClick={saveDraft} disabled={saving}>
          {saving ? "Saving..." : "💾 Save"}
        </button>
      </div>

      <div style={{ background: "#0a1f18", border: "1px solid #14b8a6", borderRadius: 10, padding: 13, marginBottom: 14 }}>
        <div style={{ fontSize: 14.5, fontWeight: 900, color: "#fff" }}>{fleet?.business}</div>
        <div style={{ fontSize: 11.5, color: "#14b8a6", marginTop: 3 }}>
          {fleet?.address}, {fleet?.city}, {fleet?.state}
        </div>
        <div style={{ fontSize: 11.5, color: "#888", marginTop: 3 }}>
          Fleet of {fleet?.vehicleCount} · {sheets.length} sheet{sheets.length !== 1 ? "s" : ""} started
        </div>
      </div>

      {/* Sheet tabs */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {sheets.map((x, i) => (
          <button key={i} onClick={() => setActive(i)}
            style={{ background: active === i ? "#14b8a6" : "#161616",
              border: `1px solid ${active === i ? "#14b8a6" : "#2a2a2a"}`,
              color: active === i ? "#000" : "#888",
              padding: "8px 13px", borderRadius: 14, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            {x.unit || x.plate || `Vehicle ${i + 1}`}
          </button>
        ))}
        <button onClick={() => { setSheets(prev => [...prev, blankSheet()]); setActive(sheets.length); }}
          style={{ background: "none", border: "1px dashed #14b8a6", color: "#14b8a6",
            padding: "8px 14px", borderRadius: 14, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
          ➕ Add Sheet
        </button>
      </div>

      {sheets.length > 1 && (
        <button onClick={() => {
            if (!window.confirm(`Remove ${sh.unit || sh.plate || `Vehicle ${active + 1}`}?`)) return;
            setSheets(prev => prev.filter((_, i) => i !== active));
            setActive(Math.max(0, active - 1));
          }}
          style={{ background: "none", border: "1px solid #E63329", color: "#E63329", padding: "7px 12px",
            borderRadius: 7, fontSize: 11.5, cursor: "pointer", marginBottom: 14 }}>
          🗑️ Remove this vehicle
        </button>
      )}

      {/* Vehicle details */}
      <div style={c.sectionLabel}>VEHICLE {active + 1} OF {sheets.length}</div>
      <Field label="Unit # / Name" value={sh.unit} onChange={e => updateSheet({ unit: e.target.value })} placeholder="e.g. Truck 12" autoCapitalize="characters" />
      <div style={{ display: "flex", gap: 9, marginBottom: 12 }}>
        <div style={{ flex: 1 }}><label style={c.label}>Year</label>
          <input style={c.input} inputMode="numeric" value={sh.year} onChange={e => updateSheet({ year: e.target.value })} placeholder="2019" /></div>
        <div style={{ flex: 1.4 }}><label style={c.label}>Make</label>
          <input style={c.input} value={sh.make} onChange={e => updateSheet({ make: e.target.value })} placeholder="Ford" /></div>
      </div>
      <div style={{ display: "flex", gap: 9, marginBottom: 12 }}>
        <div style={{ flex: 1.4 }}><label style={c.label}>Model</label>
          <input style={c.input} value={sh.model} onChange={e => updateSheet({ model: e.target.value })} placeholder="Transit 250" /></div>
        <div style={{ flex: 1 }}><label style={c.label}>Plate</label>
          <input style={c.input} value={sh.plate} onChange={e => updateSheet({ plate: e.target.value.toUpperCase() })} placeholder="ABC1234" /></div>
      </div>
      <Field label="VIN (optional)" value={sh.vin} onChange={e => updateSheet({ vin: e.target.value.toUpperCase() })} placeholder="17 characters" maxLength={17} />
      <Field label="Mileage" value={sh.mileage} onChange={e => updateSheet({ mileage: e.target.value.replace(/\D/g, "") })} inputMode="numeric" placeholder="87450" />

      {/* Checklist */}
      {INSPECTION_SECTIONS.map(sec => (
        <div key={sec.id} style={{ background: "#161616", border: "1px solid #222", borderRadius: 11, padding: 14, marginBottom: 12 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: "#14b8a6", marginBottom: 12 }}>{sec.label}</div>
          {sec.items.map(it => (
            <div key={it.id} style={{ marginBottom: 13, paddingBottom: 13, borderBottom: "1px solid #1e1e1e" }}>
              <div style={{ fontSize: 12.5, color: "#ccc", marginBottom: 7 }}>{it.label}</div>

              {(it.type === "depth" || it.type === "psi" || it.type === "number") && (
                <input style={{ ...c.input, marginBottom: 8 }} inputMode="decimal"
                  placeholder={it.type === "depth" ? "e.g. 6/32" : it.type === "psi" ? "e.g. 35" : "e.g. 12.6"}
                  value={sh.values?.[it.id] || ""} onChange={e => setValue(it.id, e.target.value)} />
              )}

              <div style={{ display: "flex", gap: 5 }}>
                {STATUS_OPTIONS.map(o => {
                  const on = sh.results?.[it.id] === o.id;
                  return (
                    <button key={o.id} onClick={() => setResult(it.id, o.id)}
                      style={{ flex: 1, background: on ? o.color : "#0f0f0f",
                        border: `1px solid ${on ? o.color : "#2a2a2a"}`,
                        color: on ? (o.id === "na" ? "#fff" : "#000") : "#777",
                        padding: "9px 2px", borderRadius: 7, fontSize: 11, fontWeight: 800, cursor: "pointer" }}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Photos */}
      <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 11, padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: "#14b8a6", marginBottom: 10 }}>
          📷 Photos ({(sh.photos || []).length})
        </div>
        {(sh.photos || []).length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 7, marginBottom: 11 }}>
            {sh.photos.map((url, i) => (
              <div key={i} style={{ position: "relative" }}>
                <img src={url} alt={`Photo ${i + 1}`} style={{ width: "100%", borderRadius: 7, border: "1px solid #333", display: "block" }} />
                <button onClick={() => updateSheet({ photos: sh.photos.filter((_, x) => x !== i) })}
                  style={{ position: "absolute", top: 3, right: 3, background: "rgba(0,0,0,0.8)", border: "1px solid #E63329",
                    color: "#E63329", width: 22, height: 22, borderRadius: "50%", fontSize: 11, cursor: "pointer", lineHeight: 1 }}>
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        {uploading && <div style={{ fontSize: 12, color: "#f59e0b", marginBottom: 9 }}>{uploading}</div>}
        <button style={{ ...c.gpsBtn, border: "2px dashed #14b8a6", color: "#14b8a6" }} onClick={addPhoto} disabled={!!uploading}>
          📷 Add Photo
        </button>
        <div style={{ fontSize: 11, color: "#555", marginTop: 8, lineHeight: 1.5 }}>
          Photograph anything you flag — worn tread, corroded terminals, cracked wipers. The photo is what sells the repair.
        </div>
      </div>

      {/* Notes */}
      <div style={c.fieldGroup}>
        <label style={c.label}>Notes on this vehicle</label>
        <textarea style={{ ...c.input, minHeight: 90, resize: "vertical" }}
          placeholder="Anything the fleet manager should know about this one."
          value={sh.notes} onChange={e => updateSheet({ notes: e.target.value })} />
      </div>

      <div style={{ background: "#1a1400", border: "1px solid #f59e0b", borderRadius: 10, padding: 13, marginBottom: 14, fontSize: 11.5, color: "#f59e0b", lineHeight: 1.65 }}>
        ⚠️ Brakes are <strong>visual only — do not remove wheels</strong>. If you can't see it clearly, mark N/A rather than guessing.
      </div>

      <button style={{ ...c.btnPrimary, background: "#14b8a6", color: "#000" }} onClick={submitReport} disabled={saving}>
        {saving ? "Submitting..." : `✅ Submit ${sheets.length} Vehicle${sheets.length !== 1 ? "s" : ""} — $${FLEET_PROVIDER_PAY}`}
      </button>
      <button style={c.btnSecondary} onClick={saveDraft} disabled={saving}>
        💾 Save & Finish Later
      </button>
    </div>
  );
}

function FleetPortal() {
  const [authUser, setAuthUser] = useState(null);
  const [screen, setScreen] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [f, setF] = useState({
    business: "", owner: "", contactName: "", phone: "", altPhone: "",
    address: "", city: "", state: "", zip: "",
    email: "", password: "", vehicles: "10", locations: "1",
    businessType: "", notes: "",
  });
  const [spLoading, setSpLoading] = useState(false);
  const [spDone, setSpDone] = useState("");

  useEffect(() => {
    try {
      const saved = localStorage.getItem("sfrs_fleet");
      if (saved) { const u = JSON.parse(saved); setAuthToken(u.token, u.refreshToken); setAuthUser(u); }
    } catch {}
  }, []);

  function signOut() {
    localStorage.removeItem("sfrs_fleet");
    setAuthToken(null, null);
    setAuthUser(null);
  }

  async function handleLogin() {
    setError("");
    if (!email || !password) return setError("Email and password required.");
    setLoading(true);
    const r = await fbSignIn(email.trim(), password);
    if (r.error) { setLoading(false); return setError(fbFormatError(r.error)); }
    setAuthToken(r.token, r.refreshToken);

    const res = await loadProfileAfterLogin(r.uid);
    if (res.error) {
      setLoading(false);
      return setError(
        res.reason === "missing"
          ? "Your login works but your fleet details didn't save. Call (561) 255-8506 — we'll fix it in a minute."
          : res.reason === "rejected" ? `Access refused: ${res.error}`
          : "Couldn't reach the server. Check your signal and try again."
      );
    }
    const profile = res.profile;
    if (profile.role !== "fleet") {
      setLoading(false);
      return setError(
        profile.role === "partner" ? "That's a business dispatch account — sign in at /partner."
        : profile.role === "provider" ? "That's a provider account — sign in at /provider."
        : profile.role === "customer" ? "That's a customer account. Sign in on the main app."
        : "This isn't a fleet maintenance account."
      );
    }
    const u = { ...r, profile };
    localStorage.setItem("sfrs_fleet", JSON.stringify(u));
    setLoading(false);
    setAuthUser(u);
  }

  async function handleSignup() {
    setError("");
    for (const [k, lbl] of [["business","business name"],["owner","owner's name"],["contactName","contact name"],["phone","phone"],["address","address"],["city","city"],["state","state"],["email","email"],["password","password"]]) {
      if (!String(f[k] || "").trim()) return setError(`Please enter your ${lbl}.`);
    }
    if (f.password.length < 6) return setError("Password must be at least 6 characters.");
    const veh = parseInt(f.vehicles, 10) || 0;
    if (veh < 1) return setError("How many vehicles are in your fleet?");

    setSpLoading(true);
    const acct = await createAccount(f.email.trim(), f.password, {
      role: "fleet",
      status: "approved",
      business: f.business.trim(), owner: f.owner.trim(),
      firstName: f.contactName.trim(), lastName: `(${f.business.trim()})`,
      contactName: f.contactName.trim(),
      phone: f.phone.trim(), altPhone: f.altPhone.trim(),
      address: f.address.trim(), city: f.city.trim(), state: f.state, zip: f.zip.trim(),
      email: f.email.trim().toLowerCase(),
      businessType: f.businessType, notes: f.notes.trim(),
      vehicleCount: veh,
      locationCount: parseInt(f.locations, 10) || 1,
      monthlyRate: fleetMonthly(veh),
      subRequired: true,
      createdAt: Date.now(),
    });

    if (acct.error) { setSpLoading(false); return setError(acct.error); }

    await fbSendVerification(acct.token);
    await fetch(`${WORKER_URL}/notify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: `🚚 New Fleet Account — $${fleetMonthly(veh)}/mo`,
        message: `${f.business}\n${f.businessType || "Type not given"}\n\n${veh} vehicles · ${f.locations} location(s)\nMonthly: $${fleetMonthly(veh)}\n\nOwner: ${f.owner}\nContact: ${f.contactName}\n📞 ${f.phone}\n📧 ${f.email}\n📍 ${f.address}, ${f.city}, ${f.state} ${f.zip}\n${f.notes ? `\nNotes: ${f.notes}` : ""}\n\n✅ Auto-approved — activates once subscribed.`,
      }),
    }).catch(() => {});

    setSpLoading(false);
    setSpDone(`✅ You're in! Verify your email, then sign in and start your $${fleetMonthly(veh)}/month account.\n\n📬 Don't see the verification email? Check your spam or junk folder.`);
  }

  if (authUser) {
    const prof = authUser.profile || {};
    if (prof.subRequired && !prof.subscribed) {
      return <FleetSubGate
        authUser={authUser}
        onSignOut={signOut}
        onSubscribed={() => {
          const u = { ...authUser, profile: { ...authUser.profile, subscribed: true } };
          localStorage.setItem("sfrs_fleet", JSON.stringify(u));
          setAuthUser(u);
        }}
      />;
    }
    return <FleetDashboard authUser={authUser} onSignOut={signOut} />;
  }

  const veh = parseInt(f.vehicles, 10) || 0;

  return (
    <div style={c.root}>
      <header style={{ ...c.header, background: "#0a1a12", borderBottom: "3px solid #14b8a6" }}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div><div style={c.logoTop}>SUPER FAST</div><div style={{ ...c.logoBot, color: "#14b8a6" }}>FLEET</div></div>
        </div>
      </header>

      <div style={c.page}>
        {screen === "signup" ? (
          spDone ? (
            <div style={{ background: "#0a1f18", border: "1px solid #14b8a6", borderRadius: 12, padding: 22, textAlign: "center", marginTop: 20 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 13.5, color: "#14b8a6", lineHeight: 1.7, whiteSpace: "pre-line" }}>{spDone}</div>
              <button style={{ ...c.btnSecondary, marginTop: 18 }} onClick={() => { setScreen("login"); setSpDone(""); }}>← Back to Login</button>
            </div>
          ) : (
            <>
              <div style={{ textAlign: "center", marginTop: 8, marginBottom: 18 }}>
                <div style={{ fontSize: 38, marginBottom: 10 }}>🚚</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: "#fff", lineHeight: 1.15, marginBottom: 8 }}>
                  Know What's About to Break.
                </div>
                <div style={{ fontSize: 13.5, color: "#999", lineHeight: 1.6 }}>
                  Monthly on-site inspection of every vehicle, with a written report and photos. Catch it in the yard instead of on the shoulder.
                </div>
              </div>

              <div style={{ background: "#0a1f18", border: "1px solid #14b8a6", borderRadius: 12, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 10, letterSpacing: 2, color: "#14b8a6", fontWeight: 800, marginBottom: 10 }}>WHAT WE CHECK, EVERY VEHICLE</div>
                {[
                  ["🛞", "Tires", "Tread depth all four corners, pressures, uneven wear, spare"],
                  ["🔋", "Battery", "Voltage, load test, terminals and cables"],
                  ["💧", "Fluids", "Oil, coolant, brake, power steering, washer"],
                  ["💡", "Lights & wipers", "Head, tail, brake, signals, blade condition"],
                  ["🛑", "Brakes", "Visual pad check, noise or pulling reported"],
                ].map(([ic, t, d]) => (
                  <div key={t} style={{ display: "flex", gap: 11, marginBottom: 10, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{ic}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>{t}</div>
                      <div style={{ fontSize: 11.5, color: "#999", lineHeight: 1.5, marginTop: 1 }}>{d}</div>
                    </div>
                  </div>
                ))}
                <div style={{ background: "#0f0f0f", borderRadius: 8, padding: 11, marginTop: 4, fontSize: 11.5, color: "#888", lineHeight: 1.6 }}>
                  You get a per-vehicle written report with photos and a flag list of what's coming due — so you can budget instead of react.
                </div>
              </div>

              {/* Live price */}
              <div style={{ background: "linear-gradient(135deg,#0a1f18 0%,#0f0f0f 100%)", border: "2px solid #14b8a6", borderRadius: 12, padding: 18, marginBottom: 12 }}>
                <div style={{ fontSize: 10, letterSpacing: 2, color: "#14b8a6", fontWeight: 800, marginBottom: 12 }}>YOUR MONTHLY RATE</div>
                <label style={c.label}>How many vehicles?</label>
                <input style={{ ...c.input, marginBottom: 12 }} type="number" inputMode="numeric" min="1"
                  value={f.vehicles} onChange={e => setF(p => ({ ...p, vehicles: e.target.value }))} />
                <div style={{ display: "flex", alignItems: "baseline", gap: 7 }}>
                  <span style={{ fontSize: 38, fontWeight: 900, color: "#14b8a6" }}>${fleetMonthly(f.vehicles)}</span>
                  <span style={{ fontSize: 14, color: "#888" }}>/month</span>
                </div>
                <div style={{ fontSize: 11.5, color: "#777", marginTop: 6, lineHeight: 1.55 }}>
                  {veh < FLEET_BREAKEVEN
                    ? `$${FLEET_MIN} monthly minimum (covers up to ${FLEET_BREAKEVEN - 1} vehicles)`
                    : `${veh} vehicles × $${FLEET_PER_VEHICLE}`}
                </div>
              </div>

              {/* Extra work rates */}
              <div style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: 12, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 10, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 10 }}>REPAIRS — BILLED SEPARATELY</div>
                {[
                  ["Tire mount & balance, on site", "$40/tire + tire at cost +30%"],
                  ["Tire rotation", "$60/vehicle"],
                  ["Battery replacement", "$45 labor + battery at cost +30%"],
                  ["Bulbs, wipers, filters", "$25 labor + parts at cost +40%"],
                  ["Anything major", "Quoted per job"],
                ].map(([svc, price]) => (
                  <div key={svc} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "7px 0", borderBottom: "1px solid #1e1e1e", fontSize: 12 }}>
                    <span style={{ color: "#bbb" }}>{svc}</span>
                    <span style={{ color: "#f59e0b", fontWeight: 700, textAlign: "right", whiteSpace: "nowrap" }}>{price}</span>
                  </div>
                ))}
                <div style={{ fontSize: 11.5, color: "#666", marginTop: 11, lineHeight: 1.6 }}>
                  You approve every repair before we touch it. Nothing gets done off a report without your say-so.
                </div>
              </div>

              <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 12, padding: 15, marginBottom: 20 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: "#3b82f6", marginBottom: 6 }}>🚨 Roadside included too</div>
                <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.65 }}>
                  Truck down between visits? Dispatch a job right from your portal — ${PARTNER_RATE} flat, 24/7. No separate account needed.
                </div>
              </div>

              <div style={c.sectionLabel}>BUSINESS INFORMATION</div>
              <Field label="Business Name" value={f.business} onChange={e => setF(p => ({ ...p, business: e.target.value }))} placeholder="e.g. Coastal Plumbing" autoCapitalize="words" />
              <div style={c.fieldGroup}><label style={c.label}>Business Type</label>
                <select style={c.input} value={f.businessType} onChange={e => setF(p => ({ ...p, businessType: e.target.value }))}>
                  <option value="">Select...</option>
                  {["Plumbing / HVAC","Electrical","Landscaping","Construction","Delivery / courier","Trucking","Pest control","Cleaning service","Security","Medical transport","Rental fleet","Auto dealership","Municipal","Other"].map(t => <option key={t}>{t}</option>)}
                </select></div>
              <Field label="Owner's Full Name" value={f.owner} onChange={e => setF(p => ({ ...p, owner: e.target.value }))} autoCapitalize="words" />
              <Field label="Fleet Contact (who we coordinate with)" value={f.contactName} onChange={e => setF(p => ({ ...p, contactName: e.target.value }))} autoCapitalize="words" />

              <div style={c.sectionLabel}>CONTACT</div>
              <Field label="Main Phone" value={f.phone} onChange={e => setF(p => ({ ...p, phone: e.target.value }))} type="tel" inputMode="tel" placeholder="(561) 000-0000" />
              <Field label="Backup Phone (optional)" value={f.altPhone} onChange={e => setF(p => ({ ...p, altPhone: e.target.value }))} type="tel" inputMode="tel" />
              <Field label="Email" value={f.email} onChange={e => setF(p => ({ ...p, email: e.target.value }))} type="email" inputMode="email" placeholder="fleet@yourbusiness.com" />
              <Field label="Password" value={f.password} onChange={e => setF(p => ({ ...p, password: e.target.value }))} type="password" placeholder="Min 6 characters" />

              <div style={c.sectionLabel}>WHERE YOUR VEHICLES ARE</div>
              <Field label="Street Address" value={f.address} onChange={e => setF(p => ({ ...p, address: e.target.value }))} autoCapitalize="words" />
              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 2 }}><label style={c.label}>City</label>
                  <input style={c.input} value={f.city} onChange={e => setF(p => ({ ...p, city: e.target.value }))} /></div>
                <div style={{ flex: 1 }}><label style={c.label}>State</label>
                  <select style={c.input} value={f.state} onChange={e => setF(p => ({ ...p, state: e.target.value }))}>
                    <option value="">—</option>
                    {US_STATES.map(st => <option key={st.code} value={st.code}>{st.code}</option>)}
                  </select></div>
              </div>
              <Field label="ZIP" value={f.zip} onChange={e => setF(p => ({ ...p, zip: e.target.value.replace(/\D/g, "") }))} inputMode="numeric" maxLength={5} />
              <div style={c.fieldGroup}><label style={c.label}>How many locations?</label>
                <select style={c.input} value={f.locations} onChange={e => setF(p => ({ ...p, locations: e.target.value }))}>
                  {[1,2,3,4,5].map(n => <option key={n} value={String(n)}>{n}{n === 5 ? "+" : ""}</option>)}
                </select></div>

              <div style={c.fieldGroup}><label style={c.label}>Anything Else? (optional)</label>
                <textarea style={{ ...c.input, minHeight: 80, resize: "vertical" }}
                  placeholder="Vehicle types, best day of the month for us to come out, gate codes, anything we should know."
                  value={f.notes} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} /></div>

              {error && <div style={c.gpsError}>⚠️ {error}</div>}
              <button style={{ ...c.btnPrimary, background: "#14b8a6", color: "#000" }} onClick={handleSignup} disabled={spLoading}>
                {spLoading ? "Creating account..." : `Start Fleet Account — $${fleetMonthly(f.vehicles)}/mo →`}
              </button>
              <button style={c.btnSecondary} onClick={() => { setScreen("login"); setError(""); }}>← Back to Login</button>
            </>
          )
        ) : (
          <>
            <div style={{ textAlign: "center", marginTop: 16, marginBottom: 26 }}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>🚚</div>
              <div style={au.title}>Fleet Maintenance</div>
              <div style={au.sub}>Sign in to see your reports and flagged items</div>
            </div>
            <div style={c.fieldGroup}><label style={c.label}>Email</label>
              <input style={c.input} type="email" value={email} onChange={e => setEmail(e.target.value)} /></div>
            <div style={c.fieldGroup}><label style={c.label}>Password</label>
              <input style={c.input} type="password" value={password} onChange={e => setPassword(e.target.value)} /></div>
            {error && <div style={c.gpsError}>⚠️ {error}</div>}
            <button style={{ ...c.btnPrimary, background: "#14b8a6", color: "#000" }} onClick={handleLogin} disabled={loading}>
              {loading ? "Signing In..." : "Sign In →"}
            </button>

            <div style={{ marginTop: 24, background: "#0a1f18", border: "1px solid #14523f", borderRadius: 10, padding: 16, textAlign: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", marginBottom: 4 }}>Running a fleet?</div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 12, lineHeight: 1.55 }}>
                Monthly inspection of every vehicle with a written report. Know what's about to break before it does.
              </div>
              <button style={{ ...c.btnPrimary, background: "none", color: "#14b8a6", border: "1px solid #14b8a6", fontSize: 14 }}
                onClick={() => { setScreen("signup"); setError(""); }}>
                🚚 Start a Fleet Account
              </button>
            </div>
            <div style={{ fontSize: 12, color: "#555", textAlign: "center", marginTop: 16 }}>
              Questions? <a href="tel:5612558506" style={{ color: "#14b8a6" }}>📞 (561) 255-8506</a>
            </div>
          </>
        )}
      </div>
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC</div>
    </div>
  );
}

function FleetDashboard({ authUser, onSignOut }) {
  const profile = authUser?.profile || {};
  const [tab, setTab] = useState("reports");
  const [reports, setReports] = useState([]);
  const [openReport, setOpenReport] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    const all = await dbGet("inspections");
    if (Array.isArray(all)) {
      setReports(all.filter(x => x.fleetId === authUser.uid).sort((a, b) => b.createdAt - a.createdAt));
    }
    setLoading(false);
  }
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, []);

  // Every flagged item across every report, newest first
  const flags = [];
  reports.forEach(r => (r.sheets || []).forEach(sh => {
    Object.entries(sh.results || {}).forEach(([k, v]) => {
      if (v === "soon" || v === "now") {
        let label = k;
        INSPECTION_SECTIONS.forEach(sec => sec.items.forEach(it => { if (it.id === k) label = it.label; }));
        flags.push({ vehicle: sh.unit || sh.plate || "Vehicle", label, severity: v, date: r.createdAt, reportKey: r._key });
      }
    });
  }));
  const urgent = flags.filter(x => x.severity === "now");

  return (
    <div style={c.root}>
      <header style={{ ...c.header, background: "#0a1a12", borderBottom: "3px solid #14b8a6" }}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div>
            <div style={{ ...c.logoTop, fontSize: 13 }}>{profile.business || "FLEET"}</div>
            <div style={{ ...c.logoBot, color: "#14b8a6", fontSize: 10 }}>FLEET MAINTENANCE</div>
          </div>
        </div>
        <button style={{ ...c.back, color: "#E63329", borderColor: "#E63329" }} onClick={onSignOut}>Sign Out</button>
      </header>

      <div style={au.tabs}>
        <button style={{ ...au.tab, ...(tab === "reports" ? { ...au.tabActive, background: "#14b8a6", color: "#000" } : {}) }}
          onClick={() => { setTab("reports"); setOpenReport(null); }}>
          📋 Reports ({reports.length})
        </button>
        <button style={{ ...au.tab, ...(tab === "flags" ? { ...au.tabActive, background: "#f59e0b", color: "#000" } : {}) }}
          onClick={() => { setTab("flags"); setOpenReport(null); }}>
          ⚠️ Flagged ({flags.length})
        </button>
        <button style={{ ...au.tab, ...(tab === "dispatch" ? { ...au.tabActive, background: "#E63329" } : {}) }}
          onClick={() => { setTab("dispatch"); setOpenReport(null); }}>
          🚨 Dispatch
        </button>
      </div>

      <div style={c.page}>
        {/* Account summary */}
        {tab === "reports" && !openReport && (
          <div style={{ background: "#0a1f18", border: "1px solid #14b8a6", borderRadius: 11, padding: 14, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#14b8a6" }}>{profile.vehicleCount} vehicles</div>
                <div style={{ fontSize: 10.5, color: "#4caf50", marginTop: 2 }}>
                  {profile.locationCount || 1} location{(profile.locationCount || 1) !== 1 ? "s" : ""} · inspected monthly
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 19, fontWeight: 900, color: "#fff" }}>${profile.monthlyRate || fleetMonthly(profile.vehicleCount)}</div>
                <div style={{ fontSize: 10, color: "#888" }}>per month</div>
              </div>
            </div>
            {urgent.length > 0 && (
              <div style={{ background: "#1a0a0a", border: "1px solid #E63329", borderRadius: 8, padding: 10, marginTop: 12, fontSize: 12, color: "#ff8080", lineHeight: 1.55 }}>
                🚨 <strong>{urgent.length} item{urgent.length !== 1 ? "s" : ""} need attention now.</strong> Check the Flagged tab.
              </div>
            )}
          </div>
        )}

        {/* REPORTS */}
        {tab === "reports" && (openReport ? (
          <FleetReportView report={openReport} onBack={() => setOpenReport(null)} />
        ) : (
          <>
            {loading && <div style={{ textAlign: "center", padding: 40, color: "#666" }}>Loading...</div>}
            {!loading && reports.length === 0 && (
              <div style={{ textAlign: "center", padding: "50px 0", color: "#555" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                <div style={{ fontSize: 14, color: "#888", marginBottom: 6 }}>No inspections yet</div>
                <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                  We'll be out for your first visit shortly.<br />Reports show up here with photos.
                </div>
              </div>
            )}
            {reports.map(r => {
              const sheets = r.sheets || [];
              const rFlags = sheets.reduce((n, sh) =>
                n + Object.values(sh.results || {}).filter(v => v === "soon" || v === "now").length, 0);
              const rNow = sheets.reduce((n, sh) =>
                n + Object.values(sh.results || {}).filter(v => v === "now").length, 0);
              return (
                <button key={r._key} onClick={() => setOpenReport(r)}
                  style={{ width: "100%", textAlign: "left", background: "#161616",
                    border: `1px solid ${rNow > 0 ? "#E63329" : "#222"}`, borderRadius: 11,
                    padding: 15, marginBottom: 11, cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 900, color: "#fff" }}>
                        {new Date(r.createdAt).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                      </div>
                      <div style={{ fontSize: 11.5, color: "#888", marginTop: 2 }}>
                        {sheets.length} vehicle{sheets.length !== 1 ? "s" : ""} inspected
                        {r.techName ? ` · ${r.techName}` : ""}
                      </div>
                    </div>
                    <span style={{ color: "#444", fontSize: 20 }}>›</span>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {rNow > 0 && (
                      <span style={{ fontSize: 10.5, fontWeight: 800, background: "#1a0a0a", border: "1px solid #E63329", color: "#E63329", padding: "3px 9px", borderRadius: 10 }}>
                        {rNow} needs now
                      </span>
                    )}
                    {rFlags - rNow > 0 && (
                      <span style={{ fontSize: 10.5, fontWeight: 800, background: "#1a1400", border: "1px solid #f59e0b", color: "#f59e0b", padding: "3px 9px", borderRadius: 10 }}>
                        {rFlags - rNow} due soon
                      </span>
                    )}
                    {rFlags === 0 && (
                      <span style={{ fontSize: 10.5, fontWeight: 800, background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e", padding: "3px 9px", borderRadius: 10 }}>
                        ✓ All clear
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#555", marginTop: 9 }}>{timeAgo(r.createdAt)}</div>
                </button>
              );
            })}
          </>
        ))}

        {/* FLAGGED */}
        {tab === "flags" && (
          <>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", marginBottom: 4 }}>What's Coming Due</div>
            <div style={{ fontSize: 11.5, color: "#666", marginBottom: 16, lineHeight: 1.55 }}>
              Everything flagged across your inspections. Call us to get any of it quoted — nothing happens without your approval.
            </div>
            {flags.length === 0 && (
              <div style={{ textAlign: "center", padding: "50px 0", color: "#555" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <div style={{ fontSize: 14, color: "#22c55e" }}>Nothing flagged — your fleet's in good shape.</div>
              </div>
            )}
            {["now", "soon"].map(sev => {
              const list = flags.filter(x => x.severity === sev);
              if (list.length === 0) return null;
              const meta = sev === "now"
                ? { label: "🚨 NEEDS ATTENTION NOW", color: "#E63329", bg: "#1a0a0a" }
                : { label: "⚠️ DUE SOON", color: "#f59e0b", bg: "#1a1400" };
              return (
                <div key={sev} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: meta.color, fontWeight: 800, marginBottom: 10 }}>{meta.label}</div>
                  {list.map((x, i) => (
                    <div key={i} style={{ background: meta.bg, border: `1px solid ${meta.color}`, borderRadius: 9, padding: 13, marginBottom: 8 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 800, color: "#fff" }}>{x.vehicle}</div>
                      <div style={{ fontSize: 12.5, color: meta.color, marginTop: 3 }}>{x.label}</div>
                      <div style={{ fontSize: 10.5, color: "#666", marginTop: 5 }}>
                        Flagged {new Date(x.date).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
            {flags.length > 0 && (
              <a href="tel:5612558506" style={{ display: "block", textAlign: "center", background: "#14b8a6", color: "#000", padding: "15px", borderRadius: 11, fontWeight: 900, fontSize: 15, textDecoration: "none", marginTop: 8 }}>
                📞 Get These Quoted — (561) 255-8506
              </a>
            )}
          </>
        )}

        {/* DISPATCH */}
        {tab === "dispatch" && (
          <FleetDispatch authUser={authUser} profile={profile} />
        )}
      </div>
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC · Fleet</div>
    </div>
  );
}

// Read-only view of one month's inspection
function FleetReportView({ report, onBack }) {
  const [sheetIdx, setSheetIdx] = useState(0);
  const [photo, setPhoto] = useState(null);
  const sheets = report.sheets || [];
  const sh = sheets[sheetIdx] || {};

  return (
    <div>
      <button style={{ background: "none", border: "1px solid #333", color: "#aaa", padding: "8px 14px", borderRadius: 6, cursor: "pointer", marginBottom: 16, fontSize: 13 }}
        onClick={onBack}>← All Reports</button>

      <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 3 }}>
        {new Date(report.createdAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
      </div>
      <div style={{ fontSize: 12, color: "#666", marginBottom: 16 }}>
        {sheets.length} vehicle{sheets.length !== 1 ? "s" : ""}{report.techName ? ` · inspected by ${report.techName}` : ""}
      </div>

      {/* Vehicle picker */}
      {sheets.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
          {sheets.map((x, i) => (
            <button key={i} onClick={() => setSheetIdx(i)}
              style={{ background: sheetIdx === i ? "#14b8a6" : "#161616",
                border: `1px solid ${sheetIdx === i ? "#14b8a6" : "#2a2a2a"}`,
                color: sheetIdx === i ? "#000" : "#888",
                padding: "7px 13px", borderRadius: 14, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
              {x.unit || x.plate || `Vehicle ${i + 1}`}
            </button>
          ))}
        </div>
      )}

      {/* Vehicle details */}
      <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 11, padding: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 900, color: "#fff", marginBottom: 8 }}>
          {sh.unit || sh.plate || `Vehicle ${sheetIdx + 1}`}
        </div>
        {[["Year / Make / Model", [sh.year, sh.make, sh.model].filter(Boolean).join(" ")],
          ["Plate", sh.plate], ["VIN", sh.vin], ["Mileage", sh.mileage ? `${sh.mileage} mi` : null]]
          .filter(([, v]) => v).map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #1e1e1e", fontSize: 12.5 }}>
            <span style={{ color: "#666" }}>{k}</span>
            <span style={{ color: "#ccc", textAlign: "right", maxWidth: "60%", wordBreak: "break-all" }}>{v}</span>
          </div>
        ))}
      </div>

      {/* Results */}
      {INSPECTION_SECTIONS.map(sec => {
        const any = sec.items.some(it => sh.results?.[it.id] !== undefined || sh.values?.[it.id]);
        if (!any) return null;
        return (
          <div key={sec.id} style={{ background: "#161616", border: "1px solid #222", borderRadius: 11, padding: 14, marginBottom: 11 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#14b8a6", marginBottom: 10 }}>{sec.label}</div>
            {sec.items.map(it => {
              const status = sh.results?.[it.id];
              const val = sh.values?.[it.id];
              if (status === undefined && !val) return null;
              const meta = STATUS_OPTIONS.find(o => o.id === status);
              return (
                <div key={it.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid #1e1e1e", fontSize: 12.5, gap: 10 }}>
                  <span style={{ color: "#aaa" }}>{it.label}</span>
                  <span style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {val && <span style={{ color: "#fff", fontWeight: 700, marginRight: meta ? 8 : 0 }}>{val}</span>}
                    {meta && (
                      <span style={{ fontSize: 10.5, fontWeight: 800, color: meta.color,
                        background: meta.id === "ok" ? "#0a1f0a" : meta.id === "soon" ? "#1a1400" : meta.id === "now" ? "#1a0a0a" : "#151515",
                        border: `1px solid ${meta.color}`, padding: "2px 8px", borderRadius: 9 }}>
                        {meta.label}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Notes */}
      {sh.notes && (
        <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 11, padding: 14, marginBottom: 11 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#14b8a6", marginBottom: 7 }}>📝 Tech Notes</div>
          <div style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.65 }}>{sh.notes}</div>
        </div>
      )}

      {/* Photos */}
      {(sh.photos || []).length > 0 && (
        <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 11, padding: 14, marginBottom: 11 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#14b8a6", marginBottom: 10 }}>📷 Photos ({sh.photos.length})</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {sh.photos.map((url, i) => (
              <img key={i} src={url} alt={`Inspection ${i + 1}`}
                onClick={() => setPhoto(url)}
                style={{ width: "100%", borderRadius: 8, border: "1px solid #333", cursor: "pointer", display: "block" }} />
            ))}
          </div>
        </div>
      )}

      <div style={{ background: "#1a1400", border: "1px solid #f59e0b", borderRadius: 10, padding: 13, fontSize: 11.5, color: "#f59e0b", lineHeight: 1.65 }}>
        ⚠️ Brake check is <strong>visual only — no wheel removal</strong>. It is not a substitute for a full brake inspection. Have any flagged brake item inspected properly before relying on it.
      </div>

      {photo && (
        <div onClick={() => setPhoto(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.96)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, cursor: "pointer" }}>
          <img src={photo} alt="full size" style={{ maxWidth: "100%", maxHeight: "85vh", borderRadius: 10, objectFit: "contain" }} />
          <button style={{ position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,0.12)", border: "1px solid #555", color: "#fff", width: 40, height: 40, borderRadius: "50%", fontSize: 18, cursor: "pointer" }}
            onClick={() => setPhoto(null)}>✕</button>
        </div>
      )}
    </div>
  );
}

// Fleet accounts can dispatch roadside at the partner rate
function FleetDispatch({ authUser, profile }) {
  const blank = {
    unit: "", customer: profile.contactName || "", phone: profile.phone || "",
    address: "", city: profile.city || "", state: profile.state || "", zip: profile.zip || "",
    service: "", note: "",
  };
  const [j, setJ] = useState(blank);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState("");

  const svc = PARTNER_SERVICES.find(x => x.id === j.service);
  const isQuote = !!svc?.quote;
  const canSend = j.customer && j.phone && j.address && j.city && j.state && j.service;

  async function send() {
    if (!canSend) return setErr("Fill in the contact, phone, address, city, state and service.");
    setErr("");
    setSending(true);
    const id = "FLT-" + genId();
    const full = `${j.address}, ${j.city}, ${j.state}${j.zip ? " " + j.zip : ""}`;

    await dbPush("jobs", {
      id,
      service: svc.label.replace(/^[^\s]+\s/, ""),
      serviceId: j.service,
      jobName: j.unit.trim() || null,
      customer: j.customer.trim(),
      phone: j.phone.trim(),
      address: full,
      note: j.note.trim() || null,
      price: isQuote ? null : PARTNER_RATE,
      quoteNeeded: isQuote,
      paid: false,
      billTo: "fleet",
      partnerId: authUser.uid,
      partnerBusiness: profile.business || "",
      partnerContact: profile.contactName || "",
      partnerPhone: profile.phone || "",
      fleetAccount: true,
      status: "new",
      tech: null, techName: null,
      createdAt: Date.now(),
    });

    await fetch(`${WORKER_URL}/notify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: `🚚 FLEET DISPATCH — ${profile.business || "Fleet"}`,
        message: `${svc.label}\n${j.unit ? `Unit ${j.unit}\n` : ""}${j.customer} · ${j.phone}\n📍 ${full}\n\n💰 ${isQuote ? "QUOTE NEEDED" : `$${PARTNER_RATE} — bill to fleet account`}\n${id}`,
      }),
    }).catch(() => {});

    setSending(false);
    setSent(id);
    setJ(blank);
  }

  if (sent) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{ fontSize: 54, marginBottom: 14 }}>✅</div>
        <div style={{ fontSize: 21, fontWeight: 900, color: "#22c55e", marginBottom: 8 }}>Dispatched</div>
        <div style={{ fontSize: 13.5, color: "#888", lineHeight: 1.65, marginBottom: 8 }}>
          We're assigning the closest tech now. It'll be added to your monthly invoice.
        </div>
        <div style={{ fontSize: 13, color: "#fff", fontWeight: 800, marginBottom: 20 }}>{sent}</div>
        <button style={{ ...c.btnPrimary, background: "#E63329" }} onClick={() => setSent(null)}>
          Dispatch Another
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ background: "#1a0a0a", border: "1px solid #E63329", borderRadius: 11, padding: 15, marginBottom: 18 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, color: "#E63329", fontWeight: 800, marginBottom: 7 }}>🚨 ROADSIDE DISPATCH</div>
        <div style={{ fontSize: 15, fontWeight: 900, color: "#fff", marginBottom: 6 }}>Truck down between visits?</div>
        <div style={{ fontSize: 12.5, color: "#999", lineHeight: 1.65 }}>
          ${PARTNER_RATE} flat per service call, 24/7. Added to your monthly invoice — no separate payment.
        </div>
      </div>

      <Field label="Unit # (optional)" value={j.unit} onChange={e => setJ(p => ({ ...p, unit: e.target.value }))} placeholder="e.g. Truck 12" />
      <Field label="Driver / Contact on Site" value={j.customer} onChange={e => setJ(p => ({ ...p, customer: e.target.value }))} autoCapitalize="words" />
      <Field label="Their Phone" value={j.phone} onChange={e => setJ(p => ({ ...p, phone: e.target.value }))} type="tel" inputMode="tel" />
      <Field label="Where Is It?" value={j.address} onChange={e => setJ(p => ({ ...p, address: e.target.value }))} placeholder="Street address" autoCapitalize="words" />

      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        <div style={{ flex: 2 }}><label style={c.label}>City</label>
          <input style={c.input} value={j.city} onChange={e => setJ(p => ({ ...p, city: e.target.value }))} /></div>
        <div style={{ flex: 1 }}><label style={c.label}>State</label>
          <select style={c.input} value={j.state} onChange={e => setJ(p => ({ ...p, state: e.target.value }))}>
            <option value="">—</option>
            {US_STATES.map(st => <option key={st.code} value={st.code}>{st.code}</option>)}
          </select></div>
      </div>

      <div style={c.sectionLabel}>WHAT'S NEEDED</div>
      <div style={{ marginBottom: 14 }}>
        {PARTNER_SERVICES.map(sv => {
          const on = j.service === sv.id;
          return (
            <button key={sv.id} onClick={() => setJ(p => ({ ...p, service: sv.id }))}
              style={{ width: "100%", textAlign: "left",
                background: on ? (sv.quote ? "#1a1000" : "#1a0a0a") : "#161616",
                border: `1px solid ${on ? (sv.quote ? "#f59e0b" : "#E63329") : "#2a2a2a"}`,
                borderRadius: 9, padding: "12px 14px", marginBottom: 7, cursor: "pointer",
                display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13.5, color: on ? (sv.quote ? "#f59e0b" : "#E63329") : "#aaa", fontWeight: on ? 700 : 500 }}>{sv.label}</span>
              <span style={{ fontSize: 11.5, fontWeight: 800, color: sv.quote ? "#f59e0b" : "#E63329" }}>
                {sv.quote ? "QUOTE" : `$${PARTNER_RATE}`}
              </span>
            </button>
          );
        })}
      </div>

      <div style={c.fieldGroup}><label style={c.label}>Notes for the Tech (optional)</label>
        <textarea style={{ ...c.input, minHeight: 80, resize: "vertical" }}
          placeholder="Gate code, where it's parked, what happened..."
          value={j.note} onChange={e => setJ(p => ({ ...p, note: e.target.value }))} /></div>

      {err && <div style={c.gpsError}>⚠️ {err}</div>}

      <button onClick={send} disabled={!canSend || sending}
        style={{ width: "100%", background: canSend ? "#E63329" : "#2a1010", border: "none",
          color: canSend ? "#fff" : "#664", padding: "19px", borderRadius: 13, fontSize: 17,
          fontWeight: 900, cursor: canSend ? "pointer" : "not-allowed", letterSpacing: 0.4 }}>
        {sending ? "Dispatching..." : isQuote ? "🚨 SEND FOR QUOTE" : `🚨 DISPATCH — $${PARTNER_RATE}`}
      </button>
    </div>
  );
}

function FleetSubGate({ authUser, onSignOut, onSubscribed }) {
  const profile = authUser?.profile || {};
  const [step, setStep] = useState("intro");
  const [error, setError] = useState("");
  const email = profile.email || authUser.email || "";
  const rate = profile.monthlyRate || fleetMonthly(profile.vehicleCount || 1);

  function openLink() {
    setError("");
    const url = `${FLEET_SUB_LINK}?prefilled_email=${encodeURIComponent(email)}&client_reference_id=${encodeURIComponent(authUser.uid)}`;
    window.open(url, "_blank");
    setStep("waiting");
  }

  async function verify(silent) {
    if (!silent) setStep("checking");
    try {
      const res = await fetch(`${WORKER_URL}/verify-membership`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, uid: authUser.uid }),
      });
      const d = await res.json();
      if (d.active) {
        await dbPatch(`users/${authUser.uid}`, {
          subscribed: true, subscriptionId: d.subscriptionId,
          stripeCustomerId: d.customerId, subscribedAt: Date.now(),
        });
        await fetch(`${WORKER_URL}/notify`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: `🚚 Fleet Account Active — $${rate}/mo`,
            message: `${profile.business} is subscribed.\n${profile.vehicleCount} vehicles · ${profile.city}, ${profile.state}\n📞 ${profile.phone}\n\n➡️ Schedule their first inspection.`,
          }),
        }).catch(() => {});
        setStep("done");
        setTimeout(onSubscribed, 1500);
        return;
      }
      if (!silent) { setError("No active subscription found yet. If you just paid, give it a few seconds and tap again."); setStep("waiting"); }
    } catch (e) {
      if (!silent) { setError(e.message); setStep("waiting"); }
    }
  }

  useEffect(() => { verify(true); }, []);
  useEffect(() => {
    if (step !== "waiting") return;
    const iv = setInterval(() => verify(true), 5000);
    return () => clearInterval(iv);
  }, [step]);

  return (
    <div style={c.root}>
      <header style={{ ...c.header, background: "#0a1a12", borderBottom: "3px solid #14b8a6" }}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div>
            <div style={{ ...c.logoTop, fontSize: 13 }}>{profile.business || "FLEET"}</div>
            <div style={{ ...c.logoBot, color: "#14b8a6", fontSize: 10 }}>FLEET MAINTENANCE</div>
          </div>
        </div>
        <button style={{ ...c.back, color: "#E63329", borderColor: "#E63329" }} onClick={onSignOut}>Sign Out</button>
      </header>

      <div style={c.page}>
        {step === "done" ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ fontSize: 56, marginBottom: 14 }}>🎉</div>
            <div style={{ fontSize: 21, fontWeight: 900, color: "#14b8a6", marginBottom: 8 }}>You're all set!</div>
            <div style={{ fontSize: 13, color: "#888" }}>Loading your fleet dashboard...</div>
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center", marginTop: 10, marginBottom: 20 }}>
              <div style={{ fontSize: 42, marginBottom: 10 }}>💳</div>
              <div style={{ fontSize: 21, fontWeight: 900, color: "#fff", marginBottom: 6 }}>Activate Your Fleet Account</div>
              <div style={{ fontSize: 13, color: "#777", lineHeight: 1.6 }}>
                One last step, {profile.contactName || "there"} — then we'll schedule your first inspection.
              </div>
            </div>

            <div style={{ background: "linear-gradient(135deg,#0a1f18 0%,#0f0f0f 100%)", border: "2px solid #14b8a6", borderRadius: 14, padding: 20, marginBottom: 18 }}>
              <div style={{ fontSize: 10, letterSpacing: 3, color: "#14b8a6", fontWeight: 800, marginBottom: 10 }}>FLEET MAINTENANCE</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 40, fontWeight: 900, color: "#fff" }}>${rate}</span>
                <span style={{ fontSize: 15, color: "#888" }}>/month</span>
              </div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 14 }}>
                {profile.vehicleCount} vehicle{profile.vehicleCount !== 1 ? "s" : ""}
                {(profile.vehicleCount || 0) < FLEET_BREAKEVEN ? ` · $${FLEET_MIN} minimum` : ` × $${FLEET_PER_VEHICLE}`}
              </div>
              {[
                "Monthly on-site inspection of every vehicle",
                "Written per-vehicle report with photos",
                "Flag list of what's coming due",
                `Roadside dispatch any time — $${PARTNER_RATE} flat`,
                "Cancel any time",
              ].map(x => (
                <div key={x} style={{ display: "flex", gap: 9, marginBottom: 8 }}>
                  <span style={{ color: "#14b8a6", fontSize: 13 }}>✓</span>
                  <span style={{ fontSize: 12.5, color: "#ccc", lineHeight: 1.45 }}>{x}</span>
                </div>
              ))}
            </div>

            {error && <div style={c.gpsError}>⚠️ {error}</div>}

            {step === "waiting" ? (
              <>
                <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 12, padding: 16, marginBottom: 14 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: "#3b82f6", marginBottom: 6 }}>💳 Finish in the Stripe tab</div>
                  <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.6 }}>
                    Enter <strong style={{ color: "#fff" }}>{profile.vehicleCount}</strong> as the quantity if it asks. Come back here when you're done — we'll activate automatically.
                  </div>
                </div>
                <button style={{ ...c.btnPrimary, background: "#14b8a6", color: "#000" }} onClick={() => verify(false)}>
                  ✅ I've Paid — Activate My Account
                </button>
                <button style={c.btnSecondary} onClick={openLink}>🔄 Reopen Checkout</button>
              </>
            ) : step === "checking" ? (
              <div style={{ textAlign: "center", padding: 24, color: "#14b8a6" }}>
                <div style={{ fontSize: 34, marginBottom: 8 }}>🔒</div>
                Checking your subscription...
              </div>
            ) : (
              <button style={{ ...c.btnPrimary, background: "#14b8a6", color: "#000" }} onClick={openLink}>
                🔒 Subscribe — ${rate}/month →
              </button>
            )}

            <div style={{ fontSize: 11, color: "#555", textAlign: "center", marginTop: 16, lineHeight: 1.6 }}>
              Repairs are billed separately and only with your approval.<br />
              Questions? <a href="tel:5612558506" style={{ color: "#14b8a6" }}>(561) 255-8506</a>
            </div>
          </>
        )}
      </div>
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC</div>
    </div>
  );
}

function PartnerPortal() {
  const [authUser, setAuthUser] = useState(null);
  const [screen, setScreen] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // signup fields
  const [f, setF] = useState({
    business: "", owner: "", contactName: "", phone: "", altPhone: "",
    address: "", city: "", state: "", zip: "", email: "", password: "",
    businessType: "", services: [], notes: "",
  });
  const [spLoading, setSpLoading] = useState(false);
  const [spDone, setSpDone] = useState("");
  const [fixBiz, setFixBiz] = useState("");
  const [fixType, setFixType] = useState("");
  const [fixing, setFixing] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("sfrs_partner");
      if (saved) { const u = JSON.parse(saved); setAuthToken(u.token, u.refreshToken); setAuthUser(u); }
    } catch {}
  }, []);

  function signOut() {
    localStorage.removeItem("sfrs_partner");
    setAuthUser(null);
  }

  async function handleLogin() {
    setError("");
    if (!email || !password) return setError("Email and password required.");
    setLoading(true);
    const r = await fbSignIn(email.trim(), password);
    if (r.error) { setLoading(false); return setError(fbFormatError(r.error)); }
    setAuthToken(r.token, r.refreshToken);
    const res = await loadProfileAfterLogin(r.uid);

    if (res.error) {
      setLoading(false);
      return setError(
        res.reason === "missing"
          ? "Your login works but the business details didn't save. Use \"Fix My Account\" below, or call (561) 255-8506."
          : res.reason === "rejected"
            ? `Access was refused: ${res.error} — call (561) 255-8506.`
            : "Couldn't reach the server. Check your signal and try again."
      );
    }
    const profile = res.profile;
    if (profile.role !== "partner") {
      setLoading(false);
      console.warn("[partner-login] profile loaded but role mismatch:", profile);
      return setError(
        profile.role === "provider" ? "That's a provider account — sign in at /provider instead."
        : profile.role === "customer" ? "That's a customer account. Sign in on the main app, or apply for a business account below."
        : `This account has no business role set (role: ${profile.role || "none"}). Tap "Fix My Account" below or call (561) 255-8506.`
      );
    }
    // Paying businesses qualify themselves — only free industry partners wait on approval
    if (profile.status === "pending" && !profile.subRequired) {
      setLoading(false);
      return setError("⏳ Your account is awaiting approval. We'll call you once you're live.");
    }
    if (profile.status === "denied")  { setLoading(false); return setError("❌ This account wasn't approved. Call (561) 255-8506."); }
    const u = { ...r, profile };
    localStorage.setItem("sfrs_partner", JSON.stringify(u));
    setLoading(false);
    setAuthUser(u);
  }

  async function handleSignup() {
    setError("");
    for (const [k, lbl] of [["business","business name"],["owner","owner's name"],["contactName","contact name"],["phone","phone"],["address","address"],["city","city"],["state","state"],["email","email"],["password","password"]]) {
      if (!String(f[k] || "").trim()) return setError(`Please enter your ${lbl}.`);
    }
    if (f.password.length < 6) return setError("Password must be at least 6 characters.");
    if (f.services.length === 0) return setError("Select at least one service you dispatch or can take on.");
    setSpLoading(true);
    const free = isFreePartner(f.businessType);

    const acct = await createAccount(f.email.trim(), f.password, {
      role: "partner",
      // Paying businesses qualify themselves — approval is only for free industry partners
      status: free ? "pending" : "approved",
      autoApproved: !free,
      business: f.business.trim(), owner: f.owner.trim(),
      firstName: f.contactName.trim(), lastName: `(${f.business.trim()})`,
      contactName: f.contactName.trim(),
      phone: f.phone.trim(), altPhone: f.altPhone.trim(),
      address: f.address.trim(), city: f.city.trim(), state: f.state, zip: f.zip.trim(),
      email: f.email.trim().toLowerCase(),
      businessType: f.businessType, services: f.services, notes: f.notes.trim(),
      rate: PARTNER_RATE,
      freePartner: isFreePartner(f.businessType),
      subRequired: !isFreePartner(f.businessType),
      createdAt: Date.now(),
    });

    if (acct.error) { setSpLoading(false); return setError(acct.error); }
    const r = acct;

    await fbSendVerification(r.token);
    await fetch(`${WORKER_URL}/notify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: isFreePartner(f.businessType) ? "🤝 New Industry Partner Application" : "🏢 New B2B Application ($20/mo)",
        message: `${f.business}\n${f.businessType || "Type not given"}\n\nOwner: ${f.owner}\nContact: ${f.contactName}\n📞 ${f.phone}\n📧 ${f.email}\n📍 ${f.address}, ${f.city}, ${f.state} ${f.zip}\n\nServices: ${f.services.join(", ")}\n${f.notes ? `\nNotes: ${f.notes}` : ""}\n\n${isFreePartner(f.businessType) ? "⏳ Free industry partner — needs your approval." : `✅ Auto-approved — pays $${PARTNER_SUB}/mo once subscribed.`}`,
      }),
    }).catch(() => {});
    setSpLoading(false);
    setSpDone(free
      ? "✅ Application submitted! Verify your email, then we'll review your account — usually within one business day.\n\n📬 Don't see the email? Check your spam or junk folder."
      : `✅ You're in! Verify your email, then sign in and start your $${PARTNER_SUB}/month account.\n\nNo waiting on approval — once you're subscribed you can dispatch right away.\n\n📬 Don't see the verification email? Check your spam or junk folder.`);
  }

  if (authUser) {
    const prof = authUser.profile || {};
    // Non-industry businesses must be subscribed before dispatching
    if (prof.subRequired && !prof.subscribed) {
      return <PartnerSubGate
        authUser={authUser}
        onSignOut={signOut}
        onSubscribed={() => {
          const u = { ...authUser, profile: { ...authUser.profile, subscribed: true } };
          localStorage.setItem("sfrs_partner", JSON.stringify(u));
          setAuthUser(u);
        }}
      />;
    }
    return <PartnerDashboard authUser={authUser} onSignOut={signOut} />;
  }

  return (
    <div style={c.root}>
      <header style={{ ...c.header, background: "#0f1a0f", borderBottom: "3px solid #22c55e" }}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div><div style={c.logoTop}>SUPER FAST</div><div style={{ ...c.logoBot, color: "#22c55e" }}>BUSINESS</div></div>
        </div>
      </header>

      <div style={c.page}>
        {screen === "signup" ? (
          spDone ? (
            <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 12, padding: 22, textAlign: "center", marginTop: 20 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 13.5, color: "#22c55e", lineHeight: 1.7, whiteSpace: "pre-line" }}>{spDone}</div>
              <button style={{ ...c.btnSecondary, marginTop: 18 }} onClick={() => { setScreen("login"); setSpDone(""); }}>← Back to Login</button>
            </div>
          ) : (
            <>
              <div style={{ textAlign: "center", marginTop: 8, marginBottom: 18 }}>
                <div style={{ fontSize: 38, marginBottom: 10 }}>🏢</div>
                <div style={{ fontSize: 25, fontWeight: 900, color: "#fff", lineHeight: 1.15, marginBottom: 8 }}>
                  Stop Turning Down Work.
                </div>
                <div style={{ fontSize: 13.5, color: "#999", lineHeight: 1.6 }}>
                  Every job you can't run is a customer calling somebody else. Send it to us instead — keep the customer, keep the relationship, keep a spread.
                </div>
              </div>

              {/* The problem */}
              <div style={{ background: "#1a0a0a", border: "1px solid #E63329", borderRadius: 12, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 10, letterSpacing: 2, color: "#E63329", fontWeight: 800, marginBottom: 9 }}>SOUND FAMILIAR?</div>
                {[
                  "\"We're booked, try somebody else\" — and they never call back",
                  "A tire size you don't stock, so the job walks",
                  "2am call you're not getting out of bed for",
                  "Customer 40 minutes away for a $60 job",
                  "One truck down and the whole day's backed up",
                ].map(x => (
                  <div key={x} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                    <span style={{ color: "#E63329", fontSize: 12 }}>✕</span>
                    <span style={{ fontSize: 12, color: "#bbb", lineHeight: 1.55 }}>{x}</span>
                  </div>
                ))}
              </div>

              {/* What they get */}
              <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 12, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 10, letterSpacing: 2, color: "#22c55e", fontWeight: 800, marginBottom: 9 }}>WHAT YOU GET FOR ${PARTNER_SUB}/MONTH</div>
                {[
                  ["🔄", "Overflow dispatch", "Post any job you can't run. We source a partner and come back with a price. You decide."],
                  ["💰", "Keep your margin", "You charge your customer whatever you want. Pay us $20 to dispatch it. The difference is yours."],
                  ["📞", "Keep your customer", "They booked with you, they stay yours. We never contact them directly."],
                  ["🌙", "24/7 coverage", "Overnight, weekends, holidays — you don't have to be awake for it."],
                  ["🤝", "Work comes back", "When we've got a job in your area and you have capacity, we send it to you."],
                ].map(([ic, t, d]) => (
                  <div key={t} style={{ display: "flex", gap: 11, marginBottom: 11, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 17, flexShrink: 0 }}>{ic}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>{t}</div>
                      <div style={{ fontSize: 11.5, color: "#999", lineHeight: 1.55, marginTop: 2 }}>{d}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Worked example */}
              <div style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: 12, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 10, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 11 }}>REAL EXAMPLE</div>
                <div style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.75 }}>
                  2am. Customer needs a <strong style={{ color: "#fff" }}>205/55R16</strong> in Fort Lauderdale. You don't have it and you're not driving out there.
                  <br /><br />
                  You post it. We find a partner who'll do it for <strong style={{ color: "#f59e0b" }}>$175</strong>.
                  <br />
                  You tell your customer <strong style={{ color: "#fff" }}>$250</strong> — $75 deposit now, $175 to the tech on arrival.
                  <br />
                  You pay us <strong style={{ color: "#22c55e" }}>$20</strong> to dispatch it.
                </div>
                <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 8, padding: 12, marginTop: 12, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "#4caf50", marginBottom: 3 }}>You made</div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: "#22c55e" }}>$55</div>
                  <div style={{ fontSize: 11, color: "#4caf50", marginTop: 3 }}>on a job you would have turned down</div>
                </div>
              </div>

              {/* Free tier callout */}
              <div style={{ background: "#150a1f", border: "1px solid #a855f7", borderRadius: 12, padding: 15, marginBottom: 20 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: "#a855f7", marginBottom: 6 }}>🤝 Free if you're in the business</div>
                <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.65 }}>
                  Roadside companies, mobile tire services, tire shops and towing companies pay <strong style={{ color: "#fff" }}>nothing monthly</strong> — you're supply as well as demand. Just the $20 when you dispatch.
                </div>
              </div>

              <div style={c.sectionLabel}>BUSINESS INFORMATION</div>
              <div style={c.fieldGroup}><label style={c.label}>Business Name</label>
                <input style={c.input} placeholder="e.g. Palm Beach Auto Group" value={f.business} onChange={e => setF(p => ({ ...p, business: e.target.value }))} /></div>
              <div style={c.fieldGroup}><label style={c.label}>Business Type</label>
                <select style={c.input} value={f.businessType} onChange={e => setF(p => ({ ...p, businessType: e.target.value }))}>
                  <option value="">Select...</option>
                  <optgroup label="Industry partners — no monthly fee">
                    {FREE_PARTNER_TYPES.map(t => <option key={t}>{t}</option>)}
                  </optgroup>
                  <optgroup label="All other businesses — $20/month">
                    {["Auto dealership","Used car lot","Rental car agency","Fleet operator","Trucking company","Property management","Apartment complex","Repair shop","Insurance / roadside network","Parking operator","Car wash","Body shop","Other"].map(t => <option key={t}>{t}</option>)}
                  </optgroup>
                </select></div>
              {f.businessType && (
                <div style={{ background: isFreePartner(f.businessType) ? "#0a1f0a" : "#1a1400",
                  border: `1px solid ${isFreePartner(f.businessType) ? "#22c55e" : "#f59e0b"}`,
                  borderRadius: 10, padding: 13, marginBottom: 14 }}>
                  {isFreePartner(f.businessType) ? (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#22c55e", marginBottom: 4 }}>🤝 Industry Partner — No Monthly Fee</div>
                      <div style={{ fontSize: 12, color: "#4caf50", lineHeight: 1.6 }}>
                        You're in the business we're in. No subscription — just ${PARTNER_RATE} per service call you dispatch.
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#f59e0b", marginBottom: 4 }}>💳 ${PARTNER_SUB}/month + ${PARTNER_RATE} per call</div>
                      <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.6 }}>
                        Business accounts are ${PARTNER_SUB}/month. Each service call you dispatch is ${PARTNER_RATE}, paid at dispatch. Cancel any time.
                      </div>
                    </>
                  )}
                </div>
              )}

              <div style={c.fieldGroup}><label style={c.label}>Owner's Full Name</label>
                <input style={c.input} placeholder="Owner of the business" value={f.owner} onChange={e => setF(p => ({ ...p, owner: e.target.value }))} /></div>
              <div style={c.fieldGroup}><label style={c.label}>Contact Person (who dispatches)</label>
                <input style={c.input} placeholder="Day-to-day contact" value={f.contactName} onChange={e => setF(p => ({ ...p, contactName: e.target.value }))} /></div>

              <div style={c.sectionLabel}>CONTACT</div>
              <div style={c.fieldGroup}><label style={c.label}>Main Phone</label>
                <input style={c.input} type="tel" placeholder="(561) 000-0000" value={f.phone} onChange={e => setF(p => ({ ...p, phone: e.target.value }))} /></div>
              <div style={c.fieldGroup}><label style={c.label}>Backup Phone (optional)</label>
                <input style={c.input} type="tel" value={f.altPhone} onChange={e => setF(p => ({ ...p, altPhone: e.target.value }))} /></div>
              <div style={c.fieldGroup}><label style={c.label}>Email</label>
                <input style={c.input} type="email" placeholder="dispatch@yourbusiness.com" value={f.email} onChange={e => setF(p => ({ ...p, email: e.target.value }))} /></div>
              <div style={c.fieldGroup}><label style={c.label}>Password</label>
                <input style={c.input} type="password" placeholder="Min 6 characters" value={f.password} onChange={e => setF(p => ({ ...p, password: e.target.value }))} /></div>

              <div style={c.sectionLabel}>BUSINESS LOCATION</div>
              <div style={c.fieldGroup}><label style={c.label}>Street Address</label>
                <input style={c.input} placeholder="123 Business Blvd" value={f.address} onChange={e => setF(p => ({ ...p, address: e.target.value }))} /></div>
              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 2 }}><label style={c.label}>City</label>
                  <input style={c.input} value={f.city} onChange={e => setF(p => ({ ...p, city: e.target.value }))} /></div>
                <div style={{ flex: 1 }}><label style={c.label}>State</label>
                  <select style={c.input} value={f.state} onChange={e => setF(p => ({ ...p, state: e.target.value }))}>
                    <option value="">—</option>
                    {US_STATES.map(st => <option key={st.code} value={st.code}>{st.code}</option>)}
                  </select></div>
              </div>
              <div style={c.fieldGroup}><label style={c.label}>ZIP Code</label>
                <input style={c.input} maxLength={5} value={f.zip} onChange={e => setF(p => ({ ...p, zip: e.target.value.replace(/\D/g, "") }))} /></div>

              <div style={c.sectionLabel}>SERVICES YOU'LL DISPATCH / RECEIVE</div>
              <div style={{ fontSize: 11.5, color: "#888", lineHeight: 1.6, marginBottom: 12, marginTop: -6 }}>
                Pick everything you deal in — both jobs you'd send us and jobs you could take on. It works both ways: send us your overflow, and we'll send you work when we have it in your area.
              </div>
              <div style={{ marginBottom: 14 }}>
                {PARTNER_SERVICES.map(sv => {
                  const on = f.services.includes(sv.id);
                  return (
                    <label key={sv.id} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", background: on ? "#0a1f0a" : "#161616", border: `1px solid ${on ? "#22c55e" : "#2a2a2a"}`, borderRadius: 8, padding: "10px 13px", marginBottom: 7 }}>
                      <input type="checkbox" checked={on} style={{ accentColor: "#22c55e" }}
                        onChange={e => setF(prev => ({ ...prev, services: e.target.checked ? [...prev.services, sv.id] : prev.services.filter(x => x !== sv.id) }))} />
                      <span style={{ fontSize: 13, color: on ? "#22c55e" : "#aaa", flex: 1 }}>{sv.label}</span>
                      <span style={{ fontSize: 10.5, color: sv.quote ? "#f59e0b" : "#666", fontWeight: 700 }}>
                        {sv.quote ? "QUOTE" : sv.perTire ? `$70 +$${sv.perTire}/tire` : "$70"}
                      </span>
                    </label>
                  );
                })}
              </div>

              <div style={c.fieldGroup}><label style={c.label}>Anything Else? (optional)</label>
                <textarea style={{ ...c.input, minHeight: 80, resize: "vertical" }}
                  placeholder="Fleet size, typical volume, hours you'd dispatch, billing preferences..."
                  value={f.notes} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} /></div>

              {error && <div style={c.gpsError}>⚠️ {error}</div>}
              <button style={{ ...c.btnPrimary, background: "#22c55e", color: "#000" }} onClick={handleSignup} disabled={spLoading}>
                {spLoading ? "Submitting..." : "Submit Application →"}
              </button>
              <button style={c.btnSecondary} onClick={() => { setScreen("login"); setError(""); }}>← Back to Login</button>
            </>
          )
        ) : (
          <>
            <div style={{ textAlign: "center", marginTop: 16, marginBottom: 26 }}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>🏢</div>
              <div style={au.title}>Business Dispatch</div>
              <div style={au.sub}>Sign in to dispatch a job you can't run</div>
            </div>
            <div style={c.fieldGroup}><label style={c.label}>Email</label>
              <input style={c.input} type="email" value={email} onChange={e => setEmail(e.target.value)} /></div>
            <div style={c.fieldGroup}><label style={c.label}>Password</label>
              <input style={c.input} type="password" value={password} onChange={e => setPassword(e.target.value)} /></div>
            {error && <div style={c.gpsError}>⚠️ {error}</div>}
            <button style={{ ...c.btnPrimary, background: "#22c55e", color: "#000" }} onClick={handleLogin} disabled={loading}>
              {loading ? "Signing In..." : "Sign In →"}
            </button>

            {/* Recovery — signup wrote a partial record */}
            {error && (error.includes("no business role") || error.includes("couldn't load")) && (
              <div style={{ background: "#1a1400", border: "1px solid #f59e0b", borderRadius: 10, padding: 15, marginTop: 14 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: "#f59e0b", marginBottom: 6 }}>🔧 Finish setting up this account</div>
                <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.6, marginBottom: 12 }}>
                  Your login works but the business details didn't save. Enter your business name and type below and we'll repair it.
                </div>
                <input style={{ ...c.input, marginBottom: 9 }} placeholder="Business name"
                  value={fixBiz} onChange={e => setFixBiz(e.target.value)} />
                <select style={{ ...c.input, marginBottom: 11 }} value={fixType} onChange={e => setFixType(e.target.value)}>
                  <option value="">Business type...</option>
                  <optgroup label="Industry partners — no monthly fee">
                    {FREE_PARTNER_TYPES.map(t => <option key={t}>{t}</option>)}
                  </optgroup>
                  <optgroup label="All other businesses — $20/month">
                    {["Auto dealership","Used car lot","Rental car agency","Fleet operator","Trucking company","Property management","Apartment complex","Repair shop","Insurance / roadside network","Parking operator","Car wash","Body shop","Other"].map(t => <option key={t}>{t}</option>)}
                  </optgroup>
                </select>
                <button style={{ width: "100%", background: "#f59e0b", border: "none", color: "#000", padding: "13px", borderRadius: 9, fontWeight: 900, fontSize: 13.5, cursor: "pointer", opacity: (fixBiz && fixType) ? 1 : 0.4 }}
                  disabled={!fixBiz || !fixType || fixing}
                  onClick={async () => {
                    setFixing(true);
                    const r = await fbSignIn(email.trim(), password);
                    if (r.error) { setFixing(false); return setError(fbFormatError(r.error)); }
                    setAuthToken(r.token, r.refreshToken);
                    const free = isFreePartner(fixType);
                    const res = await dbSet(`users/${r.uid}`, {
                      role: "partner",
                      status: free ? "pending" : "approved",
                      autoApproved: !free,
                      business: fixBiz.trim(),
                      businessType: fixType,
                      firstName: fixBiz.trim(),
                      lastName: "",
                      contactName: fixBiz.trim(),
                      email: email.trim().toLowerCase(),
                      phone: "",
                      services: [],
                      rate: PARTNER_RATE,
                      freePartner: free,
                      subRequired: !free,
                      repairedAt: Date.now(),
                      createdAt: Date.now(),
                    });
                    setFixing(false);
                    if (res?.error) return setError("Repair failed: " + res.error);
                    await fetch(`${WORKER_URL}/notify`, {
                      method: "POST", headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        title: "🔧 Business Account Repaired",
                        message: `${fixBiz} (${fixType})\n${email}\n\nTheir signup didn't save properly — record rebuilt.`,
                      }),
                    }).catch(() => {});
                    setError("");
                    setFixBiz(""); setFixType("");
                    alert("✅ Fixed. Sign in again.");
                  }}>
                  {fixing ? "Repairing..." : "🔧 Fix My Account"}
                </button>
              </div>
            )}

            <div style={{ marginTop: 24, background: "#0a1f0a", border: "1px solid #1f4d1f", borderRadius: 10, padding: 16, textAlign: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", marginBottom: 4 }}>Turning down work?</div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 12, lineHeight: 1.55 }}>
                Send us the jobs you can't run. Keep your customer, keep a spread.
              </div>
              <button style={{ ...c.btnPrimary, background: "none", color: "#22c55e", border: "1px solid #22c55e", fontSize: 14 }}
                onClick={() => { setScreen("signup"); setError(""); }}>
                🏢 Get a Business Account
              </button>
            </div>
            <div style={{ fontSize: 12, color: "#555", textAlign: "center", marginTop: 16 }}>
              Questions? <a href="tel:5612558506" style={{ color: "#22c55e" }}>📞 (561) 255-8506</a>
            </div>
          </>
        )}
      </div>
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC</div>
    </div>
  );
}

function CustomerPortal() {
  const [authUser, setAuthUser] = useState(null);
  const [authScreen, setAuthScreen] = useState("login");
  const [showLanding, setShowLanding] = useState(true);
  const [showHowItWorks, setShowHowItWorks] = useState(true);
  const [guestMode, setGuestMode] = useState(false);
  const [showSponsor, setShowSponsor] = useState(false);
  const [legalDoc, setLegalDoc] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const [showSignInChoice, setShowSignInChoice] = useState(false);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [userCoords, setUserCoords] = useState({ lat: 26.7153, lng: -80.0534 }); // Default WPB

  useEffect(() => {
    try {
      const saved = localStorage.getItem("sfrs_customer");
      if (saved) { const u = JSON.parse(saved); setAuthToken(u.token, u.refreshToken); setAuthUser(u); setShowLanding(false); }
    } catch {}
    // Get user location for map
    let watchId = null;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { timeout: 8000, maximumAge: 30000 }
      );
      // Keep the map following them while they're on this screen
      watchId = navigator.geolocation.watchPosition(
        pos => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 }
      );
    }
    return () => { if (watchId != null) navigator.geolocation.clearWatch(watchId); };
  }, []);

  function handleAuthSuccess(user) {
    setAuthToken(user.token, user.refreshToken);
    localStorage.setItem("sfrs_customer", JSON.stringify(user));
    setAuthUser(user);
    setShowLanding(false);
  }

  // Verify membership with Stripe on load — catches lapsed / cancelled subs
  useEffect(() => {
    async function verifyMembership() {
      if (!authUser?.uid) return;
      try {
        const fresh = await dbGet(`users/${authUser.uid}`);
        if (!fresh || typeof fresh !== "object") return;

        // No subscription on record — nothing to verify
        if (!fresh.subscriptionId) {
          if (fresh.subscribed !== authUser.profile?.subscribed) {
            const u = { ...authUser, profile: { ...authUser.profile, ...fresh } };
            localStorage.setItem("sfrs_customer", JSON.stringify(u));
            setAuthUser(u);
          }
          return;
        }

        const res = await fetch(`${WORKER_URL}/check-subscription`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ subscriptionId: fresh.subscriptionId }),
        });
        const data = await res.json();
        if (typeof data.active !== "boolean") return;

        if (fresh.subscribed !== data.active) {
          await dbPatch(`users/${authUser.uid}`, { subscribed: data.active, subStatus: data.status });
        }
        const u = { ...authUser, profile: { ...authUser.profile, ...fresh, subscribed: data.active, subStatus: data.status } };
        localStorage.setItem("sfrs_customer", JSON.stringify(u));
        setAuthUser(u);
      } catch {}
    }
    verifyMembership();
  }, [authUser?.uid]);

  function handleSignOut() {
    localStorage.removeItem("sfrs_customer");
    setAuthUser(null);
    setShowLanding(true);
  }

  if (legalDoc) return <LegalPage doc={legalDoc} audience="customer" onBack={() => setLegalDoc(null)} />;

  if (showSponsor) return <SponsorPage onBack={() => setShowSponsor(false)} />;

  if (guestMode) return <GuestBooking
    prefill={authUser ? {
      name: `${authUser.profile?.firstName || ""} ${authUser.profile?.lastName || ""}`.trim(),
      phone: authUser.profile?.phone || "",
      address: authUser.profile?.homeAddress || "",
    } : null}
    onBack={() => setGuestMode(false)}
    onJoin={() => {
      setGuestMode(false);
      if (!authUser) { setShowLanding(false); setAuthScreen("signup"); }
      // logged-in users land back on the membership gate
    }}
  />;

  // ── LIVE MAP LANDING SCREEN ──
  if (showLanding && !authUser) {

    const LANDING_SERVICES = [
      { icon: "🛞", name: "Tire Change",        price: "$60" },
      { icon: "🔋", name: "Jump Start",         price: "$60" },
      { icon: "🔑", name: "Lockout Service",    price: "$60" },
      { icon: "⛽", name: "Fuel Delivery",       price: "$60" },
      { icon: "🔩", name: "Tire Plug",          price: "$60" },
      { icon: "🔐", name: "Wheel Lock Removal", price: "$60" },
      { icon: "🚛", name: "Tow Truck",          price: "$25 dispatch" },
      { icon: "🔧", name: "Mobile Mechanic",    price: "$20 booking" },
      { icon: "🛞", name: "Used Tire Install",  price: "Call for quote" },
      { icon: "🔋", name: "Battery Replace",    price: "Call for quote" },
      { icon: "⚙️", name: "Rim Damage Repair",  price: "Call for quote" },
    ];

    return (
      <div style={{ position: "fixed", inset: 0, background: "#000", fontFamily: "'Inter','Helvetica Neue',sans-serif", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>

        {/* ── MAP SECTION ── */}
        <div style={{ position: "relative", height: mapExpanded ? "82vh" : "44vh", minHeight: mapExpanded ? 460 : 300, overflow: "hidden", transition: "height 0.35s ease" }}>
          <LiveMap coords={userCoords} />
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: mapExpanded
            ? "linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 78%, rgba(0,0,0,0.7) 100%)"
            : "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1) 35%, rgba(0,0,0,0.85) 100%)" }} />

          {/* Top bar */}
          <div style={{ position: "relative", zIndex: 5, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "44px 16px 0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span style={{ fontSize: 22 }}>🔥</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 900, letterSpacing: 2, color: "#fff", lineHeight: 1.2 }}>SUPER FAST</div>
                <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#E63329", fontWeight: 800 }}>$60 anywhere. anytime.</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
              <button onClick={() => setShowQr(true)}
                style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.35)", color: "#fff", padding: "9px 13px", borderRadius: 30, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                📱 QR
              </button>
              <button onClick={() => setShowSignInChoice(true)}
                style={{ background: "rgba(255,255,255,0.95)", border: "none", color: "#000", padding: "9px 16px", borderRadius: 30, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                Sign In
              </button>
            </div>
          </div>

          {/* SPONSOR CARD — Golden Mobile Tires */}
          <div style={{ position: "relative", zIndex: 5, margin: "18px 12px 0", display: mapExpanded ? "none" : "block", background: "#fff", border: "2px solid #C9A227", borderRadius: 14, overflow: "hidden", boxShadow: "0 6px 24px rgba(0,0,0,0.55)" }}>
            <div style={{ background: "#111", padding: "5px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 9.5, fontWeight: 900, color: "#C9A227", letterSpacing: 2 }}>✨ SPONSORED PARTNER</span>
              <span style={{ fontSize: 9, color: "#666", letterSpacing: 1 }}>MARGATE, FL</span>
            </div>

            <div style={{ padding: "12px 14px" }}>
              <img src="/sponsor-golden.jpg" alt="Golden Mobile Tires"
                style={{ width: "100%", maxWidth: 240, display: "block", margin: "0 auto 10px" }} />

              <div style={{ display: "flex", gap: 8 }}>
                <a href="tel:9548258540"
                  onClick={e => e.stopPropagation()}
                  style={{ flex: 1, textAlign: "center", background: "#C9A227", color: "#111", padding: "10px", borderRadius: 8, fontSize: 12.5, fontWeight: 900, textDecoration: "none" }}>
                  📞 (954) 825-8540
                </a>
                <a href="https://maps.google.com/?q=5491+NW+15th+St+%237,+Margate,+FL+33063"
                  target="_blank" rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                  style={{ flex: 1, textAlign: "center", background: "#111", color: "#fff", padding: "10px", borderRadius: 8, fontSize: 12.5, fontWeight: 800, textDecoration: "none" }}>
                  📍 Directions
                </a>
              </div>

              <div style={{ fontSize: 10.5, color: "#666", textAlign: "center", marginTop: 8, lineHeight: 1.4 }}>
                5491 NW 15th St #7, Margate, FL 33063
              </div>
            </div>
          </div>

          {/* Map legend pills */}
          <div style={{ position: "absolute", bottom: mapExpanded ? 74 : 40, left: 12, zIndex: 6, display: "flex", gap: 8, background: "rgba(255,255,255,0.95)", borderRadius: 30, padding: "6px 13px" }}>
            {[["🚛", "Tow truck", "#333"], ["🚗", "Roadside", "#333"], ["●", "You", "#22c55e"]].map(([ic, lbl, col]) => (
              <span key={lbl} style={{ fontSize: 11, color: "#333", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: ic === "●" ? 13 : 12, color: col, lineHeight: 1 }}>{ic}</span>{lbl}
              </span>
            ))}
          </div>

          {/* Expand / collapse the map */}
          <button
            onClick={() => setMapExpanded(v => !v)}
            style={{ position: "absolute", bottom: mapExpanded ? 74 : 40, right: 12, zIndex: 6,
              background: "rgba(255,255,255,0.95)", border: "none", borderRadius: 30,
              padding: "7px 14px", fontSize: 11.5, fontWeight: 800, color: "#111", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 5, boxShadow: "0 3px 12px rgba(0,0,0,0.4)" }}>
            {mapExpanded ? "▲ Smaller map" : "▼ Bigger map"}
          </button>
        </div>

        {/* ── BOTTOM SHEET ── */}
        <div style={{ position: "relative", marginTop: -22, background: "#0a0a0a", borderRadius: "24px 24px 0 0", padding: "0 20px 40px", minHeight: "62vh", border: "1px solid #1a1a1a", borderBottom: "none" }}>

          {/* Grab handle — tap to expand or shrink the map */}
          <button
            onClick={() => setMapExpanded(v => !v)}
            style={{ width: "100%", background: "none", border: "none", cursor: "pointer",
              display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 11, paddingBottom: 16 }}>
            <div style={{ width: 46, height: 5, borderRadius: 4, background: "#3a3a3a" }} />
            <div style={{ fontSize: 10, color: "#555", marginTop: 7, letterSpacing: 0.5 }}>
              {mapExpanded ? "Tap to shrink the map" : "Tap to see more of the map"}
            </div>
          </button>

          {/* Live badge */}
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginBottom: 14 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 8px #22c55e", animation: "sfPulse 2s infinite" }} />
            <span style={{ fontSize: 10, color: "#22c55e", fontWeight: 800, letterSpacing: 2 }}>BASED IN SOUTH FLORIDA · SERVING NATIONWIDE</span>
          </div>

          {/* Headline */}
          <div style={{ textAlign: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 27, fontWeight: 900, color: "#fff", letterSpacing: -0.5 }}>$60 ROADSIDE ASSISTANCE</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#E63329", marginTop: 3 }}>Anytime. Anywhere.</div>
          </div>
          <div style={{ fontSize: 13, color: "#777", textAlign: "center", marginBottom: 18, lineHeight: 1.5 }}>
            Same price 2PM or 2AM. No after-hours surcharge, ever.
          </div>

          {/* THE VAN — real proof */}
          <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", marginBottom: 18, border: "1px solid #222" }}>
            <img src="/van-side.jpg" alt="Super Fast Mobile Tires service van"
              style={{ width: "100%", display: "block" }} />
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, background: "linear-gradient(to top, rgba(0,0,0,0.92), transparent)", padding: "26px 14px 11px" }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: "#fff" }}>🚐 This is our truck. Look for it.</div>
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>Fully equipped mobile roadside unit — we come to you.</div>
            </div>
          </div>

          {/* CTAs */}
          <button style={{ width: "100%", background: "#E63329", border: "none", color: "#fff", padding: "17px", borderRadius: 13, fontSize: 16, fontWeight: 900, cursor: "pointer", marginBottom: 10 }}
            onClick={() => { setShowLanding(false); setAuthScreen("signup"); }}>
            📍 Request Roadside Help Now
          </button>
          <div style={{ fontSize: 11, color: "#666", textAlign: "center", marginTop: -4, marginBottom: 10 }}>
            New here? You'll create an account and start your $10/month membership.
          </div>
          {/* NON-MEMBER EMERGENCY OPTION */}
          <button style={{ width: "100%", background: "#1a1000", border: "2px solid #f59e0b", color: "#f59e0b", padding: "15px", borderRadius: 13, fontSize: 14.5, fontWeight: 900, cursor: "pointer", marginBottom: 10 }}
            onClick={() => setGuestMode(true)}>
            ⚡ Emergency? Book Without an Account — $89
          </button>
          <div style={{ fontSize: 11, color: "#666", textAlign: "center", marginTop: -4, marginBottom: 12, lineHeight: 1.5 }}>
            No signup required. Pay once, we dispatch. Members pay $60.
          </div>

          <a href="tel:5612558506" style={{ display: "block", textAlign: "center", width: "100%", background: "#fff", color: "#000", padding: "16px", borderRadius: 13, fontSize: 15, fontWeight: 800, textDecoration: "none", boxSizing: "border-box", marginBottom: 16 }}>
            📞 Call 24/7 Live Dispatch
          </a>

          {/* Trust row */}
          <div style={{ display: "flex", justifyContent: "space-around", marginBottom: 26, paddingBottom: 22, borderBottom: "1px solid #1a1a1a" }}>
            {[["🛡️", "Verified pros"], ["💲", "$60 flat"], ["🕐", "24 hour service"]].map(([ic, lbl]) => (
              <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ fontSize: 13 }}>{ic}</span>
                <span style={{ fontSize: 11, color: "#888", fontWeight: 600 }}>{lbl}</span>
              </div>
            ))}
          </div>

          {/* ── SERVICES WE DO ── */}
          <div style={{ fontSize: 10, letterSpacing: 3, color: "#E63329", fontWeight: 800, marginBottom: 6 }}>WHAT WE DO</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 4 }}>Every service, one app.</div>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 16, lineHeight: 1.5 }}>
            Flat-rate core services. Transparent pricing on everything else.
          </div>

          {/* Wrap-style service badge strip */}
          <div style={{ display: "flex", gap: 7, overflowX: "auto", paddingBottom: 6, marginBottom: 16, WebkitOverflowScrolling: "touch" }}>
            {[
              ["🛞", "Flat Tire"], ["🔐", "Wheel Lock"], ["🔋", "Jump Start"],
              ["⛽", "Fuel"], ["🔑", "Lockout"], ["⚙️", "Rim Damage"],
            ].map(([ic, lbl]) => (
              <div key={lbl} style={{ flexShrink: 0, textAlign: "center", minWidth: 62 }}>
                <div style={{ width: 46, height: 46, borderRadius: "50%", border: "2px solid #E63329", background: "#0f0f0f", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, margin: "0 auto 5px" }}>
                  {ic}
                </div>
                <div style={{ fontSize: 9, color: "#888", fontWeight: 700, lineHeight: 1.2 }}>{lbl}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginBottom: 26 }}>
            {LANDING_SERVICES.map(s => (
              <div key={s.name} style={{ background: "#131313", border: "1px solid #222", borderRadius: 12, padding: "13px 12px" }}>
                <div style={{ fontSize: 21, marginBottom: 6 }}>{s.icon}</div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "#eee", lineHeight: 1.25, marginBottom: 3 }}>{s.name}</div>
                <div style={{ fontSize: 12, fontWeight: 800, color: s.price === "$60" ? "#22c55e" : "#888" }}>{s.price}</div>
              </div>
            ))}
          </div>

          {/* ── HOW IT WORKS ── */}
          <button onClick={() => setShowHowItWorks(v => !v)}
            style={{ width: "100%", background: "#131313", border: "1px solid #222", borderRadius: 12, padding: "15px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showHowItWorks ? 10 : 26 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>How it works</span>
            <span style={{ color: "#666", fontSize: 13 }}>{showHowItWorks ? "▲" : "▼"}</span>
          </button>

          {showHowItWorks && (
            <div style={{ marginBottom: 26 }}>
              {[
                ["1", "Subscribe for $10/month", "Unlocks the $60 flat rate on every core service. Cancel anytime."],
                ["2", "Request help in the app", "Pick your service, drop your location, pay securely. No haggling."],
                ["3", "Track your tech live", "Watch them arrive on the map. Most jobs done in under 30 minutes."],
                ["4", "Rate & tip (optional)", "100% of your tip goes straight to your tech."],
              ].map(([n, title, body]) => (
                <div key={n} style={{ background: "#131313", border: "1px solid #1e1e1e", borderRadius: 12, padding: 15, marginBottom: 9, display: "flex", gap: 13 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#E63329", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 13, flexShrink: 0 }}>{n}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", marginBottom: 3 }}>{title}</div>
                    <div style={{ fontSize: 12, color: "#777", lineHeight: 1.5 }}>{body}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── BETTER THAN AAA ── */}
          <div style={{ background: "#E63329", borderRadius: 12, padding: "14px 16px", marginBottom: 6, textAlign: "center" }}>
            <div style={{ fontSize: 21, fontWeight: 900, color: "#fff", letterSpacing: -0.3 }}>🏆 BETTER THAN AAA.</div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "rgba(255,255,255,0.85)", letterSpacing: 2, marginTop: 3 }}>FASTER. CHEAPER. LOCAL.</div>
          </div>
          <div style={{ fontSize: 11, color: "#666", textAlign: "center", marginBottom: 16 }}>The Super Fast Roadside difference</div>

          <div style={{ border: "1px solid #222", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
            {[
              ["Average wait time", "30–45 min", "2–3+ hours", "2–3+ hours"],
              ["Membership cost", "$10/month", "$85–150/yr", "Free (long waits)"],
              ["Service call price", "$60 flat", "$100+ per call", "Up to $150+"],
              ["Book in an app", "Yes", "No", "No"],
              ["Live GPS tracking", "Yes", "No", "No"],
            ].map(([label, us, aaa, ins], i) => (
              <div key={label} style={{ borderTop: i === 0 ? "none" : "1px solid #1c1c1c", padding: "11px 13px", background: i % 2 ? "#0d0d0d" : "#131313" }}>
                <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#666", fontWeight: 700, textTransform: "uppercase", marginBottom: 7 }}>{label}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <div style={{ flex: 1, background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 7, padding: "7px 6px", textAlign: "center" }}>
                    <div style={{ fontSize: 8, color: "#22c55e", fontWeight: 800, letterSpacing: 0.5, marginBottom: 2 }}>SUPER FAST</div>
                    <div style={{ fontSize: 11.5, fontWeight: 800, color: "#fff" }}>{us} ✅</div>
                  </div>
                  <div style={{ flex: 1, background: "#1a0a0a", border: "1px solid #3a2020", borderRadius: 7, padding: "7px 6px", textAlign: "center" }}>
                    <div style={{ fontSize: 8, color: "#888", fontWeight: 800, letterSpacing: 0.5, marginBottom: 2 }}>AAA</div>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: "#999" }}>{aaa} ❌</div>
                  </div>
                  <div style={{ flex: 1, background: "#1a0a0a", border: "1px solid #3a2020", borderRadius: 7, padding: "7px 6px", textAlign: "center" }}>
                    <div style={{ fontSize: 8, color: "#888", fontWeight: 800, letterSpacing: 0.5, marginBottom: 2 }}>INSURANCE</div>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: "#999" }}>{ins} ❌</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── PRICING AT A GLANCE ── */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            <div style={{ flex: 1, background: "#131313", border: "1px solid #2a2a2a", borderRadius: 11, padding: "13px 8px", textAlign: "center" }}>
              <div style={{ fontSize: 9, letterSpacing: 1.5, color: "#888", fontWeight: 800, marginBottom: 5 }}>MEMBERSHIP</div>
              <div style={{ fontSize: 25, fontWeight: 900, color: "#E63329", lineHeight: 1 }}>$10</div>
              <div style={{ fontSize: 9.5, color: "#666", marginTop: 3 }}>a month</div>
            </div>
            <div style={{ flex: 1, background: "#E63329", borderRadius: 11, padding: "13px 8px", textAlign: "center" }}>
              <div style={{ fontSize: 9, letterSpacing: 1.5, color: "rgba(255,255,255,0.8)", fontWeight: 800, marginBottom: 5 }}>MEMBER PRICE</div>
              <div style={{ fontSize: 25, fontWeight: 900, color: "#fff", lineHeight: 1 }}>$60</div>
              <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.85)", marginTop: 3 }}>flat rate call</div>
            </div>
            <div style={{ flex: 1, background: "#131313", border: "1px solid #f59e0b", borderRadius: 11, padding: "13px 8px", textAlign: "center" }}>
              <div style={{ fontSize: 9, letterSpacing: 1.5, color: "#f59e0b", fontWeight: 800, marginBottom: 5 }}>NON-MEMBER</div>
              <div style={{ fontSize: 25, fontWeight: 900, color: "#fff", lineHeight: 1 }}>$89</div>
              <div style={{ fontSize: 9.5, color: "#666", marginTop: 3 }}>flat rate call</div>
            </div>
          </div>

          {/* ── FLYER ── */}
          <div style={{ borderRadius: 14, overflow: "hidden", border: "1px solid #222", marginBottom: 22 }}>
            <img src="/roadside-flyer.jpg" alt="Super Fast Roadside — $10/month membership, $60 member rate, $89 non-member. Better than AAA."
              style={{ width: "100%", display: "block" }} />
          </div>

          {/* ── MEMBERSHIP + AUTH ── */}
          <div style={{ background: "linear-gradient(135deg, #1a0a0a 0%, #0f0f0f 100%)", border: "2px solid #E63329", borderRadius: 16, padding: 20, marginBottom: 16 }}>
            <div style={{ fontSize: 10, letterSpacing: 3, color: "#E63329", fontWeight: 800, marginBottom: 10 }}>MEMBERSHIP</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
              <span style={{ fontSize: 38, fontWeight: 900, color: "#fff" }}>$10</span>
              <span style={{ fontSize: 15, color: "#888" }}>/month</span>
            </div>
            <div style={{ fontSize: 12, color: "#777", marginBottom: 16, lineHeight: 1.5 }}>
              Unlocks the $60 flat rate. Without a membership, service calls are billed at standard rates.
            </div>
            {[
              "$60 flat rate on all core services",
              "No after-hours surcharge — same price at 2AM",
              "Priority dispatch on every request",
              "Live tracking on every job",
              "Cancel any time — no contract",
            ].map(f => (
              <div key={f} style={{ display: "flex", gap: 9, marginBottom: 8, alignItems: "flex-start" }}>
                <span style={{ color: "#22c55e", fontSize: 13, lineHeight: 1.4 }}>✓</span>
                <span style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.4 }}>{f}</span>
              </div>
            ))}

            <button style={{ width: "100%", background: "#E63329", border: "none", color: "#fff", padding: "16px", borderRadius: 12, fontSize: 15, fontWeight: 900, cursor: "pointer", marginTop: 16, marginBottom: 10 }}
              onClick={() => { setShowLanding(false); setAuthScreen("signup"); }}>
              ⚡ Join — Sign Up for Membership
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "4px 0 12px" }}>
              <div style={{ flex: 1, height: 1, background: "#2a2a2a" }} />
              <span style={{ fontSize: 10, color: "#555", letterSpacing: 1 }}>ALREADY A MEMBER?</span>
              <div style={{ flex: 1, height: 1, background: "#2a2a2a" }} />
            </div>

            <button style={{ width: "100%", background: "transparent", border: "1px solid #666", color: "#fff", padding: "15px", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: "pointer" }}
              onClick={() => setShowSignInChoice(true)}>
              🔑 Log In to My Account
            </button>
          </div>

          {/* ── WHAT'S INCLUDED / FAQ ── */}
          <div style={{ fontSize: 10, letterSpacing: 3, color: "#E63329", fontWeight: 800, marginBottom: 6 }}>GOOD TO KNOW</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 16 }}>Questions, answered.</div>

          {[
            ["What does the $10/month actually get me?",
             "It unlocks the $60 flat rate on tire changes, jump starts, lockouts, fuel delivery, tire plugs and wheel lock removal — day or night, no surcharge. It also puts you at the front of the dispatch queue."],
            ["Is the $60 on top of the $10?",
             "Yes. The $10 is your monthly membership. The $60 is charged only when you actually request a service call. No calls that month means you only pay $10."],
            ["Do you really not charge more at night?",
             "Correct. Most roadside companies add $50–75 after 8PM. We don't. $60 at 2PM, $60 at 2AM, $60 on a holiday."],
            ["What about tows, mechanics and used tires?",
             "Those are priced separately since costs vary. Tow is a $25 dispatch fee with the driver quoting the tow on arrival. Mobile mechanic is a $20 booking fee plus an on-site quote. Used tires start at $160 installed."],
            ["How fast do you get there?",
             "Most jobs are reached in 30 minutes or less. You'll see your tech's name and watch them arrive on the live map."],
            ["Can I cancel?",
             "Any time, from your account, in two taps. No contract, no cancellation fee, no phone call required."],
          ].map(([q, a]) => (
            <div key={q} style={{ background: "#131313", border: "1px solid #1e1e1e", borderRadius: 12, padding: 15, marginBottom: 9 }}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: "#fff", marginBottom: 6, lineHeight: 1.4 }}>{q}</div>
              <div style={{ fontSize: 12, color: "#777", lineHeight: 1.6 }}>{a}</div>
            </div>
          ))}

          {/* Van rear */}
          <div style={{ borderRadius: 14, overflow: "hidden", marginBottom: 18, border: "1px solid #222" }}>
            <img src="/van-rear.jpg" alt="Super Fast Mobile Tires — call or text 561-255-8506"
              style={{ width: "100%", display: "block" }} />
          </div>

          {/* ── SERVICE AREA ── */}
          <div style={{ background: "#131313", border: "1px solid #222", borderRadius: 12, padding: 16, marginTop: 18, marginBottom: 22 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: "#22c55e", fontWeight: 800, marginBottom: 8 }}>🇺🇸 SERVICE AREA — NATIONWIDE</div>
            <div style={{ fontSize: 12.5, color: "#999", lineHeight: 1.7 }}>
              <strong style={{ color: "#fff" }}>Headquartered in South Florida. Providers nationwide.</strong><br /><br />
              We started in Palm Beach County and now dispatch verified roadside pros across the United States. Enter your location and we'll find the closest available tech — coast to coast, 24 hours a day.
            </div>
          </div>

          {/* ── PROVIDER CTA ── */}
          <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 14, padding: 18, marginBottom: 20 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: "#3b82f6", fontWeight: 800, marginBottom: 8 }}>🛻 FOR PROVIDERS</div>
            <div style={{ fontSize: 17, fontWeight: 900, color: "#fff", marginBottom: 6 }}>Turn your truck into income.</div>
            <div style={{ fontSize: 12.5, color: "#888", lineHeight: 1.6, marginBottom: 14 }}>
              $40 per call, $50 after 10PM, and you keep 100% of every tip. Pick the services you offer, work when you want, get paid every Friday.
            </div>
            <a href="/provider" style={{ display: "block", textAlign: "center", background: "#3b82f6", color: "#fff", padding: "14px", borderRadius: 10, fontSize: 14, fontWeight: 800, textDecoration: "none" }}>
              Apply to Drive with Us →
            </a>
          </div>

          {/* ── FLEET & B2B ── */}
          <div style={{ background: "linear-gradient(135deg,#0a1f0a 0%,#0f0f0f 100%)", border: "1px solid #22c55e", borderRadius: 14, padding: 18, marginBottom: 20 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: "#22c55e", fontWeight: 800, marginBottom: 8 }}>🏢 FOR BUSINESSES &amp; FLEETS</div>
            <div style={{ fontSize: 19, fontWeight: 900, color: "#fff", marginBottom: 7, lineHeight: 1.2 }}>Stop turning down work.</div>
            <div style={{ fontSize: 12.5, color: "#888", lineHeight: 1.65, marginBottom: 16 }}>
              Tire shops, towing companies, dealerships, property managers — every job you can't run is a customer calling someone else. Send it to us instead. Keep the customer, keep a spread.
            </div>

            {/* Business pricing */}
            <div style={{ fontSize: 10, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 9 }}>BUSINESS PRICING</div>

            <div style={{ display: "flex", gap: 9, marginBottom: 14 }}>
              <div style={{ flex: 1, background: "#131313", border: "1px solid #2a2a2a", borderRadius: 11, padding: "15px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 9, letterSpacing: 1.2, color: "#888", fontWeight: 800, marginBottom: 6 }}>ACCOUNT</div>
                <div style={{ fontSize: 27, fontWeight: 900, color: "#22c55e", lineHeight: 1 }}>$20</div>
                <div style={{ fontSize: 10, color: "#666", marginTop: 4 }}>per month</div>
              </div>
              <div style={{ flex: 1, background: "#22c55e", borderRadius: 11, padding: "15px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 9, letterSpacing: 1.2, color: "rgba(0,0,0,0.6)", fontWeight: 800, marginBottom: 6 }}>PER CALL</div>
                <div style={{ fontSize: 27, fontWeight: 900, color: "#000", lineHeight: 1 }}>$70</div>
                <div style={{ fontSize: 10, color: "rgba(0,0,0,0.7)", marginTop: 4 }}>flat rate</div>
              </div>
            </div>

            <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 11, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: "#22c55e", marginBottom: 7 }}>🤝 Free for industry partners</div>
              <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.65, marginBottom: 9 }}>
                No monthly fee if you're in the business we're in — you're sending us work, not buying a service.
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {["Roadside assistance", "Mobile tire service", "Tire shops", "Towing companies"].map(t => (
                  <span key={t} style={{ fontSize: 10.5, background: "#0d2a0d", border: "1px solid #2a5a2a", color: "#4caf50", padding: "4px 9px", borderRadius: 10, fontWeight: 600 }}>{t}</span>
                ))}
              </div>
            </div>

            <div style={{ fontSize: 11, color: "#666", lineHeight: 1.65, marginBottom: 16 }}>
              The $20 covers your dispatch account and priority response. Every service call is $70, paid at dispatch. Used tires, batteries, tows, rim repair and mechanic work are quoted before we send anyone. No contract — cancel any time.
            </div>

            <a href="/partner" style={{ display: "block", textAlign: "center", background: "#22c55e", color: "#000", padding: "15px", borderRadius: 11, fontSize: 15, fontWeight: 900, textDecoration: "none", marginBottom: 9 }}>
              🚨 Dispatch a Job — Business Account
            </a>
            <div style={{ fontSize: 11, color: "#666", textAlign: "center", lineHeight: 1.55 }}>
              Sign up, we approve you, then dispatch in under a minute.
            </div>
          </div>

          {/* ── FLEET MAINTENANCE ── */}
          <div style={{ background: "linear-gradient(135deg,#0a1f18 0%,#0f0f0f 100%)", border: "1px solid #14b8a6", borderRadius: 14, padding: 18, marginBottom: 20 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: "#14b8a6", fontWeight: 800, marginBottom: 8 }}>🚚 FLEET MAINTENANCE</div>
            <div style={{ fontSize: 19, fontWeight: 900, color: "#fff", marginBottom: 7, lineHeight: 1.2 }}>
              Know what's about to break.
            </div>
            <div style={{ fontSize: 12.5, color: "#888", lineHeight: 1.65, marginBottom: 16 }}>
              Plumbers, electricians, landscapers, delivery, contractors — anybody running work trucks. We come to your yard once a month and inspect every vehicle, then hand you a written report with photos showing exactly what's coming due.
            </div>

            {/* What gets checked */}
            <div style={{ fontSize: 10, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 10 }}>WHAT WE CHECK, EVERY TRUCK</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
              {[
                ["🛞", "Tires", "Tread, pressure, wear"],
                ["🔋", "Battery", "Load test, terminals"],
                ["💧", "Fluids", "Oil, coolant, brake"],
                ["💡", "Lights", "Head, tail, signals"],
                ["🌧️", "Wipers", "Blade condition"],
                ["🛑", "Brakes", "Visual pad check"],
              ].map(([ic, t, d]) => (
                <div key={t} style={{ background: "#131313", border: "1px solid #1e1e1e", borderRadius: 9, padding: 11 }}>
                  <div style={{ fontSize: 17, marginBottom: 4 }}>{ic}</div>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: "#fff" }}>{t}</div>
                  <div style={{ fontSize: 10.5, color: "#777", marginTop: 2, lineHeight: 1.35 }}>{d}</div>
                </div>
              ))}
            </div>

            {/* Why it matters */}
            <div style={{ background: "#131313", border: "1px solid #1e1e1e", borderRadius: 11, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: "#14b8a6", marginBottom: 8 }}>📋 The report is the point</div>
              <div style={{ fontSize: 12, color: "#999", lineHeight: 1.7 }}>
                Every vehicle gets its own write-up with photos and a flag list — what's fine, what's due soon, what needs attention now. You budget for repairs instead of getting surprised by them, and a truck that would've died on a job Tuesday gets caught in the yard on Monday.
              </div>
            </div>

            {/* Pricing */}
            <div style={{ fontSize: 10, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 10 }}>PRICING</div>
            <div style={{ display: "flex", gap: 9, marginBottom: 14 }}>
              <div style={{ flex: 1, background: "#131313", border: "1px solid #2a2a2a", borderRadius: 11, padding: "15px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 9, letterSpacing: 1.2, color: "#888", fontWeight: 800, marginBottom: 6 }}>UP TO 12 TRUCKS</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: "#14b8a6", lineHeight: 1 }}>$400</div>
                <div style={{ fontSize: 10, color: "#666", marginTop: 4 }}>per month</div>
              </div>
              <div style={{ flex: 1, background: "#14b8a6", borderRadius: 11, padding: "15px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 9, letterSpacing: 1.2, color: "rgba(0,0,0,0.6)", fontWeight: 800, marginBottom: 6 }}>13 OR MORE</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: "#000", lineHeight: 1 }}>$32</div>
                <div style={{ fontSize: 10, color: "rgba(0,0,0,0.7)", marginTop: 4 }}>per truck / month</div>
              </div>
            </div>

            {/* Repair rates */}
            <div style={{ background: "#131313", border: "1px solid #1e1e1e", borderRadius: 11, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#f59e0b", marginBottom: 9 }}>Repairs billed separately — only with your approval</div>
              {[
                ["Tire mount & balance, on site", "$40/tire"],
                ["Tire rotation", "$60/vehicle"],
                ["Battery replacement", "$45 + battery"],
                ["Bulbs, wipers, filters", "$25 + parts"],
              ].map(([svc, price]) => (
                <div key={svc} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "6px 0", borderBottom: "1px solid #1a1a1a", fontSize: 11.5 }}>
                  <span style={{ color: "#999" }}>{svc}</span>
                  <span style={{ color: "#f59e0b", fontWeight: 700, whiteSpace: "nowrap" }}>{price}</span>
                </div>
              ))}
              <div style={{ fontSize: 11, color: "#666", marginTop: 10, lineHeight: 1.55 }}>
                Nothing gets done off a report without your say-so.
              </div>
            </div>

            <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 10, padding: 13, marginBottom: 16, fontSize: 12, color: "#9dc4f5", lineHeight: 1.6 }}>
              🚨 <strong style={{ color: "#fff" }}>Roadside included.</strong> Truck down between visits? Dispatch from your portal — $70 flat, 24/7, added to your monthly invoice.
            </div>

            <a href="/fleet" style={{ display: "block", textAlign: "center", background: "#14b8a6", color: "#000", padding: "15px", borderRadius: 11, fontSize: 15, fontWeight: 900, textDecoration: "none", marginBottom: 9 }}>
              🚚 Start a Fleet Account
            </a>
            <div style={{ fontSize: 11, color: "#666", textAlign: "center", lineHeight: 1.55 }}>
              Cancel any time · No contract · <a href="tel:5612558506" style={{ color: "#14b8a6", textDecoration: "none", fontWeight: 700 }}>Or call (561) 255-8506</a>
            </div>
          </div>

          {/* ── SPONSORSHIP ── */}
          <div style={{ background: "linear-gradient(135deg,#1a1400 0%,#0f0f0f 100%)", border: "1px solid #f59e0b", borderRadius: 14, padding: 18, marginBottom: 20 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 8 }}>🏪 FOR LOCAL BUSINESSES</div>
            <div style={{ fontSize: 17, fontWeight: 900, color: "#fff", marginBottom: 6 }}>Get on the map in your city.</div>
            <div style={{ fontSize: 12.5, color: "#888", lineHeight: 1.6, marginBottom: 14 }}>
              Put your business in front of stranded drivers who are physically near you, phone in hand, with nowhere to go for the next 30 minutes.
            </div>
            <button style={{ width: "100%", background: "#f59e0b", border: "none", color: "#000", padding: "14px", borderRadius: 10, fontSize: 14, fontWeight: 900, cursor: "pointer" }}
              onClick={() => setShowSponsor(true)}>
              Advertise With Us →
            </button>
          </div>

          <div style={{ textAlign: "center", fontSize: 11, color: "#444", lineHeight: 1.7 }}>
            <a href="tel:5612558506" style={{ color: "#E63329", fontWeight: 700, textDecoration: "none", fontSize: 13 }}>📞 (561) 255-8506</a><br /><br />
            {(() => {
              const fl = flyerForAddress(authUser?.profile?.homeAddress || "");
              return (
                <div style={{ marginBottom: 22 }}>
                  <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#E63329", fontWeight: 900, marginBottom: 6, textAlign: "center" }}>
                    SHARE SUPER FAST
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: "#fff", marginBottom: 3, textAlign: "center" }}>
                    {fl.label}
                  </div>
                  <div style={{ fontSize: 11.5, color: "#777", lineHeight: 1.5, marginBottom: 12, textAlign: "center" }}>
                    Know somebody who's been stranded? Show them this.
                  </div>
                  <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", border: "1px solid #222" }}>
                    <img src={fl.src} alt={`Super Fast Roadside — ${fl.label}`}
                      onClick={() => setShowQr(true)}
                      style={{ width: "100%", display: "block", cursor: "pointer" }} />
                    <button onClick={() => setShowQr(true)}
                      style={{ position: "absolute", left: "50%", bottom: 14, transform: "translateX(-50%)",
                        background: "#E63329", border: "none", color: "#fff", padding: "12px 22px",
                        borderRadius: 30, fontSize: 14, fontWeight: 900, cursor: "pointer",
                        boxShadow: "0 6px 22px rgba(0,0,0,0.6)", whiteSpace: "nowrap" }}>
                      📱 Tap to Enlarge QR
                    </button>
                  </div>
                </div>
              );
            })()}

            <span style={{ color: "#888", cursor: "pointer", textDecoration: "underline" }} onClick={() => setLegalDoc("terms")}>Terms of Service</span>
            {"   ·   "}
            <span style={{ color: "#888", cursor: "pointer", textDecoration: "underline" }} onClick={() => setLegalDoc("privacy")}>Privacy Policy</span>
            <br /><br />
            © 2026 Super Fast Mobile Tires LLC · South Florida<br />
            Based in South Florida · Serving nationwide
          </div>
          </div>

        {/* SIGN IN — CUSTOMER OR BUSINESS */}
        {showSignInChoice && (
          <div onClick={() => setShowSignInChoice(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.94)", zIndex: 950,
              display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 18, padding: 24, width: "100%", maxWidth: 380 }}>
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div style={{ fontSize: 34, marginBottom: 10 }}>👋</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 5 }}>Welcome back</div>
                <div style={{ fontSize: 12.5, color: "#777", lineHeight: 1.5 }}>Which kind of account do you have?</div>
              </div>

              <button
                onClick={() => { setShowSignInChoice(false); setShowLanding(false); setAuthScreen("login"); }}
                style={{ width: "100%", background: "#161616", border: "2px solid #E63329", borderRadius: 13,
                  padding: 16, cursor: "pointer", textAlign: "left", display: "flex", gap: 13, alignItems: "center", marginBottom: 11 }}>
                <span style={{ fontSize: 27, flexShrink: 0 }}>🚗</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: "#fff", marginBottom: 2 }}>I'm a Customer</div>
                  <div style={{ fontSize: 11.5, color: "#888", lineHeight: 1.45 }}>
                    Book roadside help for my vehicle
                  </div>
                </div>
                <span style={{ color: "#E63329", fontSize: 20 }}>›</span>
              </button>

              <a href="/partner"
                style={{ display: "flex", width: "100%", background: "#161616", border: "2px solid #22c55e", borderRadius: 13,
                  padding: 16, cursor: "pointer", textAlign: "left", gap: 13, alignItems: "center",
                  textDecoration: "none", boxSizing: "border-box", marginBottom: 16 }}>
                <span style={{ fontSize: 27, flexShrink: 0 }}>🏢</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: "#fff", marginBottom: 2 }}>I'm a Business</div>
                  <div style={{ fontSize: 11.5, color: "#888", lineHeight: 1.45 }}>
                    Dispatch jobs to the network — $70/call
                  </div>
                </div>
                <span style={{ color: "#22c55e", fontSize: 20 }}>›</span>
              </a>

              <a href="/fleet"
                style={{ display: "flex", width: "100%", background: "#161616", border: "2px solid #14b8a6", borderRadius: 13,
                  padding: 16, cursor: "pointer", textAlign: "left", gap: 13, alignItems: "center",
                  textDecoration: "none", boxSizing: "border-box", marginBottom: 16 }}>
                <span style={{ fontSize: 27, flexShrink: 0 }}>🚚</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: "#fff", marginBottom: 2 }}>I Run a Fleet</div>
                  <div style={{ fontSize: 11.5, color: "#888", lineHeight: 1.45 }}>
                    Monthly inspections and reports on every vehicle
                  </div>
                </div>
                <span style={{ color: "#14b8a6", fontSize: 20 }}>›</span>
              </a>

              <a href="/provider"
                style={{ display: "block", textAlign: "center", fontSize: 12, color: "#3b82f6",
                  textDecoration: "none", fontWeight: 700, marginBottom: 14 }}>
                🛻 I'm a Provider — sign in here
              </a>

              <button onClick={() => setShowSignInChoice(false)}
                style={{ width: "100%", background: "none", border: "1px solid #333", color: "#888",
                  padding: "11px", borderRadius: 10, fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* FULL-SCREEN QR */}
        {showQr && (
          <div onClick={() => setShowQr(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.97)", zIndex: 900, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 22, cursor: "pointer" }}>
            <div style={{ fontSize: 11, letterSpacing: 3, color: "#E63329", fontWeight: 900, marginBottom: 10 }}>SUPER FAST ROADSIDE</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", marginBottom: 18, textAlign: "center", lineHeight: 1.25 }}>
              Point your camera here
            </div>
            <div style={{ background: "#fff", borderRadius: 22, padding: 20, lineHeight: 0, boxShadow: "0 12px 50px rgba(230,51,41,0.3)" }}>
              <img src="/qr-signup.png" alt="Scan to open Super Fast Roadside"
                style={{ width: "min(80vw, 360px)", height: "min(80vw, 360px)", display: "block" }} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", marginTop: 20 }}>superfast-tires.web.app</div>
            <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>$10/month · $60 flat rate · Tap anywhere to close</div>
            <button style={{ position: "absolute", top: 22, right: 22, background: "rgba(255,255,255,0.12)", border: "1px solid #555", color: "#fff", width: 40, height: 40, borderRadius: "50%", fontSize: 18, cursor: "pointer" }}
              onClick={() => setShowQr(false)}>✕</button>
          </div>
        )}

        <style>{`
          @keyframes sfPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.4; transform: scale(1.35); }
          }
        `}</style>
      </div>
    );
  }

  if (!authUser) return <CustomerAuth screen={authScreen} setScreen={setAuthScreen} onSuccess={handleAuthSuccess} />;

  // Subscription gate — must be an active member to book
  if (!authUser.profile?.subscribed) {
    return <SubscriptionGate
      authUser={authUser}
      onSignOut={handleSignOut}
      onGuestBook={() => setGuestMode(true)}
      onSubscribed={(subId) => {
        const updated = { ...authUser, profile: { ...authUser.profile, subscribed: true, subscriptionId: subId } };
        localStorage.setItem("sfrs_customer", JSON.stringify(updated));
        setAuthUser(updated);
      }}
    />;
  }

  return <CustomerApp authUser={authUser} onSignOut={handleSignOut} />;
}

// ============================================================
// CUSTOMER AUTH
// ============================================================
function CustomerAuth({ screen, setScreen, onSuccess }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [dob, setDob]             = useState("");
  const [homeAddress, setHomeAddress] = useState("");
  const [phone, setPhone]         = useState("");
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw]       = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState("");
  const [verifyPending, setVerifyPending] = useState(false);
  const [pendingToken, setPendingToken]   = useState("");
  const [pendingUid, setPendingUid]       = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [checkingVerify, setCheckingVerify] = useState(false);

  function clear() {
    setError(""); setSuccess(""); setFirstName(""); setLastName("");
    setDob(""); setHomeAddress(""); setPhone(""); setEmail("");
    setPassword(""); setConfirmPw("");
  }

  async function handleSignUp() {
    setError("");
    if (!firstName.trim() || !lastName.trim()) return setError("First and last name are required.");
    if (!dob) return setError("Date of birth is required.");
    const age = Math.floor((Date.now() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000));
    if (age < 18) return setError("You must be 18 or older to create an account.");
    if (!homeAddress.trim()) return setError("Home address is required.");
    if (!phone.replace(/\D/g, "") || phone.replace(/\D/g, "").length < 10) return setError("Valid 10-digit phone number is required.");
    if (!email.trim()) return setError("Email address is required.");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirmPw) return setError("Passwords do not match.");

    setLoading(true);
    const profile = { firstName, lastName, dob, homeAddress, phone, email, role: "customer", createdAt: Date.now() };
    const result = await createAccount(email, password, profile);
    if (result.error) { setLoading(false); return setError(result.error); }

    await fbSendVerification(result.token);

    // Notify admin of the new customer signup
    await fetch(`${WORKER_URL}/notify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: "👤 New Customer Signed Up",
        message: `${firstName} ${lastName}\n📧 ${email}\n📞 ${phone || "No phone"}\n📍 ${homeAddress || "No address"}\n\n⏳ Email not verified yet · Not yet a member.`,
      }),
    }).catch(() => {});

    setPendingToken(result.token);
    setPendingUid(result.uid);
    setLoading(false);
    setVerifyPending(true);
  }

  async function handleLogin() {
    setError("");
    if (!email.trim()) return setError("Email is required.");
    if (!password.trim()) return setError("Password is required.");
    setLoading(true);

    const result = await fbSignIn(email, password);
    if (result.error) { setLoading(false); return setError(fbFormatError(result.error)); }

    const verified = await fbCheckVerified(result.token);
    if (!verified) {
      setPendingToken(result.token);
      setPendingUid(result.uid);
      setLoading(false);
      setVerifyPending(true);
      return;
    }

    const profiles = await dbGet(`users/${result.uid}`);
    const profile = Array.isArray(profiles) ? profiles[0] : profiles;
    setLoading(false);
    onSuccess({ ...result, profile });
  }

  async function handleResend() {
    setResendLoading(true); setResendSuccess(false);
    await fbSendVerification(pendingToken);
    setResendLoading(false); setResendSuccess(true);
  }

  async function handleCheckVerified() {
    setCheckingVerify(true); setError("");
    const verified = await fbCheckVerified(pendingToken);
    if (!verified) { setCheckingVerify(false); return setError("Email not verified yet. Check your inbox and click the link first."); }
    const profileRes = await loadProfileAfterLogin(pendingUid);
    const profile = profileRes.profile || null;
    await dbPatch(`users/${pendingUid}`, { emailVerified: true, verifiedAt: Date.now() });

    await fetch(`${WORKER_URL}/notify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: "✅ Customer Verified Email",
        message: `${profile?.firstName || ""} ${profile?.lastName || ""}\n📧 ${email}\n📞 ${profile?.phone || "No phone"}\n\nNow at the $10/month membership step.`,
      }),
    }).catch(() => {});

    setCheckingVerify(false);
    onSuccess({ uid: pendingUid, token: pendingToken, email, profile });
  }

  async function handleForgot() {
    setError(""); setSuccess("");
    if (!email.trim()) return setError("Enter your email above first.");
    setLoading(true);
    const result = await fbResetPassword(email);
    setLoading(false);
    if (result.error) return setError(fbFormatError(result.error));
    setSuccess("✅ Reset link sent — check your email.");
  }

  const Header = () => (
    <header style={c.header}>
      <div style={c.logo}>
        <span style={c.flame}>🔥</span>
        <div><div style={c.logoTop}>SUPER FAST</div><div style={c.logoBot}>ROADSIDE</div></div>
        <span style={c.flame}>🔥</span>
      </div>
    </header>
  );

  if (verifyPending) return (
    <div style={c.root}>
      <Header />
      <div style={c.page}>
        <div style={{ textAlign: "center", padding: "32px 0 24px" }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>📧</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", marginBottom: 8 }}>Verify Your Email</div>
          <div style={{ fontSize: 14, color: "#666", marginBottom: 6 }}>We sent a verification link to</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#E63329", marginBottom: 20, wordBreak: "break-all" }}>{email}</div>
          <div style={{ fontSize: 13, color: "#555", lineHeight: 1.6, marginBottom: 28 }}>
            Click the link in that email then come back and tap continue.
          </div>
          {error && <div style={c.gpsError}>⚠️ {error}</div>}
          {resendSuccess && <div style={c.gpsSuccess}>✅ New link sent! Check your inbox.</div>}
        </div>
        <button style={c.btnPrimary} onClick={handleCheckVerified} disabled={checkingVerify}>
          {checkingVerify ? "Checking..." : "I Verified My Email — Continue →"}
        </button>
        <button style={c.btnSecondary} onClick={handleResend} disabled={resendLoading}>
          {resendLoading ? "Sending..." : "Resend Verification Email"}
        </button>
        <div style={{ fontSize: 11, color: "#444", textAlign: "center", marginTop: 12 }}>Check your spam folder if you don't see it</div>
        <button style={{ ...c.btnSecondary, marginTop: 8, fontSize: 12 }} onClick={() => { setVerifyPending(false); clear(); setScreen("login"); }}>
          ← Back to Sign In
        </button>
      </div>
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC · South Florida</div>
    </div>
  );

  return (
    <div style={c.root}>
      <Header />
      <div style={c.page}>
        <div style={au.tabs}>
          <button style={{ ...au.tab, ...(screen === "login" ? au.tabActive : {}) }} onClick={() => { clear(); setScreen("login"); }}>Sign In</button>
          <button style={{ ...au.tab, ...(screen === "signup" ? au.tabActive : {}) }} onClick={() => { clear(); setScreen("signup"); }}>Create Account</button>
        </div>

        {screen === "login" && (
          <div>
            <div style={au.title}>Welcome Back 👋</div>
            <div style={au.sub}>Sign in to book a service</div>
            <div style={c.fieldGroup}>
              <label style={c.label}>Email Address</label>
              <input style={c.input} type="email" placeholder="john@email.com" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div style={c.fieldGroup}>
              <label style={c.label}>Password</label>
              <div style={{ position: "relative" }}>
                <input style={{ ...c.input, paddingRight: 48 }} type={showPw ? "text" : "password"} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} />
                <span style={au.eyeBtn} onClick={() => setShowPw(!showPw)}>{showPw ? "🙈" : "👁️"}</span>
              </div>
            </div>
            <div style={{ textAlign: "right", marginBottom: 16, marginTop: -8 }}>
              <span style={au.link} onClick={() => { clear(); setScreen("forgot"); }}>Forgot password?</span>
            </div>
            {error && <div style={c.gpsError}>⚠️ {error}</div>}
            <button style={c.btnPrimary} onClick={handleLogin} disabled={loading}>
              {loading ? "Signing In..." : "Sign In →"}
            </button>
            <div style={au.switchRow}>Don't have an account? <span style={au.link} onClick={() => { clear(); setScreen("signup"); }}>Create one</span></div>
          </div>
        )}

        {screen === "signup" && (
          <div>
            <div style={au.title}>Create Account 🔥</div>
            <div style={au.sub}>One time setup — takes 60 seconds</div>
            <div style={c.sectionLabel}>PERSONAL INFO</div>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ ...c.fieldGroup, flex: 1 }}>
                <label style={c.label}>First Name</label>
                <input style={c.input} placeholder="John" value={firstName} onChange={e => setFirstName(e.target.value)} />
              </div>
              <div style={{ ...c.fieldGroup, flex: 1 }}>
                <label style={c.label}>Last Name</label>
                <input style={c.input} placeholder="Smith" value={lastName} onChange={e => setLastName(e.target.value)} />
              </div>
            </div>
            <div style={c.fieldGroup}>
              <label style={c.label}>Date of Birth</label>
              <input style={c.input} type="date" value={dob} onChange={e => setDob(e.target.value)} />
              <div style={{ fontSize: 11, color: "#555", marginTop: 5 }}>Must be 18 or older</div>
            </div>
            <div style={c.fieldGroup}>
              <label style={c.label}>Home Address</label>
              <label style={c.label}>Home Address</label>
              <AddressFields value={homeAddress} onChange={val => setHomeAddress(val)} />
            </div>
            <div style={c.fieldGroup}>
              <label style={c.label}>Phone Number</label>
              <input style={c.input} type="tel" placeholder="(561) 000-0000" value={phone} onChange={e => setPhone(e.target.value)} />
            </div>
            <div style={c.sectionLabel}>LOGIN INFO</div>
            <div style={c.fieldGroup}>
              <label style={c.label}>Email Address</label>
              <input style={c.input} type="email" placeholder="john@email.com" value={email} onChange={e => setEmail(e.target.value)} />
              <div style={{ fontSize: 11, color: "#555", marginTop: 5 }}>A verification link will be sent here</div>
            </div>
            <div style={c.fieldGroup}>
              <label style={c.label}>Password</label>
              <div style={{ position: "relative" }}>
                <input style={{ ...c.input, paddingRight: 48 }} type={showPw ? "text" : "password"} placeholder="Min 6 characters" value={password} onChange={e => setPassword(e.target.value)} />
                <span style={au.eyeBtn} onClick={() => setShowPw(!showPw)}>{showPw ? "🙈" : "👁️"}</span>
              </div>
            </div>
            <div style={c.fieldGroup}>
              <label style={c.label}>Confirm Password</label>
              <input style={c.input} type="password" placeholder="Re-enter password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
            </div>
            {error && <div style={c.gpsError}>⚠️ {error}</div>}
            <button style={c.btnPrimary} onClick={handleSignUp} disabled={loading}>
              {loading ? "Creating Account..." : "Create Account & Verify Email →"}
            </button>
            <div style={au.switchRow}>Already have an account? <span style={au.link} onClick={() => { clear(); setScreen("login"); }}>Sign in</span></div>
            <div style={{ fontSize: 11, color: "#333", textAlign: "center", marginTop: 16, lineHeight: 1.6 }}>
              By creating an account you agree to our Terms of Service and Privacy Policy.
            </div>
          </div>
        )}

        {screen === "forgot" && (
          <div>
            <div style={au.title}>Reset Password 🔑</div>
            <div style={au.sub}>We'll send a reset link to your email</div>
            <div style={c.fieldGroup}>
              <label style={c.label}>Email Address</label>
              <input style={c.input} type="email" placeholder="john@email.com" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            {error && <div style={c.gpsError}>⚠️ {error}</div>}
            {success && <div style={c.gpsSuccess}>{success}</div>}
            <button style={c.btnPrimary} onClick={handleForgot} disabled={loading}>{loading ? "Sending..." : "Send Reset Link →"}</button>
            <button style={c.btnSecondary} onClick={() => { clear(); setScreen("login"); }}>← Back to Sign In</button>
          </div>
        )}
      </div>
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC · South Florida</div>
    </div>
  );
}

// ============================================================
// CUSTOMER APP
// ============================================================
function LiveTracker({ jobId, service, form, finalPrice, etaMin, onReview, onReset }) {
  const [jobStatus, setJobStatus] = useState("new");
  const [techName, setTechName] = useState(null);
  const [techFirstName, setTechFirstName] = useState(null);
  const [techCoords, setTechCoords] = useState(null);
  const [batteryUpgradePending, setBatteryUpgradePending] = useState(false);
  const [batteryUpgradePaid, setBatteryUpgradePaid] = useState(false);
  const [batteryPayStep, setBatteryPayStep] = useState("idle"); // idle | card | verifying | paid
  const [batteryPayError, setBatteryPayError] = useState("");
  const [jobKey, setJobKey] = useState(null);
  const [tipAmount, setTipAmount] = useState(null);
  const [customTip, setCustomTip] = useState("");
  const [tipPaid, setTipPaid] = useState(false);
  const [tipStep, setTipStep] = useState("idle"); // idle | loading | card | verifying | paid
  const [tipError, setTipError] = useState("");
  const [savedCard, setSavedCard] = useState(null);
  const [addon, setAddon] = useState(null);
  const [addonPaid, setAddonPaid] = useState(false);
  const [addonStep, setAddonStep] = useState("idle");
  const [addonErr, setAddonErr] = useState("");
  const addonCardElRef = useRef(null);
  const addonCardRef = useRef(null);
  const addonStripeRef = useRef(null);
  const [showCustomTip, setShowCustomTip] = useState(false);
  const tipCardRef = useRef(null);
  const tipCardElRef = useRef(null);
  const tipStripeRef = useRef(null);
  const batteryCardRef = useRef(null);
  const batteryCardElRef = useRef(null);
  const batteryStripeRef = useRef(null);

  useEffect(() => {
    async function poll() {
      const all = await dbGet("jobs");
      if (!Array.isArray(all)) return;
      const job = all.find(j => j.id === jobId);
      if (!job) return;
      setJobStatus(job.status || "new");
      setJobKey(job._key);
      setBatteryUpgradePending(!!job.battery_upgrade_pending);
      setBatteryUpgradePaid(!!job.battery_upgrade_paid);
      setTipPaid(!!job.tipPaid);
      setAddon(job.addonPending && !job.addonPaid
        ? { label: job.addonLabel, price: job.addonPrice, by: job.addonRequestedBy } : null);
      setAddonPaid(!!job.addonPaid);
      setSavedCard(job.savedPaymentMethod && job.stripeCustomerId
        ? { pm: job.savedPaymentMethod, cust: job.stripeCustomerId } : null);
      if (job.tech) {
        try {
          const prov = await dbGet(`users/${job.tech}`);
          if (prov?.currentLocation) {
            setTechCoords({ lat: prov.currentLocation.latitude, lng: prov.currentLocation.longitude });
          }
        } catch {}
      }
      if (job.techName) {
        setTechName(job.techName);
        const firstName = job.techName.split(" ")[0];
        setTechFirstName(firstName === "Admin" ? "Your tech" : firstName);
      }
    }
    poll();
    const interval = setInterval(poll, 6000);
    return () => clearInterval(interval);
  }, [jobId]);

  async function handleBatteryPayment() {
    setBatteryPayError("");
    setBatteryPayStep("loading");
    try {
      const stripe = await loadStripe();
      batteryStripeRef.current = stripe;
      const amount = parseFloat((400 * 1.07).toFixed(2));
      const res = await fetch(`${WORKER_URL}/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount, jobId, customerName: form.name || "Customer", service: "Battery Replacement Upgrade" }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setBatteryPayStep("card");
      setTimeout(() => {
        const elements = stripe.elements();
        const card = elements.create("card", {
          style: { base: { color: "#ffffff", fontSize: "16px", "::placeholder": { color: "#666" } }, invalid: { color: "#ff6b6b" } },
        });
        card.mount(batteryCardElRef.current);
        batteryCardRef.current = { card, clientSecret: data.clientSecret, paymentIntentId: data.paymentIntentId, amount };
      }, 100);
    } catch (err) {
      setBatteryPayError(err.message);
      setBatteryPayStep("idle");
    }
  }

  async function confirmBatteryPayment() {
    if (!batteryStripeRef.current || !batteryCardRef.current) return;
    setBatteryPayStep("verifying");
    try {
      const { error, paymentIntent } = await batteryStripeRef.current.confirmCardPayment(
        batteryCardRef.current.clientSecret,
        { payment_method: { card: batteryCardRef.current.card, billing_details: { name: form.name || "Customer" } } }
      );
      if (error) { setBatteryPayError(error.message); setBatteryPayStep("card"); return; }
      if (paymentIntent.status !== "succeeded") { setBatteryPayError("Payment not completed."); setBatteryPayStep("card"); return; }

      // Update job
      await dbPatch(`jobs/${jobKey}`, {
        battery_upgrade_paid: true,
        battery_upgrade_payment_intent: paymentIntent.id,
        battery_upgrade_paid_at: Date.now(),
      });

      // Notify admin
      await fetch(`${WORKER_URL}/notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: "✅ Battery Upgrade Payment Confirmed!",
          message: `Customer has paid $${batteryCardRef.current.amount} for battery replacement.\nJob: ${jobId}\nPayment ID: ${paymentIntent.id}\nProceed with battery installation.`,
        }),
      }).catch(() => {});

      setBatteryUpgradePaid(true);
      setBatteryPayStep("paid");
    } catch (err) {
      setBatteryPayError(err.message);
      setBatteryPayStep("card");
    }
  }

  async function handleTipPayment(amount) {
    setTipError("");
    setTipStep("verifying");

    // One-tap: charge the card already on file from the service call
    if (savedCard) {
      try {
        const res = await fetch(`${WORKER_URL}/charge-saved-card`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount,
            customerId: savedCard.cust,
            paymentMethodId: savedCard.pm,
            jobId,
            description: `Tip for ${techFirstName || "provider"} — Job ${jobId}`,
          }),
        });
        const d = await res.json();
        if (!d.paid) throw new Error(d.error || "Could not process tip.");

        await dbPatch(`jobs/${jobKey}`, {
          tipAmount: amount, tipPaid: true,
          tipPaymentIntent: d.paymentIntentId, tipPaidAt: Date.now(),
        });
        await fetch(`${WORKER_URL}/notify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: "💚 Tip Received!",
            message: `${form.name || "Customer"} tipped $${amount.toFixed(2)} to ${techName || "provider"}.\nJob: ${jobId}\nProvider keeps 100% — add to their weekly payout.`,
          }),
        }).catch(() => {});

        setTipPaid(true);
        setTipStep("paid");
        return;
      } catch (err) {
        // Saved card failed — fall through to manual entry
        setTipError(err.message + " Enter a card below.");
      }
    }

    // Fallback: no saved card, collect one
    setTipStep("loading");
    try {
      const stripe = await loadStripe();
      tipStripeRef.current = stripe;
      const res = await fetch(`${WORKER_URL}/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount, jobId,
          customerName: form.name || "Customer",
          service: `Tip for ${techFirstName || "provider"}`,
          saveCard: false,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setTipStep("card");
      setTimeout(() => {
        const elements = stripe.elements();
        const card = elements.create("card", {
          style: { base: { color: "#ffffff", fontSize: "16px", "::placeholder": { color: "#666" } }, invalid: { color: "#ff6b6b" } },
        });
        card.mount(tipCardElRef.current);
        tipCardRef.current = { card, clientSecret: data.clientSecret, amount };
      }, 100);
    } catch (err) {
      setTipError(err.message);
      setTipStep("idle");
    }
  }

  async function confirmTipPayment() {
    if (!tipStripeRef.current || !tipCardRef.current) return;
    setTipStep("verifying");
    try {
      const { error, paymentIntent } = await tipStripeRef.current.confirmCardPayment(
        tipCardRef.current.clientSecret,
        { payment_method: { card: tipCardRef.current.card, billing_details: { name: form.name || "Customer" } } }
      );
      if (error) { setTipError(error.message); setTipStep("card"); return; }
      if (paymentIntent.status !== "succeeded") { setTipError("Payment not completed."); setTipStep("card"); return; }

      const amt = tipCardRef.current.amount;
      await dbPatch(`jobs/${jobKey}`, {
        tipAmount: amt, tipPaid: true,
        tipPaymentIntent: paymentIntent.id, tipPaidAt: Date.now(),
      });
      await fetch(`${WORKER_URL}/notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: "💚 Tip Received!",
          message: `${form.name || "Customer"} tipped $${amt.toFixed(2)} to ${techName || "provider"}.\nJob: ${jobId}\nProvider keeps 100% — add to their weekly payout.`,
        }),
      }).catch(() => {});

      setTipPaid(true);
      setTipStep("paid");
    } catch (err) {
      setTipError(err.message);
      setTipStep("card");
    }
  }

  async function payAddon() {
    if (!addon) return;
    setAddonErr("");
    const total = parseFloat((addon.price * 1.07).toFixed(2));

    // One tap if we already have their card
    if (savedCard) {
      setAddonStep("verifying");
      try {
        const res = await fetch(`${WORKER_URL}/charge-saved-card`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: total, customerId: savedCard.cust, paymentMethodId: savedCard.pm,
            jobId, description: `Add-on — ${addon.label}`,
          }),
        });
        const d = await res.json();
        if (!d.paid) throw new Error(d.error || "Could not process.");
        await dbPatch(`jobs/${jobKey}`, {
          addonPaid: true, addonPaymentIntent: d.paymentIntentId,
          addonAmountPaid: total, addonPaidAt: Date.now(),
        });
        await fetch(`${WORKER_URL}/notify`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: `✅ Add-On Paid — $${total.toFixed(2)}`,
            message: `${form.name || "Customer"} approved ${addon.label}.\nJob ${jobId}\nProvider earns +$${ADDON_PROVIDER_BONUS} on this job.`,
          }),
        }).catch(() => {});
        setAddonPaid(true); setAddon(null); setAddonStep("paid");
        return;
      } catch (e) {
        setAddonErr(e.message + " Enter a card below.");
      }
    }

    setAddonStep("loading");
    try {
      const stripe = await loadStripe();
      addonStripeRef.current = stripe;
      const res = await fetch(`${WORKER_URL}/create-payment-intent`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: total, jobId, customerName: form.name || "Customer", service: `Add-on — ${addon.label}` }),
      });
      const d = await res.json();
      if (d.error) throw new Error(d.error);
      setAddonStep("card");
      setTimeout(() => {
        const el = stripe.elements();
        const card = el.create("card", {
          style: { base: { color: "#fff", fontSize: "16px", "::placeholder": { color: "#666" } }, invalid: { color: "#ff6b6b" } },
        });
        card.mount(addonCardElRef.current);
        addonCardRef.current = { card, clientSecret: d.clientSecret, total };
      }, 100);
    } catch (e) {
      setAddonErr(e.message);
      setAddonStep("idle");
    }
  }

  async function confirmAddon() {
    if (!addonStripeRef.current || !addonCardRef.current) return;
    setAddonStep("verifying");
    try {
      const { error, paymentIntent } = await addonStripeRef.current.confirmCardPayment(
        addonCardRef.current.clientSecret,
        { payment_method: { card: addonCardRef.current.card, billing_details: { name: form.name || "Customer" } } }
      );
      if (error) { setAddonErr(error.message); setAddonStep("card"); return; }
      if (paymentIntent.status !== "succeeded") { setAddonErr("Payment not completed."); setAddonStep("card"); return; }
      await dbPatch(`jobs/${jobKey}`, {
        addonPaid: true, addonPaymentIntent: paymentIntent.id,
        addonAmountPaid: addonCardRef.current.total, addonPaidAt: Date.now(),
      });
      await fetch(`${WORKER_URL}/notify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: `✅ Add-On Paid — $${addonCardRef.current.total.toFixed(2)}`,
          message: `${form.name || "Customer"} approved the add-on.\nJob ${jobId}\nProvider earns +$${ADDON_PROVIDER_BONUS}.`,
        }),
      }).catch(() => {});
      setAddonPaid(true); setAddon(null); setAddonStep("paid");
    } catch (e) {
      setAddonErr(e.message);
      setAddonStep("card");
    }
  }

  const STATUS_INFO = {
    new:       { icon: "⏳", title: "Looking for a tech...", sub: "Hang tight — we're finding someone near you", color: "#666", step: 0 },
    assigned:  { icon: "🛻", title: `${techFirstName || "Your tech"} is on the way!`, sub: `${techFirstName || "Your tech"} has been dispatched and will contact you shortly`, color: "#f59e0b", step: 1 },
    en_route:  { icon: "🚗", title: `${techFirstName || "Your tech"} is en route!`, sub: `${techFirstName || "Your tech"} is heading to your location now`, color: "#3b82f6", step: 2 },
    on_scene:  { icon: "📍", title: `${techFirstName || "Your tech"} has arrived!`, sub: "Your tech is on site and starting the job", color: "#a855f7", step: 3 },
    completed: { icon: "✅", title: "Job Complete!", sub: "Your service has been completed. Thank you!", color: "#22c55e", step: 4 },
    tow_requested: { icon: "🚛", title: "Tow truck needed", sub: "A tow truck is being dispatched to your location", color: "#E63329", step: 2 },
  };

  const info = STATUS_INFO[jobStatus] || STATUS_INFO.new;
  const steps = ["Paid", "Dispatched", "En Route", "On Scene", "Done"];

  return (
    <>
      {/* Live map — watch your tech approach */}
      {techCoords && jobStatus !== "completed" && (
        <div style={{ position: "relative", height: 200, borderRadius: 14, overflow: "hidden", border: "1px solid #222", marginBottom: 16 }}>
          <LiveMap coords={techCoords} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(to top, rgba(0,0,0,0.9), transparent)", padding: "24px 12px 10px", pointerEvents: "none" }}>
            <div style={{ fontSize: 11.5, fontWeight: 800, color: "#22c55e", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", display: "inline-block" }} />
              {techFirstName || "Your tech"} is here on the map
            </div>
          </div>
        </div>
      )}

      {/* Status hero */}
      <div style={{ textAlign: "center", padding: "28px 0 20px" }}>
        <div style={{ fontSize: 64, marginBottom: 12 }}>{info.icon}</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", marginBottom: 6 }}>{info.title}</div>
        <div style={{ fontSize: 13, color: "#666", lineHeight: 1.5 }}>{info.sub}</div>
        {(jobStatus === "new") && (
          <div style={{ fontSize: 12, color: "#444", marginTop: 8 }}>Est. arrival: ~{etaMin} min</div>
        )}
      </div>

      {/* Provider name banner */}
      {techFirstName && jobStatus !== "new" && jobStatus !== "completed" && (
        <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 10, padding: "14px 16px", marginBottom: 16, textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "#3b82f6", fontWeight: 700, letterSpacing: 2, marginBottom: 4 }}>YOUR TECH</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: "#fff" }}>{techFirstName}</div>
          <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>You will be contacted shortly</div>
        </div>
      )}

      {/* ── ADD-ON REQUESTED ON SCENE ── */}
      {addon && !addonPaid && (
        <div style={{ background: "#1a1400", border: "2px solid #f59e0b", borderRadius: 12, padding: 18, marginBottom: 16 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 7 }}>
            YOUR TECH FOUND SOMETHING
          </div>
          <div style={{ fontSize: 17, fontWeight: 900, color: "#fff", marginBottom: 7 }}>{addon.label}</div>
          <div style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.65, marginBottom: 14 }}>
            {addon.by ? `${addon.by.split(" ")[0]} says this` : "Your tech says this"} needs doing while they're here. Since they're already on site, it's <strong style={{ color: "#22c55e" }}>${addon.price}</strong> instead of a full ${60} service call.
          </div>

          <div style={{ background: "#0f0f0f", borderRadius: 8, padding: 11, marginBottom: 14, fontSize: 12.5 }}>
            <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 3 }}>
              <span>{addon.label.replace(/^[^\s]+\s/, "")}</span><span>${addon.price}.00</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 5 }}>
              <span>Tax (7%)</span><span>${(addon.price * 0.07).toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", color: "#fff", fontWeight: 900, fontSize: 14.5, borderTop: "1px solid #222", paddingTop: 6 }}>
              <span>Total</span><span style={{ color: "#22c55e" }}>${(addon.price * 1.07).toFixed(2)}</span>
            </div>
          </div>

          {addonErr && <div style={{ color: "#ff6b6b", fontSize: 12, marginBottom: 10 }}>⚠️ {addonErr}</div>}

          {addonStep === "card" ? (
            <>
              <div ref={addonCardElRef} style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 8, padding: "13px 15px", marginBottom: 11 }} />
              <button style={{ ...c.btnPrimary, background: "#22c55e", color: "#000" }} onClick={confirmAddon}>
                🔒 Pay ${(addon.price * 1.07).toFixed(2)}
              </button>
              <button style={c.btnSecondary} onClick={() => setAddonStep("idle")}>← Cancel</button>
            </>
          ) : addonStep === "verifying" ? (
            <div style={{ textAlign: "center", padding: 16, color: "#22c55e" }}>🔒 Processing...</div>
          ) : addonStep === "loading" ? (
            <div style={{ textAlign: "center", padding: 16, color: "#666" }}>💳 Loading...</div>
          ) : (
            <>
              {savedCard && (
                <div style={{ fontSize: 11.5, color: "#4caf50", marginBottom: 9, display: "flex", gap: 6, alignItems: "center" }}>
                  <span>⚡</span><span>One tap — we'll use the card you paid with.</span>
                </div>
              )}
              <button style={{ ...c.btnPrimary, background: "#f59e0b", color: "#000" }} onClick={payAddon}>
                ✅ Approve & Pay ${(addon.price * 1.07).toFixed(2)}
              </button>
              <div style={{ fontSize: 11.5, color: "#666", textAlign: "center", marginTop: 9, lineHeight: 1.5 }}>
                Not now? Just tell your tech — they'll finish the original job either way.
              </div>
            </>
          )}
        </div>
      )}

      {addonPaid && addonStep === "paid" && (
        <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: 13, marginBottom: 16, textAlign: "center" }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: "#22c55e" }}>✅ Add-on approved — your tech is on it</div>
        </div>
      )}

      {/* BATTERY UPGRADE PAYMENT SECTION */}
      {batteryUpgradePending && !batteryUpgradePaid && (
        <div style={{ background: "#0a1525", border: "2px solid #3b82f6", borderRadius: 12, padding: 18, marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 900, color: "#3b82f6", marginBottom: 6 }}>🔋 Battery Replacement Needed</div>
          <div style={{ fontSize: 13, color: "#aaa", marginBottom: 14, lineHeight: 1.6 }}>
            Your tech has determined your battery cannot be jumped. To proceed with a brand new battery installation, please complete payment below.
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14, padding: "10px 14px", background: "#161616", borderRadius: 8 }}>
            <span style={{ color: "#666" }}>Battery Replacement + Tax</span>
            <span style={{ color: "#22c55e", fontWeight: 900, fontSize: 16 }}>$428.00</span>
          </div>

          {batteryPayStep === "paid" ? (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
              <div style={{ fontSize: 15, fontWeight: 900, color: "#22c55e" }}>Payment Confirmed!</div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>Your tech has been notified. Battery installation will proceed shortly.</div>
            </div>
          ) : batteryPayStep === "card" ? (
            <>
              <div style={{ fontSize: 10, letterSpacing: 3, color: "#666", fontWeight: 700, marginBottom: 8 }}>PAYMENT INFO</div>
              <div ref={batteryCardElRef} style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 8, padding: "14px 16px", marginBottom: 12 }} />
              {batteryPayError && <div style={{ color: "#ff6b6b", fontSize: 12, marginBottom: 8 }}>⚠️ {batteryPayError}</div>}
              <button style={{ ...c.btnPrimary, background: "#3b82f6" }} onClick={confirmBatteryPayment}>
                ✅ Verify & Complete Payment — $428.00
              </button>
              <button style={c.btnSecondary} onClick={() => setBatteryPayStep("idle")}>← Cancel</button>
            </>
          ) : batteryPayStep === "verifying" ? (
            <div style={{ textAlign: "center", padding: "16px", color: "#22c55e" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
              Verifying payment...
            </div>
          ) : batteryPayStep === "loading" ? (
            <div style={{ textAlign: "center", padding: "16px", color: "#666" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>💳</div>
              Loading payment...
            </div>
          ) : (
            <button style={{ ...c.btnPrimary, background: "#3b82f6", fontSize: 15 }} onClick={handleBatteryPayment}>
              💳 Pay $428.00 for Battery Replacement →
            </button>
          )}
        </div>
      )}

      {batteryUpgradePaid && (
        <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: 14, marginBottom: 16, textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#22c55e" }}>✅ Battery Payment Confirmed</div>
          <div style={{ fontSize: 12, color: "#4caf50", marginTop: 4 }}>Your tech is proceeding with battery installation</div>
        </div>
      )}

      {/* Progress bar */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
        {steps.map((step, i) => (
          <div key={step} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
              {i > 0 && <div style={{ flex: 1, height: 2, background: i <= info.step ? "#E63329" : "#222" }} />}
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: i <= info.step ? "#E63329" : "#222", flexShrink: 0 }} />
              {i < steps.length - 1 && <div style={{ flex: 1, height: 2, background: i < info.step ? "#E63329" : "#222" }} />}
            </div>
            <div style={{ fontSize: 8, color: i <= info.step ? "#E63329" : "#444", marginTop: 4, textAlign: "center" }}>{step}</div>
          </div>
        ))}
      </div>

      {/* Job details */}
      <div style={c.trackCard}>
        {[
          ["📍", "Your Location", form.address],
          ["🛞", "Service", service.name],
          ["🆔", "Job ID", jobId],
        ].map(([icon, label, val]) => (
          <div key={label}>
            <div style={c.trackRow}>
              <span style={{ fontSize: 18 }}>{icon}</span>
              <div><div style={c.trackLabel}>{label}</div><div style={{ fontSize: 13, fontWeight: 600 }}>{val}</div></div>
            </div>
            <div style={c.divider} />
          </div>
        ))}
        <div style={c.trackRow}>
          <span style={{ fontSize: 18 }}>💳</span>
          <div><div style={c.trackLabel}>Amount Paid</div><div style={{ fontSize: 16, fontWeight: 900, color: "#22c55e" }}>{finalPrice ? `$${finalPrice.toFixed(2)}` : "Quote on arrival"}</div></div>
        </div>
      </div>

      <div style={{ fontSize: 13, color: "#555", textAlign: "center", marginBottom: 16 }}>
        Need help? <a href="tel:5612558506" style={{ color: "#E63329" }}>📞 (561) 255-8506</a>
      </div>

      {jobStatus === "completed" && (
        <>
          {/* TIP YOUR PROVIDER */}
          {!tipPaid ? (
            <div style={{ background: "#0a1f0a", border: "2px solid #22c55e", borderRadius: 12, padding: 18, marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: "#22c55e", marginBottom: 6 }}>
                💚 Tip {techFirstName || "Your Tech"}?
              </div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 14, lineHeight: 1.6 }}>
                100% of your tip goes directly to {techFirstName || "your tech"} — we don't take a cut.
              </div>
              {savedCard && (
                <div style={{ background: "#0d1a0d", border: "1px solid #1f4d1f", borderRadius: 8, padding: "9px 12px", marginBottom: 14, fontSize: 11.5, color: "#4caf50", display: "flex", alignItems: "center", gap: 7 }}>
                  <span>⚡</span>
                  <span>One tap — we'll use the card you just paid with.</span>
                </div>
              )}

              {tipStep === "paid" ? (
                <div style={{ textAlign: "center", padding: "12px 0" }}>
                  <div style={{ fontSize: 36, marginBottom: 6 }}>🙏</div>
                  <div style={{ fontSize: 15, fontWeight: 900, color: "#22c55e" }}>Thank you!</div>
                </div>
              ) : tipStep === "card" ? (
                <>
                  <div style={{ fontSize: 10, letterSpacing: 3, color: "#666", fontWeight: 700, marginBottom: 8 }}>PAYMENT INFO</div>
                  <div ref={tipCardElRef} style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 8, padding: "14px 16px", marginBottom: 12 }} />
                  {tipError && <div style={{ color: "#ff6b6b", fontSize: 12, marginBottom: 8 }}>⚠️ {tipError}</div>}
                  <button style={{ ...c.btnPrimary, background: "#22c55e", color: "#000" }} onClick={confirmTipPayment}>
                    Send ${tipCardRef.current?.amount?.toFixed(2)} Tip →
                  </button>
                  <button style={c.btnSecondary} onClick={() => { setTipStep("idle"); setTipAmount(null); setCustomTip(""); }}>← Cancel</button>
                </>
              ) : tipStep === "verifying" ? (
                <div style={{ textAlign: "center", padding: "16px", color: "#22c55e" }}>
                  <div style={{ fontSize: 30, marginBottom: 6 }}>💚</div>
                  Sending your tip...
                </div>
              ) : tipStep === "loading" ? (
                <div style={{ textAlign: "center", padding: "16px", color: "#666" }}>
                  <div style={{ fontSize: 30, marginBottom: 6 }}>💳</div>
                  Loading...
                </div>
              ) : (
                <>
                  {/* One tap = charged immediately with saved card */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    {[5, 10, 15, 20].map(amt => (
                      <button key={amt}
                        onClick={() => savedCard
                          ? handleTipPayment(amt)
                          : (setTipAmount(amt), setCustomTip(""))}
                        style={{
                          flex: 1,
                          background: (!savedCard && tipAmount === amt) ? "#22c55e" : "#161616",
                          border: `1px solid ${(!savedCard && tipAmount === amt) ? "#22c55e" : "#2a5a2a"}`,
                          color: (!savedCard && tipAmount === amt) ? "#000" : "#22c55e",
                          padding: "16px 0", borderRadius: 10, cursor: "pointer",
                          fontSize: 17, fontWeight: 900,
                        }}>
                        ${amt}
                      </button>
                    ))}
                  </div>

                  {!showCustomTip ? (
                    <button style={{ width: "100%", background: "none", border: "1px dashed #2a5a2a", color: "#4caf50", padding: "11px", borderRadius: 9, fontSize: 12.5, fontWeight: 700, cursor: "pointer", marginBottom: 10 }}
                      onClick={() => setShowCustomTip(true)}>
                      Enter a custom amount
                    </button>
                  ) : (
                    <>
                      <input
                        style={{ ...c.input, marginBottom: 10 }}
                        type="number"
                        placeholder="Custom amount"
                        value={customTip}
                        onChange={e => { setCustomTip(e.target.value); setTipAmount(null); }}
                      />
                      <button
                        style={{ ...c.btnPrimary, background: "#22c55e", color: "#000", opacity: !customTip ? 0.4 : 1 }}
                        disabled={!customTip}
                        onClick={() => {
                          const amt = parseFloat(customTip);
                          if (!amt || amt <= 0) return;
                          handleTipPayment(parseFloat(amt.toFixed(2)));
                        }}>
                        {customTip ? `💚 Tip $${(parseFloat(customTip) || 0).toFixed(2)}` : "Enter an amount"}
                      </button>
                    </>
                  )}

                  <button style={{ ...c.btnSecondary, marginTop: 4 }} onClick={() => setTipPaid(true)}>
                    No thanks
                  </button>
                </>
              )}
            </div>
          ) : (
            <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: 12, marginBottom: 16, textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#22c55e" }}>💚 Thanks for supporting your tech!</div>
            </div>
          )}

          <button style={{ ...c.btnPrimary, background: "#f59e0b", color: "#000", fontWeight: 900, marginBottom: 12 }}
            onClick={() => onReview(techName)}>
            ⭐ Rate Your Service
          </button>
          <a href="https://share.google/hyMwg1BgG6yKvSCRP" target="_blank" rel="noreferrer"
            style={{ display: "block", textAlign: "center", background: "#fff", color: "#333", borderRadius: 10, padding: "14px", fontWeight: 800, fontSize: 14, textDecoration: "none", marginBottom: 12, border: "2px solid #4285F4" }}>
            <span style={{ marginRight: 8 }}>🌟</span> Leave Us a Google Review
          </a>
          <div style={{ fontSize: 11, color: "#555", textAlign: "center", marginBottom: 12, lineHeight: 1.6, padding: "0 16px" }}>
            Please mention the <strong style={{ color: "#aaa" }}>city</strong> you were helped in and the <strong style={{ color: "#aaa" }}>type of service</strong> done to your vehicle. Thank you for your support! 🙏
          </div>
        </>
      )}
      <button style={c.btnSecondary} onClick={onReset}>Book Another Service</button>
    </>
  );
}

// ============================================================
// $10/MONTH SUBSCRIPTION GATE
// ============================================================
// ============================================================
// SHARED — VIEW PROFILE + DELETE ACCOUNT
// ============================================================
function DeleteAccountBlock({ authUser, profile, role, onDeleted }) {
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  async function doDelete() {
    if (typed !== "DELETE") return;
    setBusy(true);
    try {
      // Cancel an active membership first
      if (profile.subscriptionId) {
        await fetch(`${WORKER_URL}/cancel-subscription`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ subscriptionId: profile.subscriptionId }),
        }).catch(() => {});
      }
      // Wipe their record
      await dbDelete(`users/${authUser.uid}`);

      // Remove provider message threads
      if (role === "provider") {
        const msgs = await dbGet("providerMessages");
        if (Array.isArray(msgs)) {
          for (const m of msgs.filter(x => x.providerId === authUser.uid)) {
            await dbDelete(`providerMessages/${m._key}`);
          }
        }
      }

      await fetch(`${WORKER_URL}/notify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: "🗑️ Account Deleted",
          message: `${profile.firstName || ""} ${profile.lastName || ""} (${role}) deleted their account.\nEmail: ${profile.email || ""}\nPhone: ${profile.phone || ""}`,
        }),
      }).catch(() => {});

      localStorage.removeItem(role === "provider" ? "sfrs_provider" : "sfrs_customer");
      setBusy(false);
      alert("Your account and data have been permanently deleted.");
      onDeleted();
    } catch (err) {
      setBusy(false);
      alert("Could not delete account: " + err.message);
    }
  }

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#E63329", fontWeight: 800, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
        🗑️ DANGER ZONE
      </div>
      <div style={{ background: "#160404", border: "1px solid #E63329", borderRadius: 12, padding: 18 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", marginBottom: 6 }}>Delete account &amp; data</div>
        <div style={{ fontSize: 12, color: "#999", lineHeight: 1.6, marginBottom: 14 }}>
          Permanently removes your account, profile, job history, messages, and photos. This cannot be undone.
          {profile.subscriptionId && <><br /><strong style={{ color: "#f59e0b" }}>Your $10/month membership will be cancelled.</strong></>}
        </div>

        {!confirming ? (
          <button style={{ width: "100%", background: "#2a0808", border: "2px solid #E63329", color: "#ff6b6b", padding: "14px", borderRadius: 10, fontWeight: 900, fontSize: 14.5, cursor: "pointer" }}
            onClick={() => setConfirming(true)}>
            🗑️ Delete account &amp; data
          </button>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: "#ff6b6b", fontWeight: 700, marginBottom: 9 }}>
              Type <strong>DELETE</strong> in capital letters to confirm.
            </div>
            <input
              value={typed}
              onChange={e => setTyped(e.target.value)}
              placeholder="DELETE"
              style={{ width: "100%", background: "#0f0f0f", border: "1px solid #E63329", borderRadius: 8, padding: "12px 14px", color: "#fff", fontSize: 15, letterSpacing: 2, marginBottom: 10, boxSizing: "border-box", fontWeight: 700 }} />
            <button style={{ width: "100%", background: typed === "DELETE" ? "#E63329" : "#2a0808", border: "none", color: typed === "DELETE" ? "#fff" : "#663", padding: "14px", borderRadius: 10, fontWeight: 900, fontSize: 14.5, cursor: typed === "DELETE" ? "pointer" : "not-allowed", marginBottom: 8 }}
              disabled={typed !== "DELETE" || busy}
              onClick={doDelete}>
              {busy ? "Deleting..." : "Permanently Delete My Account"}
            </button>
            <button style={{ width: "100%", background: "none", border: "1px solid #333", color: "#888", padding: "11px", borderRadius: 9, fontSize: 13, cursor: "pointer" }}
              onClick={() => { setConfirming(false); setTyped(""); }}>
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function SubscriptionGate({ authUser, onSubscribed, onSignOut, onGuestBook }) {
  const profile = authUser?.profile || {};
  const MEMBERSHIP_LAUNCH = new Date("2026-08-01").getTime();
  const isExistingCustomer = profile.createdAt && profile.createdAt < MEMBERSHIP_LAUNCH;

  const [step, setStep] = useState("intro"); // intro | waiting | checking | done
  const [error, setError] = useState("");
  const [checkCount, setCheckCount] = useState(0);

  const email = profile.email || authUser.email || "";

  function openPaymentLink() {
    setError("");
    const url = `${MEMBERSHIP_LINK}?prefilled_email=${encodeURIComponent(email)}&client_reference_id=${encodeURIComponent(authUser.uid)}`;
    window.open(url, "_blank");
    setStep("waiting");
  }

  async function verifyMembership(silent) {
    if (!silent) setStep("checking");
    setError("");
    try {
      const res = await fetch(`${WORKER_URL}/verify-membership`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, uid: authUser.uid }),
      });
      const data = await res.json();

      if (data.active) {
        await dbPatch(`users/${authUser.uid}`, {
          subscribed: true,
          subscriptionId: data.subscriptionId,
          stripeCustomerId: data.customerId,
          subscribedAt: Date.now(),
          upgradedFromLegacy: isExistingCustomer || false,
          stripeEmail: data.stripeEmail || email,
          matchedBy: data.matchedBy || null,
        });
        await fetch(`${WORKER_URL}/notify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: isExistingCustomer ? "🎉 Existing Customer Upgraded!" : "🎉 New Member Subscribed!",
            message: `${profile.firstName} ${profile.lastName} subscribed for $10/month.\nEmail: ${email}\nPhone: ${profile.phone || "N/A"}${isExistingCustomer ? "\n\n♻️ Was an existing customer." : ""}`,
          }),
        }).catch(() => {});
        setStep("done");
        setTimeout(() => onSubscribed(data.subscriptionId), 1600);
        return true;
      }

      if (!silent) {
        setCheckCount(n => n + 1);
        setError("We don't see a completed payment yet. If you just paid, give it a few seconds and tap again — it can take a moment for Stripe to confirm.");
        setStep("waiting");
      }
      return false;
    } catch (err) {
      if (!silent) { setError(err.message); setStep("waiting"); }
      return false;
    }
  }

  // Auto-check on mount and while waiting
  useEffect(() => {
    verifyMembership(true);
  }, []);

  useEffect(() => {
    if (step !== "waiting") return;
    const iv = setInterval(() => verifyMembership(true), 5000);
    return () => clearInterval(iv);
  }, [step]);

  return (
    <div style={c.root}>
      <header style={c.header}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div><div style={c.logoTop}>SUPER FAST</div><div style={c.logoBot}>ROADSIDE</div></div>
          <span style={c.flame}>🔥</span>
        </div>
        <button style={{ ...c.back, color: "#E63329", borderColor: "#E63329" }} onClick={onSignOut}>Sign Out</button>
      </header>

      <div style={c.page}>
        {step === "done" ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ fontSize: 60, marginBottom: 14 }}>🎉</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#22c55e", marginBottom: 8 }}>You're a member!</div>
            <div style={{ fontSize: 14, color: "#888" }}>Loading your dashboard...</div>
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center", marginTop: 10, marginBottom: 20 }}>
              <div style={{ fontSize: 44, marginBottom: 10 }}>{isExistingCustomer ? "👋" : "🔑"}</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", marginBottom: 6 }}>
                {isExistingCustomer ? "Welcome Back!" : "Activate Your Membership"}
              </div>
              <div style={{ fontSize: 13, color: "#777", lineHeight: 1.6 }}>
                {isExistingCustomer
                  ? `Hey ${profile.firstName || "there"} — we've changed how pricing works. Here's what's new.`
                  : `Hey ${profile.firstName || "there"} — one last step before you can book.`}
              </div>
            </div>

            {isExistingCustomer && (
              <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 12, padding: 16, marginBottom: 18 }}>
                <div style={{ fontSize: 11, letterSpacing: 2, color: "#3b82f6", fontWeight: 800, marginBottom: 9 }}>WHAT CHANGED</div>
                <div style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.65 }}>
                  We moved to flat-rate pricing so you always know what a service call costs — no more variable pricing, and no after-hours surcharge.
                  <br /><br />
                  <strong style={{ color: "#22c55e" }}>Members pay $60</strong> per service call for $10/month.
                  <br />
                  <strong style={{ color: "#f59e0b" }}>Without a membership it's $89</strong> per call.
                  <br /><br />
                  If you use us twice a year, the membership pays for itself.
                </div>
              </div>
            )}

            {/* Price card */}
            <div style={{ background: "linear-gradient(135deg, #1a0a0a 0%, #0f0f0f 100%)", border: "2px solid #E63329", borderRadius: 16, padding: 22, marginBottom: 20 }}>
              <div style={{ fontSize: 10, letterSpacing: 3, color: "#E63329", fontWeight: 800, marginBottom: 10 }}>MEMBERSHIP</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 16 }}>
                <span style={{ fontSize: 42, fontWeight: 900, color: "#fff" }}>$10</span>
                <span style={{ fontSize: 15, color: "#888" }}>/month</span>
              </div>
              {[
                "$60 flat rate on all core services",
                "No after-hours surcharge — same price 2AM",
                "Priority dispatch on every request",
                "Live tracking on every job",
                "Cancel any time — no contract",
              ].map(f => (
                <div key={f} style={{ display: "flex", gap: 9, marginBottom: 9, alignItems: "flex-start" }}>
                  <span style={{ color: "#22c55e", fontSize: 14, lineHeight: 1.4 }}>✓</span>
                  <span style={{ fontSize: 13, color: "#ccc", lineHeight: 1.4 }}>{f}</span>
                </div>
              ))}
            </div>

            {error && <div style={c.gpsError}>⚠️ {error}</div>}

            {step === "waiting" ? (
              <>
                <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 12, padding: 16, marginBottom: 14 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: "#3b82f6", marginBottom: 6 }}>
                    💳 Finish payment in the Stripe tab
                  </div>
                  <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.6 }}>
                    We opened Stripe's secure checkout in a new tab. Complete your payment there, then come back here — we'll pick it up automatically.
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 11.5, color: "#666" }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#3b82f6", animation: "sfPulse 1.4s infinite", display: "inline-block" }} />
                    Checking every few seconds...
                  </div>
                </div>

                <button style={c.btnPrimary} onClick={() => verifyMembership(false)}>
                  ✅ I've Paid — Activate My Membership
                </button>
                <button style={c.btnSecondary} onClick={openPaymentLink}>
                  🔄 Reopen Stripe Checkout
                </button>
                <button style={{ ...c.btnSecondary, marginTop: 4 }} onClick={() => { setStep("intro"); setError(""); }}>
                  ← Back
                </button>
                <style>{`@keyframes sfPulse { 0%,100%{opacity:1;} 50%{opacity:0.3;} }`}</style>
              </>
            ) : step === "checking" ? (
              <div style={{ textAlign: "center", padding: 24, color: "#22c55e" }}>
                <div style={{ fontSize: 34, marginBottom: 8 }}>🔒</div>
                Checking your membership...
              </div>
            ) : (
              <>
                <button style={c.btnPrimary} onClick={openPaymentLink}>
                  🔒 Subscribe — $10.00/month →
                </button>
                <div style={{ fontSize: 11, color: "#666", textAlign: "center", marginTop: 8, lineHeight: 1.5 }}>
                  Opens Stripe's secure checkout. Your payment is linked to this account automatically.
                </div>
              </>
            )}

            <div style={{ fontSize: 11, color: "#555", textAlign: "center", marginTop: 16, lineHeight: 1.6 }}>
              Billed monthly. Cancel any time from your account.<br />
              Service calls are charged separately at $60 flat rate.
            </div>

            {step === "intro" && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "22px 0 14px" }}>
                  <div style={{ flex: 1, height: 1, background: "#222" }} />
                  <span style={{ fontSize: 10, color: "#555", letterSpacing: 1 }}>NOT READY TO JOIN?</span>
                  <div style={{ flex: 1, height: 1, background: "#222" }} />
                </div>
                <div style={{ background: "#161616", border: "1px solid #f59e0b", borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: "#f59e0b", marginBottom: 5 }}>
                    ⚡ Book a one-time call — $89
                  </div>
                  <div style={{ fontSize: 12, color: "#888", lineHeight: 1.55, marginBottom: 12 }}>
                    No membership needed. Pay per call at the non-member rate. You can always join later.
                  </div>
                  <button style={{ width: "100%", background: "none", border: "1px solid #f59e0b", color: "#f59e0b", padding: "12px", borderRadius: 9, fontWeight: 800, fontSize: 13.5, cursor: "pointer" }}
                    onClick={onGuestBook}>
                    Book at $89 Instead →
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC · South Florida</div>
    </div>
  );
}

function CustomerApp({ authUser, onSignOut }) {
  const profile = authUser?.profile || {};
  const [screen, setScreen] = useState("home");
  const [jobHistory, setJobHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showQrTab, setShowQrTab] = useState(false);
  const [qrRegion, setQrRegion] = useState(null);
  const [qrFull, setQrFull] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    name: `${profile.firstName || ""} ${profile.lastName || ""}`.trim(),
    phone: profile.phone || "",
    address: "", tire_size: "", fuel_type: "Regular",
    tire_option: null, tire_info_mode: null,
    car_year: "", car_make: "", car_model: "",
    custom_tire_size: "", note: "", tire_qty: null, tire_disposal: null
  });
  const [locStatus, setLocStatus] = useState("idle");
  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [waiverAgreed, setWaiverAgreed] = useState(false);
  const [plugWaiverAgreed, setPlugWaiverAgreed] = useState(false);
  const [schedMode, setSchedMode] = useState("now");
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("");
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [quoteSent, setQuoteSent] = useState(null);
  const [cqStep, setCqStep] = useState("idle");
  const [cqJobKey, setCqJobKey] = useState(null);
  const cqCardRef = useRef(null);
  const cqStripeRef = useRef(null);
  const etaRef = useRef(Math.floor(Math.random() * 15) + 22);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // { code, discount, type, value }
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [reviewScreen, setReviewScreen] = useState(false);
  const [starRating, setStarRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewJobId, setReviewJobId] = useState(null);
  const [reviewTechName, setReviewTechName] = useState("");

  async function submitReview() {
    if (!starRating) return;
    const review = {
      jobId: reviewJobId,
      techName: reviewTechName,
      stars: starRating,
      comment: reviewText,
      customer: `${profile.firstName} ${profile.lastName}`,
      createdAt: Date.now(),
    };
    await dbPush("reviews", review);
    setReviewSubmitted(true);
    const rating = starRating;
    setTimeout(() => {
      setReviewScreen(false);
      setStarRating(0);
      setReviewText("");
      setReviewSubmitted(false);
      // Auto-open Google Review for 5 star ratings
      if (rating === 5) {
        window.open("https://share.google/hyMwg1BgG6yKvSCRP", "_blank");
      }
    }, 2000);
  }

  const service = SERVICES.find(s => s.id === selected);
  const tireQty = form.tire_qty || 1;
  const disposalFee = form.tire_disposal === "yes" ? tireQty * 10 : 0;

  // After hours fee — $50 after 8:00 PM
  const currentHour = new Date().getHours();
  const isAfterHours = currentHour >= 20 || currentHour < 6;
  const afterHoursFee = 0; // $60 flat rate — no after hours surcharge

  // Pricing: first tire full price, each additional $100 (need_tire)
  // Spare: $100 per tire flat
  const wheelLockFee = form.wheel_lock ? 60 : 0;
  const batteryUpgradeFee = form.battery_upgrade ? 400 : 0;

  function calcTirePrice(option, qty) {
    if (option === "plug") return 60;              // $60 flat rate
    if (option === "spare") return 60 * qty;       // $60 flat per tire
    if (option === "install") return 60 + (25 * (qty - 1)); // $60 first tire + $25 each additional
    if (option === "need_tire") {
      const pricePerTire = form.tire_size_price || 200;
      if (qty <= 1) return pricePerTire;
      return pricePerTire + (qty - 1) * 100;
    }
    return null;
  }

  const basePrice = selected === "tire"
    ? calcTirePrice(form.tire_option, tireQty)
    : (form.battery_upgrade && selected === "jump")
      ? 400
      : service?.price;

  const basePriceWithExtras = basePrice ? basePrice + disposalFee + afterHoursFee + wheelLockFee : null;

  // Apply promo discount
  const promoDiscount = promoApplied && basePriceWithExtras
    ? promoApplied.type === "percent"
      ? parseFloat((basePriceWithExtras * promoApplied.value / 100).toFixed(2))
      : Math.min(promoApplied.value, basePriceWithExtras)
    : 0;

  const priceAfterPromo = basePriceWithExtras ? basePriceWithExtras - promoDiscount : null;
  const tax = priceAfterPromo ? parseFloat((priceAfterPromo * 0.07).toFixed(2)) : 0;
  const finalPrice = priceAfterPromo ? parseFloat((priceAfterPromo + tax).toFixed(2)) : null;

  function handleBook(id) {
    if (authUser?.profile?.banned) {
      alert("⚠️ Your account has been suspended. Contact (561) 255-8506 for assistance.");
      return;
    }
    setSelected(id); setScreen("booking");
  }

  function getLocation() {
    if (!navigator.geolocation) { setLocStatus("error"); return; }
    setLocStatus("loading");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const data = await res.json();
          setForm(f => ({ ...f, address: data.display_name || `${latitude.toFixed(5)}, ${longitude.toFixed(5)}` }));
          setLocStatus("success");
        } catch {
          setForm(f => ({ ...f, address: `${latitude.toFixed(5)}, ${longitude.toFixed(5)}` }));
          setLocStatus("success");
        }
      },
      () => setLocStatus("error"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  const [payError, setPayError] = useState("");
  const [payStep, setPayStep] = useState("idle"); // idle | loading | card | verifying
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [pendingJobKey, setPendingJobKey] = useState(null);
  const stripeRef = useRef(null);
  const cardRef = useRef(null);
  const cardElRef = useRef(null);

  async function handlePay() {
    setPayError("");

    // Tow truck — no payment needed
    if (form.tire_option === "tow" || selected === "tow") {
      setSubmitting(true);
      const newJob = {
        id: genId(),
        service: "Tow Truck",
        customer: form.name, phone: form.phone, address: form.address,
        tire_option: "tow", vehicle: null, tire_size: null, fuel_type: null,
        note: form.note, price: null, paid: false, status: "new",
        tech: null, techName: null, createdAt: Date.now(), customerId: authUser.uid,
      };
      const key = await dbPush("jobs", newJob);
      setJobId(newJob.id);
      setSubmitting(false);
      setScreen("tracking");
      return;
    }

    // Load Stripe and show card form
    setPayStep("loading");
    try {
      const stripe = await loadStripe();
      stripeRef.current = stripe;

      // Create payment intent via worker
      const res = await fetch(`${WORKER_URL}/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: finalPrice,
          jobId: genId(),
          customerName: form.name,
          service: service.name,
          promoId: promoApplied?.promoId || null,
          email: profile.email || authUser.email || null,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      setPaymentIntentId(data.paymentIntentId);
      setPayStep("card");

      // Mount card element after state update
      setTimeout(() => {
        const elements = stripe.elements();
        const card = elements.create("card", {
          style: {
            base: {
              color: "#ffffff",
              fontFamily: "Inter, sans-serif",
              fontSize: "16px",
              "::placeholder": { color: "#666" },
            },
            invalid: { color: "#ff6b6b" },
          },
        });
        card.mount(cardElRef.current);
        cardRef.current = { card, clientSecret: data.clientSecret, elements, customerId: data.customerId };
      }, 100);

    } catch (err) {
      setPayError(err.message);
      setPayStep("idle");
    }
  }

  async function handleConfirmPayment() {
    if (!stripeRef.current || !cardRef.current) return;
    setPayStep("verifying");
    setPayError("");

    try {
      const { error, paymentIntent } = await stripeRef.current.confirmCardPayment(
        cardRef.current.clientSecret,
        { payment_method: { card: cardRef.current.card, billing_details: { name: form.name } } }
      );

      if (error) {
        setPayError(error.message);
        setPayStep("card");
        return;
      }

      if (paymentIntent.status !== "succeeded") {
        setPayError("Payment not completed. Please try again.");
        setPayStep("card");
        return;
      }

      // Save job to Firebase
      const isTowJob = cardRef.current?.isTow;
      const newJob = isTowJob ? {
        id: paymentIntent.id,
        service: "Tow Truck",
        customer: form.name, phone: form.phone, address: form.address,
        tire_option: "tow", vehicle: null, tire_size: null, fuel_type: null,
        note: form.note, price: cardRef.current.towPrice, dispatch_fee: 25,
        paid: true, paymentIntentId: paymentIntent.id, paidAt: Date.now(),
        stripeCustomerId: cardRef.current?.customerId || null,
        savedPaymentMethod: paymentIntent.payment_method || null,
        status: "new", tech: null, techName: null,
        createdAt: Date.now(), customerId: authUser.uid,
      } : {
        id: paymentIntent.id,
        service: service.name,
        customer: form.name, phone: form.phone, address: form.address,
        tire_option: form.tire_option || null,
        tire_qty: form.tire_qty || 1,
        tire_size_range: form.tire_size_range || null,
        tire_size_price: form.tire_size_price || null,
        tire_disposal: form.tire_disposal || "no",
        disposal_fee: disposalFee,
        wheel_lock: form.wheel_lock || false,
        wheel_lock_fee: wheelLockFee,
        scheduled: schedMode === "later" && !!schedDate,
        scheduledDate: schedMode === "later" ? schedDate : null,
        scheduledTime: schedMode === "later" ? (schedTime || "09:00") : null,
        scheduledLabel: schedMode === "later" && schedDate ? formatSchedule(schedDate, schedTime) : null,
        battery_upgrade: form.battery_upgrade || false,
        battery_upgrade_fee: batteryUpgradeFee,
        vehicle: form.tire_info_mode === "ymm" ? `${form.car_year} ${form.car_make} ${form.car_model}`.trim() : null,
        tire_size: form.tire_info_mode === "size" ? (form.tire_size === "Other" ? (form.custom_tire_size || "Other") : form.tire_size) : null,
        fuel_type: service?.id === "fuel" ? form.fuel_type : null,
        note: form.note, price: finalPrice,
        paid: true, paymentIntentId: paymentIntent.id, paidAt: Date.now(),
        stripeCustomerId: cardRef.current?.customerId || null,
        savedPaymentMethod: paymentIntent.payment_method || null,
        status: "new", tech: null, techName: null,
        waiverAgreed: form.tire_option === "need_tire" ? true : false,
        waiverAgreedAt: form.tire_option === "need_tire" ? Date.now() : null,
        afterHours: new Date().getHours() >= 20 || new Date().getHours() < 6,
        createdAt: Date.now(), customerId: authUser.uid,
      };

      // Verify with worker and update Firebase
      const jobKey = await dbPush("jobs", newJob);
      await fetch(`${WORKER_URL}/verify-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentIntentId: paymentIntent.id,
          jobKey,
          service: isTowJob ? "Tow Truck" : service.name,
          customerName: form.name,
          address: form.address,
          amount: isTowJob ? cardRef.current.towPrice?.toFixed(2) : finalPrice?.toFixed(2),
        }),
      });

      setJobId(newJob.id);
      setPendingJobKey(jobKey);
      setPayStep("idle");
      setScreen("tracking");

    } catch (err) {
      setPayError(err.message);
      setPayStep("card");
    }
  }

  async function handlePushTow() {
    setPayError("");
    setPayStep("loading");
    try {
      const towPrice = parseFloat((25 * 1.07).toFixed(2)); // $26.75
      const stripe = await loadStripe();
      stripeRef.current = stripe;

      const res = await fetch(`${WORKER_URL}/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: towPrice,
          jobId: genId(),
          customerName: form.name,
          service: "Tow Truck",
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      setPaymentIntentId(data.paymentIntentId);
      setPayStep("card");

      setTimeout(() => {
        const elements = stripe.elements();
        const card = elements.create("card", {
          style: {
            base: { color: "#ffffff", fontFamily: "Inter, sans-serif", fontSize: "16px", "::placeholder": { color: "#666" } },
            invalid: { color: "#ff6b6b" },
          },
        });
        card.mount(cardElRef.current);
        cardRef.current = { card, clientSecret: data.clientSecret, elements, isTow: true, towPrice, customerId: data.customerId };
      }, 100);
    } catch (err) {
      setPayError(err.message);
      setPayStep("idle");
    }
  }

  function reset() {
    setScreen("home"); setSelected(null); setJobId(null); setLocStatus("idle");
    setForm({ name: `${profile.firstName || ""} ${profile.lastName || ""}`.trim(), phone: profile.phone || "", address: "", tire_size: "", fuel_type: "Regular", tire_option: null, tire_info_mode: null, car_year: "", car_make: "", car_model: "", custom_tire_size: "", note: "", tire_qty: null, tire_disposal: null, tire_size_range: null, tire_size_price: null, wheel_lock: false, battery_upgrade: false });
    setWaiverAgreed(false);
    setPlugWaiverAgreed(false);
    setSchedMode("now"); setSchedDate(""); setSchedTime("");
    setPromoCode("");
    setPromoApplied(null);
    setPromoError("");
    etaRef.current = Math.floor(Math.random() * 15) + 22;
    loadHistory();
  }

  async function startCustomerQuote(job) {
    setCqJobKey(job._key);
    setCqStep("loading");
    try {
      const stripe = await loadStripe();
      cqStripeRef.current = stripe;
      const total = parseFloat(quoteTotal(job).toFixed(2));
      const res = await fetch(`${WORKER_URL}/create-payment-intent`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: total, jobId: job.id,
          customerName: `${profile.firstName || ""} ${profile.lastName || ""}`.trim(),
          service: `Quote — ${job.service}`,
          email: profile.email || null,
        }),
      });
      const d = await res.json();
      if (d.error) throw new Error(d.error);
      if (!d.clientSecret) throw new Error("Stripe did not return a payment session. Check the worker's Stripe key permissions.");
      setCqStep("card");
      const mountId = `cq-card-${job._key}`;
      let tries = 0;
      const tryMount = () => {
        const node = document.getElementById(mountId);
        if (!node) {
          if (++tries > 40) { alert("Payment form failed to load. Please try again."); setCqStep("idle"); setCqJobKey(null); return; }
          return setTimeout(tryMount, 50);
        }
        if (cqCardRef.current?.card) { try { cqCardRef.current.card.unmount(); } catch {} }
        const el = stripe.elements();
        const card = el.create("card", {
          style: { base: { color: "#fff", fontSize: "16px", "::placeholder": { color: "#666" } }, invalid: { color: "#ff6b6b" } },
        });
        card.mount(node);
        cqCardRef.current = { card, clientSecret: d.clientSecret, total, customerId: d.customerId };
      };
      tryMount();
    } catch (e) {
      console.error("[quote-pay]", e);
      alert("Could not start payment:\n\n" + e.message);
      setCqStep("idle"); setCqJobKey(null);
    }
  }

  async function confirmCustomerQuote(job) {
    if (!cqStripeRef.current || !cqCardRef.current?.card) {
      alert("The card form isn't ready yet — give it a second and try again.");
      return;
    }
    const node = document.getElementById(`cq-card-${job._key}`);
    if (!node || !node.querySelector("iframe")) {
      alert("The payment form was interrupted. Tap Pay again to reload it.");
      try { cqCardRef.current.card.unmount(); } catch {}
      cqCardRef.current = null;
      setCqStep("idle"); setCqJobKey(null);
      return;
    }
    setCqStep("verifying");
    try {
      const cref = cqCardRef.current;
      let cres;
      try {
        cres = await cqStripeRef.current.confirmCardPayment(
          cref.clientSecret,
          { payment_method: { card: cref.card, billing_details: { name: `${profile.firstName || ""} ${profile.lastName || ""}`.trim() } } }
        );
      } catch (mountErr) {
        console.error("[quote-pay] element lost:", mountErr);
        cqCardRef.current = null;
        setCqStep("idle");
        alert("The card form lost its connection. Tap Pay again and re-enter your card.");
        return;
      }
      const { error, paymentIntent } = cres;
      if (error) { alert(error.message); setCqStep("card"); return; }
      if (paymentIntent.status !== "succeeded") { alert("Payment not completed."); setCqStep("card"); return; }

      await dbPatch(`jobs/${job._key}`, {
        paid: true, price: job.quotedPrice,
        amountPaid: cqCardRef.current.total,
        paymentIntentId: paymentIntent.id, paidAt: Date.now(),
        stripeCustomerId: cqCardRef.current.customerId || null,
        savedPaymentMethod: paymentIntent.payment_method || null,
      });
      await fetch(`${WORKER_URL}/notify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: `✅ Quote Paid — $${cqCardRef.current.total.toFixed(2)}`,
          message: `${profile.firstName} ${profile.lastName} approved and paid the quote.\n${job.service}\n📍 ${job.address}\nJob ${job.id}\n\nReady to dispatch.`,
        }),
      }).catch(() => {});
      setCqStep("idle"); setCqJobKey(null);
      loadHistory();
      alert("✅ Paid — we're dispatching now.");
    } catch (e) {
      alert(e.message);
      setCqStep("card");
    }
  }

  async function loadHistory() {
    const all = await dbGet("jobs");
    if (!Array.isArray(all)) return;
    const mine = all.filter(j => j.customerId === authUser.uid).sort((a, b) => b.createdAt - a.createdAt);
    setJobHistory(mine);
  }

  useEffect(() => { loadHistory(); }, []);

  const isMechanicBlocked = selected === "mechanic" && (!form.mechanic_service || !form.mechanic_note?.trim());
  const isTireBlocked = service?.fields.includes("tire_option") && (
    !form.tire_option ||
    // need_tire is quote-first — allowed through, handled by submitQuoteRequest
    (form.tire_option === "spare" && !form.tire_info_mode) ||
    (form.tire_size === "Other" && !form.custom_tire_size)
    // plug, new_tire, tow — no extra fields required
  );
  const isQuoteOnly = service?.fields?.includes("quote_only") || form.tire_option === "need_tire" || form.tire_option === "new_tire";

  async function submitQuoteRequest() {
    setSubmittingQuote(true);
    const id = "Q-" + genId();
    const vehicle = [form.car_year, form.car_make, form.car_model].filter(Boolean).join(" ");
    const svcName = form.tire_option === "need_tire" ? "Used Tire (we supply)"
      : form.tire_option === "new_tire" ? "New Tire (we supply)"
      : service.name;

    await dbPush("jobs", {
      id,
      service: svcName,
      customer: `${profile.firstName || ""} ${profile.lastName || ""}`.trim() || form.name,
      phone: form.phone,
      address: form.address,
      vehicle: vehicle || null,
      tire_size: form.tire_size || form.custom_tire_size || null,
      tire_qty: form.tire_qty || null,
      note: form.note || null,
      price: null,
      paid: false,
      quoteNeeded: true,
      customerId: authUser.uid,
      scheduled: schedMode === "later" && !!schedDate,
      scheduledDate: schedMode === "later" ? schedDate : null,
      scheduledTime: schedMode === "later" ? (schedTime || "09:00") : null,
      scheduledLabel: schedMode === "later" && schedDate ? formatSchedule(schedDate, schedTime) : null,
      status: "new",
      tech: null, techName: null,
      createdAt: Date.now(),
    });

    await fetch(`${WORKER_URL}/notify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: `💵 QUOTE REQUEST — ${svcName}`,
        message: `${form.name} · ${form.phone}\n📍 ${form.address}\n${vehicle ? `🚗 ${vehicle}\n` : ""}${form.tire_size ? `🔢 ${form.tire_size}\n` : ""}${form.tire_qty ? `Qty: ${form.tire_qty}\n` : ""}${form.note ? `📝 ${form.note}\n` : ""}\nJob ${id}\n\n➡️ Open the job and send them a quote.`,
      }),
    }).catch(() => {});

    setSubmittingQuote(false);
    setQuoteSent(id);
    loadHistory();
  }

  const schedBlocked = schedMode === "later" && !schedDate;
  const canProceed = form.name && form.phone && form.address && !isTireBlocked && !isMechanicBlocked && !schedBlocked;

  return (
    <div style={c.root}>
      <header style={c.header}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div><div style={c.logoTop}>SUPER FAST</div><div style={c.logoBot}>ROADSIDE</div></div>
          <span style={c.flame}>🔥</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {screen !== "home" && screen !== "tracking" && (
            <button style={c.back} onClick={() => setScreen(screen === "confirm" ? "booking" : "home")}>← Back</button>
          )}
          {screen === "home" && (
            <>
              <button style={{ ...c.back, color: "#f59e0b", borderColor: "#f59e0b" }} onClick={() => { setShowHistory(v => !v); setShowProfile(false); }}>
                📋 History
              </button>
              <button style={{ ...c.back, color: "#22c55e", borderColor: "#22c55e" }} onClick={() => { setShowProfile(v => !v); setShowHistory(false); setShowQrTab(false); }}>
                👤 Profile
              </button>
              <button style={{ ...c.back, color: "#3b82f6", borderColor: "#3b82f6" }} onClick={() => { setShowQrTab(v => !v); setShowProfile(false); setShowHistory(false); }}>
                📱 QR
              </button>
            </>
          )}
          <button style={{ ...c.back, color: "#E63329", borderColor: "#E63329" }} onClick={onSignOut}>Sign Out</button>
        </div>
      </header>

      {/* HOME */}
      {screen === "home" && (
        <div style={c.page}>

          {/* QR / FLYER TAB */}
          {showQrTab && (() => {
            const fl = qrRegion && FLYERS[qrRegion] ? FLYERS[qrRegion] : flyerForAddress(profile?.homeAddress || "");
            return (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>📱 Share Super Fast</div>
                  <button style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 13 }}
                    onClick={() => setShowQrTab(false)}>✕ Close</button>
                </div>

                <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#3b82f6", fontWeight: 800, marginBottom: 5 }}>YOUR AREA</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", marginBottom: 3 }}>{fl.label}</div>
                <div style={{ fontSize: 11.5, color: "#666", marginBottom: 14 }}>{fl.blurb}</div>

                <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", border: "1px solid #222", marginBottom: 14 }}>
                  <img src={fl.src} alt={`Super Fast Roadside — ${fl.label}`}
                    onClick={() => setQrFull(true)}
                    style={{ width: "100%", display: "block", cursor: "pointer" }} />
                  <button onClick={() => setQrFull(true)}
                    style={{ position: "absolute", left: "50%", bottom: 14, transform: "translateX(-50%)",
                      background: "#E63329", border: "none", color: "#fff", padding: "12px 22px",
                      borderRadius: 30, fontSize: 14, fontWeight: 900, cursor: "pointer",
                      boxShadow: "0 6px 22px rgba(0,0,0,0.6)", whiteSpace: "nowrap" }}>
                    📱 Tap to Enlarge QR
                  </button>
                </div>

                <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 11, padding: 14, marginBottom: 11 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#3b82f6", marginBottom: 6 }}>💙 Know somebody who's been stranded?</div>
                  <div style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.7 }}>
                    Show them this and they can book from their own phone — $60 flat with a membership, $89 without. Same price at 2pm or 2am.
                  </div>
                </div>

                <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 11, padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#888", marginBottom: 9 }}>Somewhere else? Pick an area:</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {Object.values(FLYERS).map(x => (
                      <button key={x.id} onClick={() => setQrRegion(x.id)}
                        style={{ background: fl.id === x.id ? "#3b82f6" : "#1a1a1a",
                          border: `1px solid ${fl.id === x.id ? "#3b82f6" : "#333"}`,
                          color: fl.id === x.id ? "#fff" : "#888",
                          padding: "7px 12px", borderRadius: 14, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
                        {x.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* FULL-SCREEN QR (customer app) */}
          {qrFull && (
            <div onClick={() => setQrFull(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.97)", zIndex: 900,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                padding: 20, cursor: "pointer" }}>
              <div style={{ fontSize: 11, letterSpacing: 3, color: "#E63329", fontWeight: 900, marginBottom: 10 }}>
                POINT YOUR CAMERA HERE
              </div>
              <div style={{ background: "#fff", borderRadius: 22, padding: 20, lineHeight: 0,
                boxShadow: "0 14px 60px rgba(230,51,41,0.3)" }}>
                <img src="/qr-signup.png" alt="Scan to open Super Fast Roadside"
                  style={{ width: "min(78vw, 340px)", height: "min(78vw, 340px)", display: "block" }} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 900, color: "#fff", marginTop: 18 }}>superfast-tires.web.app</div>
              <div style={{ fontSize: 12.5, color: "#888", marginTop: 6 }}>$10/month · $60 flat rate</div>
              <div style={{ fontSize: 11.5, color: "#555", marginTop: 14 }}>Tap anywhere to close</div>
              <button style={{ position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,0.12)",
                border: "1px solid #555", color: "#fff", width: 40, height: 40, borderRadius: "50%",
                fontSize: 18, cursor: "pointer" }}
                onClick={() => setQrFull(false)}>✕</button>
            </div>
          )}

          {/* PROFILE PANEL */}
          {showProfile && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>👤 My Profile</div>
                <button style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 13 }} onClick={() => setShowProfile(false)}>✕ Close</button>
              </div>

              <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 12, padding: 18, marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#E63329", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 900, color: "#fff" }}>
                    {profile.firstName?.[0]}{profile.lastName?.[0]}
                  </div>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: "#fff" }}>{profile.firstName} {profile.lastName}</div>
                    <div style={{ fontSize: 12, color: "#888" }}>{jobHistory.length} booking{jobHistory.length !== 1 ? "s" : ""}</div>
                  </div>
                </div>
              </div>

              <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                {[["📧 Email", profile.email], ["📞 Phone", profile.phone], ["🏠 Address", profile.homeAddress], ["📆 Member since", profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : null]]
                  .filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #1e1e1e", fontSize: 12.5 }}>
                    <span style={{ color: "#666" }}>{k}</span>
                    <span style={{ color: "#ccc", textAlign: "right", maxWidth: "62%", wordBreak: "break-word" }}>{v}</span>
                  </div>
                ))}
              </div>

              <div style={{ background: profile.subscribed ? "#0a1f0a" : "#1a1000", border: `1px solid ${profile.subscribed ? "#22c55e" : "#f59e0b"}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: profile.subscribed ? "#22c55e" : "#f59e0b", marginBottom: 4 }}>
                  {profile.subscribed ? "✅ Active Member — $10/month" : "⚠️ No active membership"}
                </div>
                <div style={{ fontSize: 11.5, color: profile.subscribed ? "#4caf50" : "#aaa" }}>
                  {profile.subscribed ? "$60 flat rate on all core services" : "You'll pay the $89 non-member rate per call"}
                </div>
              </div>

              <DeleteAccountBlock
                authUser={authUser}
                profile={profile}
                role="customer"
                onDeleted={() => { window.location.href = "/"; }}
              />
            </div>
          )}

          {/* MEMBERSHIP STATUS */}
          <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: "11px 14px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#22c55e" }}>✓ Active Member — $10/month</div>
              <div style={{ fontSize: 10, color: "#4caf50", marginTop: 2 }}>$60 flat rate unlocked on all core services</div>
            </div>
            <button
              style={{ background: "none", border: "1px solid #333", color: "#666", padding: "5px 10px", borderRadius: 6, fontSize: 10, cursor: "pointer", flexShrink: 0 }}
              onClick={async () => {
                if (!window.confirm("Cancel your $10/month membership? You'll lose the $60 flat rate and priority dispatch.")) return;
                try {
                  await fetch(`${WORKER_URL}/cancel-subscription`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ subscriptionId: profile.subscriptionId }),
                  });
                  await dbPatch(`users/${authUser.uid}`, { subscribed: false, cancelledAt: Date.now() });
                  await fetch(`${WORKER_URL}/notify`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      title: "⚠️ Membership Cancelled",
                      message: `${profile.firstName} ${profile.lastName} cancelled their $10/month membership.\nEmail: ${profile.email}`,
                    }),
                  }).catch(() => {});
                  alert("Your membership has been cancelled.");
                  onSignOut();
                } catch (err) {
                  alert("Could not cancel: " + err.message);
                }
              }}>
              Manage
            </button>
          </div>

          {/* JOB HISTORY PANEL */}
          {showHistory && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>📋 Your Job History</div>
                <button style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 13 }} onClick={() => setShowHistory(false)}>✕ Close</button>
              </div>
              {jobHistory.length === 0 ? (
                <div style={{ textAlign: "center", color: "#444", padding: "20px 0" }}>No past bookings yet.</div>
              ) : (
                jobHistory.map(job => {
                  const meta = STATUS_META[job.status] || { label: job.status, color: "#888", bg: "#1a1a1a" };
                  return (
                    <div key={job._key} style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 14, marginBottom: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{SERVICE_ICONS[job.service] || "🔧"} {job.service}</div>
                          <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{timeAgo(job.createdAt)}</div>
                        </div>
                        <div style={{ ...meta, fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 10, background: meta.bg, color: meta.color }}>{meta.label}</div>
                      </div>
                      {job.scheduled && job.scheduledLabel && (
                        <div style={{ fontSize: 11.5, color: "#f59e0b", fontWeight: 700, marginBottom: 4 }}>
                          📅 {job.scheduledLabel}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>📍 {job.address}</div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                        <span style={{ color: "#22c55e", fontWeight: 700 }}>{job.price ? `$${job.price}` : "Quote"}</span>
                        <span style={{ color: "#666" }}>#{job.id}</span>
                      </div>
                      {/* Quote ready — pay it */}
                      {job.quotedPrice && !job.paid && (
                        <div style={{ background: "#1a1400", border: "1px solid #f59e0b", borderRadius: 9, padding: 13, marginTop: 11 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 800, color: "#f59e0b", marginBottom: 5 }}>💵 Your quote is ready</div>
                          {job.quoteNote && <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.55, marginBottom: 9 }}>{job.quoteNote}</div>}
                          <div style={{ background: "#0f0f0f", borderRadius: 7, padding: 10, marginBottom: 10, fontSize: 12 }}>
                            {job.quoteTaxable === false ? (
                              <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 5 }}>
                                <span>Flat quote — no tax</span><span>${job.quotedPrice.toFixed(2)}</span>
                              </div>
                            ) : (
                              <>
                                <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 3 }}>
                                  <span>Quote</span><span>${job.quotedPrice.toFixed(2)}</span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 5 }}>
                                  <span>Tax (7%)</span><span>${(job.quotedPrice * 0.07).toFixed(2)}</span>
                                </div>
                              </>
                            )}
                            <div style={{ display: "flex", justifyContent: "space-between", color: "#fff", fontWeight: 900, fontSize: 14, borderTop: "1px solid #222", paddingTop: 6 }}>
                              <span>Total</span><span style={{ color: "#22c55e" }}>${quoteTotal(job).toFixed(2)}</span>
                            </div>
                          </div>

                          {cqJobKey === job._key && cqStep === "card" ? (
                            <>
                              <div id={`cq-card-${job._key}`} style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 8, padding: "13px 15px", marginBottom: 10, minHeight: 44 }} />
                              <button style={{ width: "100%", background: "#22c55e", border: "none", color: "#000", padding: "12px", borderRadius: 8, fontWeight: 900, fontSize: 13.5, cursor: "pointer" }}
                                onClick={() => confirmCustomerQuote(job)}>
                                🔒 Pay ${quoteTotal(job).toFixed(2)}
                              </button>
                              <button style={{ width: "100%", background: "none", border: "1px solid #333", color: "#888", padding: "10px", borderRadius: 8, fontSize: 12, cursor: "pointer", marginTop: 7 }}
                                onClick={() => {
                                  if (cqCardRef.current?.card) { try { cqCardRef.current.card.unmount(); } catch {} }
                                  cqCardRef.current = null;
                                  setCqStep("idle"); setCqJobKey(null);
                                }}>Cancel</button>
                            </>
                          ) : cqJobKey === job._key && cqStep === "verifying" ? (
                            <div style={{ textAlign: "center", padding: 14, color: "#22c55e", fontSize: 13 }}>🔒 Processing...</div>
                          ) : cqJobKey === job._key && cqStep === "loading" ? (
                            <div style={{ textAlign: "center", padding: 14, color: "#666", fontSize: 13 }}>💳 Loading...</div>
                          ) : (
                            <button style={{ width: "100%", background: "#f59e0b", border: "none", color: "#000", padding: "12px", borderRadius: 8, fontWeight: 900, fontSize: 13.5, cursor: "pointer" }}
                              onClick={() => startCustomerQuote(job)}>
                              💳 Approve & Pay ${quoteTotal(job).toFixed(2)}
                            </button>
                          )}
                        </div>
                      )}

                      {job.quoteNeeded && !job.quotedPrice && !job.paid && (
                        <div style={{ background: "#161616", border: "1px solid #333", borderRadius: 8, padding: 11, marginTop: 10, fontSize: 12, color: "#888", lineHeight: 1.55 }}>
                          ⏳ We're pricing this out — your quote will show up here shortly.
                        </div>
                      )}

                      {/* Cancel button for new unassigned jobs */}
                      {job.status === "new" && (
                        <button style={{ width: "100%", marginTop: 10, background: "none", border: "1px solid #E63329", color: "#E63329", padding: "8px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 700 }}
                          onClick={async () => {
                            if (!window.confirm("Cancel this job? This cannot be undone.")) return;
                            await dbPatch(`jobs/${job._key}`, { status: "cancelled", cancelledAt: Date.now() });
                            setJobHistory(prev => prev.map(j => j._key === job._key ? { ...j, status: "cancelled" } : j));
                          }}>
                          ✕ Cancel Job
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          <div style={c.hero}>
            <div style={c.eyebrow}>SOUTH FLORIDA · ON DEMAND</div>
            <h1 style={c.heroTitle}>Roadside Help,<br /><span style={c.red}>Right Now.</span></h1>
            <p style={c.heroSub}>No waiting 3 hours. No AAA needed. Pay once, we show up fast.</p>
            <div style={c.badge}>⚡ Average arrival: 30 min or less</div>
            <div style={{ marginTop: 12, background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#22c55e", fontWeight: 700 }}>
              ✅ $60 flat rate — same price 2PM or 2AM. No after-hours surcharge, ever.
            </div>
            <div style={{ marginTop: 12, borderRadius: 12, overflow: "hidden", border: "1px solid #222" }}>
              <img src="/van-side.jpg" alt="Super Fast Mobile Tires van" style={{ width: "100%", display: "block" }} />
            </div>
            {profile.firstName && (
              <div style={c.welcomeRow}>
                👋 Welcome back, {profile.firstName}!
                <span style={c.signOutLink} onClick={onSignOut}>Sign Out</span>
              </div>
            )}
          </div>
          <div style={c.sectionLabel}>SELECT YOUR SERVICE</div>
          <div style={c.grid}>
            {SERVICES.map(s => (
              <button key={s.id} style={c.card} onClick={() => handleBook(s.id)}>
                <div style={c.cardIcon}>{s.icon}</div>
                <div style={c.cardName}>{s.name}</div>
                <div style={c.cardDesc}>{s.desc}</div>
                <div style={c.cardRow}>
                  <span style={c.cardPrice}>{s.id === "tire" ? "From $60" : s.price ? `$${s.price}` : "Quote"}</span>
                  <span style={c.cardEta}>ETA {s.eta}</span>
                </div>
                <div style={c.cardCta}>Book Now →</div>
              </button>
            ))}
          </div>
          <div style={c.trustRow}>
            {[["✅","Licensed LLC"],["💳","Pay in App"],["📍","GPS Tracked"],["⭐","5-Star Rated"]].map(([icon, label]) => (
              <div key={label} style={c.trustItem}><span>{icon}</span> {label}</div>
            ))}
          </div>
        </div>
      )}

      {/* QUOTE REQUEST SENT */}
      {quoteSent && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.96)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#111", border: "2px solid #f59e0b", borderRadius: 16, padding: 24, width: "100%", maxWidth: 380, textAlign: "center" }}>
            <div style={{ fontSize: 52, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#f59e0b", marginBottom: 8 }}>Quote Request Sent</div>
            <div style={{ fontSize: 13, color: "#aaa", lineHeight: 1.7, marginBottom: 14 }}>
              We got it. We'll price this out and send the quote right back to you here — usually within a few minutes.
              <br /><br />
              You'll see it under <strong style={{ color: "#fff" }}>📋 History</strong> with a Pay button once it's ready. Nothing is charged until you approve it.
            </div>
            <div style={{ fontSize: 12.5, color: "#fff", fontWeight: 800, marginBottom: 18 }}>Request {quoteSent}</div>
            <button style={{ ...c.btnPrimary, background: "#f59e0b", color: "#000" }}
              onClick={() => { setQuoteSent(null); reset(); setShowHistory(true); }}>
              View My Requests
            </button>
            <button style={c.btnSecondary} onClick={() => { setQuoteSent(null); reset(); }}>Done</button>
          </div>
        </div>
      )}

      {/* BOOKING */}
      {screen === "booking" && service && (
        <div style={c.page}>
          <div style={c.serviceHeader}>
            <span style={{ fontSize: 36 }}>{service.icon}</span>
            <div>
              <div style={c.serviceName}>{service.name}</div>
              <div style={c.servicePrice}>{service.price ? `$${service.price}` : "Quote on arrival"} · ETA {service.eta}</div>
            </div>
          </div>
          {isAfterHours && (
            <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#22c55e", fontWeight: 700, marginBottom: 16 }}>
              🌙 After hours — no surcharge. Same $60 flat rate.
            </div>
          )}
          <SchedulePicker
            mode={schedMode} setMode={setSchedMode}
            date={schedDate} setDate={setSchedDate}
            time={schedTime} setTime={setSchedTime}
          />

          <div style={c.sectionLabel}>YOUR DETAILS</div>
          {[["name","Full Name","John Smith","text"],["phone","Phone Number","(561) 000-0000","tel"]].map(([key, label, ph, type]) => (
            <div key={key} style={c.fieldGroup}>
              <label style={c.label}>{label}</label>
              <input style={c.input} type={type} placeholder={ph} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} />
            </div>
          ))}
          <div style={c.fieldGroup}>
            <label style={c.label}>Your Location / Address</label>
            <button style={c.gpsBtn} onClick={getLocation} disabled={locStatus === "loading"}>
              {locStatus === "loading" ? "📡 Getting your location..." : "📍 Use My Current Location"}
            </button>
            {locStatus === "error" && <div style={c.gpsError}>⚠️ Couldn't get location. Type address below.</div>}
            {locStatus === "success" && <div style={c.gpsSuccess}>✅ Location found! Edit below if needed.</div>}
            <AddressFields value={form.address} onChange={val => setForm({ ...form, address: val })} />
          </div>

          {/* TIRE OPTION FLOW */}
          {service.fields.includes("tire_option") && (
            <>
              <div style={c.fieldGroup}>
                <label style={c.label}>What Do You Need?</label>
                <div style={c.spareRow}>
                  <button style={{ ...c.spareBtn, ...(form.tire_option === "install" ? c.spareBtnActive : {}) }}
                    onClick={() => setForm({ ...form, tire_option: "install", tire_info_mode: null, tire_qty: null, tire_disposal: null })}>
                    🔧 Customer Has Tire — Installation Only <span style={c.sparePrice}>From $60</span>
                  </button>
                  <button style={{ ...c.spareBtn, ...(form.tire_option === "plug" ? c.spareBtnActive : {}) }}
                    onClick={() => setForm({ ...form, tire_option: "plug", tire_info_mode: null, tire_qty: 1, tire_disposal: null })}>
                    🔩 Tire Plug <span style={c.sparePrice}>$60</span>
                  </button>
                  <button style={{ ...c.spareBtn, ...(form.tire_option === "spare" ? c.spareBtnActive : {}) }}
                    onClick={() => setForm({ ...form, tire_option: "spare", tire_info_mode: null, tire_qty: 1, tire_disposal: null })}>
                    ✅ I Have a Spare Tire <span style={c.sparePrice}>$60</span>
                  </button>
                  <button style={{ ...c.spareBtn, ...(form.tire_option === "need_tire" ? c.spareBtnActiveRed : {}) }}
                    onClick={() => setForm({ ...form, tire_option: "need_tire", tire_info_mode: null, tire_qty: null, tire_disposal: null, tire_type: null })}>
                    🛞 I Need a Tire <span style={{ fontSize: 12, color: "#888" }}>Call for quote</span>
                  </button>
                  <button style={{ ...c.spareBtn, ...(form.tire_option === "new_tire" ? { background: "#0a1525", border: "1px solid #3b82f6", color: "#3b82f6" } : {}) }}
                    onClick={() => setForm({ ...form, tire_option: "new_tire", tire_info_mode: null, tire_qty: null, tire_disposal: null })}>
                    ✨ I Want a New Tire <span style={{ fontSize: 12, color: "#888" }}>Call for quote</span>
                  </button>
                  <button style={{ ...c.spareBtn, ...(form.tire_option === "tow" ? { background: "#0a1525", border: "1px solid #3b82f6", color: "#3b82f6" } : {}) }}
                    onClick={() => setForm({ ...form, tire_option: "tow", tire_info_mode: null })}>
                    🚛 I Need a Tow Truck <span style={{ fontSize: 12, color: "#888" }}>Quote on arrival</span>
                  </button>
                </div>
                {form.tire_option === "install" && <div style={c.spareNote}>🔧 $60 flat for the first tire, $25 each additional. You already have your tires — we mount & balance.</div>}
                {form.tire_option === "plug" && <div style={c.spareNote}>🔩 $60 flat rate, any hour. Small nail or screw in your tire? We come to you and plug it fast.</div>}
                {form.tire_option === "spare" && <div style={c.spareNote}>💰 $60 flat — we'll mount your spare and get you rolling.</div>}
                {form.tire_option === "need_tire" && (
                  <div style={{ background: "#1a0a00", border: "1px solid #f59e0b", borderRadius: 8, padding: 14, marginTop: 8, textAlign: "center" }}>
                    <div style={{ fontSize: 16, fontWeight: 900, color: "#f59e0b", marginBottom: 8 }}>📞 Call for Used Tire Quote</div>
                    <div style={{ fontSize: 13, color: "#aaa", marginBottom: 12, lineHeight: 1.5 }}>
                      Used tire pricing depends on your wheel size and what we have in stock. Call or text and we'll give you a price right away.
                    </div>
                    <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.6, marginBottom: 11, textAlign: "left", background: "#0f0f0f", borderRadius: 8, padding: 11 }}>
                      Fill in your details below and send the request — we'll price it and send the quote right back to you in the app. You only pay once you approve it.
                    </div>
                    <a href="tel:5612558506" style={{ display: "block", background: "none", border: "1px solid #f59e0b", color: "#f59e0b", borderRadius: 8, padding: "11px", fontWeight: 800, fontSize: 13.5, textDecoration: "none" }}>
                      📞 Or call (561) 255-8506
                    </a>
                  </div>
                )}
                {form.tire_option === "new_tire" && (
                  <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 8, padding: 14, marginTop: 8, textAlign: "center" }}>
                    <div style={{ fontSize: 16, fontWeight: 900, color: "#3b82f6", marginBottom: 8 }}>📞 Call for New Tire Quote</div>
                    <div style={{ fontSize: 13, color: "#aaa", marginBottom: 12 }}>New tire pricing depends on brand and size. Call us and we'll get you the best price.</div>
                    <a href="tel:5612558506" style={{ display: "block", background: "#3b82f6", color: "#fff", borderRadius: 8, padding: "12px", fontWeight: 800, fontSize: 15, textDecoration: "none" }}>
                      📞 Call (561) 255-8506
                    </a>
                  </div>
                )}
                {form.tire_option === "tow" && <div style={{ ...c.spareNoteRed, color: "#3b82f6", background: "#0a1525" }}>🚛 $25 dispatch fee charged now. Tow driver quotes full tow price on arrival.</div>}
              </div>

              {(form.tire_option === "spare" || form.tire_option === "install") && (
                <>
                  {/* USED TIRE SIZE PRICING — only for need_tire */}
                  {form.tire_option === "need_tire" && (
                    <div style={c.fieldGroup}>
                      <label style={c.label}>Used Tire Size Range</label>
                      <div style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>Price includes $40 service fee + $60 labor + used tire cost</div>
                      {[
                        { label: "Small (14–15 inch)", sublabel: "e.g. Honda Civic, Toyota Corolla", price: 160 },
                        { label: "Medium (16–17 inch)", sublabel: "e.g. Camry, F-150, Accord", price: 180 },
                        { label: "Large (18–20 inch)", sublabel: "e.g. SUVs, Trucks, Sedans", price: 200 },
                        { label: "XL (22 inch+)", sublabel: "e.g. Escalade, Tahoe, Ram 1500", price: 220 },
                      ].map(t => (
                        <button key={t.label} style={{ ...c.spareBtn, flexDirection: "column", alignItems: "flex-start", marginBottom: 8, ...(form.tire_size_range === t.label ? c.spareBtnActive : {}) }}
                          onClick={() => setForm({ ...form, tire_size_range: t.label, tire_size_price: t.price })}>
                          <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                            <span style={{ fontWeight: 800 }}>{t.label}</span>
                            <span style={{ fontSize: 16, fontWeight: 900 }}>${t.price}/tire</span>
                          </div>
                          <span style={{ fontSize: 11, color: form.tire_size_range === t.label ? "#4caf50" : "#666", marginTop: 2 }}>{t.sublabel}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <div style={c.fieldGroup}>
                    <label style={c.label}>Vehicle Info</label>
                    <button style={{ ...c.spareBtn, ...(form.tire_info_mode === "ymm" ? c.spareBtnActive : {}) }}
                      onClick={() => setForm({ ...form, tire_info_mode: "ymm", tire_size: "", custom_tire_size: "" })}>
                      🚗 Year / Make / Model <span style={{ fontSize: 11, color: "#888" }}>We look up the size</span>
                    </button>
                    <button style={{ ...c.spareBtn, ...(form.tire_info_mode === "size" ? c.spareBtnActive : {}) }}
                      onClick={() => setForm({ ...form, tire_info_mode: "size", car_year: "", car_make: "", car_model: "" })}>
                      🔢 I Know My Tire Size <span style={{ fontSize: 11, color: "#888" }}>Found on sidewall</span>
                    </button>
                    <button style={{ ...c.spareBtn, ...(form.tire_info_mode === "unknown" ? { background: "#1a1000", border: "1px solid #f59e0b", color: "#f59e0b" } : {}) }}
                      onClick={() => setForm({ ...form, tire_info_mode: "unknown", tire_size: "", custom_tire_size: "", car_year: "", car_make: "", car_model: "" })}>
                      ❓ I Don't Know <span style={{ fontSize: 11, color: "#888" }}>Tech will assess on arrival</span>
                    </button>
                  </div>
                  {form.tire_info_mode === "ymm" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <select style={c.input} value={form.car_year || ""} onChange={e => setForm({ ...form, car_year: e.target.value })}>
                        <option value="">Select Year...</option>
                        {CAR_YEARS.map(y => <option key={y}>{y}</option>)}
                      </select>
                      <select style={c.input} value={form.car_make || ""} onChange={e => setForm({ ...form, car_make: e.target.value, car_model: "" })}>
                        <option value="">Select Make...</option>
                        {CAR_MAKES.map(m => <option key={m}>{m}</option>)}
                      </select>
                      <input style={c.input} placeholder="Model (e.g. Camry, F-150, Civic)" value={form.car_model || ""} onChange={e => setForm({ ...form, car_model: e.target.value })} />
                    </div>
                  )}
                  {form.tire_info_mode === "size" && (
                    <div>
                      <select style={c.input} value={form.tire_size || ""} onChange={e => setForm({ ...form, tire_size: e.target.value, custom_tire_size: "" })}>
                        <option value="">Select Tire Size...</option>
                        {TIRE_SIZES.map(s => <option key={s}>{s}</option>)}
                      </select>
                      {form.tire_size === "Other" && (
                        <input style={{ ...c.input, marginTop: 10 }} placeholder="Type your tire size e.g. 235/60R18"
                          value={form.custom_tire_size || ""} onChange={e => setForm({ ...form, custom_tire_size: e.target.value })} autoFocus />
                      )}
                    </div>
                  )}
                  {form.tire_info_mode === "unknown" && (
                    <div style={{ fontSize: 12, color: "#f59e0b", padding: "8px 12px", background: "#1a1000", borderRadius: 6 }}>
                      ⚠️ No problem — our tech will assess your vehicle on arrival.
                    </div>
                  )}
                </>
              )}

              {/* WHEEL LOCK ADD-ON */}
              {(form.tire_option === "spare" || form.tire_option === "plug") && (
                <div style={c.fieldGroup}>
                  <label style={c.label}>Wheel Lock Removal (optional)</label>
                  <button style={{ ...c.spareBtn, ...(form.wheel_lock ? c.spareBtnActiveRed : {}), justifyContent: "space-between" }}
                    onClick={() => setForm({ ...form, wheel_lock: !form.wheel_lock })}>
                    <span>🔐 My wheels have a wheel lock key I lost</span>
                    <span style={{ fontSize: 14, fontWeight: 900 }}>{form.wheel_lock ? "✅ Added — $60" : "+ $60"}</span>
                  </button>
                  {form.wheel_lock && (
                    <div style={{ fontSize: 11, color: "#f59e0b", padding: "8px 12px", background: "#1a1000", borderRadius: 6, marginTop: 6 }}>
                      ⚠️ $60 wheel lock removal fee will be added to your total. Our tech will remove the wheel lock to access your lug nuts.
                    </div>
                  )}
                </div>
              )}

              {/* TIRE QUANTITY */}
              {(form.tire_option === "spare" || form.tire_option === "install") && (
                <div style={c.fieldGroup}>
                  <label style={c.label}>How Many Tires?</label>
                  {form.tire_option === "spare" ? (
                    <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 8, padding: "12px 16px", color: "#22c55e", fontSize: 14, fontWeight: 700 }}>
                      ✅ 1 Spare Tire — $100 flat
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        {[1,2,3,4].map(n => (
                          <button key={n} style={{ ...c.spareBtn, justifyContent: "center", ...(form.tire_qty === n ? c.spareBtnActive : {}) }}
                            onClick={() => setForm({ ...form, tire_qty: n })}>
                            {n} {n === 1 ? "Tire" : "Tires"}
                          </button>
                        ))}
                      </div>
                      {form.tire_qty && form.tire_option === "install" && (
                        <div style={c.spareNote}>
                          🔧 $60 first tire{form.tire_qty > 1 ? ` + ${form.tire_qty - 1} × $25` : ""} = <strong>${60 + ((form.tire_qty - 1) * 25)}</strong>
                        </div>
                      )}
                      {form.tire_qty && form.tire_option === "need_tire" && (
                        <div style={c.spareNote}>
                          🛞 1st tire $200
                          {form.tire_qty > 1 && ` + ${form.tire_qty - 1} additional × $100`}
                          {" = "}<strong>${calcTirePrice("need_tire", form.tire_qty)}</strong>
                          {form.tire_qty > 1 && <span style={{ color: "#f59e0b", marginLeft: 8 }}>💰 You save ${(form.tire_qty - 1) * 100}!</span>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* TIRE DISPOSAL */}
              {(form.tire_option === "spare" || form.tire_option === "install") && form.tire_qty && (
                <div style={c.fieldGroup}>
                  <label style={c.label}>Tire Disposal — $10 per tire</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <button style={{ ...c.spareBtn, ...(form.tire_disposal === "yes" ? c.spareBtnActive : {}) }}
                      onClick={() => setForm({ ...form, tire_disposal: "yes" })}>
                      ✅ Yes — Dispose My Tires
                      <span style={c.sparePrice}>${form.tire_qty * 10}</span>
                    </button>
                    <button style={{ ...c.spareBtn, ...(form.tire_disposal === "no" ? c.spareBtnActiveRed : {}) }}
                      onClick={() => setForm({ ...form, tire_disposal: "no" })}>
                      ❌ No — I'll Dispose Myself
                      <span style={{ fontSize: 12, color: "#888" }}>$0</span>
                    </button>
                  </div>
                  {form.tire_disposal === "no" && (
                    <div style={{ fontSize: 11, color: "#f59e0b", padding: "8px 12px", background: "#1a1000", borderRadius: 6, marginTop: 6 }}>
                      ⚠️ You are responsible for proper tire disposal.
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {service.fields.includes("quote_only") && (
            <div style={{ background: "#1a1000", border: "2px solid #f59e0b", borderRadius: 12, padding: 20, marginBottom: 18, textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>{service.icon}</div>
              <div style={{ fontSize: 19, fontWeight: 900, color: "#f59e0b", marginBottom: 8 }}>
                Call for a Quote
              </div>
              <div style={{ fontSize: 13, color: "#aaa", lineHeight: 1.6, marginBottom: 16 }}>
                {service.id === "battery_replace"
                  ? "Battery pricing depends on your vehicle and the battery it takes. Call or text and we'll give you a price right away."
                  : "Rim repair pricing depends on the damage and your wheel. Send us a photo or call and we'll quote it fast."}
              </div>
              <div style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.65, marginBottom: 14, textAlign: "left", background: "#0f0f0f", borderRadius: 9, padding: 12 }}>
                <strong style={{ color: "#fff" }}>How it works:</strong><br />
                Fill in your details below and send the request. We'll price it and send the quote back to you in the app — usually within minutes. You only pay once you've seen the number and approved it.
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <a href="tel:5612558506" style={{ flex: 1, textAlign: "center", background: "none", border: "1px solid #f59e0b", color: "#f59e0b", borderRadius: 10, padding: "12px", fontWeight: 800, fontSize: 13, textDecoration: "none" }}>
                  📞 Call Instead
                </a>
                <a href="sms:5612558506" style={{ flex: 1, textAlign: "center", background: "none", border: "1px solid #f59e0b", color: "#f59e0b", borderRadius: 10, padding: "12px", fontWeight: 800, fontSize: 13, textDecoration: "none" }}>
                  💬 Text Us
                </a>
              </div>
            </div>
          )}

          {service.fields.includes("mechanic_service") && (
            <div style={c.fieldGroup}>
              <label style={c.label}>What Service Do You Need?</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  { id: "oil_change", label: "🛢️ Oil Change" },
                  { id: "brake", label: "🛑 Brake Inspection" },
                  { id: "battery", label: "🔋 Battery Replacement" },
                  { id: "belt_hose", label: "⚙️ Belt / Hose Check" },
                  { id: "diagnostic", label: "🔍 General Diagnostic" },
                  { id: "other", label: "🔧 Other — Describe Below" },
                ].map(svc => (
                  <button key={svc.id}
                    style={{ ...c.spareBtn, ...(form.mechanic_service === svc.id ? c.spareBtnActive : {}) }}
                    onClick={() => setForm({ ...form, mechanic_service: svc.id })}>
                    {svc.label}
                  </button>
                ))}
              </div>
              <div style={{ marginTop: 12 }}>
                <label style={c.label}>Describe Your Issue (required)</label>
                <textarea style={{ ...c.input, minHeight: 80, resize: "vertical" }}
                  placeholder="Describe what's wrong with your vehicle, any sounds, warning lights, etc."
                  value={form.mechanic_note || ""}
                  onChange={e => setForm({ ...form, mechanic_note: e.target.value })} />
              </div>
              <div style={{ fontSize: 11, color: "#555", marginTop: 8, padding: "8px 12px", background: "#1a1a1a", borderRadius: 6 }}>
                💡 $20 booking fee charged now. Mechanic will diagnose and quote the repair price on site before any work begins.
              </div>
            </div>
          )}

          {service.fields.includes("battery_upgrade") && (
            <div style={c.fieldGroup}>
              <label style={c.label}>Add Battery Replacement? (optional)</label>
              <button style={{ ...c.spareBtn, ...(form.battery_upgrade ? c.spareBtnActiveRed : {}), justifyContent: "space-between" }}
                onClick={() => setForm({ ...form, battery_upgrade: !form.battery_upgrade })}>
                <div>
                  <div style={{ fontWeight: 700 }}>🔋 Upgrade to Battery Replacement</div>
                  <div style={{ fontSize: 11, color: form.battery_upgrade ? "#4caf50" : "#666", marginTop: 2 }}>
                    If your battery is dead beyond jumping — we'll install a brand new one
                  </div>
                </div>
                <span style={{ fontSize: 14, fontWeight: 900, flexShrink: 0, marginLeft: 10 }}>
                  {form.battery_upgrade ? "✅ Added — $400" : "+ $400"}
                </span>
              </button>
              {form.battery_upgrade && (
                <div style={{ fontSize: 11, color: "#3b82f6", padding: "8px 12px", background: "#0a1525", borderRadius: 6, marginTop: 6 }}>
                  ✅ Jump start fee removed. You'll only pay $400 + tax for battery replacement. Tech will install a brand new battery at your location.
                </div>
              )}
            </div>
          )}

          {service.fields.includes("fuel_type") && (
            <div style={c.fieldGroup}>
              <label style={c.label}>Fuel Type</label>
              <select style={c.input} value={form.fuel_type} onChange={e => setForm({ ...form, fuel_type: e.target.value })}>
                {["Regular","Premium","Diesel"].map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
          )}

          <div style={c.fieldGroup}>
            <label style={c.label}>Additional Notes (optional)</label>
            <input style={c.input} placeholder="e.g. I'm on I-95 near exit 68, blue Honda Civic" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          </div>

          {/* New tire goes to call — no booking */}
          {form.tire_option === "new_tire" ? (
            <a href="tel:5612558506" style={{ ...c.btnPrimary, display: "block", textAlign: "center", textDecoration: "none", marginTop: 8 }}>
              📞 Call (561) 255-8506 for New Tire Quote
            </a>
          ) : isQuoteOnly ? (
            <>
              <button style={{ ...c.btnPrimary, background: "#f59e0b", color: "#000", opacity: canProceed ? 1 : 0.5 }}
                disabled={!canProceed || submittingQuote}
                onClick={submitQuoteRequest}>
                {submittingQuote ? "Sending..." : "📋 Send Quote Request →"}
              </button>
              <div style={{ fontSize: 11.5, color: "#666", textAlign: "center", marginTop: 9, lineHeight: 1.5 }}>
                No charge yet. We'll send you a price to approve first.
              </div>
            </>
          ) : (
            <button style={{ ...c.btnPrimary, opacity: canProceed ? 1 : 0.5 }} disabled={!canProceed} onClick={() => setScreen("confirm")}>
              {form.tire_option === "tow" || selected === "tow" ? "Request Tow Truck →" : "Review & Pay →"}
            </button>
          )}
        </div>
      )}

      {/* CONFIRM */}
      {screen === "confirm" && service && (
        <div style={c.page}>
          <div style={c.confirmCard}>
            <div style={c.confirmTitle}>ORDER SUMMARY</div>
            {[
              ["Service", form.tire_option === "tow" ? "Tow Truck" : service.name],
              ["Name", form.name], ["Phone", form.phone], ["Location", form.address],
              ...(form.tire_option === "spare" ? [["Option","Has Spare — Mount & Balance"]] : []),
              ...(form.tire_option === "install" ? [["Option","Customer Has Tire — Installation Only"]] : []),
              ...(form.tire_option === "need_tire" ? [["Option","Need Tire — We Bring It"]] : []),
              ...(form.tire_option === "plug" ? [["Option","Tire Plug Service"]] : []),
              ...(form.tire_option === "tow" ? [["Option","Tow Truck — Quote on Arrival"]] : []),
              ...(schedMode === "later" && schedDate ? [["📅 Scheduled", formatSchedule(schedDate, schedTime)]] : []),
              ...(form.tire_qty ? [["Qty", `${form.tire_qty} Tire${form.tire_qty > 1 ? "s" : ""}`]] : []),
              ...(form.tire_disposal ? [["Disposal", form.tire_disposal === "yes" ? `Yes — $${form.tire_qty * 10}` : "No — Customer responsible"]] : []),
              ...(form.tire_info_mode === "ymm" && form.car_year ? [["Vehicle",`${form.car_year} ${form.car_make} ${form.car_model}`]] : []),
              ...(form.tire_info_mode === "size" && form.tire_size ? [["Tire Size", form.tire_size === "Other" ? (form.custom_tire_size || "Other") : form.tire_size]] : []),
              ...(form.tire_info_mode === "unknown" ? [["Vehicle Info","Tech assesses on arrival"]] : []),
              ...(service.id === "fuel" ? [["Fuel Type", form.fuel_type]] : []),
              ...(form.note ? [["Note", form.note]] : []),
            ].map(([k, v]) => (
              <div key={k} style={c.confirmRow}><span style={{ color: "#666" }}>{k}</span><span style={{ maxWidth: "60%", textAlign: "right" }}>{v}</span></div>
            ))}
            <div style={c.divider} />
            {basePriceWithExtras && (
              <>
                {form.tire_option === "plug" ? (
                  <div style={{ ...c.confirmRow, fontSize: 13 }}>
                    <span style={{ color: "#666" }}>Tire Plug — Flat Rate</span>
                    <span>$60.00</span>
                  </div>
                ) : form.tire_option === "install" ? (
                  <>
                    <div style={{ ...c.confirmRow, fontSize: 13 }}>
                      <span style={{ color: "#666" }}>First Tire — Flat Rate</span>
                      <span>$60.00</span>
                    </div>
                    {tireQty > 1 && (
                      <div style={{ ...c.confirmRow, fontSize: 13 }}>
                        <span style={{ color: "#666" }}>Additional Tires ({tireQty - 1} × $25)</span>
                        <span>${(tireQty - 1) * 25}.00</span>
                      </div>
                    )}
                  </>
                ) : form.tire_option === "need_tire" && tireQty > 1 ? (
                  <>
                    <div style={{ ...c.confirmRow, fontSize: 13 }}>
                      <span style={{ color: "#666" }}>1st Tire ({form.tire_size_range || "Standard"})</span>
                      <span>${form.tire_size_price || 200}.00</span>
                    </div>
                    <div style={{ ...c.confirmRow, fontSize: 13 }}>
                      <span style={{ color: "#666" }}>{tireQty - 1} Additional Tire{tireQty > 2 ? "s" : ""} × $100</span>
                      <span>${(tireQty - 1) * 100}.00</span>
                    </div>
                    <div style={{ ...c.confirmRow, fontSize: 12, color: "#f59e0b" }}>
                      <span>Multi-tire discount</span>
                      <span>Save ${(tireQty - 1) * ((form.tire_size_price || 200) - 100)}</span>
                    </div>
                  </>
                ) : (
                  <div style={{ ...c.confirmRow, fontSize: 13 }}>
                    <span style={{ color: "#666" }}>Subtotal ({tireQty} tire{tireQty > 1 ? "s" : ""}{form.tire_size_range ? ` · ${form.tire_size_range}` : ""})</span>
                    <span>${basePrice}.00</span>
                  </div>
                )}
                {disposalFee > 0 && (
                  <div style={{ ...c.confirmRow, fontSize: 13 }}>
                    <span style={{ color: "#666" }}>Tire Disposal ({tireQty} × $10)</span>
                    <span>${disposalFee}.00</span>
                  </div>
                )}
                {form.battery_upgrade && selected === "jump" ? (
                  <div style={{ ...c.confirmRow, fontSize: 13 }}>
                    <span style={{ color: "#3b82f6" }}>🔋 Battery Replacement (jump start replaced)</span>
                    <span style={{ color: "#3b82f6" }}>$400.00</span>
                  </div>
                ) : null}
                {wheelLockFee > 0 && (
                  <div style={{ ...c.confirmRow, fontSize: 13 }}>
                    <span style={{ color: "#f59e0b" }}>🔐 Wheel Lock Removal</span>
                    <span style={{ color: "#f59e0b" }}>$60.00</span>
                  </div>
                )}
                {afterHoursFee > 0 && (
                  <div style={{ ...c.confirmRow, fontSize: 13 }}>
                    <span style={{ color: "#f59e0b" }}>🌙 After Hours Fee (8PM–6AM)</span>
                    <span style={{ color: "#f59e0b" }}>${afterHoursFee}.00</span>
                  </div>
                )}
                {promoApplied && promoDiscount > 0 && (
                  <div style={{ ...c.confirmRow, fontSize: 13 }}>
                    <span style={{ color: "#22c55e" }}>🎉 Promo ({promoApplied.code})</span>
                    <span style={{ color: "#22c55e" }}>-${promoDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div style={{ ...c.confirmRow, fontSize: 13 }}>
                  <span style={{ color: "#666" }}>Tax (7%)</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                <div style={c.divider} />
              </>
            )}
            <div style={{ ...c.confirmRow, fontWeight: 800, fontSize: 18 }}>
              <span>Total</span>
              <span style={c.red}>{finalPrice ? `$${finalPrice.toFixed(2)}` : "Quote on arrival"}</span>
            </div>
          </div>

          {/* PROMO CODE */}
          {payStep === "idle" && form.tire_option !== "tow" && selected !== "tow" && (
            <div style={{ marginBottom: 16 }}>
              <label style={c.label}>Promo Code (optional)</label>
              {promoApplied ? (
                <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 8, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#22c55e", fontWeight: 700 }}>✅ {promoApplied.code} applied!</div>
                    <div style={{ fontSize: 11, color: "#4caf50" }}>
                      {promoApplied.type === "percent" ? `${promoApplied.value}% off` : `$${promoApplied.value} off`}
                    </div>
                  </div>
                  <button onClick={() => { setPromoApplied(null); setPromoCode(""); }}
                    style={{ background: "none", border: "none", color: "#E63329", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>Remove</button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <input style={{ ...c.input, flex: 1, textTransform: "uppercase" }}
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(""); }} />
                  <button style={{ background: "#E63329", border: "none", color: "#fff", padding: "0 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}
                    disabled={!promoCode.trim() || promoLoading}
                    onClick={async () => {
                      setPromoError("");
                      setPromoLoading(true);
                      try {
                        const res = await fetch(`${WORKER_URL}/validate-promo`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ code: promoCode.trim() }),
                        });
                        const data = await res.json();
                        if (data.valid) {
                          setPromoApplied({ ...data, code: promoCode.trim() });
                        } else {
                          setPromoError(data.error || "Invalid promo code.");
                        }
                      } catch {
                        setPromoError("Could not validate code. Try again.");
                      }
                      setPromoLoading(false);
                    }}>
                    {promoLoading ? "..." : "Apply"}
                  </button>
                </div>
              )}
              {promoError && <div style={{ fontSize: 11, color: "#E63329", marginTop: 6 }}>⚠️ {promoError}</div>}
            </div>
          )}
          {form.tire_option === "plug" && payStep === "idle" && (
            <div style={{ background: "#161616", border: `2px solid ${plugWaiverAgreed ? "#22c55e" : "#f59e0b"}`, borderRadius: 10, padding: 16, marginBottom: 16, transition: "border-color 0.3s" }}>
              <div style={{ fontSize: 11, letterSpacing: 3, color: "#f59e0b", fontWeight: 700, marginBottom: 10 }}>⚠️ TIRE PLUG DISCLAIMER — MUST AGREE TO PROCEED</div>
              <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.7, marginBottom: 14, whiteSpace: "pre-line" }}>
                {`By proceeding, I acknowledge that a tire plug is a TEMPORARY REPAIR, not a permanent fix. A plugged tire can fail at any time, without warning, including at highway speed.

I understand plugs are not appropriate for sidewall damage, large punctures, or sustained high-speed or long-distance driving, and that I should have the tire professionally inspected or replaced as soon as possible.

I hereby RELEASE, WAIVE, AND DISCHARGE Super Fast Mobile Tires LLC, its owners, employees, and independent service providers from any and all claims, damages, losses, or liabilities arising from the plug repair or the continued use of the plugged tire — including but not limited to tire failure, blowout, loss of vehicle control, property damage, injury, or death.

I accept full responsibility for my decision to have this tire plugged rather than replaced, and I understand the risks involved.`}
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer", background: plugWaiverAgreed ? "#0a1f0a" : "#1a1000", border: `1px solid ${plugWaiverAgreed ? "#22c55e" : "#f59e0b"}`, borderRadius: 8, padding: "12px" }}>
                <input type="checkbox" checked={plugWaiverAgreed} onChange={e => setPlugWaiverAgreed(e.target.checked)}
                  style={{ width: 20, height: 20, marginTop: 2, flexShrink: 0, cursor: "pointer", accentColor: "#22c55e" }} />
                <span style={{ fontSize: 13, color: plugWaiverAgreed ? "#22c55e" : "#f59e0b", lineHeight: 1.5, fontWeight: 600 }}>
                  {plugWaiverAgreed
                    ? "✅ Agreed — You may now proceed to payment"
                    : "☐ I have read and agree to the tire plug disclaimer above. I understand a plug is a temporary repair and I am waiving my right to hold Super Fast Mobile Tires LLC or its providers liable."}
                </span>
              </label>
              {!plugWaiverAgreed && (
                <div style={{ fontSize: 11, color: "#f59e0b", textAlign: "center", marginTop: 10, fontWeight: 700 }}>
                  ⬆️ You must check the box above before you can pay
                </div>
              )}
            </div>
          )}

          {form.tire_option === "need_tire" && payStep === "idle" && (
            <div style={{ background: "#161616", border: `2px solid ${waiverAgreed ? "#22c55e" : "#f59e0b"}`, borderRadius: 10, padding: 16, marginBottom: 16, transition: "border-color 0.3s" }}>
              <div style={{ fontSize: 11, letterSpacing: 3, color: "#f59e0b", fontWeight: 700, marginBottom: 10 }}>⚠️ USED TIRE DISCLAIMER — MUST AGREE TO PROCEED</div>
              <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.7, marginBottom: 14 }}>
                By proceeding with this purchase, I acknowledge and agree that I am purchasing a <strong style={{ color: "#fff" }}>used tire</strong>. Used tires may have prior wear, road damage, or reduced tread life. Super Fast Mobile Tires LLC makes no warranties, express or implied, regarding the condition, longevity, or fitness of used tires.
                {"\n\n"}
                I hereby <strong style={{ color: "#fff" }}>release, waive, and discharge</strong> Super Fast Mobile Tires LLC, its owners, employees, and agents from any and all claims, damages, losses, or liabilities arising from the purchase, installation, or use of used tires, including but not limited to tire failure, property damage, or personal injury.
                {"\n\n"}
                I accept full responsibility for my decision to purchase a used tire and understand the inherent risks involved.
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer", background: waiverAgreed ? "#0a1f0a" : "#1a1000", border: `1px solid ${waiverAgreed ? "#22c55e" : "#f59e0b"}`, borderRadius: 8, padding: "12px" }}>
                <input type="checkbox" checked={waiverAgreed} onChange={e => setWaiverAgreed(e.target.checked)}
                  style={{ width: 20, height: 20, marginTop: 2, flexShrink: 0, cursor: "pointer", accentColor: "#22c55e" }} />
                <span style={{ fontSize: 13, color: waiverAgreed ? "#22c55e" : "#f59e0b", lineHeight: 1.5, fontWeight: 600 }}>
                  {waiverAgreed ? "✅ Agreed — You may now proceed to payment" : "☐ I have read and agree to the used tire disclaimer above. I understand I am waiving my right to hold Super Fast Mobile Tires LLC liable for any issues related to used tires."}
                </span>
              </label>
              {!waiverAgreed && (
                <div style={{ fontSize: 11, color: "#f59e0b", textAlign: "center", marginTop: 10, fontWeight: 700 }}>
                  ⬆️ You must check the box above before you can pay
                </div>
              )}
            </div>
          )}

          {/* STRIPE CARD FORM */}
          {payStep === "card" && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, letterSpacing: 4, color: "#666", fontWeight: 700, marginBottom: 10 }}>PAYMENT INFO</div>
              <div ref={cardElRef} style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 8, padding: "14px 16px", marginBottom: 12 }} />
              {payError && <div style={c.gpsError}>⚠️ {payError}</div>}
              <button style={c.btnPrimary} onClick={handleConfirmPayment} disabled={payStep === "verifying"}>
                {payStep === "verifying" ? "Verifying Payment..." : `Pay $${(cardRef.current?.isTow ? 26.75 : finalPrice)?.toFixed(2)} & ${cardRef.current?.isTow ? "Request Tow" : "Dispatch Tech"} →`}
              </button>
              <button style={c.btnSecondary} onClick={() => setPayStep("idle")}>← Cancel</button>
            </div>
          )}

          {payStep === "loading" && (
            <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>💳</div>
              Loading secure payment...
            </div>
          )}

          {payStep === "verifying" && (
            <div style={{ textAlign: "center", padding: "20px", color: "#22c55e" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
              Verifying your payment...
            </div>
          )}

          {payStep === "idle" && (
            <>
              {payError && <div style={c.gpsError}>⚠️ {payError}</div>}
              <div style={c.payNotice}>
                {form.tire_option === "tow" || selected === "tow"
                  ? "🚛 $25 dispatch fee + 7% tax charged now ($26.75). Tow driver quotes and collects full tow price on arrival."
                  : "🔒 Secure payment powered by Stripe. Tech dispatched immediately after payment is confirmed."}
              </div>
              {form.tire_option === "tow" || selected === "tow" ? (
                <button style={c.btnPrimary} onClick={handlePushTow}>
                  Enter Payment Info — $26.75 →
                </button>
              ) : (
                <button style={{ ...c.btnPrimary, opacity: (form.tire_option === "need_tire" && !waiverAgreed) ? 0.5 : 1 }}
                  disabled={form.tire_option === "need_tire" && !waiverAgreed}
                  onClick={handlePay}>
                  {form.tire_option === "need_tire" && !waiverAgreed
                    ? "⚠️ Please Agree to Disclaimer Above"
                    : `Enter Payment Info — $${finalPrice?.toFixed(2)} →`}
                </button>
              )}
              <button style={c.btnSecondary} onClick={() => setScreen("booking")}>Edit Details</button>
            </>
          )}
        </div>
      )}

      {/* TRACKING */}
      {screen === "tracking" && service && (
        <div style={c.page}>

          {/* REVIEW POPUP */}
          {reviewScreen && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.95)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
              <div style={{ background: "#111", border: "2px solid #f59e0b", borderRadius: 16, padding: 24, width: "100%", maxWidth: 380, textAlign: "center" }}>
                {reviewSubmitted ? (
                  <>
                    <div style={{ fontSize: 56, marginBottom: 12 }}>{starRating === 5 ? "🌟" : "🙏"}</div>
                    <div style={{ fontSize: 20, fontWeight: 900, color: "#22c55e" }}>{starRating === 5 ? "Amazing! Thank You!" : "Thank You!"}</div>
                    <div style={{ fontSize: 13, color: "#666", marginTop: 8 }}>Your review has been submitted.</div>
                    {starRating === 5 && (
                      <div style={{ fontSize: 12, color: "#f59e0b", marginTop: 10 }}>Opening Google Reviews...</div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 40, marginBottom: 8 }}>⭐</div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", marginBottom: 4 }}>Rate Your Service</div>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>
                      {reviewTechName ? `How was your experience with ${reviewTechName}?` : "How was your experience?"}
                    </div>
                    <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 20 }}>
                      {[1,2,3,4,5].map(star => (
                        <button key={star} onClick={() => setStarRating(star)}
                          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 36, filter: star <= starRating ? "none" : "grayscale(100%)", transform: star <= starRating ? "scale(1.1)" : "scale(1)", transition: "all 0.15s" }}>
                          ⭐
                        </button>
                      ))}
                    </div>
                    {starRating > 0 && (
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#f59e0b", marginBottom: 12 }}>
                        {["","⭐ Poor","⭐⭐ Fair","⭐⭐⭐ Good","⭐⭐⭐⭐ Great","⭐⭐⭐⭐⭐ Excellent!"][starRating]}
                      </div>
                    )}
                    <textarea style={{ ...c.input, minHeight: 70, resize: "none", marginBottom: 16, textAlign: "left" }}
                      placeholder="Leave a comment (optional)..." value={reviewText} onChange={e => setReviewText(e.target.value)} />
                    <button style={{ ...c.btnPrimary, opacity: !starRating ? 0.5 : 1 }} disabled={!starRating} onClick={submitReview}>Submit Review →</button>
                    <button style={c.btnSecondary} onClick={() => setReviewScreen(false)}>Skip for Now</button>
                  </>
                )}
              </div>
            </div>
          )}

          <LiveTracker jobId={jobId} service={service} form={form} finalPrice={finalPrice} etaMin={etaRef.current}
            onReview={(techName) => { setReviewTechName(techName); setReviewScreen(true); }}
            onReset={reset} />
        </div>
      )}
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC · South Florida</div>
    </div>
  );
}

// ============================================================
// ██████╗ ██████╗  ██████╗ ██╗   ██╗██╗██████╗ ███████╗██████╗
// ██╔══██╗██╔══██╗██╔═══██╗██║   ██║██║██╔══██╗██╔════╝██╔══██╗
// ██████╔╝██████╔╝██║   ██║██║   ██║██║██║  ██║█████╗  ██████╔╝
// ██╔═══╝ ██╔══██╗██║   ██║╚██╗ ██╔╝██║██║  ██║██╔══╝  ██╔══██╗
// ██║     ██║  ██║╚██████╔╝ ╚████╔╝ ██║██████╔╝███████╗██║  ██║
// ============================================================
// ============================================================
// BROWSER PUSH NOTIFICATIONS FOR PROVIDERS
// ============================================================
const VAPID_PUBLIC_KEY = null; // Using basic push without VAPID for simplicity

async function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    return reg;
  } catch (err) {
    console.log('SW registration failed:', err);
    return null;
  }
}

async function requestPushPermission(uid) {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

async function savePushSubscription(uid, reg) {
  try {
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await dbPatch(`users/${uid}`, { pushSubscription: JSON.stringify(sub), pushEnabled: true });
      return sub;
    }
  } catch (err) {
    console.log('Push sub error:', err);
  }
  return null;
}

function showLocalNotification(title, body, jobId) {
  if (Notification.permission !== 'granted') return;
  const n = new Notification(title, {
    body,
    icon: '/logo192.png',
    badge: '/logo192.png',
    tag: jobId || 'new-job',
    requireInteraction: true,
    vibrate: [500, 200, 500],
  });
  n.onclick = () => { window.focus(); n.close(); };
  // Auto vibrate
  if (navigator.vibrate) navigator.vibrate([500, 200, 500, 200, 500]);
}

function ProviderPortal() {
  const [diag, setDiag] = useState(null);
  const [recoverUid, setRecoverUid] = useState(null);
  const [recovering, setRecovering] = useState(false);
  const [rcFirst, setRcFirst] = useState("");
  const [rcLast, setRcLast] = useState("");
  const [rcPhone, setRcPhone] = useState("");
  const [rcCity, setRcCity] = useState("");
  const [rcState, setRcState] = useState("");
  const [authUser, setAuthUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      const saved = localStorage.getItem("sfrs_provider");
      if (saved) { const u = JSON.parse(saved); setAuthToken(u.token, u.refreshToken); setAuthUser(u); }
    } catch {}
  }, []);

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  async function handleForgot() {
    setForgotMsg("");
    if (!forgotEmail.trim()) return setForgotMsg("Enter your email address.");
    setForgotLoading(true);
    const result = await fbResetPassword(forgotEmail.trim());
    setForgotLoading(false);
    if (result.error) return setForgotMsg("⚠️ " + fbFormatError(result.error));
    setForgotMsg("✅ Reset link sent! Check your email.");
  }

  async function handleLogin() {
    setError("");
    if (!email || !password) return setError("Email and password required.");
    setLoading(true);
    const result = await fbSignIn(email, password);
    if (result.error) { setLoading(false); return setError(fbFormatError(result.error)); }

    setAuthToken(result.token, result.refreshToken);
    const res = await loadProfileAfterLogin(result.uid);

    if (res.error) {
      setLoading(false);
      if (res.reason === "missing") {
        // Orphaned account from the signup outage — let them finish it themselves
        setRecoverUid(result.uid);
        return setError("Your login works but your provider details never saved. Finish setup below — takes 30 seconds.");
      }
      setDiag({ uid: result.uid, email: email.trim(), ...res });
      return setError(
        res.reason === "rejected"
          ? `Access refused: ${res.error}`
          : `Couldn't reach the server: ${res.error}`
      );
    }
    const profile = res.profile;
    const role = profile.role;

    if (role !== "provider") {
      setLoading(false);
      return setError(
        role === "partner" ? "That's a business account — sign in at /partner instead."
        : role === "customer" ? "That's a customer account. Sign in on the main app instead."
        : "This account is not a provider account. Contact your admin."
      );
    }
    if (profile?.status === "pending") { setLoading(false); return setError("⏳ Your application is pending admin approval. You will be notified once approved."); }
    if (profile?.status === "denied") { setLoading(false); return setError("❌ Your application was not approved. Contact (561) 255-8506 for more info."); }
    if (profile?.status !== "approved") { setLoading(false); return setError("⏳ Your account is not yet active. Contact admin."); }

    await dbPatch(`users/${result.uid}`, { online: false, lastSeen: Date.now() });
    const user = { ...result, profile };
    localStorage.setItem("sfrs_provider", JSON.stringify(user));

    // Request push notification permission
    const granted = await requestPushPermission(result.uid);
    if (granted) {
      const reg = await registerServiceWorker();
      if (reg) await savePushSubscription(result.uid, reg);
    }

    setLoading(false);
    setAuthUser(user);
  }

  function handleSignOut() {
    // Set offline before signing out
    if (authUser?.uid) {
      dbPatch(`users/${authUser.uid}`, { online: false, lastSeen: Date.now() });
    }
    localStorage.removeItem("sfrs_provider");
    setAuthUser(null);
  }

  // Signup state
  const [screen, setScreen] = useState("login");
  const [spFirst, setSpFirst] = useState("");
  const [spLast, setSpLast] = useState("");
  const [spEmail, setSpEmail] = useState("");
  const [spPhone, setSpPhone] = useState("");
  const [spPass, setSpPass] = useState("");
  const [spServices, setSpServices] = useState([]);
  const [spExperience, setSpExperience] = useState("");
  const [spVehicle, setSpVehicle] = useState("");
  const [spState, setSpState] = useState("");
  const [spCity, setSpCity] = useState("");
  const [spRadius, setSpRadius] = useState("");
  const [payoutSkipped, setPayoutSkipped] = useState(false);
  const [spSelfie, setSpSelfie] = useState(null);
  const [spSelfieUploading, setSpSelfieUploading] = useState(false);
  const [spSelfieConfirmed, setSpSelfieConfirmed] = useState(false);
  const [spLoading, setSpLoading] = useState(false);
  const [spSuccess, setSpSuccess] = useState("");
  const [spError, setSpError] = useState("");

  const SERVICE_OPTIONS = [
    { id: "tire", label: "🛞 Tire Change" },
    { id: "jump", label: "🔋 Jump Start" },
    { id: "fuel", label: "⛽ Fuel Delivery" },
    { id: "lockout", label: "🔑 Lockout Service" },
    { id: "tow", label: "🚛 Tow Truck" },
    { id: "mechanic", label: "🔧 Mobile Mechanic" },
    { id: "wheellock", label: "🔐 Wheel Lock Removal" },
    { id: "battery_replace", label: "🔋 Battery Replacement" },
    { id: "rim", label: "⚙️ Rim Damage Repair" },
  ];

  async function handleSignup() {
    setSpError("");
    if (!spFirst || !spLast || !spEmail || !spPhone || !spPass) return setSpError("All fields are required.");
    if (spServices.length === 0) return setSpError("Select at least one service you offer.");
    if (spPass.length < 6) return setSpError("Password must be at least 6 characters.");
    if (!spSelfie) return setSpError("A live selfie is required for identity verification.");
    if (!spSelfieConfirmed) return setSpError("Please confirm your selfie meets the photo requirements.");
    if (!spState || !spCity.trim()) return setSpError("Enter the state and city you'll be servicing.");
    if (!spRadius) return setSpError("Select your service radius.");
    setSpLoading(true);
    try {
      const result = await createAccount(spEmail.trim(), spPass, {
        firstName: spFirst.trim(), lastName: spLast.trim(),
        email: spEmail.trim().toLowerCase(), phone: spPhone.trim(),
        role: "provider", status: "pending",
        services: spServices, experience: spExperience, vehicle: spVehicle,
        selfieUrl: spSelfie, selfieVerified: false, selfieTakenAt: Date.now(), selfieConfirmed: true,
        state: spState, city: spCity.trim(), serviceRadius: spRadius,
        appliedAt: Date.now(), online: false,
      });

      if (result.error) { setSpLoading(false); return setSpError(result.error); }
      await fbSendVerification(result.token);
      await fetch(`${WORKER_URL}/notify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: "🆕 New Provider Application",
          message: `${spFirst} ${spLast} applied to be a provider.\nEmail: ${spEmail}\nPhone: ${spPhone}\nLocation: ${spCity}, ${spState} (${spRadius} mi radius)\nServices: ${spServices.join(", ")}\nApprove or deny in admin panel.`,
        }),
      }).catch(() => {});
      setSpLoading(false);
              setSpSuccess("✅ Application submitted! A verification email has been sent to your inbox. Please verify your email then wait for admin approval before logging in.\n\n📬 Don't see the email? Check your spam or junk folder.");
    } catch (err) { setSpLoading(false); setSpError(err.message); }
  }

  // Payout setup gate — skippable, but shown until bank is verified
  if (authUser && !payoutSkipped && !authUser.profile?.payoutsEnabled) {
    return <ConnectPayoutSetup
      authUser={authUser}
      profile={authUser.profile || {}}
      onSignOut={handleSignOut}
      onDone={() => setPayoutSkipped(true)}
    />;
  }

  if (!authUser) return (
    <div style={c.root}>
      <header style={{ ...c.header, background: "#0a1525", borderBottom: "3px solid #3b82f6" }}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div><div style={c.logoTop}>SUPER FAST</div><div style={{ ...c.logoBot, color: "#3b82f6" }}>PROVIDER</div></div>
          <span style={c.flame}>🔥</span>
        </div>
      </header>
      <div style={c.page}>

        {/* SIGNUP SCREEN */}
        {screen === "signup" && (
          <>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🛻</div>
              <div style={au.title}>Apply as a Provider</div>
              <div style={au.sub}>Submit your application — we'll review and notify you</div>
              <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #1e3a5a", marginTop: 14 }}>
                <img src="/van-side.jpg" alt="Super Fast Mobile Tires" style={{ width: "100%", display: "block" }} />
              </div>
              <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 10, padding: 14, marginTop: 12, textAlign: "left" }}>
                <div style={{ fontSize: 11, letterSpacing: 2, color: "#3b82f6", fontWeight: 800, marginBottom: 8 }}>WHAT YOU EARN</div>
                {[
                  "$40 per completed call",
                  "$50 per call after 10PM",
                  "100% of every tip — we take nothing",
                  "Paid every Friday (Thu–Wed period)",
                ].map(f => (
                  <div key={f} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                    <span style={{ color: "#22c55e", fontSize: 12 }}>✓</span>
                    <span style={{ fontSize: 12, color: "#bbb" }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
            {spSuccess ? (
              <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: 20, textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
                <div style={{ fontSize: 13, color: "#22c55e", lineHeight: 1.8, whiteSpace: "pre-line" }}>{spSuccess}</div>
                <div style={{ fontSize: 11, color: "#555", marginTop: 12, padding: "8px 12px", background: "#111", borderRadius: 6 }}>
                  📬 Don't see the verification email? Check your <strong style={{ color: "#f59e0b" }}>spam or junk folder</strong>
                </div>
                <button style={{ ...c.btnSecondary, marginTop: 16 }} onClick={() => { setScreen("login"); setSpSuccess(""); }}>← Back to Login</button>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}><label style={c.label}>First Name</label><input style={c.input} placeholder="Carlos" value={spFirst} onChange={e => setSpFirst(e.target.value)} /></div>
                  <div style={{ flex: 1 }}><label style={c.label}>Last Name</label><input style={c.input} placeholder="Martinez" value={spLast} onChange={e => setSpLast(e.target.value)} /></div>
                </div>
                <div style={c.fieldGroup}><label style={c.label}>Email</label><input style={c.input} type="email" placeholder="you@email.com" value={spEmail} onChange={e => setSpEmail(e.target.value)} /></div>
                <div style={c.fieldGroup}><label style={c.label}>Phone Number</label><input style={c.input} type="tel" placeholder="(561) 000-0000" value={spPhone} onChange={e => setSpPhone(e.target.value)} /></div>
                <div style={c.fieldGroup}><label style={c.label}>Password</label><input style={c.input} type="password" placeholder="Min 6 characters" value={spPass} onChange={e => setSpPass(e.target.value)} /></div>
                <div style={c.fieldGroup}>
                  <label style={c.label}>Services You Can Provide</label>
                  {SERVICE_OPTIONS.map(s => (
                    <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", background: spServices.includes(s.id) ? "#0a1f0a" : "#161616", border: `1px solid ${spServices.includes(s.id) ? "#22c55e" : "#2a2a2a"}`, borderRadius: 8, padding: "10px 14px", marginBottom: 8 }}>
                      <input type="checkbox" checked={spServices.includes(s.id)} onChange={e => setSpServices(prev => e.target.checked ? [...prev, s.id] : prev.filter(x => x !== s.id))} style={{ accentColor: "#22c55e" }} />
                      <span style={{ fontSize: 14, color: spServices.includes(s.id) ? "#22c55e" : "#aaa" }}>{s.label}</span>
                    </label>
                  ))}
                </div>
                <div style={c.fieldGroup}>
                  <label style={c.label}>Years of Experience</label>
                  <select style={c.input} value={spExperience} onChange={e => setSpExperience(e.target.value)}>
                    <option value="">Select...</option>
                    <option>Less than 1 year</option><option>1-2 years</option>
                    <option>3-5 years</option><option>5+ years</option><option>10+ years</option>
                  </select>
                </div>
                <div style={c.fieldGroup}><label style={c.label}>Your Vehicle</label><input style={c.input} placeholder="e.g. 2019 Ford F-150" value={spVehicle} onChange={e => setSpVehicle(e.target.value)} /></div>

                {/* SERVICE AREA */}
                <div style={c.fieldGroup}>
                  <label style={c.label}>Where Will You Be Working?</label>
                  <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                    <div style={{ flex: 1 }}>
                      <select style={c.input} value={spState} onChange={e => setSpState(e.target.value)}>
                        <option value="">State...</option>
                        {US_STATES.map(st => <option key={st.code} value={st.code}>{st.code} — {st.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <input style={{ ...c.input, marginBottom: 10 }} placeholder="City you'll service (e.g. West Palm Beach)"
                    value={spCity} onChange={e => setSpCity(e.target.value)} />
                  <label style={c.label}>Service Radius From That City</label>
                  <select style={c.input} value={spRadius} onChange={e => setSpRadius(e.target.value)}>
                    <option value="">Select radius...</option>
                    <option value="50">Up to 50 miles</option>
                    <option value="100">Up to 100 miles</option>
                    <option value="200">Up to 200 miles</option>
                  </select>
                  <div style={{ fontSize: 11, color: "#555", marginTop: 6, lineHeight: 1.5 }}>
                    We'll only send you jobs inside this area. You can ask us to change it any time.
                  </div>
                </div>

                {/* LIVE SELFIE VERIFICATION */}
                <div style={c.fieldGroup}>
                  <label style={c.label}>Identity Verification — Live Selfie</label>
                  <div style={{ fontSize: 11, color: "#666", marginBottom: 10, lineHeight: 1.5 }}>
                    Required. <strong style={{ color: "#f59e0b" }}>Camera only — uploads are not accepted.</strong> This protects our customers and keeps the platform trusted.
                  </div>
                  <div style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 8, padding: 12, marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#f59e0b", letterSpacing: 1, marginBottom: 7 }}>PHOTO REQUIREMENTS</div>
                    {[
                      "Clear, well-lit view of your face",
                      "No sunglasses or tinted glasses",
                      "No hat brim, mask, or hood covering your face",
                      "Face the camera directly — no side angles",
                      "You alone in the photo",
                    ].map(r => (
                      <div key={r} style={{ fontSize: 11.5, color: "#bbb", marginBottom: 4, display: "flex", gap: 7 }}>
                        <span style={{ color: "#f59e0b" }}>•</span><span>{r}</span>
                      </div>
                    ))}
                    <div style={{ fontSize: 10.5, color: "#888", marginTop: 8, lineHeight: 1.5 }}>
                      Photos that don't meet these will be denied and you'll be asked to retake.
                    </div>
                  </div>

                  {spSelfie ? (
                    <div>
                      <img src={spSelfie} alt="selfie" style={{ width: "100%", borderRadius: 10, marginBottom: 8, maxHeight: 280, objectFit: "cover" }} />
                      <div style={{ fontSize: 12, color: "#22c55e", fontWeight: 700, marginBottom: 10 }}>✅ Selfie captured</div>
                      <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", background: spSelfieConfirmed ? "#0a1f0a" : "#161616", border: `1px solid ${spSelfieConfirmed ? "#22c55e" : "#f59e0b"}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
                        <input type="checkbox" checked={spSelfieConfirmed}
                          onChange={e => setSpSelfieConfirmed(e.target.checked)}
                          style={{ width: 18, height: 18, marginTop: 1, flexShrink: 0, accentColor: "#22c55e", cursor: "pointer" }} />
                        <span style={{ fontSize: 12, color: spSelfieConfirmed ? "#22c55e" : "#ccc", lineHeight: 1.5, fontWeight: 600 }}>
                          I confirm this is a clear, visible photo of my face — no sunglasses, and nothing covering my face.
                        </span>
                      </label>
                      <button style={{ ...c.btnSecondary, fontSize: 13 }} onClick={() => { setSpSelfie(null); setSpSelfieConfirmed(false); }}>🔄 Retake Selfie</button>
                    </div>
                  ) : (
                    <button
                      style={{ ...c.gpsBtn, border: "2px dashed #3b82f6", color: "#3b82f6", opacity: spSelfieUploading ? 0.6 : 1 }}
                      disabled={spSelfieUploading}
                      onClick={() => {
                        const input = document.createElement("input");
                        input.type = "file";
                        input.accept = "image/*";
                        input.capture = "user"; // front camera, blocks gallery on mobile
                        input.onchange = async e => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = async ev => {
                            try {
                              setSpSelfieUploading(true);
                              const url = await uploadPhoto(ev.target.result, `superfast/providers/selfies/${Date.now()}`);
                              setSpSelfie(url);
                              setSpSelfieUploading(false);
                            } catch (err) {
                              setSpSelfieUploading(false);
                              setSpError("Selfie upload failed: " + err.message);
                            }
                          };
                          reader.readAsDataURL(file);
                        };
                        input.click();
                      }}>
                      {spSelfieUploading ? "Uploading..." : "📸 Take Live Selfie"}
                    </button>
                  )}
                </div>
                {spError && <div style={c.gpsError}>⚠️ {spError}</div>}
                <button style={{ ...c.btnPrimary, background: "#3b82f6" }} onClick={handleSignup} disabled={spLoading}>{spLoading ? "Submitting..." : "Submit Application →"}</button>
                <button style={c.btnSecondary} onClick={() => setScreen("login")}>← Back to Login</button>
              </>
            )}
          </>
        )}

        {/* LOGIN SCREEN */}
        {screen === "login" && (
          <>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🛻</div>
              <div style={au.title}>Provider Login</div>
              <div style={au.sub}>Sign in to see your assigned jobs</div>
            </div>
            <div style={c.fieldGroup}><label style={c.label}>Email</label><input style={c.input} type="email" placeholder="provider@email.com" value={email} onChange={e => setEmail(e.target.value)} /></div>
            <div style={c.fieldGroup}><label style={c.label}>Password</label><input style={c.input} type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} /></div>
            {error && <div style={c.gpsError}>⚠️ {error}</div>}

            {recoverUid && (
              <div style={{ background: "#0a1525", border: "2px solid #3b82f6", borderRadius: 11, padding: 16, marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 900, color: "#3b82f6", marginBottom: 6 }}>🔧 Finish Your Setup</div>
                <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.6, marginBottom: 13 }}>
                  Your login is fine — the details from your application didn't save. Re-enter them here and you're done.
                </div>

                <div style={{ display: "flex", gap: 9, marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={c.label}>First Name</label>
                    <input style={c.input} value={rcFirst} onChange={e => setRcFirst(e.target.value)} autoCapitalize="words" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={c.label}>Last Name</label>
                    <input style={c.input} value={rcLast} onChange={e => setRcLast(e.target.value)} autoCapitalize="words" />
                  </div>
                </div>

                <label style={c.label}>Phone</label>
                <input style={{ ...c.input, marginBottom: 10 }} type="tel" inputMode="tel" placeholder="(561) 000-0000"
                  value={rcPhone} onChange={e => setRcPhone(e.target.value)} />

                <div style={{ display: "flex", gap: 9, marginBottom: 13 }}>
                  <div style={{ flex: 2 }}>
                    <label style={c.label}>City</label>
                    <input style={c.input} value={rcCity} onChange={e => setRcCity(e.target.value)} autoCapitalize="words" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={c.label}>State</label>
                    <select style={c.input} value={rcState} onChange={e => setRcState(e.target.value)}>
                      <option value="">—</option>
                      {US_STATES.map(st => <option key={st.code} value={st.code}>{st.code}</option>)}
                    </select>
                  </div>
                </div>

                <button style={{ ...c.btnPrimary, background: "#3b82f6", opacity: (rcFirst && rcLast && rcPhone) ? 1 : 0.4 }}
                  disabled={!rcFirst || !rcLast || !rcPhone || recovering}
                  onClick={async () => {
                    setRecovering(true);
                    const signIn = await fbSignIn(email.trim(), password);
                    if (signIn.error) { setRecovering(false); return setError(fbFormatError(signIn.error)); }
                    setAuthToken(signIn.token, signIn.refreshToken);

                    const w = await dbSet(`users/${signIn.uid}`, {
                      firstName: rcFirst.trim(), lastName: rcLast.trim(),
                      email: email.trim().toLowerCase(), phone: rcPhone.trim(),
                      role: "provider", status: "pending",
                      city: rcCity.trim(), state: rcState,
                      services: [], online: false,
                      appliedAt: Date.now(), createdAt: Date.now(), recoveredAt: Date.now(),
                    });
                    setRecovering(false);
                    if (w?.error) return setError("Couldn't save: " + w.error);

                    await fetch(`${WORKER_URL}/notify`, {
                      method: "POST", headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        title: "🔧 Provider Completed Setup",
                        message: `${rcFirst} ${rcLast}\n${email}\n${rcPhone}\n${rcCity}, ${rcState}\n\nTheir original signup didn't save. Approve them in the admin panel.`,
                      }),
                    }).catch(() => {});

                    setRecoverUid(null);
                    setError("");
                    alert("✅ All set. Your application is in — we'll approve you shortly.");
                  }}>
                  {recovering ? "Saving..." : "✅ Finish Setup"}
                </button>
              </div>
            )}

            {diag && (
              <div style={{ background: "#161616", border: "1px solid #f59e0b", borderRadius: 11, padding: 15, marginBottom: 14 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: "#f59e0b", marginBottom: 9 }}>🔍 Diagnosis</div>
                <div style={{ background: "#0d0d0d", borderRadius: 8, padding: 11, fontSize: 11.5, color: "#bbb", lineHeight: 1.8, fontFamily: "monospace", wordBreak: "break-all", marginBottom: 11 }}>
                  <div>Account ID: {diag.uid}</div>
                  <div>Email: {diag.email}</div>
                  <div>Problem: {diag.reason}</div>
                  <div style={{ color: "#ff8080" }}>Detail: {diag.error}</div>
                </div>
                <div style={{ fontSize: 12, color: "#999", lineHeight: 1.6, marginBottom: 11 }}>
                  Screenshot this and send it to dispatch — it says exactly what's wrong.
                </div>
                <a href="tel:5612558506" style={{ display: "block", textAlign: "center", background: "#E63329", color: "#fff", padding: "12px", borderRadius: 8, fontWeight: 800, fontSize: 13.5, textDecoration: "none" }}>
                  📞 Call Dispatch — (561) 255-8506
                </a>
              </div>
            )}
            <button style={{ ...c.btnPrimary, background: "#3b82f6" }} onClick={handleLogin} disabled={loading}>{loading ? "Signing In..." : "Sign In →"}</button>

            {!showForgot ? (
              <div style={{ textAlign: "center", marginTop: 12 }}>
                <span style={{ ...au.link, color: "#3b82f6", fontSize: 13 }} onClick={() => setShowForgot(true)}>Forgot password?</span>
              </div>
            ) : (
              <div style={{ marginTop: 16, background: "#0a1525", border: "1px solid #1e3a5a", borderRadius: 10, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 10 }}>Reset Your Password</div>
                <input style={c.input} type="email" placeholder="Enter your email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} />
                {forgotMsg && <div style={{ fontSize: 12, color: forgotMsg.startsWith("✅") ? "#4caf50" : "#ff6b6b", marginTop: 8 }}>{forgotMsg}</div>}
                <button style={{ ...c.btnPrimary, background: "#3b82f6", marginTop: 10, fontSize: 14 }} onClick={handleForgot} disabled={forgotLoading}>{forgotLoading ? "Sending..." : "Send Reset Link →"}</button>
                <button style={{ ...c.btnSecondary, marginTop: 8, fontSize: 13 }} onClick={() => { setShowForgot(false); setForgotMsg(""); setForgotEmail(""); }}>← Back to Login</button>
              </div>
            )}

            <div style={{ marginTop: 24, background: "#0a1525", border: "1px solid #1e3a5a", borderRadius: 10, padding: 16, textAlign: "center" }}>
              <div style={{ fontSize: 13, color: "#aaa", marginBottom: 10 }}>Don't have an account?</div>
              <button style={{ ...c.btnPrimary, background: "none", color: "#3b82f6", border: "1px solid #3b82f6", fontSize: 14 }}
                onClick={() => { setScreen("signup"); setError(""); }}>
                📋 Sign Up as a Provider
              </button>
            </div>
          </>
        )}
      </div>
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC · South Florida</div>
    </div>
  );

  return <ProviderApp authUser={authUser} onSignOut={handleSignOut} />;
}

// ============================================================
// CLOUDINARY CONFIG
// ============================================================
const CLOUDINARY_CLOUD = "cokfxrh3";
const CLOUDINARY_PRESET = "superfast_photos";

async function uploadPhoto(dataUrl, folder) {
  const formData = new FormData();
  formData.append("file", dataUrl);
  formData.append("upload_preset", CLOUDINARY_PRESET);
  formData.append("folder", folder);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`, {
    method: "POST",
    body: formData,
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message);
  return data.secure_url;
}

// ============================================================
// PROVIDER PAY CALCULATOR
// ============================================================
function getProviderPay(job) {
  // Manual job with custom pay set by admin
  if (job.manual && job.providerPay != null) {
    return { pay: job.providerPay, note: "Set by admin" };
  }

  const service = job.service || "";
  // Customer pays $60 flat any hour. Provider gets $50 for 10PM–6AM calls, $40 otherwise.
  const jobHour = new Date(job.createdAt || Date.now()).getHours();
  const isAfterHours = jobHour >= 22 || jobHour < 6;
  const base = isAfterHours ? 50 : 40;

  // Tow truck — provider collects on arrival
  if (service.includes("Tow")) {
    return { pay: null, note: "You collect payment directly from the customer on arrival." };
  }

  // Tire change with used tire — base pay + $60 tire reimbursement
  if (service.includes("Tire") && job.tire_option === "need_tire") {
    const qty = job.tire_qty || 1;
    const tireReimburse = qty * 60;
    const total = base + tireReimburse;
    return {
      pay: total,
      breakdown: `$${base} labor${isAfterHours ? " (after hours)" : ""} + $60 × ${qty} tire${qty > 1 ? "s" : ""} reimbursement`,
      note: "You provide the used tire(s) from your own stock",
    };
  }

  // Wheel lock removal
  if (service.includes("Wheel Lock")) {
    return { pay: base, note: "Wheel lock removal service" };
  }

  // Battery replacement — provider supplies battery
  if (service.includes("Battery Replacement")) {
    return { 
      pay: base, 
      note: `$${base} labor. You purchase the battery — upload your receipt below for reimbursement. Admin will reimburse battery cost separately.`,
      showReceiptUpload: true,
    };
  }

  // All other services — flat rate
  if (job.addonPaid && job.addonBonus) {
    return {
      pay: base + job.addonBonus,
      note: `$${base} call + $${job.addonBonus} upsell (${job.addonLabel || "add-on"}) — plus 100% of your tips`,
    };
  }
  return {
    pay: base,
    note: isAfterHours
      ? "$50 after-hours rate (10PM–6AM) — plus 100% of your tips"
      : "$40 flat per call — plus 100% of your tips",
  };
}

// ============================================================
// STRIPE CONNECT — PROVIDER PAYOUT SETUP
// ============================================================
function ConnectPayoutSetup({ authUser, profile, onDone, onSignOut }) {
  const [status, setStatus] = useState("checking"); // checking | needed | pending | ready | error
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { checkStatus(); }, []);

  async function checkStatus() {
    setStatus("checking");
    try {
      const fresh = await dbGet(`users/${authUser.uid}`);
      const acctId = fresh?.stripeAccountId;
      if (!acctId) { setStatus("needed"); return; }

      const res = await fetch(`${WORKER_URL}/connect-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accountId: acctId }),
      });
      const data = await res.json();
      setInfo(data);

      if (data.payoutsEnabled) {
        const patch = {
          payoutsEnabled: true,
          bankLast4: data.bankLast4 || null,
          bankName: data.bankName || null,
        };
        await dbPatch(`users/${authUser.uid}`, patch);
        syncProviderProfile(patch);
        setStatus("ready");
        setTimeout(onDone, 1200);
      } else if (data.detailsSubmitted) {
        setStatus("pending");
      } else {
        setStatus("needed");
      }
    } catch (err) {
      setError(err.message);
      setStatus("error");
    }
  }

  async function startOnboarding() {
    setError("");
    setLoading(true);
    try {
      const fresh = await dbGet(`users/${authUser.uid}`);
      const existing = fresh?.stripeAccountId;
      const returnUrl = window.location.origin + "/provider";

      let url;
      if (existing) {
        const res = await fetch(`${WORKER_URL}/connect-refresh-link`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accountId: existing, returnUrl }),
        });
        const d = await res.json();
        if (d.error) throw new Error(d.error);
        url = d.onboardingUrl;
      } else {
        const res = await fetch(`${WORKER_URL}/connect-onboard`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uid: authUser.uid,
            email: profile.email || authUser.email,
            firstName: profile.firstName,
            lastName: profile.lastName,
            phone: profile.phone,
            returnUrl,
          }),
        });
        const d = await res.json();
        if (d.error) throw new Error(d.error);
        await dbPatch(`users/${authUser.uid}`, { stripeAccountId: d.accountId, connectStartedAt: Date.now() });
        url = d.onboardingUrl;
      }
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  return (
    <div style={c.root}>
      <header style={{ ...c.header, background: "#0a1525", borderBottom: "3px solid #3b82f6" }}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div><div style={c.logoTop}>SUPER FAST</div><div style={{ ...c.logoBot, color: "#3b82f6" }}>PROVIDER</div></div>
        </div>
        <button style={{ ...c.back, color: "#E63329", borderColor: "#E63329" }} onClick={onSignOut}>Sign Out</button>
      </header>

      <div style={c.page}>
        {status === "checking" ? (
          <div style={{ textAlign: "center", padding: "70px 0", color: "#666" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔄</div>
            Checking your payout status...
          </div>
        ) : status === "ready" ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ fontSize: 60, marginBottom: 14 }}>✅</div>
            <div style={{ fontSize: 21, fontWeight: 900, color: "#22c55e", marginBottom: 8 }}>You're verified!</div>
            <div style={{ fontSize: 13, color: "#888" }}>
              {info?.bankLast4 ? `Payouts going to ${info.bankName || "your bank"} ••••${info.bankLast4}` : "Bank account connected"}
            </div>
          </div>
        ) : status === "pending" ? (
          <>
            <div style={{ textAlign: "center", marginTop: 20, marginBottom: 22 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: "#f59e0b", marginBottom: 8 }}>Verification In Progress</div>
              <div style={{ fontSize: 13, color: "#888", lineHeight: 1.6 }}>
                Stripe is reviewing your information. This usually takes a few minutes but can take up to 2 business days.
              </div>
            </div>
            {info?.requirementsDue?.length > 0 && (
              <div style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#f59e0b", marginBottom: 8 }}>⚠️ Stripe still needs:</div>
                {info.requirementsDue.slice(0, 6).map(r => (
                  <div key={r} style={{ fontSize: 12, color: "#aaa", marginBottom: 4 }}>
                    • {r.replace(/_/g, " ").replace(/\./g, " → ")}
                  </div>
                ))}
              </div>
            )}
            <button style={{ ...c.btnPrimary, background: "#3b82f6" }} onClick={startOnboarding} disabled={loading}>
              {loading ? "Loading..." : "Finish Verification →"}
            </button>
            <button style={c.btnSecondary} onClick={checkStatus}>🔄 Refresh Status</button>
            <button style={{ ...c.btnSecondary, marginTop: 4 }} onClick={onDone}>Skip for now — view jobs</button>
          </>
        ) : (
          <>
            <div style={{ textAlign: "center", marginTop: 14, marginBottom: 22 }}>
              <div style={{ fontSize: 46, marginBottom: 12 }}>🏦</div>
              <div style={{ fontSize: 21, fontWeight: 900, color: "#fff", marginBottom: 8 }}>Set Up Your Payouts</div>
              <div style={{ fontSize: 13, color: "#888", lineHeight: 1.6 }}>
                Hey {profile.firstName || "there"} — connect your bank account so we can pay you every Friday.
              </div>
            </div>

            <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 12, padding: 18, marginBottom: 18 }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: "#3b82f6", fontWeight: 800, marginBottom: 12 }}>WHAT YOU'LL NEED</div>
              {[
                ["🪪", "Photo ID", "Driver's license or state ID"],
                ["🔢", "SSN", "Last 4 digits for identity verification"],
                ["🏦", "Bank account", "Routing and account number for deposits"],
              ].map(([ic, t, d]) => (
                <div key={t} style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{ic}</span>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: "#fff" }}>{t}</div>
                    <div style={{ fontSize: 11.5, color: "#888", marginTop: 1 }}>{d}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: 14, marginBottom: 18 }}>
              <div style={{ fontSize: 12.5, color: "#22c55e", fontWeight: 700, marginBottom: 4 }}>🔒 Handled entirely by Stripe</div>
              <div style={{ fontSize: 11.5, color: "#4caf50", lineHeight: 1.55 }}>
                You'll enter your details on Stripe's secure site — Super Fast never sees or stores your SSN or bank information.
              </div>
            </div>

            {error && <div style={c.gpsError}>⚠️ {error}</div>}

            <button style={{ ...c.btnPrimary, background: "#3b82f6" }} onClick={startOnboarding} disabled={loading}>
              {loading ? "Loading Stripe..." : "🏦 Connect My Bank Account →"}
            </button>
            <button style={c.btnSecondary} onClick={onDone}>Skip for now — view jobs</button>
            <div style={{ fontSize: 11, color: "#555", textAlign: "center", marginTop: 14, lineHeight: 1.6 }}>
              You can accept jobs before finishing this, but we can't pay you<br />until your bank account is verified.
            </div>
          </>
        )}
      </div>
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC</div>
    </div>
  );
}

// Keep the cached provider profile in localStorage in sync with Firebase
function syncProviderProfile(patch) {
  try {
    const raw = localStorage.getItem("sfrs_provider");
    if (!raw) return;
    const u = JSON.parse(raw);
    u.profile = { ...(u.profile || {}), ...patch };
    localStorage.setItem("sfrs_provider", JSON.stringify(u));
  } catch {}
}

// ============================================================
// PROVIDER — HANDBOOK, TOOLKIT, SERVICES, SUPPORT
// ============================================================
function ProviderMore({ authUser, profile, jobs, onProfileChange }) {
  const [section, setSection] = useState(null);
  const [svcs, setSvcs] = useState(profile.services || []);
  const [savingSvcs, setSavingSvcs] = useState(false);
  const [svcSaved, setSvcSaved] = useState(false);
  const [msgJobId, setMsgJobId] = useState("");
  const [msgText, setMsgText] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  const [msgSent, setMsgSent] = useState(false);
  const [openTool, setOpenTool] = useState(null);
  const [viewSelfie, setViewSelfie] = useState(false);
  const [selfieUploading, setSelfieUploading] = useState(false);
  const [poKey, setPoKey] = useState(profile.pushoverKey || "");
  const [poSaving, setPoSaving] = useState(false);
  const [poMsg, setPoMsg] = useState("");
  const [legalDoc, setLegalDoc] = useState(null);
  const [myThreads, setMyThreads] = useState([]);
  const [threadReply, setThreadReply] = useState({});

  const prevUnreadRef = useRef(0);
  async function loadThreads() {
    const all = await dbGet("providerMessages");
    if (!Array.isArray(all)) return;
    const mine = all.filter(m => m.providerId === authUser.uid).sort((a, b) => b.createdAt - a.createdAt);
    setMyThreads(mine);

    // Buzz + notify when dispatch replies
    const unread = mine.filter(m => m.unreadByProvider).length;
    if (unread > prevUnreadRef.current && prevUnreadRef.current !== 0) {
      const newest = mine.find(m => m.unreadByProvider);
      const lastReply = newest?.replies?.[newest.replies.length - 1];
      if (navigator.vibrate) navigator.vibrate([300, 150, 300]);
      showLocalNotification(
        "💬 Dispatch Replied" + (newest?.jobId ? ` — Job ${newest.jobId}` : ""),
        lastReply?.text || "You have a new message from dispatch.",
        "msg-" + (newest?._key || "")
      );
    }
    prevUnreadRef.current = unread;
  }
  useEffect(() => {
    loadThreads();
    const iv = setInterval(loadThreads, 15000);
    return () => clearInterval(iv);
  }, []);

  const unreadReplies = myThreads.filter(m => m.unreadByProvider).length;
  const [areaState, setAreaState] = useState(profile.state || "");
  const [areaCity, setAreaCity] = useState(profile.city || "");
  const [areaRadius, setAreaRadius] = useState(profile.serviceRadius || "");
  const [areaSaving, setAreaSaving] = useState(false);
  const [areaSaved, setAreaSaved] = useState(false);

  async function saveArea() {
    if (!areaState || !areaCity.trim() || !areaRadius) {
      return alert("Pick your state, city, and service radius.");
    }
    setAreaSaving(true);
    await dbPatch(`users/${authUser.uid}`, {
      state: areaState,
      city: areaCity.trim(),
      serviceRadius: areaRadius,
      areaUpdatedAt: Date.now(),
    });
    await fetch(`${WORKER_URL}/notify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: "📍 Provider Set Service Area",
        message: `${profile.firstName} ${profile.lastName}\n${areaCity.trim()}, ${areaState}\nRadius: ${areaRadius} miles`,
      }),
    }).catch(() => {});
    syncProviderProfile({ state: areaState, city: areaCity.trim(), serviceRadius: areaRadius });
    setAreaSaving(false);
    setAreaSaved(true);
    if (onProfileChange) onProfileChange({ state: areaState, city: areaCity.trim(), serviceRadius: areaRadius });
    setTimeout(() => setAreaSaved(false), 2500);
  }

  const ALL_SERVICES = [
    { id: "tire", label: "🛞 Tire Change", need: "Floor jack, jack stands, 4-way iron, breaker bar, torque socket set" },
    { id: "jump", label: "🔋 Jump Start", need: "Jump box or jumper cables" },
    { id: "fuel", label: "⛽ Fuel Delivery", need: "Approved gas can and funnel" },
    { id: "lockout", label: "🔑 Lockout Service", need: "Lockout kit (wedge, long reach tool)" },
    { id: "wheellock", label: "🔐 Wheel Lock Removal", need: "Wheel lock removal socket set" },
    { id: "mechanic", label: "🔧 Mobile Mechanic", need: "Full tool kit, diagnostic scanner" },
    { id: "tow", label: "🚛 Tow Truck", need: "Tow rig, proper licensing and insurance" },
    { id: "battery_replace", label: "🔋 Battery Replacement", need: "Terminal tools, ability to source batteries" },
    { id: "rim", label: "⚙️ Rim Damage Repair", need: "Rim straightening / welding equipment or a shop partner" },
  ];

  const TOOLKIT = [
    { cat: "LIFTING", items: [
      ["3-Ton Floor Jack", "Low-profile hydraulic. Don't cheap out — this is what holds the car off your hands."],
      ["3-Ton Jack Stands", "Never work under a car on a jack alone. Ever. Stands go under every time."],
      ["Breaker Bar", "For lug nuts an impact won't budge. 24-inch minimum."],
    ]},
    { cat: "TIRES", items: [
      ["4-Way Tire Iron", "Covers most common lug sizes in one tool."],
      ["Tire Socket Wrench Set", "Deep sockets, thin-wall so you don't scratch rims."],
      ["Tire Plug Kit", "Reamer, plugs, insertion tool, rubber cement."],
      ["Air Compressor", "12V portable that runs off the vehicle. Non-negotiable for tire work."],
    ]},
    { cat: "POWER", items: [
      ["Battery Jump Box", "Lithium pack, 1000A+. Keep it charged before every shift."],
      ["Jumper Cables", "Heavy gauge backup for when the box is dead or the battery's too far gone."],
    ]},
    { cat: "LOCKOUT & FUEL", items: [
      ["Lockout Kit", "Air wedge, long reach tool, non-marring wedges."],
      ["Gas Can & Funnel", "DOT-approved container. Never transport fuel in anything else."],
    ]},
    { cat: "FORCE", items: [
      ["Metal Sledge", "Small. For seized rotors and stuck wheels."],
      ["Rubber Mallet", "Break a wheel loose without damaging it."],
    ]},
    { cat: "EVERYDAY CARRY", items: [
      ["Pocket Knife", "You'll use it constantly."],
      ["Basic Tool Kit", "Sockets, wrenches, screwdrivers, pliers."],
      ["Soap & Water Spray Bottle", "Find slow leaks in seconds. Bubbles show you the hole."],
    ]},
    { cat: "SAFETY — REQUIRED", items: [
      ["Reflective Safety Vest", "Required on every roadside job. No exceptions."],
      ["Road Flares or LED Triangles", "Set out behind you on any highway or shoulder job."],
      ["Work Gloves", "Protects you and looks professional."],
      ["Flashlight / Headlamp", "Most of your money is made after dark."],
    ]},
  ];

  const HANDBOOK = [
    { t: "1. Before You Accept", body: [
      "Only accept jobs you're equipped to finish. Accepting and cancelling hurts the customer and your rating.",
      "You have 60 seconds to accept. If you're busy, let it pass — it goes to the next provider.",
      "Check the tire size on tire jobs before accepting. If you don't have it, decline and let dispatch know.",
    ]},
    { t: "2. On The Way", body: [
      "Mark En Route as soon as you're moving. The customer is watching the tracker.",
      "Call the customer if you'll be more than 10 minutes past the ETA. A phone call prevents most complaints.",
      "Drive safe. No job is worth a ticket or a wreck.",
    ]},
    { t: "3. On Scene", body: [
      "Wear your safety vest. Set out flares or triangles on any road or shoulder job.",
      "Introduce yourself by name. You are Super Fast Mobile Tires to that customer.",
      "Take all check-in photos before you touch anything. This protects you if damage is claimed later.",
      "Never work under a vehicle supported only by a jack. Stands, every time.",
    ]},
    { t: "4. Doing The Job", body: [
      "Do the job the customer paid for. Don't upsell repairs on scene — call dispatch if more is needed.",
      "If you can't complete it, use Can't Complete in the app. Don't just leave.",
      "Torque lug nuts properly. A wheel coming off is the worst thing that can happen on this platform.",
    ]},
    { t: "5. Finishing Up", body: [
      "Take all checkout photos and get the customer's signature before marking complete.",
      "Clean up. Take your trash and the old tire if disposal was paid for.",
      "Tell them they'll get a chance to rate and tip in the app. Don't ask for a tip directly.",
    ]},
    { t: "6. Getting Paid", body: [
      "$40 per completed call. $50 for calls between 10PM and 6AM.",
      "Used tire jobs: $40 labor plus $60 per tire reimbursed.",
      "You keep 100% of every tip. We take nothing.",
      "Pay period runs Thursday through Wednesday. Paid every Friday by direct deposit.",
      "Your bank must be verified through Stripe or we can't pay you.",
    ]},
    { t: "7. Rules That Get You Removed", body: [
      "Asking a customer to pay you directly or off the app. Instant removal.",
      "Showing up under the influence of anything.",
      "Marking a job complete that you didn't do.",
      "Being rude, aggressive, or unprofessional with a customer.",
      "Working without insurance or a valid license.",
      "Letting anyone else use your account.",
    ]},
    { t: "8. Your Rating", body: [
      "Customers rate you 1 to 5 stars after every job.",
      "Dispatch prioritizes higher-rated providers. Your rating directly affects how much work you get.",
      "Falling below 4.0 gets a conversation. Staying there gets you deactivated.",
    ]},
  ];

  async function saveServices() {
    if (svcs.length === 0) return alert("Select at least one service.");
    setSavingSvcs(true);
    await dbPatch(`users/${authUser.uid}`, { services: svcs, servicesUpdatedAt: Date.now() });
    await fetch(`${WORKER_URL}/notify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: "🔧 Provider Updated Services",
        message: `${profile.firstName} ${profile.lastName} changed their services to:\n${svcs.join(", ")}`,
      }),
    }).catch(() => {});
    syncProviderProfile({ services: svcs });
    setSavingSvcs(false);
    setSvcSaved(true);
    if (onProfileChange) onProfileChange({ services: svcs });
    setTimeout(() => setSvcSaved(false), 2500);
  }

  async function sendMessage() {
    if (!msgText.trim()) return;
    setMsgSending(true);
    await dbPush("providerMessages", {
      providerId: authUser.uid,
      providerName: `${profile.firstName} ${profile.lastName}`,
      providerPhone: profile.phone || "",
      jobId: msgJobId.trim() || null,
      message: msgText.trim(),
      createdAt: Date.now(),
      resolved: false,
    });
    await fetch(`${WORKER_URL}/notify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: "💬 Provider Message" + (msgJobId ? ` — Job ${msgJobId}` : ""),
        message: `From: ${profile.firstName} ${profile.lastName}\n📞 ${profile.phone || "No phone"}\n${msgJobId ? `Job: ${msgJobId}\n` : ""}\n"${msgText.trim()}"`,
      }),
    }).catch(() => {});
    setMsgSending(false);
    setMsgSent(true);
    setMsgText(""); setMsgJobId("");
    loadThreads();
    setTimeout(() => setMsgSent(false), 4000);
  }

  const Back = () => (
    <button style={{ background: "none", border: "1px solid #333", color: "#aaa", padding: "8px 14px", borderRadius: 6, cursor: "pointer", marginBottom: 16, fontSize: 13 }}
      onClick={() => setSection(null)}>← Back</button>
  );

  const SelfieViewer = () => viewSelfie && profile.selfieUrl ? (
    <div onClick={() => setViewSelfie(false)}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.96)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, cursor: "pointer" }}>
      <img src={profile.selfieUrl} alt="full size" style={{ maxWidth: "100%", maxHeight: "85vh", borderRadius: 10, objectFit: "contain" }} />
      <button style={{ position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,0.12)", border: "1px solid #555", color: "#fff", width: 40, height: 40, borderRadius: "50%", fontSize: 18, cursor: "pointer" }}
        onClick={() => setViewSelfie(false)}>✕</button>
    </div>
  ) : null;

  // ── MENU ──
  if (!section) {
    return (
      <div>
        {!profile.payoutsEnabled && (
          <div style={{ background: "#1a0a00", border: "2px solid #E63329", borderRadius: 10, padding: 15, marginBottom: 14 }}>
            <div style={{ fontSize: 13.5, fontWeight: 900, color: "#E63329", marginBottom: 6 }}>🏦 Bank Not Connected</div>
            <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.55, marginBottom: 11 }}>
              You won't be able to go online or receive payouts until your bank is verified with Stripe. Go to the Active tab to finish setup.
            </div>
            <div style={{ fontSize: 11, color: "#666", lineHeight: 1.5 }}>
              Need help? Use Message Dispatch below.
            </div>
          </div>
        )}
        {[
          ["profile", "👤", "My Profile", "Your info, services, and account settings", "#22c55e"],
          ["alerts", "🔔", "Job Alerts", profile.pushoverKey ? "✅ Backup alerts on — you'll get jobs when the app is closed" : "⚠️ Set this up so you don't miss jobs", profile.pushoverKey ? "#22c55e" : "#E63329"],
          ["handbook", "📕", "Provider Handbook", "Rules, expectations, and how you get paid", "#E63329"],
          ["toolkit", "🧰", "Toolkit Checklist", "Every tool you need, by service type", "#f59e0b"],
          ["services", "🔧", "My Services", "Add or remove the services you offer", "#22c55e"],
          ["area", "📍", "My Service Area", profile.city && profile.state ? `${profile.city}, ${profile.state} · ${profile.serviceRadius || "?"} mi` : "⚠️ Not set — tap to set it", "#a855f7"],
          ["legal", "📄", "Terms & Privacy", "Provider agreement and privacy policy", "#666"],
          ["support", "💬", "Message Dispatch", unreadReplies > 0 ? `🔴 ${unreadReplies} new repl${unreadReplies === 1 ? "y" : "ies"} from dispatch` : "Question about a job? Send it with the job ID", "#3b82f6"],
        ].map(([id, icon, title, sub, color]) => (
          <button key={id} onClick={() => setSection(id)}
            style={{ width: "100%", background: "#161616", border: "1px solid #222", borderLeft: `4px solid ${color}`, borderRadius: 10, padding: 16, marginBottom: 10, cursor: "pointer", textAlign: "left", display: "flex", gap: 14, alignItems: "center" }}>
            <span style={{ fontSize: 26, flexShrink: 0 }}>{icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", marginBottom: 2 }}>{title}</div>
              <div style={{ fontSize: 12, color: "#777", lineHeight: 1.4 }}>{sub}</div>
            </div>
            <span style={{ color: "#444", fontSize: 18 }}>›</span>
          </button>
        ))}

        <div style={{ background: "#0a1525", border: "1px solid #1e3a5a", borderRadius: 10, padding: 14, marginTop: 8 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, color: "#3b82f6", fontWeight: 800, marginBottom: 8 }}>YOUR ACCOUNT</div>
          {[
            ["Name", `${profile.firstName || ""} ${profile.lastName || ""}`],
            ["Phone", profile.phone || "—"],
            ["Service area", profile.city && profile.state ? `${profile.city}, ${profile.state} · ${profile.serviceRadius || "?"} mi` : "Not set"],
            ["Bank", profile.payoutsEnabled ? `✅ Verified ••••${profile.bankLast4 || "____"}` : "⚠️ Not connected"],
            ["Jobs completed", String(jobs.filter(j => j.status === "completed").length)],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #12233a", fontSize: 12.5 }}>
              <span style={{ color: "#666" }}>{k}</span>
              <span style={{ color: "#ccc", textAlign: "right", maxWidth: "60%" }}>{v}</span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: "#555", marginTop: 10, lineHeight: 1.5 }}>
            Need to change your service area or personal info? Message dispatch above.
          </div>
        </div>
      </div>
    );
  }

  // ── HANDBOOK ──
  if (section === "handbook") {
    return (
      <div>
        <Back />
        <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 4 }}>📕 Provider Handbook</div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 20, lineHeight: 1.5 }}>
          What we expect on every job. Read it once, follow it every time.
        </div>
        {HANDBOOK.map(sec => (
          <div key={sec.t} style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 16, marginBottom: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: sec.t.startsWith("7.") ? "#E63329" : "#fff", marginBottom: 10 }}>{sec.t}</div>
            {sec.body.map(line => (
              <div key={line} style={{ display: "flex", gap: 9, marginBottom: 7 }}>
                <span style={{ color: sec.t.startsWith("7.") ? "#E63329" : "#22c55e", fontSize: 12, flexShrink: 0 }}>
                  {sec.t.startsWith("7.") ? "✕" : "•"}
                </span>
                <span style={{ fontSize: 12.5, color: "#aaa", lineHeight: 1.55 }}>{line}</span>
              </div>
            ))}
          </div>
        ))}
        <div style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 10, padding: 14, fontSize: 12, color: "#f59e0b", lineHeight: 1.6 }}>
          ⚠️ You are an independent contractor, not an employee. You're responsible for your own insurance, licensing, taxes, and equipment.
        </div>
      </div>
    );
  }

  // ── TOOLKIT ──
  if (section === "toolkit") {
    return (
      <div>
        <Back />
        <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 4 }}>🧰 Toolkit Checklist</div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 16, lineHeight: 1.55 }}>
          You don't need a tow rig to start. Most jobs are jumps, tire changes, lockouts and fuel — all doable with a truck and basic gear. Start with what covers the services you offer, add as you grow.
        </div>

        <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: 14, marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#22c55e", marginBottom: 6 }}>💡 Minimum to start earning</div>
          <div style={{ fontSize: 12, color: "#4caf50", lineHeight: 1.6 }}>
            A jump box, a floor jack, jack stands, a 4-way iron, and a safety vest. That alone covers jump starts and spare tire changes — the two most common jobs on the platform.
          </div>
        </div>

        {TOOLKIT.map(group => (
          <div key={group.cat} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, letterSpacing: 2.5, color: group.cat.includes("SAFETY") ? "#E63329" : "#f59e0b", fontWeight: 800, marginBottom: 9 }}>
              {group.cat}
            </div>
            {group.items.map(([name, why]) => (
              <div key={name}
                onClick={() => setOpenTool(openTool === name ? null : name)}
                style={{ background: "#161616", border: "1px solid #222", borderRadius: 9, padding: "12px 14px", marginBottom: 7, cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: "#eee" }}>{name}</span>
                  <span style={{ color: "#555", fontSize: 12 }}>{openTool === name ? "▲" : "▼"}</span>
                </div>
                {openTool === name && (
                  <div style={{ fontSize: 12, color: "#888", marginTop: 8, lineHeight: 1.55 }}>{why}</div>
                )}
              </div>
            ))}
          </div>
        ))}

        <div style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 10, padding: 14, fontSize: 12, color: "#f59e0b", lineHeight: 1.6 }}>
          ⚠️ Safety gear isn't optional. Vest, flares or triangles, and gloves on every roadside job — no exceptions.
        </div>
      </div>
    );
  }

  // ── SERVICES ──
  if (section === "services") {
    return (
      <div>
        <Back />
        <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 4 }}>🔧 My Services</div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 18, lineHeight: 1.55 }}>
          Turn services on as you get the gear for them. You'll only get dispatched jobs you've turned on.
        </div>

        {ALL_SERVICES.map(sv => {
          const on = svcs.includes(sv.id);
          return (
            <div key={sv.id}
              onClick={() => setSvcs(prev => on ? prev.filter(x => x !== sv.id) : [...prev, sv.id])}
              style={{ background: on ? "#0a1f0a" : "#161616", border: `1px solid ${on ? "#22c55e" : "#2a2a2a"}`, borderRadius: 10, padding: 14, marginBottom: 9, cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: on ? 7 : 0 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: on ? "#22c55e" : "#888" }}>{sv.label}</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: on ? "#22c55e" : "#555" }}>{on ? "✅ ON" : "OFF"}</span>
              </div>
              {on && <div style={{ fontSize: 11, color: "#4caf50", lineHeight: 1.5 }}>🧰 Needs: {sv.need}</div>}
            </div>
          );
        })}

        {svcSaved && <div style={c.gpsSuccess}>✅ Services updated. Dispatch will send you these jobs.</div>}
        <button style={{ ...c.btnPrimary, background: "#22c55e", color: "#000" }} onClick={saveServices} disabled={savingSvcs}>
          {savingSvcs ? "Saving..." : "Save My Services"}
        </button>
        <div style={{ fontSize: 11, color: "#555", textAlign: "center", marginTop: 10, lineHeight: 1.5 }}>
          Only turn on services you have the tools and skill to complete. Accepting a job you can't finish hurts your rating.
        </div>
      </div>
    );
  }

  // ── LEGAL ──
  if (section === "legal") {
    if (legalDoc) return <LegalPage doc={legalDoc} audience="provider" onBack={() => setLegalDoc(null)} />;
    return (
      <div>
        <Back />
        <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 4 }}>📄 Terms &amp; Privacy</div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 18, lineHeight: 1.55 }}>
          What you agreed to when you signed up, and how we handle your data.
        </div>
        {[
          ["terms", "📋", "Provider Agreement", "Independent contractor status, pay, conduct, and removal"],
          ["privacy", "🔒", "Privacy Policy", "What we collect, who sees it, and how long we keep it"],
        ].map(([d, ic, t, sub]) => (
          <button key={d} onClick={() => setLegalDoc(d)}
            style={{ width: "100%", background: "#161616", border: "1px solid #222", borderLeft: "4px solid #3b82f6", borderRadius: 10, padding: 16, marginBottom: 10, cursor: "pointer", textAlign: "left", display: "flex", gap: 14, alignItems: "center" }}>
            <span style={{ fontSize: 24, flexShrink: 0 }}>{ic}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14.5, fontWeight: 800, color: "#fff", marginBottom: 2 }}>{t}</div>
              <div style={{ fontSize: 11.5, color: "#777", lineHeight: 1.4 }}>{sub}</div>
            </div>
            <span style={{ color: "#444", fontSize: 18 }}>›</span>
          </button>
        ))}
      </div>
    );
  }

  // ── JOB ALERTS ──
  if (section === "alerts") {
    const ios = isIOS();
    return (
      <div>
        <Back />
        <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 4 }}>🔔 Job Alerts</div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 18, lineHeight: 1.55 }}>
          Getting alerted the second a job comes in is how you make money on this platform. Set up both of these.
        </div>

        {/* 1. In-app */}
        <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 11, padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: "#3b82f6", fontWeight: 800, marginBottom: 8 }}>1 · IN-APP ALERTS</div>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: "#fff", marginBottom: 5 }}>
            {typeof Notification !== "undefined" && Notification.permission === "granted"
              ? "✅ Enabled" : "⚠️ Not enabled"}
          </div>
          <div style={{ fontSize: 12, color: "#888", lineHeight: 1.6, marginBottom: 12 }}>
            Buzzes your phone while the app is open or recently used.
            {ios && " On iPhone this only works if you added Super Fast to your Home Screen."}
          </div>
          {ios && !isStandalone() && (
            <div style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 8, padding: 11, marginBottom: 10, fontSize: 11.5, color: "#f59e0b", lineHeight: 1.55 }}>
              📲 You're in Safari, not the installed app. Tap Share → <strong>Add to Home Screen</strong>, then open it from your Home Screen.
            </div>
          )}
          {typeof Notification !== "undefined" && Notification.permission !== "granted" && (
            <button style={{ ...c.btnSecondary, marginTop: 0 }}
              onClick={async () => {
                const perm = await Notification.requestPermission();
                if (perm === "granted") {
                  new Notification("✅ Alerts are on", { body: "You'll be notified when a job comes in." });
                  if (navigator.vibrate) navigator.vibrate([300, 120, 300]);
                }
                setSection(null); setTimeout(() => setSection("alerts"), 50);
              }}>
              🔔 Turn On Notifications
            </button>
          )}
        </div>

        {/* 2. Pushover */}
        <div style={{ background: "#161616", border: `1px solid ${profile.pushoverKey ? "#22c55e" : "#E63329"}`, borderRadius: 11, padding: 16, marginBottom: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: profile.pushoverKey ? "#22c55e" : "#E63329", fontWeight: 800, marginBottom: 8 }}>
            2 · BACKUP ALERTS {profile.pushoverKey ? "· ACTIVE" : "· STRONGLY RECOMMENDED"}
          </div>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: "#fff", marginBottom: 5 }}>Get jobs even with the app closed</div>
          <div style={{ fontSize: 12, color: "#888", lineHeight: 1.6, marginBottom: 14 }}>
            Pushover is a free app that delivers job alerts to your phone like a text message — even when Super Fast is completely closed. This is the most reliable way to catch jobs, especially on iPhone.
          </div>

          {!profile.pushoverKey && (
            <div style={{ marginBottom: 14 }}>
              {[
                ["1", <>Install <strong style={{ color: "#fff" }}>Pushover</strong> from the {ios ? "App Store" : "Play Store"} (one-time $5 after a 30-day free trial)</>],
                ["2", <>Open it and create a free account</>],
                ["3", <>On the main screen you'll see <strong style={{ color: "#fff" }}>"Your User Key"</strong> — a 30-character code</>],
                ["4", <>Copy it and paste it below</>],
              ].map(([n, t]) => (
                <div key={n} style={{ display: "flex", gap: 10, marginBottom: 9, alignItems: "flex-start" }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#E63329", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 11, flexShrink: 0 }}>{n}</div>
                  <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.55 }}>{t}</div>
                </div>
              ))}
              <a href="https://pushover.net/clients" target="_blank" rel="noreferrer"
                style={{ display: "block", textAlign: "center", background: "none", border: "1px solid #3b82f6", color: "#3b82f6", padding: "11px", borderRadius: 8, fontSize: 12.5, fontWeight: 700, textDecoration: "none", marginTop: 4 }}>
                📲 Get the Pushover app →
              </a>
            </div>
          )}

          <label style={c.label}>Your Pushover User Key</label>
          <input style={{ ...c.input, marginBottom: 10, fontFamily: "monospace", fontSize: 13 }}
            placeholder="e.g. uQiRzpo4DXghDmr9QzzfQu27cmVRsG"
            value={poKey} onChange={e => setPoKey(e.target.value.trim())} />

          {poMsg && <div style={{ fontSize: 12, color: poMsg.startsWith("✅") ? "#22c55e" : "#ff6b6b", marginBottom: 10, lineHeight: 1.5 }}>{poMsg}</div>}

          <button style={{ ...c.btnPrimary, background: "#22c55e", color: "#000" }} disabled={poSaving || !poKey}
            onClick={async () => {
              setPoSaving(true); setPoMsg("");
              const r = await fetch(`${WORKER_URL}/notify`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  title: "✅ Super Fast alerts are working",
                  message: `Nice — you'll now get job alerts here even when the app is closed.`,
                  userKey: poKey,
                }),
              });
              const d = await r.json().catch(() => ({}));
              if (d && d.ok === false) {
                setPoMsg("⚠️ Pushover rejected that key. Double-check you copied the whole thing.");
                setPoSaving(false);
                return;
              }
              await dbPatch(`users/${authUser.uid}`, { pushoverKey: poKey, pushoverAddedAt: Date.now() });
              syncProviderProfile({ pushoverKey: poKey });
              if (onProfileChange) onProfileChange({ pushoverKey: poKey });
              setPoMsg("✅ Saved — check your phone, a test alert should have arrived.");
              setPoSaving(false);
            }}>
            {poSaving ? "Testing..." : profile.pushoverKey ? "Update & Send Test Alert" : "Save & Send Test Alert"}
          </button>

          {profile.pushoverKey && (
            <button style={{ ...c.btnSecondary, marginTop: 8 }}
              onClick={async () => {
                if (!window.confirm("Turn off backup alerts? You'll only get notified when the app is open.")) return;
                await dbPatch(`users/${authUser.uid}`, { pushoverKey: null });
                syncProviderProfile({ pushoverKey: null });
                if (onProfileChange) onProfileChange({ pushoverKey: null });
                setPoKey(""); setPoMsg("");
              }}>
              Remove backup alerts
            </button>
          )}
        </div>

        <div style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 10, padding: 14, fontSize: 12, color: "#f59e0b", lineHeight: 1.6 }}>
          ⚠️ Jobs expire after 60 seconds and go to the next provider. If your alerts aren't working, you're losing money without knowing it.
        </div>
      </div>
    );
  }

  // ── MY PROFILE ──
  if (section === "profile") {
    const completed = jobs.filter(j => j.status === "completed");
    const totalEarned = completed.reduce((n, j) => n + (getProviderPay(j).pay || 0) + (j.tipPaid ? (j.tipAmount || 0) : 0), 0);
    return (
      <div>
        <SelfieViewer />
        <Back />

        {/* Header card */}
        <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 12, padding: 18, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {profile.selfieUrl ? (
              <img src={profile.selfieUrl} alt="you"
                onClick={() => setViewSelfie(true)}
                style={{ width: 62, height: 62, borderRadius: "50%", objectFit: "cover", border: "2px solid #22c55e", cursor: "pointer" }} />
            ) : (
              <div style={{ width: 62, height: 62, borderRadius: "50%", background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 900, color: "#fff" }}>
                {profile.firstName?.[0]}{profile.lastName?.[0]}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 19, fontWeight: 900, color: "#fff" }}>{profile.firstName} {profile.lastName}</div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                ⭐ {completed.length} job{completed.length !== 1 ? "s" : ""} completed
              </div>
              {profile.vehicle && <div style={{ fontSize: 11.5, color: "#666", marginTop: 2 }}>🚗 {profile.vehicle}</div>}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 14, textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#22c55e" }}>{completed.length}</div>
            <div style={{ fontSize: 9.5, letterSpacing: 1.5, color: "#666", fontWeight: 700, marginTop: 3 }}>JOBS COMPLETED</div>
          </div>
          <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 14, textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#f59e0b" }}>${totalEarned}</div>
            <div style={{ fontSize: 9.5, letterSpacing: 1.5, color: "#666", fontWeight: 700, marginTop: 3 }}>TOTAL EARNED</div>
          </div>
        </div>

        {/* Identity photo */}
        <div style={{ fontSize: 10, letterSpacing: 2.5, color: profile.selfieUrl ? "#22c55e" : "#f59e0b", fontWeight: 800, marginBottom: 9 }}>📸 YOUR IDENTITY PHOTO</div>
        <div style={{ background: "#161616", border: `1px solid ${profile.selfieUrl ? "#222" : "#f59e0b"}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
          {profile.selfieUrl ? (
            <>
              <img src={profile.selfieUrl} alt="your selfie"
                onClick={() => setViewSelfie(true)}
                style={{ width: "100%", maxWidth: 220, borderRadius: 10, border: "1px solid #333", cursor: "pointer", display: "block", marginBottom: 10 }} />
              <div style={{ fontSize: 11, color: "#666", lineHeight: 1.55, marginBottom: 10 }}>
                Customers may see this so they know who's arriving. Tap to view full size.
                {profile.selfieTakenAt && <><br />Taken {new Date(profile.selfieTakenAt).toLocaleDateString()}.</>}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.6, marginBottom: 12 }}>
              You don't have an identity photo on file. Add one so customers know who's showing up.
            </div>
          )}

          <button style={{ ...c.gpsBtn, border: "2px dashed #3b82f6", color: "#3b82f6", opacity: selfieUploading ? 0.6 : 1 }}
            disabled={selfieUploading}
            onClick={() => {
              const input = document.createElement("input");
              input.type = "file"; input.accept = "image/*"; input.capture = "user";
              input.onchange = async e => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = async ev => {
                  try {
                    setSelfieUploading(true);
                    const url = await uploadPhoto(ev.target.result, `superfast/providers/selfies/${authUser.uid}_${Date.now()}`);
                    await dbPatch(`users/${authUser.uid}`, { selfieUrl: url, selfieTakenAt: Date.now(), selfieConfirmed: true });
                    syncProviderProfile({ selfieUrl: url, selfieTakenAt: Date.now() });
                    if (onProfileChange) onProfileChange({ selfieUrl: url, selfieTakenAt: Date.now() });
                    await fetch(`${WORKER_URL}/notify`, {
                      method: "POST", headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        title: "📸 Provider Updated Photo",
                        message: `${profile.firstName} ${profile.lastName} uploaded a new identity selfie.`,
                      }),
                    }).catch(() => {});
                    setSelfieUploading(false);
                  } catch (err) {
                    setSelfieUploading(false);
                    alert("Upload failed: " + err.message);
                  }
                };
                reader.readAsDataURL(file);
              };
              input.click();
            }}>
            {selfieUploading ? "Uploading..." : profile.selfieUrl ? "📸 Retake My Photo" : "📸 Take My Photo"}
          </button>
          <div style={{ fontSize: 10.5, color: "#555", marginTop: 8, lineHeight: 1.5 }}>
            Camera only. Clear view of your face — no sunglasses, hats, or anything covering it.
          </div>
        </div>

        {/* Contact */}
        <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#3b82f6", fontWeight: 800, marginBottom: 9 }}>CONTACT</div>
        <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 14, marginBottom: 14 }}>
          {[["📧 Email", profile.email], ["📞 Phone", profile.phone], ["📅 Experience", profile.experience], ["📆 Joined", profile.appliedAt ? new Date(profile.appliedAt).toLocaleDateString() : null]]
            .filter(([, v]) => v).map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #1e1e1e", fontSize: 12.5 }}>
              <span style={{ color: "#666" }}>{k}</span>
              <span style={{ color: "#ccc", textAlign: "right", maxWidth: "62%", wordBreak: "break-word" }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Services */}
        <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#22c55e", fontWeight: 800, marginBottom: 9 }}>SERVICES I OFFER</div>
        <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 14, marginBottom: 14 }}>
          {(profile.services || []).length === 0 ? (
            <div style={{ fontSize: 12, color: "#666" }}>No services set.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {(profile.services || []).map(sv => (
                <span key={sv} style={{ fontSize: 11.5, background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e", padding: "5px 11px", borderRadius: 12, fontWeight: 700 }}>
                  {SERVICE_LABELS[sv] || sv}
                </span>
              ))}
            </div>
          )}
          <button style={{ width: "100%", background: "none", border: "1px solid #2a5a2a", color: "#22c55e", padding: "9px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", marginTop: 12 }}
            onClick={() => setSection("services")}>
            Edit my services →
          </button>
        </div>

        {/* Status */}
        <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#f59e0b", fontWeight: 800, marginBottom: 9 }}>ACCOUNT STATUS</div>
        <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 14, marginBottom: 14 }}>
          {[
            ["🏦 Bank / payouts", profile.payoutsEnabled ? `✅ Verified ••••${profile.bankLast4 || "____"}` : "⚠️ Not connected", profile.payoutsEnabled],
            ["📍 Service area", profile.city && profile.state ? `${profile.city}, ${profile.state} · ${profile.serviceRadius || "?"} mi` : "⚠️ Not set", !!(profile.city && profile.state)],
            ["🔔 Push alerts", typeof Notification !== "undefined" && Notification.permission === "granted" ? "✅ Enabled" : "⚠️ Not enabled", typeof Notification !== "undefined" && Notification.permission === "granted"],
            ["📸 Identity selfie", profile.selfieUrl ? "✅ On file" : "⚠️ Missing", !!profile.selfieUrl],
            ["✔️ Test dispatch", profile.testJobPassed ? "✅ Passed" : "⚠️ Not completed", !!profile.testJobPassed],
          ].map(([k, v, ok]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #1e1e1e", fontSize: 12.5 }}>
              <span style={{ color: "#666" }}>{k}</span>
              <span style={{ color: ok ? "#22c55e" : "#f59e0b", fontWeight: 600, textAlign: "right" }}>{v}</span>
            </div>
          ))}
          <div style={{ fontSize: 10.5, color: "#555", marginTop: 10, lineHeight: 1.5 }}>
            Need to change your name, phone or vehicle? Use Message Dispatch.
          </div>
        </div>

        <DeleteAccountBlock
          authUser={authUser}
          profile={profile}
          role="provider"
          onDeleted={() => { window.location.href = "/provider"; }}
        />
      </div>
    );
  }

  // ── SERVICE AREA ──
  if (section === "area") {
    return (
      <div>
        <Back />
        <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 4 }}>📍 My Service Area</div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 18, lineHeight: 1.55 }}>
          Tell us where you work so we only send you jobs you can actually reach.
        </div>

        <div style={c.fieldGroup}>
          <label style={c.label}>State</label>
          <select style={c.input} value={areaState} onChange={e => setAreaState(e.target.value)}>
            <option value="">Select state...</option>
            {US_STATES.map(st => <option key={st.code} value={st.code}>{st.code} — {st.name}</option>)}
          </select>
        </div>

        <div style={c.fieldGroup}>
          <label style={c.label}>City You Work Out Of</label>
          <input style={c.input} placeholder="e.g. West Palm Beach"
            value={areaCity} onChange={e => setAreaCity(e.target.value)} />
        </div>

        <div style={c.fieldGroup}>
          <label style={c.label}>How Far Will You Travel?</label>
          <select style={c.input} value={areaRadius} onChange={e => setAreaRadius(e.target.value)}>
            <option value="">Select radius...</option>
            <option value="50">Up to 50 miles</option>
            <option value="100">Up to 100 miles</option>
            <option value="200">Up to 200 miles</option>
          </select>
        </div>

        {areaCity && areaState && areaRadius && (
          <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: "#22c55e", fontWeight: 700 }}>
              ✅ You'll get jobs within {areaRadius} miles of {areaCity}, {areaState}
            </div>
          </div>
        )}

        {areaSaved && <div style={c.gpsSuccess}>✅ Service area saved.</div>}
        <button style={{ ...c.btnPrimary, background: "#a855f7" }} onClick={saveArea} disabled={areaSaving}>
          {areaSaving ? "Saving..." : "Save My Service Area"}
        </button>
        <div style={{ fontSize: 11, color: "#555", textAlign: "center", marginTop: 10, lineHeight: 1.5 }}>
          Moving or expanding? Come back and update this any time.
        </div>
      </div>
    );
  }

  // ── SUPPORT ──
  return (
    <div>
      <Back />
      <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 4 }}>💬 Message Dispatch</div>
      <div style={{ fontSize: 12, color: "#666", marginBottom: 18, lineHeight: 1.55 }}>
        Problem on a job, a pay question, or need something changed? Send it here and we'll get back to you.
      </div>

      <div style={c.fieldGroup}>
        <label style={c.label}>Job ID (optional)</label>
        <input style={c.input} placeholder="e.g. A7K2M9" value={msgJobId}
          onChange={e => setMsgJobId(e.target.value.toUpperCase())} />
        {jobs.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
            {jobs.slice(0, 6).map(j => (
              <button key={j._key} onClick={() => setMsgJobId(j.id)}
                style={{ background: msgJobId === j.id ? "#3b82f6" : "#161616", border: `1px solid ${msgJobId === j.id ? "#3b82f6" : "#2a2a2a"}`, color: msgJobId === j.id ? "#fff" : "#888", padding: "5px 10px", borderRadius: 14, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>
                {j.id}
              </button>
            ))}
          </div>
        )}
        <div style={{ fontSize: 11, color: "#555", marginTop: 6 }}>Tap a recent job or type the ID.</div>
      </div>

      <div style={c.fieldGroup}>
        <label style={c.label}>What's going on?</label>
        <textarea style={{ ...c.input, minHeight: 130, resize: "vertical" }}
          placeholder="Explain the issue — the more detail, the faster we can sort it out."
          value={msgText} onChange={e => setMsgText(e.target.value)} />
      </div>

      {msgSent && <div style={c.gpsSuccess}>✅ Message sent. We'll get back to you shortly.</div>}

      <button style={{ ...c.btnPrimary, background: "#3b82f6", opacity: msgText.trim() ? 1 : 0.4 }}
        disabled={!msgText.trim() || msgSending} onClick={sendMessage}>
        {msgSending ? "Sending..." : "Send to Dispatch →"}
      </button>

      {/* ── MY CONVERSATIONS ── */}
      {myThreads.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, color: "#3b82f6", fontWeight: 800, marginBottom: 12 }}>💬 MY CONVERSATIONS</div>
          {myThreads.map(m => {
            const thread = m.replies || [];
            return (
              <div key={m._key} style={{ background: "#161616", border: `1px solid ${m.unreadByProvider ? "#3b82f6" : "#222"}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 11, color: "#666" }}>{timeAgo(m.createdAt)}</span>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {m.jobId && (
                      <span style={{ fontSize: 10.5, background: "#0a1525", border: "1px solid #3b82f6", color: "#3b82f6", padding: "2px 8px", borderRadius: 10, fontWeight: 700 }}>
                        Job {m.jobId}
                      </span>
                    )}
                    <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 10, background: m.resolved ? "#0a1f0a" : "#1a1000", color: m.resolved ? "#22c55e" : "#f59e0b" }}>
                      {m.resolved ? "RESOLVED" : "OPEN"}
                    </span>
                  </div>
                </div>

                <div style={{ background: "#0d0d0d", borderRadius: 8, padding: 10, marginBottom: 10 }}>
                  <div style={{ marginBottom: 8, textAlign: "right" }}>
                    <div style={{ fontSize: 9.5, color: "#666", fontWeight: 700, letterSpacing: 1, marginBottom: 3 }}>YOU</div>
                    <div style={{ display: "inline-block", maxWidth: "88%", textAlign: "left", background: "#0a2a1a", border: "1px solid #22c55e", borderRadius: 8, padding: "9px 11px", fontSize: 12.5, color: "#c8f5da", lineHeight: 1.55 }}>
                      {m.message}
                    </div>
                  </div>
                  {thread.map((r, i) => (
                    <div key={i} style={{ marginBottom: 8, textAlign: r.from === "admin" ? "left" : "right" }}>
                      <div style={{ fontSize: 9.5, color: "#666", fontWeight: 700, letterSpacing: 1, marginBottom: 3 }}>
                        {r.from === "admin" ? "DISPATCH" : "YOU"} · {timeAgo(r.at)}
                      </div>
                      <div style={{ display: "inline-block", maxWidth: "88%", textAlign: "left", background: r.from === "admin" ? "#1a1a1a" : "#0a2a1a", border: r.from === "admin" ? "1px solid #3b82f6" : "1px solid #22c55e", borderRadius: 8, padding: "9px 11px", fontSize: 12.5, color: r.from === "admin" ? "#ddd" : "#c8f5da", lineHeight: 1.55 }}>
                        {r.text}
                      </div>
                    </div>
                  ))}
                </div>

                {!m.resolved && (
                  <>
                    <textarea
                      value={threadReply[m._key] || ""}
                      onChange={e => setThreadReply(prev => ({ ...prev, [m._key]: e.target.value }))}
                      placeholder="Reply to dispatch..."
                      style={{ width: "100%", background: "#0f0f0f", border: "1px solid #2a2a2a", borderRadius: 8, padding: "10px 12px", color: "#fff", fontSize: 13, minHeight: 54, resize: "vertical", boxSizing: "border-box", marginBottom: 8, fontFamily: "inherit" }} />
                    <button style={{ width: "100%", background: "#3b82f6", border: "none", color: "#fff", padding: "10px", borderRadius: 7, fontSize: 12.5, fontWeight: 800, cursor: "pointer", opacity: (threadReply[m._key] || "").trim() ? 1 : 0.4 }}
                      disabled={!(threadReply[m._key] || "").trim()}
                      onClick={async () => {
                        const text = (threadReply[m._key] || "").trim();
                        if (!text) return;
                        const updated = [...(m.replies || []), { from: "provider", text, at: Date.now() }];
                        await dbPatch(`providerMessages/${m._key}`, { replies: updated, lastReplyAt: Date.now(), unreadByProvider: false });
                        await fetch(`${WORKER_URL}/notify`, {
                          method: "POST", headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            title: "💬 Provider Replied" + (m.jobId ? ` — Job ${m.jobId}` : ""),
                            message: `${profile.firstName} ${profile.lastName}\n📞 ${profile.phone || ""}\n\n"${text}"`,
                          }),
                        }).catch(() => {});
                        setThreadReply(prev => ({ ...prev, [m._key]: "" }));
                        loadThreads();
                      }}>
                      Send Reply →
                    </button>
                  </>
                )}

                {m.unreadByProvider && (
                  <button style={{ width: "100%", background: "none", border: "1px solid #333", color: "#666", padding: "7px", borderRadius: 6, fontSize: 11, cursor: "pointer", marginTop: 8 }}
                    onClick={async () => {
                      await dbPatch(`providerMessages/${m._key}`, { unreadByProvider: false });
                      loadThreads();
                    }}>
                    Mark as read
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ background: "#1a0a00", border: "1px solid #E63329", borderRadius: 10, padding: 14, marginTop: 16 }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: "#E63329", marginBottom: 5 }}>🚨 Urgent or unsafe?</div>
        <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.55, marginBottom: 10 }}>
          Don't wait on a message. Call dispatch directly.
        </div>
        <a href="tel:5612558506" style={{ display: "block", textAlign: "center", background: "#E63329", color: "#fff", padding: "12px", borderRadius: 8, fontWeight: 800, fontSize: 14, textDecoration: "none" }}>
          📞 Call (561) 255-8506
        </a>
      </div>
    </div>
  );
}

function ProviderApp({ authUser, onSignOut }) {
  const [liveProfile, setLiveProfile] = useState(authUser?.profile || {});
  const profile = liveProfile;

  // Pull the freshest profile from Firebase so banners reflect reality
  useEffect(() => {
    let alive = true;
    async function pull() {
      try {
        const fresh = await dbGet(`users/${authUser.uid}`);
        if (!alive || !fresh || typeof fresh !== "object") return;
        setLiveProfile(prev => ({ ...prev, ...fresh }));
        syncProviderProfile(fresh);
      } catch {}
    }
    pull();
    const iv = setInterval(pull, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [authUser?.uid]);
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentErr, setConsentErr] = useState("");
  const [gpsConsent, setGpsConsent] = useState(() => {
    return localStorage.getItem(`sfrs_gps_consent_${authUser?.uid}`) === "true";
  });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [activeJob, setActiveJob] = useState(null);
  const [jobScreen, setJobScreen] = useState("detail");
  const [photos, setPhotos] = useState({});
  const [checkoutPhotos, setCheckoutPhotos] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [sigError, setSigError] = useState("");
  const [incomingJob, setIncomingJob] = useState(null);
  const [countdown, setCountdown] = useState(60);
  const [isOnline, setIsOnline] = useState(!!profile.online);
  const [testPassed, setTestPassed] = useState(!!profile.testJobPassed);
  const [testJob, setTestJob] = useState(null);
  const [testCountdown, setTestCountdown] = useState(60);
  const [toggling, setToggling] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [showAddons, setShowAddons] = useState(false);
  const [showGpsConsent, setShowGpsConsent] = useState(false);
  const [gpsAsking, setGpsAsking] = useState(false);
  const [gpsError, setGpsError] = useState("");
  const [showIosInstall, setShowIosInstall] = useState(false);
  const [showFlyerQr, setShowFlyerQr] = useState(false);
  const testTimerRef = useRef(null);

  // Launch Stripe Connect onboarding from anywhere in the provider app
  async function startConnectOnboarding() {
    setConnectLoading(true);
    try {
      const fresh = await dbGet(`users/${authUser.uid}`);
      const existing = fresh?.stripeAccountId;
      const returnUrl = window.location.origin + "/provider";
      let url;
      if (existing) {
        const r = await fetch(`${WORKER_URL}/connect-refresh-link`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accountId: existing, returnUrl }),
        });
        const d = await r.json();
        if (d.error) throw new Error(d.error);
        url = d.onboardingUrl;
      } else {
        const r = await fetch(`${WORKER_URL}/connect-onboard`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uid: authUser.uid,
            email: profile.email || authUser.email,
            firstName: profile.firstName,
            lastName: profile.lastName,
            phone: profile.phone,
            returnUrl,
          }),
        });
        const d = await r.json();
        if (d.error) throw new Error(d.error);
        await dbPatch(`users/${authUser.uid}`, { stripeAccountId: d.accountId, connectStartedAt: Date.now() });
        url = d.onboardingUrl;
      }
      window.location.href = url;
    } catch (err) {
      setConnectLoading(false);
      alert("Couldn't open Stripe: " + err.message);
    }
  }
  const countdownRef = useRef(null);
  const prevJobCountRef = useRef(0);
  const sigCanvasRef = useRef(null);
  const sigDrawing = useRef(false);
  const locationRef = useRef(null);
  const trackingRef = useRef(null);
  const alwaysOnTrackingRef = useRef(null);

  // Always-on GPS tracking — updates every 60 seconds when logged in
  function startAlwaysOnTracking() {
    if (!navigator.geolocation || !authUser?.uid) return;
    function updateLocation() {
      navigator.geolocation.getCurrentPosition(pos => {
        dbPatch(`users/${authUser.uid}`, {
          currentLocation: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            updatedAt: Date.now(),
          },
          online: true,
          lastSeen: Date.now(),
        });
      }, null, { enableHighAccuracy: true });
    }
    updateLocation();
    alwaysOnTrackingRef.current = setInterval(updateLocation, 60000);
  }

  function stopAlwaysOnTracking() {
    if (alwaysOnTrackingRef.current) clearInterval(alwaysOnTrackingRef.current);
  }

  // GPS tracking — update location every 30 seconds tied to specific job
  function startTracking(jobKey) {
    if (!navigator.geolocation) return;
    trackingRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(pos => {
        const { latitude, longitude } = pos.coords;
        locationRef.current = { latitude, longitude, updatedAt: Date.now() };
        dbPatch(`jobs/${jobKey}`, {
          providerLocation: { latitude, longitude, updatedAt: Date.now() }
        });
      }, null, { enableHighAccuracy: true });
    }, 30000);
    navigator.geolocation.getCurrentPosition(pos => {
      dbPatch(`jobs/${jobKey}`, {
        providerLocation: { latitude: pos.coords.latitude, longitude: pos.coords.longitude, updatedAt: Date.now() }
      });
    });
  }

  function stopTracking() {
    if (trackingRef.current) clearInterval(trackingRef.current);
  }

  async function goOffline() {
    setToggling(true);
    await dbPatch(`users/${authUser.uid}`, { online: false, lastSeen: Date.now() });
    setIsOnline(false);
    setToggling(false);
  }

  async function goOnline() {
    if (!profile.payoutsEnabled) {
      alert("⚠️ You must verify your identity and connect your bank account with Stripe before going online.");
      return;
    }
    // Must accept GPS tracking terms before first time online
    if (!profile.gpsConsentAccepted && !localStorage.getItem(`sfrs_gps_ok_${authUser.uid}`)) {
      setShowGpsConsent(true);
      return;
    }
    // First time online — run the test dispatch
    if (!testPassed) {
      startTestJob();
      return;
    }
    setToggling(true);
    await dbPatch(`users/${authUser.uid}`, { online: true, lastSeen: Date.now(), wentOnlineAt: Date.now() });
    setIsOnline(true);
    setToggling(false);
  }

  function startTestJob(skipInstallPrompt) {
    // iOS only delivers web notifications from a Home Screen install.
    // Tell them once — but never block them from working.
    if (isIOS() && !isStandalone() && !skipInstallPrompt) {
      setShowIosInstall(true);
      return;
    }
    const t = {
      id: "TEST-" + genId(),
      service: "Jump Start",
      customer: "TEST DISPATCH",
      phone: "(561) 255-8506",
      address: "This is a test — no real customer",
      price: 64.20,
      isTest: true,
      createdAt: Date.now(),
    };
    setTestJob(t);
    setTestCountdown(60);
    if (navigator.vibrate) navigator.vibrate([500, 200, 500, 200, 500]);
    try { showLocalNotification("🔥 TEST JOB — Jump Start", "Tap to accept and finish setup. This is a test dispatch.", t.id); } catch {}
    if (testTimerRef.current) clearInterval(testTimerRef.current);
    testTimerRef.current = setInterval(() => {
      setTestCountdown(prev => {
        if (prev <= 1) {
          clearInterval(testTimerRef.current);
          setTestJob(null);
          alert("⏱️ Test dispatch expired. Tap Go Online to try again — make sure notifications are enabled.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  async function acceptTestJob() {
    if (testTimerRef.current) clearInterval(testTimerRef.current);
    setTestJob(null);
    setTestPassed(true);
    setToggling(true);
    await dbPatch(`users/${authUser.uid}`, {
      testJobPassed: true,
      testJobPassedAt: Date.now(),
      online: true,
      lastSeen: Date.now(),
      wentOnlineAt: Date.now(),
    });
    syncProviderProfile({ testJobPassed: true, online: true });
    setLiveProfile(prev => ({ ...prev, testJobPassed: true, online: true }));
    setIsOnline(true);
    setToggling(false);
    await fetch(`${WORKER_URL}/notify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: "✅ Provider Passed Test Dispatch",
        message: `${profile.firstName} ${profile.lastName} accepted the test job and is now ONLINE.\n${profile.city || ""}, ${profile.state || ""}`,
      }),
    }).catch(() => {});
  }

  function showJobAlert(job) {
    setIncomingJob(job);
    setCountdown(60);
    // Vibrate device
    if (navigator.vibrate) navigator.vibrate([500, 200, 500, 200, 500]);
    // Fire browser push notification
    const pay = getProviderPay(job);
    showLocalNotification(
      `🔥 New Job — ${job.service}`,
      `${job.customer} · 📍 ${job.address}\n💰 Your pay: $${pay.pay || "See app"}`,
      job.id
    );
    // Start countdown
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          declineJob(job);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  async function acceptJob(job) {
    clearInterval(countdownRef.current);
    await dbPatch(`jobs/${job._key}`, { accepted: true, acceptedAt: Date.now() });
    setJobs(prev => prev.map(j => j._key === job._key ? { ...j, accepted: true } : j));
    setIncomingJob(null);
    setCountdown(60);
  }

  async function declineJob(job) {
    clearInterval(countdownRef.current);
    // Unassign the job — sends it back to new for reassignment
    await dbPatch(`jobs/${job._key}`, { 
      status: "new", tech: null, techName: null, techEmail: null, 
      accepted: false, declinedAt: Date.now(),
      declinedBy: `${profile.firstName} ${profile.lastName}`
    });
    setJobs(prev => prev.filter(j => j._key !== job._key));
    setIncomingJob(null);
    setCountdown(60);
  }

  // Start always-on GPS when consent given
  useEffect(() => {
    if (gpsConsent) startAlwaysOnTracking();
    return () => stopAlwaysOnTracking();
  }, [gpsConsent]);



  useEffect(() => {
    async function loadJobs() {
      const all = await dbGet("jobs");
      if (!Array.isArray(all)) { setLoading(false); return; }
      
      const uid = authUser.uid;
      const p = authUser.profile || {};
      const firstName = (p.firstName || "").toLowerCase().trim();
      const lastName = (p.lastName || "").toLowerCase().trim();
      const fullName = `${firstName} ${lastName}`.trim();
      const email = (p.email || authUser.email || "").toLowerCase().trim();

      const mine = all.filter(j => {
        const tech = (j.tech || "").toLowerCase().trim();
        const techName = (j.techName || "").toLowerCase().trim();
        const techEmail = (j.techEmail || "").toLowerCase().trim();
        
        // Match on UID (primary — most reliable)
        if (j.tech === uid) return true;
        // Match on full name
        if (fullName && techName === fullName) return true;
        // Match on email
        if (email && (techEmail === email || tech === email)) return true;
        // Match on first+last name contained in techName
        if (firstName && lastName && techName.includes(firstName) && techName.includes(lastName)) return true;
        return false;
      });

      mine.sort((a, b) => b.createdAt - a.createdAt);
      
      // Detect newly assigned jobs and show alert
      const newlyAssigned = mine.filter(j => j.status === "assigned" && !j.accepted);
      const prevCount = prevJobCountRef.current;
      const newCount = mine.filter(j => j.status === "assigned").length;
      
      if (newlyAssigned.length > 0 && newCount > prevCount && !incomingJob && isOnline) {
        const newest = newlyAssigned[0];
        showJobAlert(newest);
      }
      prevJobCountRef.current = newCount;
      
      setJobs(mine);
      setLoading(false);
    }
    loadJobs();
    const interval = setInterval(loadJobs, 8000);
    return () => { clearInterval(interval); stopTracking(); if (countdownRef.current) clearInterval(countdownRef.current); };
  }, []);

  // Camera capture
  function takePhoto(key, isCheckout = false) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.capture = "environment";
    input.onchange = e => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = ev => {
        if (isCheckout) setCheckoutPhotos(p => ({ ...p, [key]: ev.target.result }));
        else setPhotos(p => ({ ...p, [key]: ev.target.result }));
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }

  // Signature canvas
  function initCanvas() {
    setTimeout(() => {
      const canvas = sigCanvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#1a1a1a";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
    }, 100);
  }

  function getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  }

  function sigStart(e) {
    e.preventDefault();
    sigDrawing.current = true;
    const canvas = sigCanvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }

  function sigMove(e) {
    e.preventDefault();
    if (!sigDrawing.current) return;
    const canvas = sigCanvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  }

  function sigEnd(e) { e.preventDefault(); sigDrawing.current = false; }

  function clearSig() {
    const canvas = sigCanvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#1a1a1a";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  // Get service-specific photo label
  function getServicePhotoLabel(service) {
    if (!service) return "Service Photo";
    const s = service.toLowerCase();
    if (s.includes("tire") && s.includes("spare")) return "Photo of Flat Tire with Spare";
    if (s.includes("tire")) return "Photo of Flat Tire";
    if (s.includes("jump") || s.includes("battery")) return "Photo of Battery";
    if (s.includes("fuel") || s.includes("gas")) return "Photo of Fuel Gauge (Empty)";
    if (s.includes("lockout") || s.includes("lock")) return "Photo of Locked Door/Vehicle";
    if (s.includes("tow")) return "Photo of Vehicle Being Towed";
    return "Service Photo";
  }

  const PHOTO_SLOTS = [
    { key: "front", label: "Front of Vehicle", icon: "🚗" },
    { key: "back", label: "Back of Vehicle", icon: "🚗" },
    { key: "driver", label: "Driver Side", icon: "🚗" },
    { key: "passenger", label: "Passenger Side", icon: "🚗" },
  ];

  async function handleCheckIn() {
    const required = ["front", "back", "driver", "passenger", "service"];
    const missing = required.filter(k => !photos[k]);
    if (missing.length > 0) return alert(`Please take all required photos. Missing: ${missing.join(", ")}`);

    setUploading(true);
    setUploadProgress("Uploading photos...");
    const job = activeJob;
    const urls = {};

    for (const key of required) {
      setUploadProgress(`Uploading ${key} photo...`);
      urls[`checkin_${key}`] = await uploadPhoto(photos[key], `superfast/jobs/${job._key}/checkin`);
    }

    await dbPatch(`jobs/${job._key}`, {
      ...urls,
      status: "on_scene",
      checkinAt: Date.now(),
      providerName: `${profile.firstName} ${profile.lastName}`,
    });

    setJobs(prev => prev.map(j => j._key === job._key ? { ...j, ...urls, status: "on_scene", checkinAt: Date.now() } : j));
    setActiveJob(prev => ({ ...prev, ...urls, status: "on_scene", checkinAt: Date.now() }));
    setUploading(false);
    setUploadProgress("");
    setJobScreen("detail");
  }

  async function handleCheckOut() {
    const required = ["front", "back", "driver", "passenger", "service"];
    const missing = required.filter(k => !checkoutPhotos[k]);
    if (missing.length > 0) return alert(`Please take all required after photos. Missing: ${missing.join(", ")}`);

    setJobScreen("signature");
    initCanvas();
  }

  async function handleSubmitComplete() {
    setSigError("");
    const canvas = sigCanvasRef.current;
    if (!canvas) return;

    // Check if canvas has any signature (not just blank)
    const ctx = canvas.getContext("2d");
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    const hasSignature = Array.from(data).some((val, i) => i % 4 !== 3 && val > 30);
    if (!hasSignature) { setSigError("Please have the customer sign above."); return; }

    setUploading(true);
    setUploadProgress("Uploading after photos...");
    const job = activeJob;
    const urls = {};

    const required = ["front", "back", "driver", "passenger", "service"];
    for (const key of required) {
      setUploadProgress(`Uploading ${key} photo...`);
      urls[`checkout_${key}`] = await uploadPhoto(checkoutPhotos[key], `superfast/jobs/${job._key}/checkout`);
    }

    setUploadProgress("Saving signature...");
    const sigDataUrl = canvas.toDataURL("image/png");
    const sigUrl = await uploadPhoto(sigDataUrl, `superfast/jobs/${job._key}/signature`);

    await dbPatch(`jobs/${job._key}`, {
      ...urls,
      signatureUrl: sigUrl,
      signedBy: job.customer,
      status: "completed",
      checkoutAt: Date.now(),
    });

    setJobs(prev => prev.map(j => j._key === job._key ? { ...j, status: "completed" } : j));
    stopTracking();
    setUploading(false);
    setUploadProgress("");
    setActiveJob(null);
    setJobScreen("detail");
    setPhotos({});
    setCheckoutPhotos({});
  }

  const filtered = filter === "active"
    ? jobs.filter(j => j.status !== "completed")
    : jobs.filter(j => j.status === "completed");

  // ── JOB DETAIL SCREEN ──
  if (activeJob) {
    const job = activeJob;
    const servicePhotoLabel = getServicePhotoLabel(job.service);
    const providerPay = getProviderPay(job);
    const isTireJob = job.service?.includes("Tire") && job.tire_option !== "spare";

    return (
      <div style={c.root}>
        <header style={{ ...c.header, background: "#0a1525", borderBottom: "3px solid #3b82f6" }}>
          <div style={c.logo}>
            <span style={c.flame}>🔥</span>
            <div><div style={c.logoTop}>SUPER FAST</div><div style={{ ...c.logoBot, color: "#3b82f6" }}>PROVIDER</div></div>
            <span style={c.flame}>🔥</span>
          </div>
          <button style={c.back} onClick={() => { setActiveJob(null); setJobScreen("detail"); setPhotos({}); setCheckoutPhotos({}); stopTracking(); }}>← Back</button>
        </header>

        <div style={c.page}>
          {/* JOB DETAIL */}
          {jobScreen === "detail" && (
            <>
              <div style={{ background: "#0a1525", border: "1px solid #1e3a5a", borderRadius: 12, padding: 16, marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 28 }}>{SERVICE_ICONS[job.service] || "🔧"}</span>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 900, color: "#fff" }}>{job.service}</div>
                    <div style={{ fontSize: 11, color: "#3b82f6" }}>{job.id}</div>
                  </div>
                  <div style={{ marginLeft: "auto", ...pv.pill, background: STATUS_META[job.status]?.bg || "#1a1a1a", color: STATUS_META[job.status]?.color || "#888" }}>
                    {STATUS_META[job.status]?.label || job.status}
                  </div>
                </div>
                {[
                  ["👤 Customer", job.customer],
                  ["📞 Phone", job.phone, true],
                  ...(job.vehicle ? [["🚗 Vehicle", job.vehicle]] : []),
                  ...(job.tire_size ? [["🛞 Tire Size", job.tire_size]] : []),
                  ...(job.tire_qty ? [["🔢 Qty", `${job.tire_qty} tire${job.tire_qty > 1 ? "s" : ""}`]] : []),
                  ...(job.note ? [["📝 Note", job.note]] : []),
                ].map(([k, v, isPhone]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #1a2a3a", fontSize: 13 }}>
                    <span style={{ color: "#666" }}>{k}</span>
                    {isPhone ? <a href={`tel:${v}`} style={{ color: "#E63329", fontWeight: 700 }}>{v}</a> : <span style={{ color: "#ccc", maxWidth: "55%", textAlign: "right" }}>{v}</span>}
                  </div>
                ))}
              </div>

              {/* Provider pay */}
              <div style={{ background: providerPay.pay ? "#0a1f0a" : "#1a0a00", border: `1px solid ${providerPay.pay ? "#22c55e" : "#f59e0b"}`, borderRadius: 8, padding: "12px 14px", marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 11, color: providerPay.pay ? "#4caf50" : "#f59e0b", fontWeight: 700, marginBottom: 2 }}>
                    {providerPay.pay ? "💰 YOUR PAY FOR THIS JOB" : "💵 COLLECT PAYMENT ON ARRIVAL"}
                  </div>
                  {providerPay.breakdown && (
                    <div style={{ fontSize: 11, color: "#22c55e", marginBottom: 2 }}>{providerPay.breakdown}</div>
                  )}
                  <div style={{ fontSize: 11, color: "#aaa" }}>{providerPay.note}</div>
                </div>
                {providerPay.pay && <div style={{ fontSize: 28, fontWeight: 900, color: "#22c55e" }}>${providerPay.pay}</div>}
              </div>

                {/* Tire stock warning */}
                {isTireJob && (
                  <div style={{ background: "#1a0a00", border: "1px solid #f59e0b", borderRadius: 8, padding: "12px 14px", marginTop: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 900, color: "#f59e0b", marginBottom: 4 }}>⚠️ Tire Stock Required</div>
                    <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.5 }}>
                      You must have a used tire in stock to complete this job. If you do not have the tire, contact admin before accepting. Do not dispatch without the tire.
                    </div>
                  </div>
                )}
                {job.wheel_lock && (
                  <div style={{ background: "#1a0a00", border: "1px solid #E63329", borderRadius: 8, padding: "12px 14px", marginTop: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 900, color: "#E63329", marginBottom: 4 }}>🔐 Wheel Lock Removal Required</div>
                    <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.5 }}>
                      Customer has lost their wheel lock key. You must remove the wheel lock before changing the tire. Additional $60 fee applies.
                    </div>
                  </div>
                )}
                {job.battery_upgrade && (
                  <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 8, padding: "12px 14px", marginTop: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 900, color: "#3b82f6", marginBottom: 4 }}>🔋 Battery Replacement Add-on</div>
                    <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.5 }}>
                      Customer added battery replacement. Attempt jump start first — if battery cannot hold charge, proceed with battery replacement. New battery required.
                    </div>
                  </div>
                )}
                {/* Battery receipt upload */}
                {providerPay.showReceiptUpload && (
                  <div style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: 8, padding: "12px 14px", marginTop: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", marginBottom: 6, letterSpacing: 1 }}>📄 BATTERY RECEIPT — UPLOAD FOR REIMBURSEMENT</div>
                    {job.batteryReceiptUrl ? (
                      <div>
                        <div style={{ fontSize: 12, color: "#22c55e", marginBottom: 8 }}>✅ Receipt uploaded</div>
                        <img src={job.batteryReceiptUrl} alt="receipt" style={{ width: "100%", borderRadius: 6, maxHeight: 200, objectFit: "cover" }} />
                      </div>
                    ) : (
                      <button style={{ ...c.gpsBtn, border: "2px dashed #f59e0b", color: "#f59e0b" }}
                        onClick={async () => {
                          const input = document.createElement("input");
                          input.type = "file";
                          input.accept = "image/*";
                          input.capture = "environment";
                          input.onchange = async e => {
                            const file = e.target.files[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = async ev => {
                              try {
                                setUploadProgress("Uploading receipt...");
                                setUploading(true);
                                const url = await uploadPhoto(ev.target.result, `superfast/jobs/${job._key}/battery_receipt`);
                                await dbPatch(`jobs/${job._key}`, { batteryReceiptUrl: url, batteryReceiptAt: Date.now() });
                                setActiveJob(prev => ({ ...prev, batteryReceiptUrl: url }));
                                
                                // Notify admin
                                await fetch(`${WORKER_URL}/notify`, {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({
                                    title: "📄 Battery Receipt Uploaded",
                                    message: `${profile.firstName} ${profile.lastName} uploaded battery receipt for reimbursement.\nJob: ${job.id}\nCustomer: ${job.customer}`,
                                  }),
                                }).catch(() => {});
                                
                                setUploading(false);
                                setUploadProgress("");
                              } catch (err) {
                                setUploading(false);
                                alert("Upload failed: " + err.message);
                              }
                            };
                            reader.readAsDataURL(file);
                          };
                          input.click();
                        }}>
                        📷 Take Photo of Battery Receipt
                      </button>
                    )}
                    <div style={{ fontSize: 10, color: "#555", marginTop: 6 }}>
                      Admin will reimburse battery cost after reviewing your receipt
                    </div>
                  </div>
                )}
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.address)}&travelmode=driving`}
                target="_blank" rel="noreferrer"
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e", borderRadius: 10, padding: "14px", fontSize: 15, fontWeight: 800, textDecoration: "none", marginBottom: 12 }}>
                🗺️ Open GPS Navigation →
              </a>

              {/* En Route */}
              {job.status === "assigned" && (
                <button style={{ ...c.btnPrimary, background: "#3b82f6", marginBottom: 12 }}
                  onClick={async () => {
                    await dbPatch(`jobs/${job._key}`, { status: "en_route" });
                    setActiveJob(prev => ({ ...prev, status: "en_route" }));
                    setJobs(prev => prev.map(j => j._key === job._key ? { ...j, status: "en_route" } : j));
                    startTracking(job._key);
                  }}>
                  🛻 I'm En Route — Start GPS Tracking
                </button>
              )}

              {/* Check In */}
              {job.status === "en_route" && (
                <button style={{ ...c.btnPrimary, background: "#f59e0b", marginBottom: 12 }}
                  onClick={() => { setJobScreen("checkin"); }}>
                  📍 Check In — I've Arrived
                </button>
              )}

              {/* ── ADD A SERVICE ON SCENE ── */}
              {(job.status === "on_scene" || job.status === "en_route") && !job.addonPending && !job.addonPaid && (
                <div style={{ background: "#161616", border: "1px solid #f59e0b", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 7 }}>
                    ➕ NEED TO ADD A SERVICE?
                  </div>
                  <div style={{ fontSize: 12, color: "#999", lineHeight: 1.6, marginBottom: 12 }}>
                    Found something else while you're out there? Add it at a discounted ${ADDON_PRICE} for the customer — you earn an extra <strong style={{ color: "#22c55e" }}>${ADDON_PROVIDER_BONUS}</strong>.
                  </div>
                  {!showAddons ? (
                    <button style={{ width: "100%", background: "#1a1400", border: "1px solid #f59e0b", color: "#f59e0b", padding: "12px", borderRadius: 8, fontWeight: 800, fontSize: 13.5, cursor: "pointer" }}
                      onClick={() => setShowAddons(true)}>
                      ➕ Add a Service
                    </button>
                  ) : (
                    <>
                      {ADDONS.filter(a => !(job.service || "").toLowerCase().includes(a.id)).map(a => (
                        <button key={a.id}
                          onClick={async () => {
                            if (!window.confirm(`Add ${a.label.replace(/^[^\s]+\s/, "")} for $${ADDON_PRICE}? The customer will be asked to approve and pay.`)) return;
                            const patch = {
                              addonPending: true,
                              addonId: a.id,
                              addonLabel: a.label,
                              addonPrice: ADDON_PRICE,
                              addonBonus: ADDON_PROVIDER_BONUS,
                              addonRequestedAt: Date.now(),
                              addonRequestedBy: `${profile.firstName} ${profile.lastName}`,
                            };
                            await dbPatch(`jobs/${job._key}`, patch);
                            setActiveJob(prev => ({ ...prev, ...patch }));
                            setShowAddons(false);
                            const who = job.partnerId ? job.partnerBusiness : job.customer;
                            await fetch(`${WORKER_URL}/notify`, {
                              method: "POST", headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                title: `➕ Add-On Requested — $${ADDON_PRICE}`,
                                message: `${profile.firstName} ${profile.lastName} added ${a.label} on scene.\n${who} · ${job.address}\nJob ${job.id}\n\nWaiting on their approval and payment.`,
                              }),
                            }).catch(() => {});
                            alert("✅ Sent. The customer will see it in their app to approve and pay.");
                          }}
                          style={{ width: "100%", textAlign: "left", background: "#0f0f0f", border: "1px solid #2a2a2a", borderRadius: 8, padding: "12px 13px", marginBottom: 7, cursor: "pointer" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 13.5, color: "#eee", fontWeight: 700 }}>{a.label}</span>
                            <span style={{ fontSize: 13, color: "#22c55e", fontWeight: 900 }}>${ADDON_PRICE}</span>
                          </div>
                          <div style={{ fontSize: 11, color: "#666", marginTop: 3 }}>{a.why}</div>
                        </button>
                      ))}
                      <button style={{ width: "100%", background: "none", border: "1px solid #333", color: "#888", padding: "10px", borderRadius: 8, fontSize: 12.5, cursor: "pointer" }}
                        onClick={() => setShowAddons(false)}>Cancel</button>
                    </>
                  )}
                </div>
              )}

              {job.addonPending && !job.addonPaid && (
                <div style={{ background: "#1a1400", border: "1px solid #f59e0b", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 900, color: "#f59e0b", marginBottom: 5 }}>
                    ⏳ {job.addonLabel} — awaiting payment
                  </div>
                  <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.6, marginBottom: 11 }}>
                    They've been asked to approve ${job.addonPrice}. Once they pay, you'll see it here and can do the work. Your pay goes up ${job.addonBonus}.
                  </div>
                  <button style={{ width: "100%", background: "#2a0a0a", border: "1px solid #E63329", color: "#E63329", padding: "10px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                    onClick={async () => {
                      if (!window.confirm("Cancel this add-on request?")) return;
                      await dbPatch(`jobs/${job._key}`, { addonPending: false, addonId: null, addonLabel: null, addonPrice: null, addonBonus: null });
                      setActiveJob(prev => ({ ...prev, addonPending: false, addonLabel: null }));
                    }}>
                    ↩️ Cancel Add-On
                  </button>
                </div>
              )}

              {job.addonPaid && (
                <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 900, color: "#22c55e", marginBottom: 4 }}>
                    ✅ {job.addonLabel} — PAID
                  </div>
                  <div style={{ fontSize: 12, color: "#4caf50", lineHeight: 1.55 }}>
                    Go ahead and do it. You've earned an extra ${job.addonBonus} on this job.
                  </div>
                </div>
              )}

              {/* Battery upgrade on-site for jump start jobs */}
              {job.status === "on_scene" && job.service?.includes("Jump") && !job.battery_upgrade && (
                <button style={{ width: "100%", background: "#0a1525", border: "1px solid #3b82f6", color: "#3b82f6", borderRadius: 8, padding: "12px", cursor: "pointer", fontWeight: 700, fontSize: 13, marginBottom: 12 }}
                  onClick={async () => {
                    if (!window.confirm("Battery can't be jumped? Send customer a $400 battery replacement payment link?")) return;
                    
                    // Create a new payment link via worker
                    const res = await fetch(`${WORKER_URL}/create-payment-intent`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        amount: parseFloat((400 * 1.07).toFixed(2)),
                        jobId: job.id,
                        customerName: job.customer,
                        service: "Battery Replacement Upgrade",
                      }),
                    });
                    const data = await res.json();

                    // Update job with upgrade pending
                    await dbPatch(`jobs/${job._key}`, {
                      battery_upgrade_pending: true,
                      battery_upgrade_amount: parseFloat((400 * 1.07).toFixed(2)),
                      battery_upgrade_requested_at: Date.now(),
                      battery_upgrade_by: `${profile.firstName} ${profile.lastName}`,
                    });

                    // Notify admin via Pushover
                    try {
                      const notifyRes = await fetch(`${WORKER_URL}/notify`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          title: "🔋 Battery Upgrade Requested On-Site",
                          message: `${profile.firstName} ${profile.lastName} could not jump start the vehicle.\nCustomer: ${job.customer}\n📍 ${job.address}\nBattery replacement needed — $428.00\nJob ID: ${job.id}\nCustomer needs to pay via app tracking screen.`,
                        }),
                      });
                      console.log("Notify response:", await notifyRes.text());
                    } catch(e) { console.log("Notify error:", e); }

                    setActiveJob(prev => ({ ...prev, battery_upgrade_pending: true }));
                    alert("✅ Admin has been notified. Customer will see payment option on their tracking screen.");
                  }}>
                  🔋 Battery Dead — Request Upgrade ($428.00)
                </button>
              )}

              {job.battery_upgrade_pending && !job.battery_upgrade_paid && (
                <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 8, padding: 14, marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 900, color: "#3b82f6", marginBottom: 6 }}>🔋 Battery Upgrade Pending Payment</div>
                  <div style={{ fontSize: 12, color: "#aaa", marginBottom: 10 }}>Admin notified. Customer will see payment on their tracking screen.</div>
                  <button style={{ width: "100%", background: "#2a0a0a", border: "1px solid #E63329", color: "#E63329", padding: "10px", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 12 }}
                    onClick={async () => {
                      if (!window.confirm("Cancel the battery upgrade request?")) return;
                      await dbPatch(`jobs/${job._key}`, {
                        battery_upgrade_pending: false,
                        battery_upgrade_cancelled: true,
                        battery_upgrade_cancelled_at: Date.now(),
                      });
                      setActiveJob(prev => ({ ...prev, battery_upgrade_pending: false }));
                      await fetch(`${WORKER_URL}/notify`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          title: "🔋 Battery Upgrade Cancelled",
                          message: `${profile.firstName} ${profile.lastName} cancelled the battery upgrade request for Job ${job.id} — ${job.customer}.`,
                        }),
                      }).catch(() => {});
                    }}>
                    ↩️ Cancel Battery Upgrade Request
                  </button>
                </div>
              )}

              {/* Check Out — show for on_scene AND tow_requested */}
              {(job.status === "on_scene" || job.status === "tow_requested") && !job.battery_upgrade_pending && (
                <button style={{ ...c.btnPrimary, background: "#22c55e", marginBottom: 12 }}
                  onClick={() => setJobScreen("checkout")}>
                  ✅ Check Out — Job Done
                </button>
              )}

              {job.status === "completed" && (
                <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: 16, textAlign: "center", color: "#22c55e", fontWeight: 800 }}>
                  ✅ Job Complete
                  {job.checkoutAt && <div style={{ fontSize: 11, fontWeight: 400, color: "#4caf50", marginTop: 4 }}>Completed {timeAgo(job.checkoutAt)}</div>}
                </div>
              )}

              {/* Can't Complete */}
              {job.status !== "completed" && job.status !== "tow_requested" && job.status !== "returned" && !job.service?.includes("Tow") && (
                <div style={{ marginTop: 16, borderTop: "1px solid #1a1a1a", paddingTop: 16 }}>
                  <button style={{ width: "100%", background: "#1a0a00", border: "1px solid #E63329", color: "#E63329", borderRadius: 8, padding: "12px", cursor: "pointer", fontWeight: 700, fontSize: 13 }}
                    onClick={async () => {
                      const isMechanic = job.service?.includes("Mechanic");
                      const msg = isMechanic
                        ? "Can't complete this job? It will be returned to dispatch and admin will be notified."
                        : "Can't complete this job? This will notify admin and request a tow truck for this location.";
                      if (!window.confirm(msg)) return;

                      if (isMechanic) {
                        // Return to dispatch — no tow needed
                        await dbPatch(`jobs/${job._key}`, {
                          status: "returned",
                          returnedAt: Date.now(),
                          returnedBy: `${profile.firstName} ${profile.lastName}`,
                          returnReason: "Mechanic could not complete job",
                        });

                        // Notify admin via Pushover
                        await fetch(`${WORKER_URL}/notify`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            title: "🔧 Mechanic Job Returned to Dispatch",
                            message: `${profile.firstName} ${profile.lastName} could not complete the Mobile Mechanic job.\nJob ${job.id}\nCustomer: ${job.customer}\n📍 ${job.address}\nJob returned to New — reassignment needed.`,
                          }),
                        }).catch(() => {});

                        // Return to new for reassignment
                        await dbPatch(`jobs/${job._key}`, { status: "new", tech: null, techName: null, techEmail: null });
                        setJobs(prev => prev.filter(j => j._key !== job._key));
                        setActiveJob(null);
                        alert("Job returned to dispatch. Admin has been notified.");
                      } else {
                        // All other services — create tow request
                        await dbPatch(`jobs/${job._key}`, {
                          status: "tow_requested",
                          towRequestedAt: Date.now(),
                          towRequestedBy: `${profile.firstName} ${profile.lastName}`,
                        });

                        const towJob = {
                          id: genId(),
                          service: "Tow Truck",
                          customer: job.customer,
                          phone: job.phone,
                          address: job.address,
                          note: `⚠️ ESCALATED — Original job ${job.id} (${job.service}) could not be completed by ${profile.firstName} ${profile.lastName}. Tow requested.`,
                          price: parseFloat((25 * 1.07).toFixed(2)),
                          dispatch_fee: 25,
                          tire_option: "tow",
                          originalJobId: job.id,
                          status: "new",
                          paid: false,
                          tech: null, techName: null,
                          createdAt: Date.now(),
                          customerId: job.customerId || null,
                        };
                        await dbPush("jobs", towJob);

                        setActiveJob(prev => ({ ...prev, status: "tow_requested" }));
                        setJobs(prev => prev.map(j => j._key === job._key ? { ...j, status: "tow_requested" } : j));
                        alert("Admin has been notified. A tow truck request has been created for this location.");
                      }
                    }}>
                    {job.service?.includes("Mechanic") ? "↩️ Can't Complete — Return to Dispatch" : "🚛 Can't Complete — Request Tow Truck"}
                  </button>
                  <div style={{ fontSize: 10, color: "#555", textAlign: "center", marginTop: 6 }}>
                    {job.service?.includes("Mechanic")
                      ? "This will notify admin and return the job for reassignment"
                      : "This will notify admin and create a tow truck dispatch request"}
                  </div>
                </div>
              )}

              {job.status === "tow_requested" && (
                <div style={{ background: "#1a0a00", border: "1px solid #E63329", borderRadius: 10, padding: 16, textAlign: "center", marginTop: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: "#E63329", marginBottom: 4 }}>🚛 Tow Requested</div>
                  <div style={{ fontSize: 12, color: "#aaa" }}>Admin has been notified. Tow truck is being dispatched to this location.</div>
                </div>
              )}
            </>
          )}

          {/* CHECK IN PHOTOS */}
          {jobScreen === "checkin" && (
            <>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", marginBottom: 4 }}>📸 Check In Photos</div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>Take all required photos before starting the job</div>

              {[...PHOTO_SLOTS, { key: "service", label: servicePhotoLabel, icon: "🔧" }].map(slot => (
                <div key={slot.key} style={{ marginBottom: 14 }}>
                  <label style={{ ...c.label, marginBottom: 8 }}>{slot.icon} {slot.label}</label>
                  {photos[slot.key] ? (
                    <div style={{ position: "relative" }}>
                      <img src={photos[slot.key]} alt={slot.label} style={{ width: "100%", borderRadius: 8, maxHeight: 180, objectFit: "cover" }} />
                      <button onClick={() => setPhotos(p => ({ ...p, [slot.key]: null }))}
                        style={{ position: "absolute", top: 8, right: 8, background: "#E63329", border: "none", color: "#fff", borderRadius: "50%", width: 28, height: 28, cursor: "pointer", fontSize: 14 }}>✕</button>
                    </div>
                  ) : (
                    <button style={{ ...c.gpsBtn, border: "2px dashed #2a5a2a" }} onClick={() => takePhoto(slot.key)}>
                      📷 Take Photo
                    </button>
                  )}
                </div>
              ))}

              {uploading && (
                <div style={{ textAlign: "center", color: "#f59e0b", padding: 16 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
                  {uploadProgress}
                </div>
              )}

              {!uploading && (
                <>
                  <button style={{ ...c.btnPrimary, background: "#f59e0b" }} onClick={handleCheckIn}>
                    Submit Check In →
                  </button>
                  <button style={c.btnSecondary} onClick={() => setJobScreen("detail")}>← Back</button>
                </>
              )}
            </>
          )}

          {/* CHECK OUT PHOTOS */}
          {jobScreen === "checkout" && (
            <>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", marginBottom: 4 }}>📸 Check Out Photos</div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>Take after photos showing the completed work</div>

              {[...PHOTO_SLOTS, { key: "service", label: `After: ${servicePhotoLabel}`, icon: "✅" }].map(slot => (
                <div key={slot.key} style={{ marginBottom: 14 }}>
                  <label style={{ ...c.label, marginBottom: 8 }}>{slot.icon} {slot.label}</label>
                  {checkoutPhotos[slot.key] ? (
                    <div style={{ position: "relative" }}>
                      <img src={checkoutPhotos[slot.key]} alt={slot.label} style={{ width: "100%", borderRadius: 8, maxHeight: 180, objectFit: "cover" }} />
                      <button onClick={() => setCheckoutPhotos(p => ({ ...p, [slot.key]: null }))}
                        style={{ position: "absolute", top: 8, right: 8, background: "#E63329", border: "none", color: "#fff", borderRadius: "50%", width: 28, height: 28, cursor: "pointer", fontSize: 14 }}>✕</button>
                    </div>
                  ) : (
                    <button style={{ ...c.gpsBtn, border: "2px dashed #2a5a2a" }} onClick={() => takePhoto(slot.key, true)}>
                      📷 Take Photo
                    </button>
                  )}
                </div>
              ))}

              {!uploading && (
                <>
                  <button style={{ ...c.btnPrimary, background: "#22c55e" }} onClick={handleCheckOut}>
                    Continue to Customer Signature →
                  </button>
                  <button style={c.btnSecondary} onClick={() => setJobScreen("detail")}>← Back</button>
                </>
              )}
            </>
          )}

          {/* CUSTOMER SIGNATURE */}
          {jobScreen === "signature" && (
            <>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", marginBottom: 4 }}>✍️ Customer Signature</div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>Have the customer review and sign below</div>

              <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>SERVICE COMPLETED</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>{job.service}</div>
                <div style={{ fontSize: 13, color: "#aaa", marginTop: 4 }}>{job.address}</div>
              </div>

              <div style={{ fontSize: 11, letterSpacing: 2, color: "#666", fontWeight: 700, marginBottom: 8 }}>CUSTOMER NAME</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", marginBottom: 16, padding: "12px 16px", background: "#1a1a1a", borderRadius: 8 }}>
                {job.customer}
              </div>

              <div style={{ fontSize: 11, letterSpacing: 2, color: "#666", fontWeight: 700, marginBottom: 8 }}>CUSTOMER SIGNATURE</div>
              <div style={{ border: "2px solid #2a2a2a", borderRadius: 8, overflow: "hidden", marginBottom: 8, touchAction: "none" }}>
                <canvas
                  ref={sigCanvasRef}
                  width={380}
                  height={160}
                  style={{ display: "block", width: "100%", height: 160, background: "#1a1a1a" }}
                  onMouseDown={sigStart} onMouseMove={sigMove} onMouseUp={sigEnd}
                  onTouchStart={sigStart} onTouchMove={sigMove} onTouchEnd={sigEnd}
                />
              </div>
              <button style={{ ...c.btnSecondary, fontSize: 12, marginBottom: 16 }} onClick={clearSig}>Clear Signature</button>

              {sigError && <div style={c.gpsError}>⚠️ {sigError}</div>}

              {uploading ? (
                <div style={{ textAlign: "center", color: "#22c55e", padding: 16 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
                  {uploadProgress}
                </div>
              ) : (
                <>
                  <button style={{ ...c.btnPrimary, background: "#22c55e", fontSize: 15 }} onClick={handleSubmitComplete}>
                    ✅ Submit — Mark Job Complete
                  </button>
                  <button style={c.btnSecondary} onClick={() => setJobScreen("checkout")}>← Back</button>
                </>
              )}
            </>
          )}
        </div>
        <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC · South Florida</div>
      </div>
    );
  }

  // ── JOB LIST ──
  return (
    <div style={c.root}>
      <header style={{ ...c.header, background: "#0a1525", borderBottom: "3px solid #3b82f6" }}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div><div style={c.logoTop}>SUPER FAST</div><div style={{ ...c.logoBot, color: "#3b82f6" }}>PROVIDER</div></div>
          <span style={c.flame}>🔥</span>
        </div>
        <button style={c.back} onClick={onSignOut}>Sign Out</button>
      </header>

      {/* GPS CONSENT SCREEN */}
      {!gpsConsent ? (
        <div style={c.page}>
          <div style={{ textAlign: "center", padding: "32px 0 24px" }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>📍</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", marginBottom: 10 }}>Location Access Required</div>
            <div style={{ fontSize: 14, color: "#aaa", lineHeight: 1.7, marginBottom: 28 }}>
              Super Fast Roadside requires access to your location at all times while you are logged in as a provider. This allows dispatch to track your location, assign you nearby jobs, and verify your arrival at customer locations.
            </div>
          </div>
          <div style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: 12, padding: 16, marginBottom: 20 }}>
            {[
              ["📍", "Real-time location tracking while logged in"],
              ["🛻", "Faster job assignments based on your position"],
              ["✅", "Automatic arrival verification for customers"],
              ["🔒", "Location data is only shared with Super Fast Roadside dispatch"],
            ].map(([icon, text]) => (
              <div key={text} style={{ display: "flex", gap: 12, alignItems: "center", padding: "10px 0", borderBottom: "1px solid #1a1a1a" }}>
                <span style={{ fontSize: 20 }}>{icon}</span>
                <span style={{ fontSize: 13, color: "#aaa" }}>{text}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#555", textAlign: "center", marginBottom: 20, lineHeight: 1.6 }}>
            By tapping Accept you agree to share your location with Super Fast Roadside while logged in. You can withdraw consent at any time by logging out.
          </div>
          {consentErr && (
            <div style={{ background: "#1a0a00", border: "1px solid #E63329", borderRadius: 10, padding: 14, marginBottom: 14, textAlign: "left" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#E63329", marginBottom: 7 }}>
                {consentErr === "denied" ? "Location is blocked on this device" : "Couldn't get your location"}
              </div>
              {isIOS() ? (
                <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.75 }}>
                  <strong style={{ color: "#fff" }}>1.</strong> Open <strong style={{ color: "#fff" }}>Settings</strong> on your iPhone<br />
                  <strong style={{ color: "#fff" }}>2.</strong> Go to <strong style={{ color: "#fff" }}>Privacy &amp; Security → Location Services</strong> — make sure it's <strong style={{ color: "#fff" }}>ON</strong><br />
                  <strong style={{ color: "#fff" }}>3.</strong> Scroll down that same list, tap <strong style={{ color: "#fff" }}>Safari Websites</strong>, choose <strong style={{ color: "#fff" }}>While Using the App</strong><br />
                  <strong style={{ color: "#fff" }}>4.</strong> Back in Settings, go to <strong style={{ color: "#fff" }}>Apps → Safari → Location</strong> and set it to <strong style={{ color: "#fff" }}>Ask</strong> or <strong style={{ color: "#fff" }}>Allow</strong><br />
                  <strong style={{ color: "#fff" }}>5.</strong> Fully close Safari (swipe up), reopen the app, and tap Accept again
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.7 }}>
                  Tap the <strong style={{ color: "#fff" }}>lock icon</strong> next to the address bar → <strong style={{ color: "#fff" }}>Permissions</strong> → set <strong style={{ color: "#fff" }}>Location</strong> to Allow, then reload and tap Accept again.
                </div>
              )}
              <div style={{ fontSize: 11.5, color: "#888", marginTop: 12, lineHeight: 1.55 }}>
                Still stuck? Call dispatch at <a href="tel:5612558506" style={{ color: "#E63329", fontWeight: 700 }}>(561) 255-8506</a> — you can still be dispatched manually.
              </div>
            </div>
          )}

          <button style={{ ...c.btnPrimary, background: "#3b82f6", fontSize: 16, opacity: consentBusy ? 0.6 : 1 }}
            disabled={consentBusy}
            onClick={async () => {
              setConsentBusy(true);
              setConsentErr("");
              const r = await requestLocation();     // has a 20s timeout, always resolves
              setConsentBusy(false);
              if (r.ok) {
                localStorage.setItem(`sfrs_gps_consent_${authUser?.uid}`, "true");
                dbPatch(`users/${authUser.uid}`, { gpsConsentAccepted: true, gpsConsentAt: Date.now() });
                setGpsConsent(true);
                return;
              }
              setConsentErr(r.reason);
            }}>
            {consentBusy ? "Waiting for your phone..." : "✅ Accept & Enable Location"}
          </button>

          {consentErr && (
            <button style={{ ...c.btnSecondary, borderColor: "#f59e0b", color: "#f59e0b" }}
              onClick={() => {
                localStorage.setItem(`sfrs_gps_consent_${authUser?.uid}`, "true");
                dbPatch(`users/${authUser.uid}`, { gpsConsentAccepted: true, gpsConsentSkipped: true, gpsConsentAt: Date.now() });
                setGpsConsent(true);
              }}>
              Continue Without Location For Now →
            </button>
          )}

          <button style={c.btnSecondary} onClick={onSignOut}>Decline & Sign Out</button>
          <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC · South Florida</div>
        </div>
      ) : (
        <div style={c.page}>

          {/* INCOMING JOB ALERT */}
          {incomingJob && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
              <div style={{ background: "#111", border: "2px solid #E63329", borderRadius: 16, padding: 24, width: "100%", maxWidth: 400, textAlign: "center" }}>
                {/* Flashing ring */}
                <div style={{ fontSize: 56, marginBottom: 8, animation: "pulse 1s infinite" }}>🔔</div>
                <div style={{ fontSize: 11, letterSpacing: 3, color: "#E63329", fontWeight: 700, marginBottom: 8 }}>NEW JOB ALERT</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 4 }}>{incomingJob.service}</div>
                <div style={{ fontSize: 13, color: "#aaa", marginBottom: 4 }}>{incomingJob.customer}</div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 16 }}>📍 {incomingJob.address}</div>

                {/* Provider pay */}
                <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 8, padding: "10px 14px", marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: "#4caf50", marginBottom: 2 }}>💰 YOUR PAY</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: "#22c55e" }}>
                    {getProviderPay(incomingJob).pay ? `$${getProviderPay(incomingJob).pay}` : "Collect on arrival"}
                  </div>
                  {getProviderPay(incomingJob).breakdown && (
                    <div style={{ fontSize: 10, color: "#4caf50", marginTop: 2 }}>{getProviderPay(incomingJob).breakdown}</div>
                  )}
                </div>

                {/* Countdown ring */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 36, fontWeight: 900, color: countdown <= 10 ? "#E63329" : "#f59e0b" }}>{countdown}</div>
                  <div style={{ fontSize: 11, color: "#555" }}>seconds to respond</div>
                  <div style={{ height: 4, background: "#1a1a1a", borderRadius: 2, marginTop: 8, overflow: "hidden" }}>
                    <div style={{ height: "100%", background: countdown <= 10 ? "#E63329" : "#f59e0b", width: `${(countdown / 60) * 100}%`, transition: "width 1s linear, background 0.3s" }} />
                  </div>
                </div>

                {/* Tire size check for need_tire jobs */}
                {incomingJob.tire_option === "need_tire" && incomingJob.tire_size && (
                  <div style={{ background: "#1a0a00", border: "1px solid #f59e0b", borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700, marginBottom: 4 }}>🛞 TIRE SIZE REQUIRED</div>
                    <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 4 }}>{incomingJob.tire_size}</div>
                    <div style={{ fontSize: 11, color: "#aaa" }}>
                      {incomingJob.tire_qty > 1 ? `${incomingJob.tire_qty} tires needed` : "1 tire needed"}
                      {incomingJob.vehicle ? ` · ${incomingJob.vehicle}` : ""}
                    </div>
                    <div style={{ fontSize: 12, color: "#f59e0b", marginTop: 8, fontWeight: 700 }}>
                      ⚠️ Only accept if you have this tire in stock
                    </div>
                  </div>
                )}

                {/* Accept / Decline */}
                <div style={{ display: "flex", gap: 12 }}>
                  <button style={{ flex: 1, background: "#E63329", border: "none", color: "#fff", padding: "14px", borderRadius: 8, fontSize: 15, fontWeight: 900, cursor: "pointer" }}
                    onClick={async () => {
                      clearInterval(countdownRef.current);
                      // Send Pushover to admin if tire job declined due to no tire
                      if (incomingJob.tire_option === "need_tire") {
                        await fetch(`${WORKER_URL}/notify`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            title: "🛞 Provider Declined — No Tire in Stock",
                            message: `${profile.firstName} ${profile.lastName} does not have ${incomingJob.tire_size || "the required tire"} in stock.\nJob ${incomingJob.id} returned to New.\nCustomer: ${incomingJob.customer}\n📍 ${incomingJob.address}`,
                          }),
                        }).catch(() => {});
                      }
                      await declineJob(incomingJob);
                    }}>
                    ❌ {incomingJob.tire_option === "need_tire" ? "No Tire — Decline" : "Decline"}
                  </button>
                  <button style={{ flex: 1, background: "#22c55e", border: "none", color: "#fff", padding: "14px", borderRadius: 8, fontSize: 15, fontWeight: 900, cursor: "pointer" }}
                    onClick={() => acceptJob(incomingJob)}>
                    ✅ {incomingJob.tire_option === "need_tire" ? "Have Tire — Accept" : "Accept"}
                  </button>
                </div>
              </div>
            </div>
          )}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", marginBottom: 4 }}>
              👋 {profile.firstName} {profile.lastName}
            </div>
            <div style={{ fontSize: 12, color: "#666" }}>Your assigned jobs</div>
          </div>

        {/* ── PERSISTENT BANK VERIFICATION BANNER ── */}
        {!profile.payoutsEnabled && (
          <div style={{ margin: "0 16px 12px", background: "#1a0a00", border: "2px solid #E63329", borderRadius: 12, padding: 15 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 7 }}>
              <span style={{ fontSize: 20 }}>🏦</span>
              <div style={{ fontSize: 14, fontWeight: 900, color: "#E63329" }}>Action Required — Verify Your Bank</div>
            </div>
            <div style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.6, marginBottom: 12 }}>
              We've moved to automatic direct deposit through Stripe. Verify your identity and connect your bank account so we can pay you every Friday. <strong style={{ color: "#fff" }}>You can't go online or get paid until this is done.</strong>
            </div>
            <div style={{ fontSize: 11.5, color: "#888", marginBottom: 12, lineHeight: 1.5 }}>
              Takes about 3 minutes. You'll need a photo ID, the last 4 of your SSN, and your bank routing + account number.
            </div>
            <button style={{ width: "100%", background: "#E63329", border: "none", color: "#fff", padding: "14px", borderRadius: 9, fontWeight: 900, fontSize: 14.5, cursor: "pointer" }}
              onClick={startConnectOnboarding} disabled={connectLoading}>
              {connectLoading ? "Opening Stripe..." : "🏦 Verify My Bank Account →"}
            </button>
            <div style={{ fontSize: 10.5, color: "#666", textAlign: "center", marginTop: 8, lineHeight: 1.5 }}>
              🔒 Handled entirely by Stripe — Super Fast never sees your SSN or bank details.
            </div>
          </div>
        )}

        {/* ── SERVICE AREA PROMPT ── */}
        {(!profile.city || !profile.state || !profile.serviceRadius) && (
          <div style={{ margin: "0 16px 12px", background: "#150a1f", border: "2px solid #a855f7", borderRadius: 12, padding: 15 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 7 }}>
              <span style={{ fontSize: 20 }}>📍</span>
              <div style={{ fontSize: 14, fontWeight: 900, color: "#a855f7" }}>Set Your Service Area</div>
            </div>
            <div style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.6, marginBottom: 12 }}>
              We're expanding nationwide. Tell us your city, state, and how far you'll travel so we only send you jobs you can reach.
            </div>
            <button style={{ width: "100%", background: "#a855f7", border: "none", color: "#fff", padding: "13px", borderRadius: 9, fontWeight: 900, fontSize: 14, cursor: "pointer" }}
              onClick={() => setFilter("more")}>
              📍 Set My Service Area →
            </button>
          </div>
        )}

        {/* ── GO ONLINE / OFFLINE ── */}
        <div style={{ padding: "0 16px", marginBottom: 14 }}>
          {!profile.payoutsEnabled ? (
            <div style={{ background: "#1a1000", border: "2px solid #f59e0b", borderRadius: 12, padding: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: "#f59e0b", marginBottom: 6 }}>🔒 Verification Required</div>
              <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.55, marginBottom: 12 }}>
                You must verify your identity and connect your bank account with Stripe before you can go online and receive jobs.
              </div>
              <button style={{ width: "100%", background: "#f59e0b", border: "none", color: "#000", padding: "13px", borderRadius: 9, fontWeight: 900, fontSize: 14, cursor: "pointer" }}
                onClick={startConnectOnboarding} disabled={connectLoading}>
                {connectLoading ? "Opening Stripe..." : "🏦 Verify With Stripe →"}
              </button>
            </div>
          ) : (
            <button
              disabled={toggling}
              onClick={isOnline ? goOffline : goOnline}
              style={{
                width: "100%",
                background: isOnline ? "#22c55e" : "#161616",
                border: `2px solid ${isOnline ? "#22c55e" : "#333"}`,
                color: isOnline ? "#000" : "#888",
                padding: "17px",
                borderRadius: 13,
                fontWeight: 900,
                fontSize: 16,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                transition: "all 0.2s",
              }}>
              <span style={{
                width: 11, height: 11, borderRadius: "50%",
                background: isOnline ? "#000" : "#555",
                boxShadow: isOnline ? "0 0 10px rgba(0,0,0,0.4)" : "none",
                animation: isOnline ? "sfPulse 2s infinite" : "none",
              }} />
              {toggling ? "..." : isOnline ? "YOU'RE ONLINE — TAP TO GO OFFLINE" : "GO ONLINE"}
            </button>
          )}

          <div style={{ fontSize: 11, color: isOnline ? "#22c55e" : "#555", textAlign: "center", marginTop: 8, lineHeight: 1.5 }}>
            {!profile.payoutsEnabled
              ? "Identity verification protects customers and lets us pay you."
              : isOnline
                ? "✅ Accepting jobs. You'll get a notification when one comes in."
                : !testPassed
                  ? "First time? We'll send a test job to make sure your notifications work."
                  : "You won't receive any job alerts while offline."}
          </div>
        </div>

        {/* ── GPS TRACKING CONSENT ── */}
        {showGpsConsent && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.96)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 18, overflowY: "auto" }}>
            <div style={{ background: "#111", border: "2px solid #3b82f6", borderRadius: 16, padding: 22, width: "100%", maxWidth: 400, margin: "auto" }}>
              <div style={{ fontSize: 19, fontWeight: 900, color: "#fff", marginBottom: 8, lineHeight: 1.25 }}>
                Share your live location with customers
              </div>
              <div style={{ fontSize: 12.5, color: "#888", lineHeight: 1.6, marginBottom: 18 }}>
                Before we ask for permission, here's exactly what we do with your location.
              </div>

              {[
                ["📍", <>While you're <strong style={{ color: "#fff" }}>online</strong>, Super Fast collects your device location — <strong style={{ color: "#fff" }}>including when the app is in the background or the screen is off</strong> — so customers can watch you approaching on their live tracking map.</>],
                ["🔔", <>Your location is only shared with the customer on your <strong style={{ color: "#fff" }}>active job</strong>, and with dispatch.</>],
                ["🛑", <>Tracking <strong style={{ color: "#fff" }}>stops the moment you go offline</strong> or complete the job.</>],
                ["⚙️", <>You can revoke location access any time in your phone's <strong style={{ color: "#fff" }}>Settings → Apps → Permissions</strong>.</>],
              ].map(([ic, text], i) => (
                <div key={i} style={{ display: "flex", gap: 12, marginBottom: 14, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{ic}</span>
                  <div style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.6 }}>{text}</div>
                </div>
              ))}

              <div style={{ background: "#0a1525", border: "1px solid #1e3a5a", borderRadius: 9, padding: 12, marginBottom: 16, fontSize: 11.5, color: "#888", lineHeight: 1.55 }}>
                Location sharing is required to accept jobs. Customers need to see their tech arriving, and we use it to dispatch the closest provider.
              </div>

              <button style={{ ...c.btnPrimary, background: "#3b82f6" }}
                disabled={gpsAsking}
                onClick={async () => {
                  setGpsAsking(true);
                  setGpsError("");
                  const r = await requestLocation();       // waits for the real answer
                  setGpsAsking(false);

                  if (!r.ok) {
                    setGpsError(r.reason);
                    return;                                 // stay on the modal, show help
                  }

                  localStorage.setItem(`sfrs_gps_ok_${authUser.uid}`, "true");
                  await dbPatch(`users/${authUser.uid}`, { gpsConsentAccepted: true, gpsConsentAt: Date.now() });
                  syncProviderProfile({ gpsConsentAccepted: true });
                  setLiveProfile(prev => ({ ...prev, gpsConsentAccepted: true }));
                  setShowGpsConsent(false);
                  goOnline();
                }}>
                {gpsAsking ? "Waiting for permission..." : "I Agree — Allow Location"}
              </button>

              {gpsError && (
                <div style={{ background: "#1a0a00", border: "1px solid #E63329", borderRadius: 10, padding: 14, marginTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#E63329", marginBottom: 7 }}>
                    {gpsError === "denied" ? "Location is blocked" : gpsError === "timeout" ? "Location timed out" : "Couldn't get your location"}
                  </div>
                  {gpsError === "denied" && isIOS() ? (
                    <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.7 }}>
                      On iPhone, turn it back on here:<br /><br />
                      <strong style={{ color: "#fff" }}>1.</strong> Open <strong style={{ color: "#fff" }}>Settings</strong><br />
                      <strong style={{ color: "#fff" }}>2.</strong> Scroll to <strong style={{ color: "#fff" }}>Safari</strong> (or this app if you added it to your Home Screen)<br />
                      <strong style={{ color: "#fff" }}>3.</strong> Tap <strong style={{ color: "#fff" }}>Location</strong> → choose <strong style={{ color: "#fff" }}>Ask</strong> or <strong style={{ color: "#fff" }}>Allow</strong><br />
                      <strong style={{ color: "#fff" }}>4.</strong> Also check <strong style={{ color: "#fff" }}>Settings → Privacy &amp; Security → Location Services</strong> is ON<br />
                      <strong style={{ color: "#fff" }}>5.</strong> Come back and tap Allow Location again
                    </div>
                  ) : gpsError === "denied" ? (
                    <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.7 }}>
                      Tap the <strong style={{ color: "#fff" }}>lock icon</strong> in your browser's address bar → <strong style={{ color: "#fff" }}>Permissions</strong> → set Location to <strong style={{ color: "#fff" }}>Allow</strong>, then try again.
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.6 }}>
                      Make sure Location Services is on and you have signal, then try again. If you're indoors, step outside for a moment.
                    </div>
                  )}
                  <button style={{ ...c.btnSecondary, marginTop: 12 }} onClick={() => setGpsError("")}>Try Again</button>
                </div>
              )}
              <button style={c.btnSecondary} onClick={() => setShowGpsConsent(false)}>Not now</button>
            </div>
          </div>
        )}

        {/* ── iOS: ADD TO HOME SCREEN ── */}
        {showIosInstall && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.96)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 18, overflowY: "auto" }}>
            <div style={{ background: "#111", border: "2px solid #3b82f6", borderRadius: 16, padding: 22, width: "100%", maxWidth: 400, margin: "auto" }}>
              <div style={{ fontSize: 40, textAlign: "center", marginBottom: 10 }}>📲</div>
              <div style={{ fontSize: 19, fontWeight: 900, color: "#fff", marginBottom: 8, textAlign: "center" }}>
                Add Super Fast to your Home Screen
              </div>
              <div style={{ fontSize: 12.5, color: "#999", lineHeight: 1.6, marginBottom: 18, textAlign: "center" }}>
                iPhone only delivers job alerts to apps installed on your Home Screen. Takes 15 seconds and you only do it once.
              </div>

              {[
                ["1", <>Tap the <strong style={{ color: "#fff" }}>Share button</strong> at the bottom of Safari — the square with an arrow pointing up ⬆️</>],
                ["2", <>Scroll down and tap <strong style={{ color: "#fff" }}>"Add to Home Screen"</strong></>],
                ["3", <>Tap <strong style={{ color: "#fff" }}>Add</strong> in the top right</>],
                ["4", <>Close Safari and open <strong style={{ color: "#fff" }}>Super Fast</strong> from your Home Screen icon</>],
                ["5", <>Sign in, tap Go Online, and tap <strong style={{ color: "#fff" }}>Allow</strong> when it asks about notifications</>],
              ].map(([n, t]) => (
                <div key={n} style={{ display: "flex", gap: 12, marginBottom: 13, alignItems: "flex-start" }}>
                  <div style={{ width: 25, height: 25, borderRadius: "50%", background: "#3b82f6", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 12, flexShrink: 0 }}>{n}</div>
                  <div style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.6 }}>{t}</div>
                </div>
              ))}

              <div style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 9, padding: 12, marginTop: 6, marginBottom: 16, fontSize: 11.5, color: "#f59e0b", lineHeight: 1.55 }}>
                ⚠️ You must use <strong>Safari</strong> for this — Chrome on iPhone can't add apps to the Home Screen.
              </div>

              <button style={{ ...c.btnPrimary, background: "#3b82f6" }}
                onClick={() => { setShowIosInstall(false); startTestJob(true); }}>
                Continue Anyway — Go Online Now
              </button>
              <button style={c.btnSecondary} onClick={() => setShowIosInstall(false)}>
                I'll install it first
              </button>
              <div style={{ fontSize: 11, color: "#666", textAlign: "center", marginTop: 12, lineHeight: 1.55 }}>
                You can work either way — installing just means your phone can alert you when the app is closed.
              </div>
            </div>
          </div>
        )}

        {/* ── TEST JOB MODAL ── */}
        {testJob && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.96)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ background: "#111", border: "2px solid #f59e0b", borderRadius: 16, padding: 24, width: "100%", maxWidth: 380 }}>
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <div style={{ fontSize: 10, letterSpacing: 3, color: "#f59e0b", fontWeight: 900, marginBottom: 8 }}>⚡ TEST DISPATCH — NOT A REAL JOB</div>
                <div style={{ fontSize: 44, fontWeight: 900, color: testCountdown <= 15 ? "#E63329" : "#f59e0b", lineHeight: 1 }}>
                  {testCountdown}
                </div>
                <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>seconds to accept</div>
              </div>

              <div style={{ background: "#161616", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <div style={{ fontSize: 17, fontWeight: 900, color: "#fff", marginBottom: 8 }}>🔋 Jump Start</div>
                {[["👤","TEST DISPATCH"],["📍","This is a test — no real customer"],["💰","Your pay: $40"]].map(([ic,v]) => (
                  <div key={v} style={{ fontSize: 12.5, color: "#aaa", marginBottom: 5, display: "flex", gap: 8 }}>
                    <span>{ic}</span><span>{v}</span>
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 12, color: "#888", lineHeight: 1.55, marginBottom: 16, textAlign: "center" }}>
                Did your phone buzz and show a notification? Accept below to confirm alerts are working and go online.
              </div>

              <button style={{ ...c.btnPrimary, background: "#22c55e", color: "#000" }} onClick={acceptTestJob}>
                ✅ Accept Test Job & Go Online
              </button>
              <button style={c.btnSecondary}
                onClick={() => { if (testTimerRef.current) clearInterval(testTimerRef.current); setTestJob(null); }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        <style>{`@keyframes sfPulse { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.45;transform:scale(1.3);} }`}</style>

        <div style={au.tabs}>
          <button style={{ ...au.tab, ...(filter === "active" ? { ...au.tabActive, background: "#3b82f6" } : {}) }} onClick={() => setFilter("active")}>
            Active ({jobs.filter(j => j.status !== "completed").length})
          </button>
          <button style={{ ...au.tab, ...(filter === "completed" ? { ...au.tabActive, background: "#22c55e" } : {}) }} onClick={() => setFilter("completed")}>
            Completed ({jobs.filter(j => j.status === "completed").length})
          </button>
          <button style={{ ...au.tab, ...(filter === "earnings" ? { ...au.tabActive, background: "#f59e0b" } : {}) }} onClick={() => setFilter("earnings")}>
            💰 Earnings
          </button>
          <button style={{ ...au.tab, ...(filter === "fleet" ? { ...au.tabActive, background: "#14b8a6", color: "#000" } : {}) }} onClick={() => setFilter("fleet")}>
            🚚 Fleet
          </button>
          <button style={{ ...au.tab, ...(filter === "qr" ? { ...au.tabActive, background: "#22c55e" } : {}) }} onClick={() => setFilter("qr")}>
            📱 QR Code
          </button>
          <button style={{ ...au.tab, ...(filter === "more" ? { ...au.tabActive, background: "#a855f7" } : {}) }} onClick={() => setFilter("more")}>
            📚 More
          </button>
        </div>

        {/* FLEET INSPECTIONS */}
        {filter === "fleet" && (
          <FleetInspection authUser={authUser} profile={profile} />
        )}

        {/* CUSTOMER QR CODE — regional flyer */}
        {filter === "qr" && (() => {
          const flyer = flyerForProvider(profile);
          return (
            <div style={{ textAlign: "center", padding: "4px 0 20px" }}>
              <div style={{ fontSize: 10, letterSpacing: 3, color: "#22c55e", fontWeight: 800, marginBottom: 6 }}>
                SHOW THIS TO YOUR CUSTOMER
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 4, lineHeight: 1.2 }}>
                {flyer.label} Flyer
              </div>
              <div style={{ fontSize: 11.5, color: "#666", marginBottom: 16 }}>{flyer.blurb}</div>

              {/* The flyer */}
              <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", border: "1px solid #222", marginBottom: 14 }}>
                <img src={flyer.src} alt={`Super Fast Roadside — ${flyer.label}`}
                  onClick={() => setShowFlyerQr(true)}
                  style={{ width: "100%", display: "block", cursor: "pointer" }} />
                <button
                  onClick={() => setShowFlyerQr(true)}
                  style={{ position: "absolute", left: "50%", bottom: 14, transform: "translateX(-50%)",
                    background: "#22c55e", border: "none", color: "#000", padding: "12px 22px",
                    borderRadius: 30, fontSize: 14, fontWeight: 900, cursor: "pointer",
                    boxShadow: "0 6px 22px rgba(0,0,0,0.6)", whiteSpace: "nowrap" }}>
                  📱 Tap to Enlarge QR
                </button>
              </div>

              <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 12, padding: 15, marginBottom: 11, textAlign: "left" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#22c55e", marginBottom: 7 }}>💡 What to say</div>
                <div style={{ fontSize: 12.5, color: "#bbb", lineHeight: 1.7 }}>
                  "Scan this and you can book us straight from your phone next time — $60 flat rate, same price day or night. Takes about a minute to set up."
                </div>
              </div>

              <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 12, padding: 15, marginBottom: 11, textAlign: "left" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#f59e0b", marginBottom: 7 }}>📵 Phone won't scan it?</div>
                <div style={{ fontSize: 12, color: "#999", lineHeight: 1.65 }}>
                  Have them open their camera app and hold steady over the code. If it still won't catch, tell them to type <strong style={{ color: "#fff" }}>superfast-tires.web.app</strong> into their browser.
                </div>
              </div>

              {/* Region override */}
              <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 12, padding: 15, textAlign: "left" }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#888", marginBottom: 9 }}>
                  Wrong area? Pick your flyer:
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {Object.values(FLYERS).map(fl => (
                    <button key={fl.id}
                      onClick={async () => {
                        await dbPatch(`users/${authUser.uid}`, { flyerRegion: fl.id });
                        syncProviderProfile({ flyerRegion: fl.id });
                        setLiveProfile(prev => ({ ...prev, flyerRegion: fl.id }));
                      }}
                      style={{ background: flyer.id === fl.id ? "#22c55e" : "#1a1a1a",
                        border: `1px solid ${flyer.id === fl.id ? "#22c55e" : "#333"}`,
                        color: flyer.id === fl.id ? "#000" : "#888",
                        padding: "7px 13px", borderRadius: 14, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
                      {fl.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ fontSize: 11, color: "#555", marginTop: 14, lineHeight: 1.55 }}>
                Turn your screen brightness up — it scans faster.
              </div>
            </div>
          );
        })()}

        {/* FULL-SCREEN FLYER + QR */}
        {showFlyerQr && (() => {
          const flyer = flyerForProvider(profile);
          return (
            <div onClick={() => setShowFlyerQr(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.97)", zIndex: 999,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                padding: 14, cursor: "pointer" }}>
              <div style={{ fontSize: 11, letterSpacing: 3, color: "#22c55e", fontWeight: 900, marginBottom: 10 }}>
                POINT YOUR CAMERA AT THE CODE
              </div>
              <div style={{ background: "#fff", borderRadius: 22, padding: 20, lineHeight: 0,
                boxShadow: "0 14px 60px rgba(34,197,94,0.35)" }}>
                <img src="/qr-signup.png" alt="Scan to book Super Fast Roadside"
                  style={{ width: "min(78vw, 340px)", height: "min(78vw, 340px)", display: "block" }} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 900, color: "#fff", marginTop: 18 }}>superfast-tires.web.app</div>
              <div style={{ fontSize: 12.5, color: "#888", marginTop: 6 }}>$10/month · $60 flat rate · {flyer.label}</div>
              <div style={{ fontSize: 11.5, color: "#555", marginTop: 14 }}>Tap anywhere to close</div>
              <button style={{ position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,0.12)",
                border: "1px solid #555", color: "#fff", width: 40, height: 40, borderRadius: "50%",
                fontSize: 18, cursor: "pointer" }}
                onClick={() => setShowFlyerQr(false)}>✕</button>
            </div>
          );
        })()}

        {/* MORE TAB — handbook, toolkit, services, support */}
        {filter === "more" && (
          <ProviderMore authUser={authUser} profile={profile} jobs={jobs} onProfileChange={patch => setLiveProfile(prev => ({ ...prev, ...patch }))} />
        )}

        {/* EARNINGS TAB */}
        {filter === "earnings" && (() => {
          const completedJobs = jobs.filter(j => j.status === "completed");

          // Pay period: Thursday to Wednesday, paid on Friday
          const now = new Date();
          const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
          // Days since last Thursday
          const daysSinceThursday = (dayOfWeek + 3) % 7; // Thu=0, Fri=1, Sat=2, Sun=3, Mon=4, Tue=5, Wed=6
          const thursday = new Date(now);
          thursday.setDate(now.getDate() - daysSinceThursday);
          thursday.setHours(0, 0, 0, 0);
          const wednesday = new Date(thursday);
          wednesday.setDate(thursday.getDate() + 6);
          wednesday.setHours(23, 59, 59, 999);

          // Next Friday payout
          const nextFriday = new Date(thursday);
          nextFriday.setDate(thursday.getDate() + 8); // Friday after the period ends

          const thisWeekJobs = completedJobs.filter(j => {
            const d = new Date(j.createdAt);
            return d >= thursday && d <= wednesday;
          });

          const thisWeekBasePay = thisWeekJobs.reduce((sum, j) => sum + (getProviderPay(j).pay || 0), 0);
          const thisWeekTips = thisWeekJobs.reduce((sum, j) => sum + (j.tipPaid ? (j.tipAmount || 0) : 0), 0);
          const thisWeekPay = thisWeekBasePay + thisWeekTips;
          const allTimePay = completedJobs.reduce((sum, j) => sum + (getProviderPay(j).pay || 0), 0);

          return (
            <div>
              {/* Weekly summary card */}
              <div style={{ background: "#161616", border: "1px solid #f59e0b", borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ fontSize: 10, letterSpacing: 3, color: "#f59e0b", fontWeight: 700, marginBottom: 12 }}>THIS WEEK</div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 36, fontWeight: 900, color: "#22c55e" }}>${thisWeekPay}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>{thisWeekJobs.length} job{thisWeekJobs.length !== 1 ? "s" : ""} completed</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>NEXT PAYDAY</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#f59e0b" }}>
                      📅 {nextFriday.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                    </div>
                    <div style={{ fontSize: 10, color: "#555", marginTop: 2 }}>Every Friday</div>
                  </div>
                </div>
                <div style={{ borderTop: "1px solid #2a2a2a", paddingTop: 12 }}>
                  {thisWeekTips > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ fontSize: 12, color: "#666" }}>💚 Tips This Week (100% yours)</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: "#22c55e" }}>${thisWeekTips.toFixed(2)}</div>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ fontSize: 12, color: "#666" }}>All Time Earned</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>${allTimePay}</div>
                  </div>
                </div>
              </div>

              {/* Week date range */}
              <div style={{ fontSize: 11, color: "#555", marginBottom: 12, textAlign: "center" }}>
                {thursday.toLocaleDateString("en-US", { month: "short", day: "numeric" })} — {wednesday.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </div>

              {/* This week jobs */}
              {thisWeekJobs.length === 0 && (
                <div style={{ textAlign: "center", color: "#444", padding: "40px 0" }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>💰</div>
                  <div>No completed jobs this week yet.</div>
                </div>
              )}

              {thisWeekJobs.map(job => {
                const pay = getProviderPay(job);
                return (
                  <div key={job._key} style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 14, marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 2 }}>
                          {SERVICE_ICONS[job.service] || "🔧"} {job.service}
                        </div>
                        <div style={{ fontSize: 11, color: "#666" }}>{job.customer}</div>
                        <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
                          {new Date(job.createdAt).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                        </div>
                        {pay.breakdown && (
                          <div style={{ fontSize: 10, color: "#4caf50", marginTop: 4 }}>{pay.breakdown}</div>
                        )}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 20, fontWeight: 900, color: "#22c55e" }}>${(pay.pay || 0) + (job.tipPaid ? (job.tipAmount || 0) : 0)}</div>
                        {job.tipPaid && job.tipAmount > 0 && (
                          <div style={{ fontSize: 10, color: "#4caf50", marginTop: 2 }}>${pay.pay} + ${job.tipAmount.toFixed(2)} tip 💚</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {thisWeekJobs.length === 0 && (
                <div style={{ textAlign: "center", color: "#444", padding: "40px 0" }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>💰</div>
                  <div>No completed jobs this week yet.</div>
                  <div style={{ fontSize: 11, color: "#555", marginTop: 8 }}>Complete jobs to see your earnings here.</div>
                </div>
              )}

              {thisWeekJobs.map(job => {
                const pay = getProviderPay(job);
                return (
                  <div key={job._key} style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 14, marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 2 }}>
                          {SERVICE_ICONS[job.service] || "🔧"} {job.service}
                        </div>
                        <div style={{ fontSize: 11, color: "#666" }}>{job.customer}</div>
                        <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
                          {new Date(job.createdAt).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                        </div>
                        {pay.breakdown && (
                          <div style={{ fontSize: 10, color: "#4caf50", marginTop: 4 }}>{pay.breakdown}</div>
                        )}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 20, fontWeight: 900, color: "#22c55e" }}>${(pay.pay || 0) + (job.tipPaid ? (job.tipAmount || 0) : 0)}</div>
                        {job.tipPaid && job.tipAmount > 0 && (
                          <div style={{ fontSize: 10, color: "#4caf50", marginTop: 2 }}>${pay.pay} + ${job.tipAmount.toFixed(2)} tip 💚</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Payout request */}
              {thisWeekPay > 0 && (
                <div style={{ marginTop: 16 }}>
                  <button style={{ ...c.btnPrimary, background: "#f59e0b", color: "#000", fontWeight: 900 }}
                    onClick={async () => {
                      if (!window.confirm(`Request payout of $${thisWeekPay} for this week?`)) return;
                      await fetch(`${WORKER_URL}/notify`, {
                        method: "POST", headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          title: "💰 Payout Request",
                          message: `${profile.firstName} ${profile.lastName} requesting weekly payout.\nAmount: $${thisWeekPay}\nJobs: ${thisWeekJobs.length}\nPhone: ${profile.phone || "N/A"}\nPay via Zelle or CashApp.`,
                        }),
                      }).catch(() => {});
                      alert("✅ Payout request sent! Admin will process your payment on Friday.");
                    }}>
                    💸 Request Payout — ${thisWeekPay}
                  </button>
                  <div style={{ fontSize: 10, color: "#555", textAlign: "center", marginTop: 6 }}>
                    {profile.payoutsEnabled
                      ? `Direct deposit to ${profile.bankName || "your bank"} ••••${profile.bankLast4 || "____"} every Friday`
                      : "⚠️ Connect your bank account to receive payouts"}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {loading && <div style={{ color: "#444", textAlign: "center", marginTop: 40 }}>Loading your jobs...</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: "center", marginTop: 60, color: "#444" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div>{filter === "active" ? "No active jobs right now." : "No completed jobs yet."}</div>
          </div>
        )}

        {filtered.map(job => {
          const meta = STATUS_META[job.status] || { label: job.status, color: "#888", bg: "#1a1a1a" };
          const providerPay = getProviderPay(job);
          const isTireJob = job.service?.includes("Tire") && job.tire_option !== "spare";
          return (
            <div key={job._key} style={pv.jobCard} onClick={() => { setActiveJob(job); setJobScreen("detail"); }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 22 }}>{SERVICE_ICONS[job.service] || "🔧"}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{job.service}</div>
                  <div style={{ fontSize: 11, color: "#666" }}>{job.customer}</div>
                </div>
                <div style={{ ...pv.pill, background: meta.bg, color: meta.color }}>{meta.label}</div>
              </div>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 8 }}>📍 {job.address}</div>

              {/* Provider pay highlight */}
              <div style={{ background: providerPay.pay ? "#0a1f0a" : "#1a0a00", border: `1px solid ${providerPay.pay ? "#22c55e" : "#f59e0b"}`, borderRadius: 6, padding: "8px 12px", marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: providerPay.pay ? "#4caf50" : "#f59e0b" }}>
                    {providerPay.pay ? "💰 Your Pay" : "💵 Collect on Arrival"}
                  </span>
                  {providerPay.pay && <span style={{ fontSize: 16, fontWeight: 900, color: "#22c55e" }}>${providerPay.pay}</span>}
                </div>
                {providerPay.breakdown && (
                  <div style={{ fontSize: 10, color: "#4caf50", marginTop: 2 }}>{providerPay.breakdown}</div>
                )}
                {!providerPay.pay && (
                  <div style={{ fontSize: 10, color: "#f59e0b", marginTop: 2 }}>You collect tow payment directly from customer</div>
                )}
              </div>

              {/* Tire stock warning */}
              {job.scheduled && job.scheduledLabel && (
                <div style={{ background: "#1a1400", border: "1px solid #f59e0b", borderRadius: 6, padding: "8px 12px", marginBottom: 8, fontSize: 12, color: "#f59e0b", fontWeight: 700 }}>
                  📅 Scheduled — {job.scheduledLabel}
                </div>
              )}

              {/* Cash collection notice */}
              {job.cashCollection && (
                <div style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 6, padding: "8px 12px", marginBottom: 8, fontSize: 12, color: "#f59e0b", fontWeight: 700 }}>
                  💵 Collect ${job.providerPay || 40} cash from customer on arrival
                </div>
              )}

              {isTireJob && (
                <div style={{ background: "#1a0a00", border: "1px solid #f59e0b", borderRadius: 6, padding: "7px 12px", marginBottom: 8, fontSize: 11, color: "#f59e0b", fontWeight: 700 }}>
                  ⚠️ You must have a used tire in stock to do this job
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span style={{ color: "#444" }}>{timeAgo(job.createdAt)}</span>
              </div>
            </div>
          );
        })}
        <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC · South Florida</div>
      </div>
      )} {/* end gpsConsent conditional */}
    </div>
  );
}


// ============================================================
// █████╗ ██████╗ ███╗   ███╗██╗███╗   ██╗
// ██╔══██╗██╔══██╗████╗ ████║██║████╗  ██║
// ███████║██║  ██║██╔████╔██║██║██╔██╗ ██║
// ██╔══██║██║  ██║██║╚██╔╝██║██║██║╚██╗██║
// ██║  ██║██████╔╝██║ ╚═╝ ██║██║██║ ╚████║
// ============================================================
function ArchiveWeek({ weekLabel, weekJobs, onSelect }) {
  const [open, setOpen] = useState(false);
  const revenue = weekJobs.reduce((s, j) => s + (j.price || 0), 0);
  return (
    <div style={{ marginBottom: 10 }}>
      <button style={{ width: "100%", background: "#161616", border: "1px solid #2a2a2a", borderRadius: 10, padding: "14px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
        onClick={() => setOpen(v => !v)}>
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#aaa" }}>📁 {weekLabel}</div>
          <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{weekJobs.length} jobs · ${revenue.toFixed(2)} collected</div>
        </div>
        <span style={{ color: "#555", fontSize: 16 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ background: "#111", border: "1px solid #1a1a1a", borderRadius: "0 0 10px 10px", padding: "8px 12px" }}>
          {weekJobs.map(job => {
            const meta = STATUS_META[job.status] || { label: job.status, color: "#888", bg: "#1a1a1a" };
            return (
              <div key={job._key} style={{ padding: "10px 0", borderBottom: "1px solid #1a1a1a", cursor: "pointer" }}
                onClick={() => onSelect(job)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#aaa" }}>{SERVICE_ICONS[job.service] || "🔧"} {job.service}</div>
                    <div style={{ fontSize: 11, color: "#555" }}>{job.customer} · {new Date(job.createdAt).toLocaleDateString()}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 12, color: "#666" }}>{job.price ? `$${job.price}` : "Quote"}</div>
                    <div style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 6, background: meta.bg, color: meta.color, marginTop: 2 }}>{meta.label}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminPortal() {
  const [authUser, setAuthUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      const saved = localStorage.getItem("sfrs_admin");
      if (saved) { const u = JSON.parse(saved); setAuthToken(u.token, u.refreshToken); setAuthUser(u); }
    } catch {}
  }, []);

  async function handleLogin() {
    setError("");
    if (email.trim().toLowerCase() !== ADMIN_EMAIL.toLowerCase()) return setError("Access denied.");
    setLoading(true);
    const result = await fbSignIn(email.trim(), password);
    if (result.error) { setLoading(false); return setError("Invalid credentials. Check your email and password."); }
    setAuthToken(result.token, result.refreshToken);
    localStorage.setItem("sfrs_admin", JSON.stringify(result));
    setLoading(false);
    setAuthUser(result);
  }

  function handleSignOut() {
    localStorage.removeItem("sfrs_admin");
    setAuthUser(null);
  }

  if (!authUser) return (
    <div style={c.root}>
      <header style={{ ...c.header, background: "#1a0a0a", borderBottom: "3px solid #E63329" }}>
        <div style={c.logo}>
          <span style={c.flame}>🔥</span>
          <div><div style={c.logoTop}>SUPER FAST</div><div style={c.logoBot}>ADMIN</div></div>
          <span style={c.flame}>🔥</span>
        </div>
      </header>
      <div style={c.page}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔐</div>
          <div style={au.title}>Admin Access Only</div>
          <div style={au.sub}>Authorized personnel only</div>
        </div>
        <div style={c.fieldGroup}>
          <label style={c.label}>Email</label>
          <input style={c.input} type="email" value={email} onChange={e => setEmail(e.target.value)} />
        </div>
        <div style={c.fieldGroup}>
          <label style={c.label}>Password</label>
          <input style={c.input} type="password" value={password} onChange={e => setPassword(e.target.value)} />
        </div>
        {error && <div style={c.gpsError}>⚠️ {error}</div>}
        <button style={c.btnPrimary} onClick={handleLogin} disabled={loading}>
          {loading ? "Verifying..." : "Access Admin Panel →"}
        </button>
      </div>
      <div style={c.footer}>© 2026 Super Fast Mobile Tires LLC</div>
    </div>
  );

  return <AdminApp authUser={authUser} onSignOut={handleSignOut} />;
}

function AdminApp({ authUser, onSignOut }) {
  const [jobs, setJobs] = useState([]);
  const [providers, setProviders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [partners, setPartners] = useState([]);
  const [netJobs, setNetJobs] = useState([]);
  const [fleetAccts, setFleetAccts] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [netQuote, setNetQuote] = useState({});
  const [netDispatch, setNetDispatch] = useState({});
  const [replyDrafts, setReplyDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("jobs");
  const [assignTech, setAssignTech] = useState("");
  const [newJobAlert, setNewJobAlert] = useState(false);
  const [batteryAlert, setBatteryAlert] = useState(null); // job with battery upgrade
  const prevBatteryRef = useRef(0);
  const [selectedJob, setSelectedJob] = useState(null);
  const prevCountRef = useRef(0);

  // New provider form
  const [npFirst, setNpFirst]   = useState("");
  const [npLast, setNpLast]     = useState("");
  const [npEmail, setNpEmail]   = useState("");
  const [npPhone, setNpPhone]   = useState("");
  const [npPass, setNpPass]     = useState("");
  const [npLoading, setNpLoading] = useState(false);
  const [npError, setNpError]   = useState("");
  const [npSuccess, setNpSuccess] = useState("");

  useEffect(() => {
    async function poll() {
      const [allJobs, allUsers, allReviews, allMsgs, allSponsors] = await Promise.all([dbGet("jobs"), dbGet("users"), dbGet("reviews"), dbGet("providerMessages"), dbGet("sponsorLeads")]);
      if (Array.isArray(allSponsors)) setSponsors(allSponsors.sort((a, b) => b.createdAt - a.createdAt));
      const allNet = await dbGet("networkJobs");
      if (Array.isArray(allNet)) setNetJobs(allNet.sort((a, b) => b.createdAt - a.createdAt));
      const allInsp = await dbGet("inspections");
      if (Array.isArray(allInsp)) setInspections(allInsp.sort((a, b) => b.createdAt - a.createdAt));
      if (Array.isArray(allReviews)) setReviews(allReviews);
      if (Array.isArray(allMsgs)) setMessages(allMsgs.sort((a, b) => b.createdAt - a.createdAt));
      allJobs.sort((a, b) => b.createdAt - a.createdAt);
      if (allJobs.length > prevCountRef.current) {
        setNewJobAlert(true);
        setTimeout(() => setNewJobAlert(false), 4000);
        if (Notification.permission === "granted") {
          new Notification("🔥 New Job — Super Fast Roadside", {
            body: `${allJobs[0]?.service} · ${allJobs[0]?.customer}`,
            requireInteraction: true,
          });
        }
      }
      prevCountRef.current = allJobs.length;

      // Detect battery upgrades requested
      const batteryJobs = allJobs.filter(j => j.battery_upgrade_pending && !j.battery_upgrade_paid && !j.battery_upgrade_cancelled);
      if (batteryJobs.length > prevBatteryRef.current) {
        setBatteryAlert(batteryJobs[0]);
        setTimeout(() => setBatteryAlert(null), 10000);
        if (Notification.permission === "granted") {
          new Notification("🔋 Battery Upgrade Requested", {
            body: `${batteryJobs[0]?.customer} · ${batteryJobs[0]?.address}\nCustomer needs to pay $428.00`,
            requireInteraction: true,
          });
        }
      }
      prevBatteryRef.current = batteryJobs.length;

      setJobs(allJobs);
      const usersList = allUsers && typeof allUsers === 'object' && !Array.isArray(allUsers)
        ? Object.entries(allUsers).map(([uid, u]) => ({ ...u, _key: uid }))
        : (Array.isArray(allUsers) ? allUsers : []);
      const provs = usersList.filter(u => u?.role === "provider");
      const custs = usersList.filter(u => u?.role === "customer");
      setPartners(usersList.filter(u => u?.role === "partner"));
      setFleetAccts(usersList.filter(u => u?.role === "fleet"));
      setProviders(provs);
      setCustomers(custs);

      // Providers who started Stripe onboarding but haven't been marked verified yet —
      // check Stripe directly so admin reflects reality even if they never reopened the app.
      const pending = provs.filter(p => p.stripeAccountId && !p.payoutsEnabled);
      for (const p of pending) {
        try {
          const r = await fetch(`${WORKER_URL}/connect-status`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ accountId: p.stripeAccountId }),
          });
          const d = await r.json();
          if (d.payoutsEnabled) {
            const patch = {
              payoutsEnabled: true,
              bankLast4: d.bankLast4 || null,
              bankName: d.bankName || null,
              verifiedAt: Date.now(),
            };
            await dbPatch(`users/${p._key}`, patch);
            setProviders(prev => prev.map(x => x._key === p._key ? { ...x, ...patch } : x));
            await fetch(`${WORKER_URL}/notify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                title: "🏦 Provider Bank Verified",
                message: `${p.firstName} ${p.lastName} completed Stripe verification.\n${d.bankName || "Bank"} ••••${d.bankLast4 || "____"}\nThey can now go online and be paid.`,
              }),
            }).catch(() => {});
          }
        } catch {}
      }
      setLoading(false);
    }
    poll();
    const interval = setInterval(poll, 8000);
    return () => clearInterval(interval);
  }, []);

  // Week boundary helpers
  function getWeekStart(d) {
    const date = new Date(d);
    const day = date.getDay(); // 0=Sun, 4=Thu
    const daysSinceThursday = (day + 3) % 7; // Thu=0, Fri=1, Sat=2, Sun=3, Mon=4, Tue=5, Wed=6
    date.setDate(date.getDate() - daysSinceThursday);
    date.setHours(0, 0, 0, 0);
    return date;
  }
  const thisWeekStart = getWeekStart(new Date());
  const thisWeekStartMs = thisWeekStart.getTime();

  // Split jobs into current week and archived
  const currentWeekJobs = jobs.filter(j => j.createdAt >= thisWeekStartMs);
  const archivedJobs = jobs.filter(j => j.createdAt < thisWeekStartMs);

  // Group archived by week
  const archiveWeeks = {};
  archivedJobs.forEach(j => {
    const ws = getWeekStart(j.createdAt);
    const wsEnd = new Date(ws); wsEnd.setDate(ws.getDate() + 6);
    const fmt = d => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const key = `${fmt(ws)} — ${fmt(wsEnd)}, ${ws.getFullYear()}`;
    if (!archiveWeeks[key]) archiveWeeks[key] = [];
    archiveWeeks[key].push(j);
  });

  const filteredByStatus = filter === "all" ? currentWeekJobs : currentWeekJobs.filter(j => j.status === filter);
  const filtered = search.trim()
    ? filteredByStatus.filter(j => {
        const q = search.toLowerCase();
        return (j.customer || "").toLowerCase().includes(q) ||
               (j.phone || "").includes(q) ||
               (j.id || "").toLowerCase().includes(q) ||
               (j.address || "").toLowerCase().includes(q) ||
               (j.techName || "").toLowerCase().includes(q);
      })
    : filteredByStatus;

  const counts = {
    all: currentWeekJobs.length,
    new: currentWeekJobs.filter(j => j.status === "new").length,
    assigned: currentWeekJobs.filter(j => j.status === "assigned").length,
    en_route: currentWeekJobs.filter(j => j.status === "en_route").length,
    on_scene: currentWeekJobs.filter(j => j.status === "on_scene").length,
    tow_requested: currentWeekJobs.filter(j => j.status === "tow_requested").length,
    returned: currentWeekJobs.filter(j => j.status === "returned").length,
    completed: currentWeekJobs.filter(j => j.status === "completed").length,
  };
  const totalRevenue = jobs.filter(j => j.status === "completed" && j.price).reduce((s, j) => s + j.price, 0);
  const todayRevenue = jobs.filter(j => j.price).reduce((s, j) => s + j.price, 0);

  const [editTireSize, setEditTireSize] = useState("");
  const [editingTireSize, setEditingTireSize] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [provSearch, setProvSearch] = useState("");
  const [provState, setProvState] = useState("");
  const [collapsedStates, setCollapsedStates] = useState({});
  const [editingProvider, setEditingProvider] = useState(false);
  const [epForm, setEpForm] = useState({});
  const [epSaving, setEpSaving] = useState(false);
  const [groupFilter, setGroupFilter] = useState("");
  const [photoViewer, setPhotoViewer] = useState(null);
  const [dispatchSearch, setDispatchSearch] = useState("");
  const [quoteAmt, setQuoteAmt] = useState("");
  const [quoteNote, setQuoteNote] = useState("");
  const [quoteTax, setQuoteTax] = useState(true);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [cjName, setCjName] = useState("");
  const [cjPhone, setCjPhone] = useState("");
  const [cjAddress, setCjAddress] = useState("");
  const [cjService, setCjService] = useState("");
  const [cjNote, setCjNote] = useState("");
  const [cjPay, setCjPay] = useState("");
  const [cjPayType, setCjPayType] = useState("cash");
  const [cjLoading, setCjLoading] = useState(false);
  const [editingJob, setEditingJob] = useState(false);
  const [ejName, setEjName] = useState("");
  const [ejPhone, setEjPhone] = useState("");
  const [ejAddress, setEjAddress] = useState("");
  const [ejService, setEjService] = useState("");
  const [ejNote, setEjNote] = useState("");
  const [ejPay, setEjPay] = useState("");

  async function saveTireSize(job) {
    if (!editTireSize.trim()) return;
    await dbPatch(`jobs/${job._key}`, { tire_size: editTireSize.trim(), tire_info_mode: "size" });
    setJobs(prev => prev.map(j => j._key === job._key ? { ...j, tire_size: editTireSize.trim(), tire_info_mode: "size" } : j));
    setSelectedJob(prev => ({ ...prev, tire_size: editTireSize.trim(), tire_info_mode: "size" }));
    setEditingTireSize(false);
    setEditTireSize("");
  }

  // Check if tire job needs size before dispatch
  function needsTireSize(job) {
    if (!job?.service?.includes("Tire")) return false;
    if (job?.tire_option !== "need_tire") return false;
    return !job?.tire_size || job?.tire_info_mode === "unknown";
  }

  async function assignJob(job) {
    if (!assignTech) return;
    if (needsTireSize(job)) return alert("⚠️ Please enter the tire size before dispatching this job.");
    let techName, techUid, techEmail;
    if (assignTech === "admin") {
      techName = "Admin"; techUid = "admin"; techEmail = "superfastmobiletires@gmail.com";
    } else {
      const provider = providers.find(p => p._key === assignTech);
      techName = provider ? `${provider.firstName} ${provider.lastName}` : assignTech;
      techUid = provider?._key || assignTech;
      techEmail = provider?.email || "";
    }
    await dbPatch(`jobs/${job._key}`, { tech: techUid, techName, techEmail, status: "assigned" });
    setJobs(prev => prev.map(j => j._key === job._key ? { ...j, tech: techUid, techName, status: "assigned" } : j));

    // Background alert so they get it even with the app closed
    if (techUid && techUid !== "admin") {
      const pay = getProviderPay(job);
      fetch(`${WORKER_URL}/notify-provider`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          providerId: techUid,
          title: `🔥 NEW JOB — ${job.service}`,
          message: `${job.customer}\n📍 ${job.address}\n💰 Your pay: $${pay.pay || "—"}\n\n⏱️ Open the app within 60 seconds to accept.`,
        }),
      }).catch(() => {});
    }

    setAssignTech("");
  }

  async function updateStatus(job, status) {
    await dbPatch(`jobs/${job._key}`, { status });
    setJobs(prev => prev.map(j => j._key === job._key ? { ...j, status } : j));
  }

  async function createProvider() {
    setNpError(""); setNpSuccess("");
    if (!npFirst || !npLast || !npEmail || !npPass) return setNpError("All fields required.");
    if (npPass.length < 6) return setNpError("Password must be at least 6 characters.");
    setNpLoading(true);
    // Keep the admin's own token so we can restore it after creating the account
    const adminToken = getAuthToken();
    const adminRefresh = getRefreshToken();

    const profile = { firstName: npFirst, lastName: npLast, email: npEmail, phone: npPhone, role: "provider", status: "pending", createdAt: Date.now() };
    const result = await createAccount(npEmail, npPass, profile);

    // Restore admin session — createAccount switched the token to the new user
    setAuthToken(adminToken, adminRefresh);

    if (result.error) { setNpLoading(false); return setNpError(result.error); }
    setNpLoading(false);
    setNpSuccess(`✅ Provider account created for ${npFirst} ${npLast}`);
    setNpFirst(""); setNpLast(""); setNpEmail(""); setNpPhone(""); setNpPass("");
    // Refresh providers
    const allUsers = await dbGet("users");
    const usersList = allUsers && typeof allUsers === 'object' && !Array.isArray(allUsers) ? Object.entries(allUsers).map(([uid, u]) => ({ ...u, _key: uid })) : (Array.isArray(allUsers) ? allUsers : []); const provs = usersList.filter(u => u?.role === "provider");
    setProviders(provs);
  }

  const providerNames = providers.map(p => `${p.firstName} ${p.lastName}`);

  return (
    <div style={{ background: BLK, minHeight: "100vh", color: WHITE, fontFamily: "'Inter','Helvetica Neue',sans-serif", maxWidth: 600, margin: "0 auto" }}>
      {/* FULL-SCREEN PHOTO VIEWER */}
      {photoViewer && (
        <div onClick={() => setPhotoViewer(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.96)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, cursor: "pointer" }}>
          <img src={photoViewer} alt="full size" style={{ maxWidth: "100%", maxHeight: "85vh", borderRadius: 10, objectFit: "contain" }} />
          <button style={{ position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,0.12)", border: "1px solid #555", color: "#fff", width: 40, height: 40, borderRadius: "50%", fontSize: 18, cursor: "pointer" }}
            onClick={() => setPhotoViewer(null)}>✕</button>
          <div style={{ position: "absolute", bottom: 26, left: 0, right: 0, textAlign: "center", fontSize: 12, color: "#777" }}>
            Tap anywhere to close
          </div>
        </div>
      )}

      {newJobAlert && <div style={ad.alert}>🔔 New job just came in!</div>}
      {batteryAlert && (
        <div style={{ ...ad.alert, background: "#0a1525", borderColor: "#3b82f6", color: "#3b82f6", cursor: "pointer" }}
          onClick={() => { setSelectedJob(batteryAlert); setBatteryAlert(null); }}>
          🔋 Battery upgrade requested — {batteryAlert.customer} · Tap to view job
        </div>
      )}

      {/* HEADER */}
      <div style={{ background: "#111", borderBottom: "3px solid #E63329", padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: 2, color: WHITE }}>SFRS ADMIN</div>
          <div style={{ fontSize: 10, letterSpacing: 3, color: "#E63329", fontWeight: 700 }}>DISPATCH</div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 10, color: "#666", letterSpacing: 1 }}>TODAY</div>
            <div style={{ fontSize: 16, fontWeight: 900, color: WHITE }}>${todayRevenue.toFixed(0)}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 10, color: "#666", letterSpacing: 1 }}>ACTIVE</div>
            <div style={{ fontSize: 16, fontWeight: 900, color: "#E63329" }}>{counts.new + counts.assigned + counts.en_route}</div>
          </div>
          <button style={{ background: "none", border: "1px solid #333", color: "#666", padding: "6px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11 }} onClick={onSignOut}>Sign Out</button>
        </div>
      </div>

      {/* JOB DETAIL MODAL */}
      {selectedJob && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.95)", zIndex: 200, overflowY: "auto", maxWidth: 600, margin: "0 auto" }}>
          <div style={{ padding: "16px 16px 100px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <button style={{ background: "none", border: "1px solid #333", color: "#aaa", padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}
                onClick={() => setSelectedJob(null)}>← Back to Jobs</button>
              <button style={{ background: "#2a0a0a", border: "1px solid #E63329", color: "#E63329", padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 700 }}
                onClick={async () => {
                  if (!window.confirm(`Delete job ${selectedJob.id}? This cannot be undone.`)) return;
                  await dbDelete(`jobs/${selectedJob._key}`);
                  setJobs(prev => prev.filter(j => j._key !== selectedJob._key));
                  setSelectedJob(null);
                }}>
                🗑️ Delete Job
              </button>
            </div>

            <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 28 }}>{SERVICE_ICONS[selectedJob.service] || "🔧"}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 900 }}>{selectedJob.service}</div>
                  <div style={{ fontSize: 11, color: "#666" }}>{selectedJob.id}</div>
                </div>
                <div style={{ ...ad.statusPill, background: (STATUS_META[selectedJob.status] || {}).bg || "#1a1a1a", color: (STATUS_META[selectedJob.status] || {}).color || "#888" }}>
                  {(STATUS_META[selectedJob.status] || {}).label || selectedJob.status}
                </div>
              </div>
              {[
                ["👤 Customer", selectedJob.customer],
                ["📞 Phone", selectedJob.phone, true],
                ["📍 Location", selectedJob.address],
                ...(selectedJob.tire_option ? [["🛞 Option", selectedJob.tire_option === "spare" ? "Has Spare — $100/tire" : selectedJob.tire_option === "need_tire" ? "Needs Tire — $200/tire" : "Tow Truck"]] : []),
                ...(selectedJob.tire_qty ? [["🔢 Qty", `${selectedJob.tire_qty} tire${selectedJob.tire_qty > 1 ? "s" : ""}`]] : []),
                ...(selectedJob.tire_disposal ? [["🗑️ Disposal", selectedJob.tire_disposal === "yes" ? `Yes — $${selectedJob.disposal_fee || 0}` : "No — Customer responsible"]] : []),
                ...(selectedJob.wheel_lock ? [["🔐 Wheel Lock", `Removal needed — $${selectedJob.wheel_lock_fee || 60}`]] : []),
                ...(selectedJob.battery_upgrade ? [["🔋 Battery Upgrade", `Replacement included — $${selectedJob.battery_upgrade_fee || 400}`]] : []),
                ...(selectedJob.scheduled && selectedJob.scheduledLabel ? [["📅 Scheduled For", selectedJob.scheduledLabel]] : []),
                ...(selectedJob.nonMember ? [["⚡ Non-Member", `Guest booking — paid $${selectedJob.price} (no account)`]] : []),
                ...(selectedJob.tipPaid ? [["💚 Tip", `$${(selectedJob.tipAmount || 0).toFixed(2)} — 100% to ${selectedJob.techName || "provider"}`]] : []),
                ...(selectedJob.battery_upgrade_pending ? [["⚠️ Battery Upgrade", `Requested on-site — $428.00 payment pending`]] : []),
                ...(selectedJob.vehicle ? [["🚗 Vehicle", selectedJob.vehicle]] : []),
                ...(selectedJob.tire_size ? [["🔢 Tire Size", selectedJob.tire_size]] : []),
                ...(selectedJob.fuel_type ? [["⛽ Fuel", selectedJob.fuel_type]] : []),
                ...(selectedJob.note ? [["📝 Note", selectedJob.note]] : []),
              ].map(([k, v, isPhone]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid #222", fontSize: 13, gap: 10 }}>
                  <span style={{ color: "#666", flexShrink: 0 }}>{k}</span>
                  {isPhone ? <a href={`tel:${v}`} style={{ color: "#E63329", fontWeight: 700 }}>{v}</a>
                    : <span style={{ color: "#ccc", maxWidth: "60%", textAlign: "right" }}>{v}</span>}
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", fontSize: 13 }}>
                <span style={{ color: "#666" }}>💰 Amount</span>
                <span style={{ color: "#22c55e", fontWeight: 900, fontSize: 16 }}>{selectedJob.price ? `$${selectedJob.price}` : "Quote"}</span>
              </div>
            </div>

            {/* Send a quote */}
            {(selectedJob.quoteNeeded || selectedJob.partnerId || selectedJob.manual) && !selectedJob.paid && (
              <div style={{ background: "#1a1400", border: "1px solid #f59e0b", borderRadius: 11, padding: 15, marginBottom: 12 }}>
                <div style={{ fontSize: 11, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 8 }}>
                  💵 {selectedJob.quotedPrice ? "QUOTE SENT" : "SEND A QUOTE"}
                </div>

                {selectedJob.quotedPrice ? (
                  <>
                    <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", marginBottom: 4 }}>
                      ${(selectedJob.quotedPrice * (selectedJob.quoteTaxable === false ? 1 : 1.07)).toFixed(2)}
                      <span style={{ fontSize: 12, color: "#888", fontWeight: 600 }}>
                        {selectedJob.quoteTaxable === false ? " flat · no tax" : ` (incl. 7% tax on $${selectedJob.quotedPrice.toFixed(2)})`}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#f59e0b", marginBottom: 12 }}>
                      ⏳ Waiting on payment from {selectedJob.partnerBusiness || selectedJob.customer}
                    </div>
                    {selectedJob.quoteNote && (
                      <div style={{ fontSize: 12, color: "#999", lineHeight: 1.55, marginBottom: 12, background: "#0f0f0f", borderRadius: 7, padding: 10 }}>
                        "{selectedJob.quoteNote}"
                      </div>
                    )}
                    <button style={{ width: "100%", background: "none", border: "1px solid #666", color: "#888", padding: "10px", borderRadius: 8, fontSize: 12, cursor: "pointer" }}
                      onClick={async () => {
                        await dbPatch(`jobs/${selectedJob._key}`, { quotedPrice: null, quoteNote: null, quotedAt: null });
                        setJobs(prev => prev.map(x => x._key === selectedJob._key ? { ...x, quotedPrice: null, quoteNote: null } : x));
                        setSelectedJob(prev => ({ ...prev, quotedPrice: null, quoteNote: null }));
                      }}>
                      Change / cancel this quote
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 12, color: "#bbb", lineHeight: 1.6, marginBottom: 12 }}>
                      Enter what you quoted them. They'll get an alert and can pay it right in their portal — then you dispatch.
                    </div>
                    <label style={c.label}>Quoted Amount ($)</label>
                    <input style={{ ...c.input, marginBottom: 10 }} type="number" inputMode="decimal"
                      placeholder="e.g. 125" value={quoteAmt}
                      onChange={e => setQuoteAmt(e.target.value)} />
                    {/* Tax toggle */}
                    <label style={c.label}>Tax</label>
                    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                      {[[true, "Add 7% tax"], [false, "No tax — flat"]].map(([val, lbl]) => (
                        <button key={String(val)} onClick={() => setQuoteTax(val)}
                          style={{ flex: 1, background: quoteTax === val ? "#f59e0b" : "#161616",
                            border: `1px solid ${quoteTax === val ? "#f59e0b" : "#2a2a2a"}`,
                            color: quoteTax === val ? "#000" : "#888",
                            padding: "11px", borderRadius: 8, fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
                          {lbl}
                        </button>
                      ))}
                    </div>

                    <label style={c.label}>What's Included (optional)</label>
                    <input style={{ ...c.input, marginBottom: 12 }}
                      placeholder="e.g. 2 used tires 205/55R16, mounted & balanced"
                      value={quoteNote} onChange={e => setQuoteNote(e.target.value)} />

                    {quoteAmt && parseFloat(quoteAmt) > 0 && (
                      <div style={{ background: "#0f0f0f", borderRadius: 8, padding: 11, marginBottom: 12, fontSize: 12.5 }}>
                        {quoteTax ? (
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 3 }}>
                              <span>Quote</span><span>${parseFloat(quoteAmt).toFixed(2)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 5 }}>
                              <span>Tax (7%)</span><span>${(parseFloat(quoteAmt) * 0.07).toFixed(2)}</span>
                            </div>
                          </>
                        ) : (
                          <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 5 }}>
                            <span>Flat quote — no tax</span><span>${parseFloat(quoteAmt).toFixed(2)}</span>
                          </div>
                        )}
                        <div style={{ display: "flex", justifyContent: "space-between", color: "#22c55e", fontWeight: 900, fontSize: 14, borderTop: "1px solid #222", paddingTop: 6 }}>
                          <span>They pay</span>
                          <span>${(parseFloat(quoteAmt) * (quoteTax ? 1.07 : 1)).toFixed(2)}</span>
                        </div>
                      </div>
                    )}

                    <button style={{ width: "100%", background: "#f59e0b", border: "none", color: "#000", padding: "13px", borderRadius: 9, fontWeight: 900, fontSize: 14, cursor: "pointer", opacity: quoteAmt ? 1 : 0.4 }}
                      disabled={!quoteAmt || parseFloat(quoteAmt) <= 0}
                      onClick={async () => {
                        const amt = parseFloat(quoteAmt);
                        const patch = { quotedPrice: amt, quoteTaxable: quoteTax, quoteNote: quoteNote.trim() || null, quotedAt: Date.now() };
                        await dbPatch(`jobs/${selectedJob._key}`, patch);
                        setJobs(prev => prev.map(x => x._key === selectedJob._key ? { ...x, ...patch } : x));
                        setSelectedJob(prev => ({ ...prev, ...patch }));
                        setQuoteAmt(""); setQuoteNote(""); setQuoteTax(true);
                        if (selectedJob.partnerId) {
                          await fetch(`${WORKER_URL}/notify-provider`, {
                            method: "POST", headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              providerId: selectedJob.partnerId,
                              title: `💵 Quote Ready — $${(amt * (quoteTax ? 1.07 : 1)).toFixed(2)}`,
                              message: `${selectedJob.service} for ${selectedJob.customer}\n${quoteNote ? quoteNote + "\n" : ""}\nOpen your portal to pay and we'll dispatch.`,
                            }),
                          }).catch(() => {});
                        }
                        alert(`✅ Quote sent — $${(amt * (quoteTax ? 1.07 : 1)).toFixed(2)}${quoteTax ? " with tax" : " flat, no tax"}. They'll see it in their portal.`);
                      }}>
                      💵 Send Quote
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Battery receipt */}
            {selectedJob.batteryReceiptUrl && (
              <div style={{ background: "#161616", border: "1px solid #f59e0b", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                <div style={{ fontSize: 11, letterSpacing: 3, color: "#f59e0b", fontWeight: 700, marginBottom: 8 }}>📄 BATTERY RECEIPT</div>
                <div style={{ fontSize: 11, color: "#666", marginBottom: 8 }}>Uploaded by {selectedJob.techName} — reimburse battery cost</div>
                <img src={selectedJob.batteryReceiptUrl} alt="battery receipt" style={{ width: "100%", borderRadius: 8 }} />
              </div>
            )}

            {/* Tire size warning + edit */}
            {needsTireSize(selectedJob) && (
              <div style={{ background: "#1a0a00", border: "1px solid #f59e0b", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 900, color: "#f59e0b", marginBottom: 6 }}>
                  ⚠️ Tire Size Required Before Dispatch
                </div>
                {selectedJob.vehicle && (
                  <div style={{ fontSize: 12, color: "#aaa", marginBottom: 8 }}>Vehicle: {selectedJob.vehicle} — look up the tire size and enter it below</div>
                )}
                {selectedJob.tire_info_mode === "unknown" && (
                  <div style={{ fontSize: 12, color: "#aaa", marginBottom: 8 }}>Customer did not know their tire size — please enter it manually before dispatching</div>
                )}
                {!editingTireSize ? (
                  <button style={{ background: "#f59e0b", border: "none", color: "#000", padding: "10px 16px", borderRadius: 6, fontWeight: 800, fontSize: 13, cursor: "pointer", width: "100%" }}
                    onClick={() => { setEditingTireSize(true); setEditTireSize(""); }}>
                    ✏️ Enter Tire Size
                  </button>
                ) : (
                  <div>
                    <select style={{ ...c.input, marginBottom: 8 }} value={editTireSize} onChange={e => setEditTireSize(e.target.value)}>
                      <option value="">Select tire size...</option>
                      {["175/65R14","185/65R15","195/65R15","205/55R16","205/60R16","215/55R17","225/45R17","225/50R17","225/55R17","235/45R17","235/55R17","245/45R18","245/50R18","255/45R18","265/50R19","275/40R20","285/40R22","305/35R22"].map(s => <option key={s}>{s}</option>)}
                      <option value="other">Other — type below</option>
                    </select>
                    {editTireSize === "other" && (
                      <input style={{ ...c.input, marginBottom: 8 }} placeholder="Type size e.g. 235/60R18"
                        onChange={e => setEditTireSize(e.target.value)} />
                    )}
                    <div style={{ display: "flex", gap: 8 }}>
                      <button style={{ flex: 1, background: "#E63329", border: "none", color: "#fff", padding: "10px", borderRadius: 6, fontWeight: 800, cursor: "pointer" }}
                        onClick={() => saveTireSize(selectedJob)}>Save Size</button>
                      <button style={{ flex: 1, background: "none", border: "1px solid #444", color: "#888", padding: "10px", borderRadius: 6, cursor: "pointer" }}
                        onClick={() => setEditingTireSize(false)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* GPS Links */}
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedJob.address)}`}
              target="_blank" rel="noreferrer"
              style={{ display: "block", textAlign: "center", background: "#0a1525", border: "1px solid #3b82f6", color: "#3b82f6", padding: 12, borderRadius: 8, marginBottom: 10, textDecoration: "none", fontWeight: 700, fontSize: 14 }}>
              📍 Open Customer Location in Maps
            </a>
            {selectedJob.providerLocation && (
              <a href={`https://www.google.com/maps?q=${selectedJob.providerLocation.latitude},${selectedJob.providerLocation.longitude}`}
                target="_blank" rel="noreferrer"
                style={{ display: "block", textAlign: "center", background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e", padding: 12, borderRadius: 8, marginBottom: 16, textDecoration: "none", fontWeight: 700, fontSize: 14 }}>
                🟢 Track Provider Live Location
              </a>
            )}

            {/* Edit Job — available on new/unassigned jobs */}
            {(selectedJob.status === "new") && (
              <div style={{ marginBottom: 16 }}>
                {!editingJob ? (
                  <button style={{ width: "100%", background: "none", border: "1px solid #f59e0b", color: "#f59e0b", padding: "11px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13, marginBottom: 10 }}
                    onClick={() => {
                      setEjName(selectedJob.customer || "");
                      setEjPhone(selectedJob.phone || "");
                      setEjAddress(selectedJob.address || "");
                      setEjService(selectedJob.service || "");
                      setEjNote(selectedJob.note || "");
                      setEjPay(selectedJob.providerPay || "");
                      setEditingJob(true);
                    }}>
                    ✏️ Edit Job Details
                  </button>
                ) : (
                  <div style={{ background: "#161616", border: "1px solid #f59e0b", borderRadius: 10, padding: 14, marginBottom: 10 }}>
                    <div style={{ fontSize: 11, letterSpacing: 3, color: "#f59e0b", fontWeight: 700, marginBottom: 12 }}>EDIT JOB</div>
                    <div style={{ marginBottom: 10 }}><label style={c.label}>Customer Name</label><input style={c.input} value={ejName} onChange={e => setEjName(e.target.value)} /></div>
                    <div style={{ marginBottom: 10 }}><label style={c.label}>Phone</label><input style={c.input} type="tel" value={ejPhone} onChange={e => setEjPhone(e.target.value)} /></div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={c.label}>Address</label>
                      <AddressFields value={ejAddress} onChange={val => setEjAddress(val)} />
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={c.label}>Service</label>
                      <select style={c.input} value={ejService} onChange={e => setEjService(e.target.value)}>
                        <option>Tire Change</option>
                        <option>Jump Start</option>
                        <option>Fuel Delivery</option>
                        <option>Lockout Service</option>
                        <option>Tow Truck</option>
                        <option>Mobile Mechanic</option>
                      </select>
                    </div>
                    <div style={{ marginBottom: 12 }}><label style={c.label}>Notes</label><input style={c.input} value={ejNote} onChange={e => setEjNote(e.target.value)} placeholder="Optional notes..." /></div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={c.label}>Provider Pay ($)</label>
                      <input style={c.input} type="number" placeholder="e.g. 40" value={ejPay} onChange={e => setEjPay(e.target.value)} />
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button style={{ flex: 1, background: "#E63329", border: "none", color: "#fff", padding: "11px", borderRadius: 8, fontWeight: 800, cursor: "pointer" }}
                        onClick={async () => {
                          const updates = { customer: ejName, phone: ejPhone, address: ejAddress, service: ejService, note: ejNote, providerPay: ejPay ? parseFloat(ejPay) : null };
                          await dbPatch(`jobs/${selectedJob._key}`, updates);
                          setJobs(prev => prev.map(j => j._key === selectedJob._key ? { ...j, ...updates } : j));
                          setSelectedJob(prev => ({ ...prev, ...updates }));
                          setEditingJob(false);
                        }}>
                        Save Changes
                      </button>
                      <button style={{ flex: 1, background: "none", border: "1px solid #444", color: "#888", padding: "11px", borderRadius: 8, cursor: "pointer" }}
                        onClick={() => setEditingJob(false)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Assign provider */}
            {selectedJob.status === "new" && (
              <div style={{ marginBottom: 16 }}>
                <label style={c.label}>ASSIGN PROVIDER</label>

                <input
                  style={{ ...c.input, marginBottom: 8 }}
                  placeholder="🔍 Search by name, city, or state..."
                  value={dispatchSearch}
                  onChange={e => setDispatchSearch(e.target.value)}
                />

                {(() => {
                  const q = dispatchSearch.trim().toLowerCase();
                  const eligible = providers.filter(p => p.status !== "pending");
                  const matches = q
                    ? eligible.filter(p => [
                        p.firstName, p.lastName, `${p.firstName} ${p.lastName}`,
                        p.city, p.state, STATE_NAME[p.state], p.group, p.phone,
                      ].some(v => (v || "").toLowerCase().includes(q)))
                    : eligible;

                  return (
                    <>
                      {q && (
                        <div style={{ fontSize: 11, color: "#666", marginBottom: 8 }}>
                          {matches.length} provider{matches.length !== 1 ? "s" : ""} match "{dispatchSearch}"
                        </div>
                      )}

                      {/* Tappable results — no scrolling a giant dropdown */}
                      <div style={{ maxHeight: 260, overflowY: "auto", marginBottom: 10 }}>
                        <button
                          onClick={() => setAssignTech("admin")}
                          style={{ width: "100%", textAlign: "left", background: assignTech === "admin" ? "#1a0a0a" : "#141414", border: `1px solid ${assignTech === "admin" ? "#E63329" : "#242424"}`, borderRadius: 9, padding: "11px 13px", marginBottom: 7, cursor: "pointer" }}>
                          <span style={{ fontSize: 13.5, fontWeight: 800, color: assignTech === "admin" ? "#E63329" : "#ccc" }}>
                            👤 Admin (Yourself)
                          </span>
                        </button>

                        {matches.length === 0 && (
                          <div style={{ textAlign: "center", padding: "22px 0", color: "#555", fontSize: 12.5 }}>
                            No providers match that search.
                          </div>
                        )}

                        {matches.map(p => {
                          const on = assignTech === p._key;
                          const svc = (p.services || []).map(x => (SERVICE_LABELS[x] || "").split(" ")[0]).join(" ");
                          return (
                            <button key={p._key}
                              onClick={() => setAssignTech(p._key)}
                              style={{ width: "100%", textAlign: "left", background: on ? "#0a1f0a" : "#141414", border: `1px solid ${on ? "#22c55e" : "#242424"}`, borderRadius: 9, padding: "11px 13px", marginBottom: 7, cursor: "pointer", display: "flex", alignItems: "center", gap: 11 }}>
                              {p.selfieUrl ? (
                                <img src={p.selfieUrl} alt="" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: `1px solid ${on ? "#22c55e" : "#333"}` }} />
                              ) : (
                                <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#E63329", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, flexShrink: 0 }}>
                                  {p.firstName?.[0]}{p.lastName?.[0]}
                                </div>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13.5, fontWeight: 800, color: on ? "#22c55e" : "#eee" }}>
                                  {p.firstName} {p.lastName}
                                </div>
                                <div style={{ fontSize: 10.5, color: "#777", marginTop: 1 }}>
                                  {p.city ? `📍 ${p.city}, ${p.state || ""}` : "📍 No area set"}
                                  {p.serviceRadius ? ` · ${p.serviceRadius}mi` : ""}
                                </div>
                                {svc && <div style={{ fontSize: 11, marginTop: 2 }}>{svc}</div>}
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, flexShrink: 0 }}>
                                <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 9, background: p.online ? "#0a1f0a" : "#1a1a1a", color: p.online ? "#22c55e" : "#555" }}>
                                  {p.online ? "● ONLINE" : "○ OFF"}
                                </span>
                                {!p.payoutsEnabled && (
                                  <span style={{ fontSize: 9, color: "#f59e0b" }}>no bank</span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
                <button style={{ ...c.btnPrimary, marginTop: 10 }} onClick={async () => {
                  if (!assignTech) return;
                  if (needsTireSize(selectedJob)) return alert("⚠️ Please enter the tire size before dispatching.");
                  let techName, techUid, techEmail;
                  if (assignTech === "admin") {
                    techName = "Admin"; techUid = "admin"; techEmail = "superfastmobiletires@gmail.com";
                  } else {
                    const provider = providers.find(p => p._key === assignTech);
                    techName = provider ? `${provider.firstName} ${provider.lastName}` : assignTech;
                    techUid = provider?._key || assignTech;
                    techEmail = provider?.email || "";
                  }
                  await dbPatch(`jobs/${selectedJob._key}`, { tech: techUid, techName, techEmail, status: "assigned" });
                  setJobs(prev => prev.map(j => j._key === selectedJob._key ? { ...j, tech: techUid, techName, status: "assigned" } : j));
                  setSelectedJob(prev => ({ ...prev, tech: techUid, techName, status: "assigned" }));
                  if (techUid && techUid !== "admin") {
                    const pay = getProviderPay(selectedJob);
                    fetch(`${WORKER_URL}/notify-provider`, {
                      method: "POST", headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        providerId: techUid,
                        title: `🔥 NEW JOB — ${selectedJob.service}`,
                        message: `${selectedJob.customer}\n📍 ${selectedJob.address}\n💰 Your pay: $${pay.pay || "—"}\n\n⏱️ Open the app within 60 seconds to accept.`,
                      }),
                    }).catch(() => {});
                  }
                  setAssignTech("");
                }} disabled={!assignTech}>Assign & Dispatch →</button>
              </div>
            )}
            {selectedJob.status === "assigned" && (
              <>
                <button style={{ ...c.btnPrimary, background: "#0a1525", color: "#3b82f6", border: "1px solid #3b82f6" }}
                  onClick={async () => {
                    await dbPatch(`jobs/${selectedJob._key}`, { status: "en_route" });
                    setJobs(prev => prev.map(j => j._key === selectedJob._key ? { ...j, status: "en_route" } : j));
                    setSelectedJob(prev => ({ ...prev, status: "en_route" }));
                  }}>🛻 Mark En Route</button>
                <button style={{ background: "none", border: "1px solid #f59e0b", color: "#f59e0b", width: "100%", padding: "11px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13, marginTop: 8 }}
                  onClick={async () => {
                    if (!window.confirm("Unassign this job? It will go back to New.")) return;
                    await dbPatch(`jobs/${selectedJob._key}`, { status: "new", tech: null, techName: null, techEmail: null });
                    setJobs(prev => prev.map(j => j._key === selectedJob._key ? { ...j, status: "new", tech: null, techName: null } : j));
                    setSelectedJob(prev => ({ ...prev, status: "new", tech: null, techName: null }));
                  }}>↩️ Unassign Job</button>
              </>
            )}
            {selectedJob.status === "en_route" && (
              <>
                <button style={{ ...c.btnPrimary, background: "#0a1f0a", color: "#22c55e", border: "1px solid #22c55e" }}
                  onClick={async () => {
                    await dbPatch(`jobs/${selectedJob._key}`, { status: "on_scene" });
                    setJobs(prev => prev.map(j => j._key === selectedJob._key ? { ...j, status: "on_scene" } : j));
                    setSelectedJob(prev => ({ ...prev, status: "on_scene" }));
                  }}>📍 Mark On Scene</button>
                <button style={{ background: "none", border: "1px solid #f59e0b", color: "#f59e0b", width: "100%", padding: "11px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13, marginTop: 8 }}
                  onClick={async () => {
                    if (!window.confirm("Unassign this job? Provider is en route. It will go back to New.")) return;
                    await dbPatch(`jobs/${selectedJob._key}`, { status: "new", tech: null, techName: null, techEmail: null });
                    setJobs(prev => prev.map(j => j._key === selectedJob._key ? { ...j, status: "new", tech: null, techName: null } : j));
                    setSelectedJob(prev => ({ ...prev, status: "new", tech: null, techName: null }));
                  }}>↩️ Unassign Job</button>
              </>
            )}
            {selectedJob.status === "on_scene" && (
              <button style={{ ...c.btnPrimary, background: "#0a1f0a", color: "#22c55e", border: "1px solid #22c55e" }}
                onClick={async () => {
                  await dbPatch(`jobs/${selectedJob._key}`, { status: "completed" });
                  setJobs(prev => prev.map(j => j._key === selectedJob._key ? { ...j, status: "completed" } : j));
                  setSelectedJob(prev => ({ ...prev, status: "completed" }));
                }}>✅ Mark Complete</button>
            )}
            {selectedJob.status === "completed" && selectedJob.paymentIntentId && (
              <button style={{ width: "100%", background: "#1a0000", border: "1px solid #E63329", color: "#E63329", padding: "11px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", marginTop: 8 }}
                onClick={async () => {
                  if (!window.confirm(`Issue refund of $${selectedJob.price} to ${selectedJob.customer}?`)) return;
                  try {
                    const res = await fetch(`${WORKER_URL}/refund`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ paymentIntentId: selectedJob.paymentIntentId }),
                    });
                    const data = await res.json();
                    if (data.refunded) {
                      await dbPatch(`jobs/${selectedJob._key}`, { refunded: true, refundedAt: Date.now() });
                      setSelectedJob(prev => ({ ...prev, refunded: true }));
                      alert("✅ Refund issued successfully.");
                    } else {
                      alert("⚠️ Refund failed: " + (data.error || "Unknown error"));
                    }
                  } catch (err) {
                    alert("⚠️ Error: " + err.message);
                  }
                }}>
                💸 Issue Refund
              </button>
            )}
            {selectedJob.refunded && (
              <div style={{ textAlign: "center", fontSize: 12, color: "#E63329", marginTop: 8 }}>⚠️ Refund issued</div>
            )}

            {/* Photos */}
            {selectedJob.checkin_front && (
              <>
                <div style={{ fontSize: 11, letterSpacing: 3, color: "#666", fontWeight: 700, margin: "20px 0 10px" }}>CHECK-IN PHOTOS</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {["front","back","driver","passenger","service"].map(k => selectedJob[`checkin_${k}`] && (
                    <div key={k}>
                      <div style={{ fontSize: 9, color: "#666", marginBottom: 4, textTransform: "uppercase" }}>{k}</div>
                      <img src={selectedJob[`checkin_${k}`]} alt={k} style={{ width: "100%", borderRadius: 6, height: 90, objectFit: "cover" }} />
                    </div>
                  ))}
                </div>
              </>
            )}
            {selectedJob.checkout_front && (
              <>
                <div style={{ fontSize: 11, letterSpacing: 3, color: "#666", fontWeight: 700, margin: "20px 0 10px" }}>CHECK-OUT PHOTOS</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {["front","back","driver","passenger","service"].map(k => selectedJob[`checkout_${k}`] && (
                    <div key={k}>
                      <div style={{ fontSize: 9, color: "#666", marginBottom: 4, textTransform: "uppercase" }}>{k}</div>
                      <img src={selectedJob[`checkout_${k}`]} alt={k} style={{ width: "100%", borderRadius: 6, height: 90, objectFit: "cover" }} />
                    </div>
                  ))}
                </div>
              </>
            )}
            {selectedJob.signatureUrl && (
              <>
                <div style={{ fontSize: 11, letterSpacing: 3, color: "#666", fontWeight: 700, margin: "20px 0 10px" }}>CUSTOMER SIGNATURE</div>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>Signed by: {selectedJob.signedBy}</div>
                <img src={selectedJob.signatureUrl} alt="signature" style={{ width: "100%", borderRadius: 8, background: "#1a1a1a", padding: 8 }} />
              </>
            )}
          </div>
        </div>
      )}

      {/* MAIN CONTENT */}
      <div style={{ padding: "16px 16px 100px" }}>

        {/* STATS VIEW */}
        {view === "stats" && (() => {
          // Build weekly periods
          const completedJobs = jobs.filter(j => j.status === "completed");
          
          // Group jobs by week
          function getWeekStart(date) {
            const d = new Date(date);
            const day = d.getDay();
            const daysSinceThursday = (day + 3) % 7;
            d.setDate(d.getDate() - daysSinceThursday);
            d.setHours(0, 0, 0, 0);
            return d;
          }

          const weekMap = {};
          completedJobs.forEach(job => {
            const weekStart = getWeekStart(job.createdAt);
            const key = weekStart.toISOString();
            if (!weekMap[key]) weekMap[key] = { start: weekStart, jobs: [] };
            weekMap[key].jobs.push(job);
          });

          const weeks = Object.values(weekMap).sort((a, b) => b.start - a.start);

          return (
            <>
              <div style={{ fontSize: 14, fontWeight: 800, color: WHITE, marginBottom: 16 }}>Revenue Overview</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 24 }}>
                {[["📋","Total Jobs",jobs.length,"#3b82f6"],["💰","Collected",`$${totalRevenue}`,"#22c55e"],["⏳","Pending",`$${todayRevenue - totalRevenue}`,"#f59e0b"],["🆕","New",counts.new,"#E63329"],["🛻","En Route",counts.en_route,"#3b82f6"],["✅","Done",counts.completed,"#22c55e"]].map(([icon,label,val,color]) => (
                  <div key={label} style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 14 }}>
                    <div style={{ fontSize: 20, marginBottom: 6 }}>{icon}</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color, marginBottom: 2 }}>{val}</div>
                    <div style={{ fontSize: 10, color: "#666", letterSpacing: 1 }}>{label}</div>
                  </div>
                ))}
              </div>

              {/* Weekly breakdown */}
              <div style={{ fontSize: 11, letterSpacing: 3, color: "#666", fontWeight: 700, marginBottom: 14 }}>WEEKLY EARNINGS HISTORY</div>
              {weeks.length === 0 && <div style={{ color: "#444", fontSize: 13 }}>No completed jobs yet.</div>}
              {weeks.map(({ start, jobs: weekJobs }) => {
                const end = new Date(start);
                end.setDate(start.getDate() + 6);
                const weekRevenue = weekJobs.reduce((s, j) => s + (j.price || 0), 0);
                const providerPayout = weekJobs.reduce((s, j) => s + (getProviderPay(j).pay || 0), 0);
                const yourKeep = weekRevenue - providerPayout;
                const fmt = d => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                
                return (
                  <div key={start.toISOString()} style={{ background: "#161616", border: "1px solid #222", borderRadius: 12, padding: 16, marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: WHITE }}>{fmt(start)} — {fmt(end)}</div>
                        <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{weekJobs.length} job{weekJobs.length !== 1 ? "s" : ""} completed</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: "#22c55e" }}>${weekRevenue.toFixed(2)}</div>
                        <div style={{ fontSize: 10, color: "#666" }}>collected</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #222", paddingTop: 10, fontSize: 12 }}>
                      <div>
                        <div style={{ color: "#666", marginBottom: 2 }}>Provider Payout</div>
                        <div style={{ color: "#f59e0b", fontWeight: 700 }}>-${providerPayout}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ color: "#666", marginBottom: 2 }}>You Keep</div>
                        <div style={{ color: "#22c55e", fontWeight: 700 }}>${yourKeep.toFixed(2)}</div>
                      </div>
                    </div>
                    {/* Job breakdown */}
                    <div style={{ marginTop: 10, borderTop: "1px solid #1a1a1a", paddingTop: 10 }}>
                      {weekJobs.map(job => (
                        <div key={job._key} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "4px 0", color: "#666" }}>
                          <span>{SERVICE_ICONS[job.service] || "🔧"} {job.service} — {job.customer}</span>
                          <span style={{ color: "#aaa" }}>${job.price || "Quote"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          );
        })()}

        {/* PROVIDERS VIEW */}
        {view === "providers" && (() => {
          const pendingProviders = providers.filter(p => p.status === "pending");
          const activeProviders = providers.filter(p => p.status !== "pending");

          async function approveProvider(p) {
            await dbPatch(`users/${p._key}`, { 
              status: "approved",
              approvedAt: Date.now(),
              role: "provider",
            });
            await fetch(`${WORKER_URL}/notify`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                title: "✅ Provider Approved",
                message: `${p.firstName} ${p.lastName} approved.\nEmail: ${p.email}\nPhone: ${p.phone}\nThey can now log in to the provider portal.`,
              }),
            }).catch(() => {});
            setProviders(prev => prev.map(x => x._key === p._key ? { ...x, status: "approved" } : x));
          }

          async function denyProvider(p) {
            if (!window.confirm(`Deny ${p.firstName} ${p.lastName}'s application?`)) return;
            await dbDelete(`users/${p._key}`);
            setProviders(prev => prev.filter(x => x._key !== p._key));
          }

          return (
            <>
              {/* Pending Applications */}
              {pendingProviders.length > 0 && (
                <>
                  <div style={{ fontSize: 11, letterSpacing: 3, color: "#f59e0b", fontWeight: 700, marginBottom: 12 }}>⏳ PENDING APPLICATIONS ({pendingProviders.length})</div>
                  {pendingProviders.map(p => (
                    <div key={p._key} style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: WHITE, marginBottom: 4 }}>{p.firstName} {p.lastName}</div>
                      <a href={`tel:${p.phone}`} style={{ fontSize: 13, color: "#E63329", display: "block", marginBottom: 4 }}>{p.phone}</a>
                      <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>{p.email}</div>
                      {p.vehicle && <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>🚗 {p.vehicle}</div>}
                      {p.city && p.state && (
                        <div style={{ fontSize: 11, color: "#22c55e", marginBottom: 4, fontWeight: 700 }}>
                          📍 {p.city}, {p.state} · {p.serviceRadius || "?"} mi radius
                        </div>
                      )}
                      {p.selfieUrl && (
                        <div style={{ margin: "10px 0" }}>
                          <div style={{ fontSize: 10, letterSpacing: 2, color: "#f59e0b", fontWeight: 700, marginBottom: 6 }}>📸 IDENTITY SELFIE</div>
                          <img src={p.selfieUrl} alt="provider selfie"
                            onClick={() => setPhotoViewer(p.selfieUrl)}
                            style={{ width: "100%", maxWidth: 220, borderRadius: 10, border: "1px solid #333", cursor: "pointer", display: "block" }} />
                          <div style={{ fontSize: 10, color: "#666", marginTop: 5 }}>Tap to view full size before approving</div>
                        </div>
                      )}
                      {p.experience && <div style={{ fontSize: 11, color: "#666", marginBottom: 8 }}>📅 {p.experience} experience</div>}
                      {p.services && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                          {p.services.map(s => (
                            <span key={s} style={{ fontSize: 11, background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e", padding: "3px 8px", borderRadius: 10 }}>
                              {SERVICE_LABELS[s] || s}
                            </span>
                          ))}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8 }}>
                        <button style={{ flex: 1, background: "#22c55e", border: "none", color: "#000", padding: "10px", borderRadius: 6, fontWeight: 800, cursor: "pointer" }}
                          onClick={() => approveProvider(p)}>✅ Approve</button>
                        <button style={{ flex: 1, background: "none", border: "1px solid #E63329", color: "#E63329", padding: "10px", borderRadius: 6, fontWeight: 700, cursor: "pointer" }}
                          onClick={() => denyProvider(p)}>❌ Deny</button>
                      </div>
                    </div>
                  ))}
                  <div style={{ height: 1, background: "#222", marginBottom: 16 }} />
                </>
              )}

              {/* Create provider manually */}
              <div style={{ fontSize: 14, fontWeight: 800, color: WHITE, marginBottom: 16 }}>➕ Create Provider Account</div>
              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1 }}><label style={c.label}>First Name</label><input style={c.input} placeholder="Carlos" value={npFirst} onChange={e => setNpFirst(e.target.value)} /></div>
                <div style={{ flex: 1 }}><label style={c.label}>Last Name</label><input style={c.input} placeholder="Martinez" value={npLast} onChange={e => setNpLast(e.target.value)} /></div>
              </div>
              <div style={{ marginBottom: 12 }}><label style={c.label}>Email</label><input style={c.input} type="email" placeholder="carlos@email.com" value={npEmail} onChange={e => setNpEmail(e.target.value)} /></div>
              <div style={{ marginBottom: 12 }}><label style={c.label}>Phone (optional)</label><input style={c.input} type="tel" placeholder="(561) 000-0000" value={npPhone} onChange={e => setNpPhone(e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={c.label}>Password</label><input style={c.input} type="password" placeholder="Min 6 characters" value={npPass} onChange={e => setNpPass(e.target.value)} /></div>
              {npError && <div style={c.gpsError}>⚠️ {npError}</div>}
              {npSuccess && <div style={c.gpsSuccess}>{npSuccess}</div>}
              <button style={c.btnPrimary} onClick={createProvider} disabled={npLoading}>{npLoading ? "Creating..." : "Create Provider Account →"}</button>

              {/* Provider Profile Modal */}
              {selectedProvider && (() => {
                const p = selectedProvider;
                const pName = `${p.firstName} ${p.lastName}`;
                const pJobs = jobs.filter(j => j.techName === pName || j.tech === p._key);
                const pCompleted = pJobs.filter(j => j.status === "completed");
                const pRevenue = pCompleted.reduce((s, j) => s + (getProviderPay(j).pay || 0), 0);
                const pReviews = reviews.filter(r => r.techName === pName);
                const avgRating = pReviews.length > 0 ? (pReviews.reduce((s, r) => s + r.stars, 0) / pReviews.length).toFixed(1) : null;

                return (
                  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.95)", zIndex: 300, overflowY: "auto", maxWidth: 600, margin: "0 auto" }}>
                    <div style={{ padding: "16px 16px 100px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                        <button style={{ background: "none", border: "1px solid #333", color: "#aaa", padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}
                          onClick={() => { setSelectedProvider(null); setEditingProvider(false); }}>← Back to Providers</button>
                        <button style={{ background: editingProvider ? "#2a2a2a" : "#1a1400", border: `1px solid ${editingProvider ? "#555" : "#f59e0b"}`, color: editingProvider ? "#aaa" : "#f59e0b", padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12.5, fontWeight: 700 }}
                          onClick={() => {
                            if (!editingProvider) {
                              setEpForm({
                                firstName: p.firstName || "", lastName: p.lastName || "",
                                phone: p.phone || "", email: p.email || "", vehicle: p.vehicle || "",
                                city: p.city || "", state: p.state || "", serviceRadius: p.serviceRadius || "",
                                group: p.group || "", services: p.services || [],
                                experience: p.experience || "", notes: p.adminNotes || "",
                              });
                            }
                            setEditingProvider(v => !v);
                          }}>
                          {editingProvider ? "✕ Cancel" : "✏️ Edit Provider"}
                        </button>
                      </div>

                      {editingProvider && (
                        <div style={{ background: "#161616", border: "2px solid #f59e0b", borderRadius: 12, padding: 18, marginBottom: 18 }}>
                          <div style={{ fontSize: 11, letterSpacing: 2.5, color: "#f59e0b", fontWeight: 800, marginBottom: 16 }}>EDIT PROVIDER</div>

                          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                            <div style={{ flex: 1 }}><label style={c.label}>First Name</label>
                              <input style={c.input} value={epForm.firstName} onChange={e => setEpForm({ ...epForm, firstName: e.target.value })} /></div>
                            <div style={{ flex: 1 }}><label style={c.label}>Last Name</label>
                              <input style={c.input} value={epForm.lastName} onChange={e => setEpForm({ ...epForm, lastName: e.target.value })} /></div>
                          </div>
                          <div style={{ marginBottom: 12 }}><label style={c.label}>Phone</label>
                            <input style={c.input} type="tel" value={epForm.phone} onChange={e => setEpForm({ ...epForm, phone: e.target.value })} /></div>
                          <div style={{ marginBottom: 12 }}><label style={c.label}>Vehicle</label>
                            <input style={c.input} value={epForm.vehicle} onChange={e => setEpForm({ ...epForm, vehicle: e.target.value })} /></div>

                          {/* GROUP */}
                          <div style={{ marginBottom: 12 }}>
                            <label style={c.label}>Group / Team</label>
                            <input style={{ ...c.input, marginBottom: 8 }} placeholder="e.g. Palm Beach Crew, Night Shift, Tow Team"
                              value={epForm.group} onChange={e => setEpForm({ ...epForm, group: e.target.value })} />
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {[...new Set(providers.map(x => x.group).filter(Boolean))].map(g => (
                                <button key={g} onClick={() => setEpForm({ ...epForm, group: g })}
                                  style={{ background: epForm.group === g ? "#a855f7" : "#1a1a1a", border: `1px solid ${epForm.group === g ? "#a855f7" : "#333"}`, color: epForm.group === g ? "#fff" : "#888", padding: "5px 11px", borderRadius: 12, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>
                                  {g}
                                </button>
                              ))}
                              {["Night Shift", "Tow Team", "Senior Tech", "New / Training"].filter(g => !providers.some(x => x.group === g)).map(g => (
                                <button key={g} onClick={() => setEpForm({ ...epForm, group: g })}
                                  style={{ background: epForm.group === g ? "#a855f7" : "none", border: `1px dashed ${epForm.group === g ? "#a855f7" : "#444"}`, color: epForm.group === g ? "#fff" : "#666", padding: "5px 11px", borderRadius: 12, fontSize: 11, cursor: "pointer" }}>
                                  + {g}
                                </button>
                              ))}
                              {epForm.group && (
                                <button onClick={() => setEpForm({ ...epForm, group: "" })}
                                  style={{ background: "none", border: "1px solid #E63329", color: "#E63329", padding: "5px 11px", borderRadius: 12, fontSize: 11, cursor: "pointer" }}>
                                  ✕ Clear
                                </button>
                              )}
                            </div>
                          </div>

                          {/* SERVICE AREA */}
                          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                            <div style={{ flex: 1 }}><label style={c.label}>State</label>
                              <select style={c.input} value={epForm.state} onChange={e => setEpForm({ ...epForm, state: e.target.value })}>
                                <option value="">—</option>
                                {US_STATES.map(st => <option key={st.code} value={st.code}>{st.code}</option>)}
                              </select></div>
                            <div style={{ flex: 2 }}><label style={c.label}>City</label>
                              <input style={c.input} value={epForm.city} onChange={e => setEpForm({ ...epForm, city: e.target.value })} /></div>
                          </div>
                          <div style={{ marginBottom: 12 }}><label style={c.label}>Service Radius</label>
                            <select style={c.input} value={epForm.serviceRadius} onChange={e => setEpForm({ ...epForm, serviceRadius: e.target.value })}>
                              <option value="">—</option>
                              <option value="50">50 miles</option>
                              <option value="100">100 miles</option>
                              <option value="200">200 miles</option>
                            </select></div>

                          {/* SERVICES */}
                          <div style={{ marginBottom: 12 }}>
                            <label style={c.label}>Services</label>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {Object.entries(SERVICE_LABELS).map(([id, lbl]) => {
                                const on = (epForm.services || []).includes(id);
                                return (
                                  <button key={id}
                                    onClick={() => setEpForm(f => ({ ...f, services: on ? f.services.filter(x => x !== id) : [...(f.services || []), id] }))}
                                    style={{ background: on ? "#0a1f0a" : "#1a1a1a", border: `1px solid ${on ? "#22c55e" : "#333"}`, color: on ? "#22c55e" : "#777", padding: "6px 11px", borderRadius: 12, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>
                                    {lbl}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <div style={{ marginBottom: 14 }}><label style={c.label}>Admin Notes (private)</label>
                            <textarea style={{ ...c.input, minHeight: 70, resize: "vertical" }}
                              placeholder="Notes only you can see — reliability, equipment, reminders..."
                              value={epForm.notes} onChange={e => setEpForm({ ...epForm, notes: e.target.value })} /></div>

                          <button style={{ ...c.btnPrimary, background: "#f59e0b", color: "#000" }} disabled={epSaving}
                            onClick={async () => {
                              setEpSaving(true);
                              const patch = {
                                firstName: epForm.firstName.trim(), lastName: epForm.lastName.trim(),
                                phone: epForm.phone.trim(), vehicle: epForm.vehicle.trim(),
                                city: epForm.city.trim(), state: epForm.state,
                                serviceRadius: epForm.serviceRadius,
                                group: epForm.group.trim() || null,
                                services: epForm.services,
                                adminNotes: epForm.notes.trim() || null,
                                editedByAdminAt: Date.now(),
                              };
                              await dbPatch(`users/${p._key}`, patch);
                              setProviders(prev => prev.map(x => x._key === p._key ? { ...x, ...patch } : x));
                              setSelectedProvider(prev => ({ ...prev, ...patch }));
                              setEpSaving(false);
                              setEditingProvider(false);
                            }}>
                            {epSaving ? "Saving..." : "💾 Save Changes"}
                          </button>
                        </div>
                      )}

                      {/* Provider header */}
                      <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 12, padding: 20, marginBottom: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                          {p.selfieUrl ? (
                            <img src={p.selfieUrl} alt={`${p.firstName} ${p.lastName}`}
                              onClick={() => setPhotoViewer(p.selfieUrl)}
                              style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", border: "2px solid #22c55e", cursor: "pointer", flexShrink: 0 }} />
                          ) : (
                            <div style={{ width: 64, height: 64, background: "#E63329", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, color: "#fff", fontSize: 22, flexShrink: 0 }}>
                              {p.firstName?.[0]}{p.lastName?.[0]}
                            </div>
                          )}
                          <div>
                            <div style={{ fontSize: 18, fontWeight: 900, color: WHITE }}>{p.firstName} {p.lastName}</div>
                            <div style={{ fontSize: 12, color: p.online ? "#22c55e" : "#666" }}>{p.online ? "● Online Now" : "○ Offline"}</div>
                          </div>
                        </div>

                        {[
                          ["📞 Phone", p.phone, true],
                          ["📧 Email", p.email],
                          ["🚗 Vehicle", p.vehicle],
                          ["👥 Group", p.group],
                          ["📍 Service Area", p.city && p.state ? `${p.city}, ${p.state}` : null],
                          ["🎯 Radius", p.serviceRadius ? `${p.serviceRadius} miles` : null],
                          ["📅 Experience", p.experience],
                          ["📆 Joined", p.appliedAt ? new Date(p.appliedAt).toLocaleDateString() : "N/A"],
                        ].filter(([,v]) => v).map(([k, v, isPhone]) => (
                          <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #222", fontSize: 13 }}>
                            <span style={{ color: "#666" }}>{k}</span>
                            {isPhone
                              ? <a href={`tel:${v}`} style={{ color: "#E63329", fontWeight: 700 }}>{v}</a>
                              : <span style={{ color: "#ccc" }}>{v}</span>}
                          </div>
                        ))}

                        {/* Services */}
                        {p.services && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 10, color: "#666", letterSpacing: 2, marginBottom: 8 }}>SERVICES</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                              {p.services.map(s => <span key={s} style={{ fontSize: 12, background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e", padding: "4px 10px", borderRadius: 10 }}>{SERVICE_LABELS[s] || s}</span>)}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Identity selfie */}
                      <div style={{ background: "#161616", border: `1px solid ${p.selfieUrl ? "#22c55e" : "#f59e0b"}`, borderRadius: 12, padding: 14, marginBottom: 16 }}>
                        <div style={{ fontSize: 10, letterSpacing: 2, color: p.selfieUrl ? "#22c55e" : "#f59e0b", fontWeight: 800, marginBottom: 10 }}>
                          📸 IDENTITY SELFIE
                        </div>
                        {p.selfieUrl ? (
                          <>
                            <img src={p.selfieUrl} alt="identity selfie"
                              onClick={() => setPhotoViewer(p.selfieUrl)}
                              style={{ width: "100%", maxWidth: 260, borderRadius: 10, border: "1px solid #333", cursor: "pointer", display: "block" }} />
                            <div style={{ fontSize: 10.5, color: "#666", marginTop: 8 }}>
                              Tap to view full size{p.selfieTakenAt ? ` · taken ${new Date(p.selfieTakenAt).toLocaleDateString()}` : ""}
                              {p.selfieConfirmed && " · provider confirmed requirements"}
                            </div>
                          </>
                        ) : (
                          <div style={{ fontSize: 12, color: "#888", lineHeight: 1.55 }}>
                            No selfie on file — this provider signed up before identity photos were required. Message them to add one.
                          </div>
                        )}
                      </div>

                      {/* Admin notes */}
                      {p.adminNotes && (
                        <div style={{ background: "#1a1400", border: "1px solid #f59e0b", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                          <div style={{ fontSize: 10, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 6 }}>📝 ADMIN NOTES (PRIVATE)</div>
                          <div style={{ fontSize: 12.5, color: "#ccc", lineHeight: 1.6 }}>{p.adminNotes}</div>
                        </div>
                      )}

                      {/* Stats */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
                        {[
                          ["📋", "Total Jobs", pJobs.length, "#3b82f6"],
                          ["✅", "Completed", pCompleted.length, "#22c55e"],
                          ["💰", "Total Earned", `$${pRevenue}`, "#f59e0b"],
                        ].map(([icon, label, val, color]) => (
                          <div key={label} style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 12, textAlign: "center" }}>
                            <div style={{ fontSize: 20 }}>{icon}</div>
                            <div style={{ fontSize: 16, fontWeight: 900, color, marginTop: 4 }}>{val}</div>
                            <div style={{ fontSize: 9, color: "#555", letterSpacing: 1, marginTop: 2 }}>{label}</div>
                          </div>
                        ))}
                      </div>

                      {/* Rating */}
                      {avgRating && (
                        <div style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                          <div style={{ fontSize: 10, color: "#666", letterSpacing: 2, marginBottom: 8 }}>CUSTOMER RATINGS</div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ fontSize: 28, fontWeight: 900, color: "#f59e0b" }}>{avgRating} ⭐</div>
                            <div style={{ fontSize: 12, color: "#666" }}>{pReviews.length} review{pReviews.length !== 1 ? "s" : ""}</div>
                          </div>
                          {pReviews.slice(0, 3).map((r, i) => (
                            <div key={i} style={{ borderTop: "1px solid #222", paddingTop: 8, marginTop: 8 }}>
                              <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span style={{ fontSize: 12, color: "#aaa" }}>{r.customer}</span>
                                <span style={{ color: "#f59e0b" }}>{"⭐".repeat(r.stars)}</span>
                              </div>
                              {r.comment && <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{r.comment}</div>}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Pay provider via Stripe Connect */}
                      {p.payoutsEnabled && p.stripeAccountId && (
                        <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                          <div style={{ fontSize: 10, letterSpacing: 2, color: "#22c55e", fontWeight: 800, marginBottom: 6 }}>🏦 DIRECT DEPOSIT ENABLED</div>
                          <div style={{ fontSize: 11, color: "#4caf50", marginBottom: 10 }}>
                            {p.bankName || "Bank"} ••••{p.bankLast4 || "____"}
                          </div>
                          <button style={{ width: "100%", background: "#22c55e", border: "none", color: "#000", padding: "11px", borderRadius: 8, fontWeight: 800, fontSize: 13, cursor: "pointer" }}
                            onClick={async () => {
                              const amt = window.prompt(`Pay ${pName} — enter amount in dollars:`, String(pRevenue || ""));
                              if (!amt) return;
                              const n = parseFloat(amt);
                              if (!n || n <= 0) return alert("Enter a valid amount.");
                              if (!window.confirm(`Send $${n.toFixed(2)} to ${pName}'s bank account? This cannot be undone.`)) return;
                              try {
                                const res = await fetch(`${WORKER_URL}/connect-payout`, {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({
                                    accountId: p.stripeAccountId,
                                    amount: n,
                                    providerName: pName,
                                    period: new Date().toLocaleDateString(),
                                  }),
                                });
                                const d = await res.json();
                                if (d.paid) {
                                  await dbPush(`payouts`, {
                                    providerId: p._key, providerName: pName,
                                    amount: n, transferId: d.transferId, paidAt: Date.now(),
                                  });
                                  alert(`✅ $${n.toFixed(2)} sent to ${pName}. Arrives in 1-2 business days.`);
                                } else {
                                  alert("⚠️ Payout failed: " + (d.error || "Unknown error"));
                                }
                              } catch (err) {
                                alert("⚠️ Error: " + err.message);
                              }
                            }}>
                            💸 Send Payout to {p.firstName}
                          </button>
                        </div>
                      )}
                      {!p.payoutsEnabled && (
                        <div style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 12, color: "#f59e0b" }}>
                          ⚠️ This provider hasn't connected a bank account yet — you can't send them a payout.
                        </div>
                      )}

                      {/* Live location */}
                      {p.currentLocation && (
                        <a href={`https://www.google.com/maps?q=${p.currentLocation.latitude},${p.currentLocation.longitude}`}
                          target="_blank" rel="noreferrer"
                          style={{ display: "block", textAlign: "center", background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e", padding: 12, borderRadius: 8, marginBottom: 16, textDecoration: "none", fontWeight: 700 }}>
                          🗺️ View Live Location
                        </a>
                      )}

                      {/* Job history */}
                      <div style={{ fontSize: 10, color: "#666", letterSpacing: 2, fontWeight: 700, marginBottom: 12 }}>JOB HISTORY</div>
                      {pJobs.length === 0 && <div style={{ color: "#444", fontSize: 13 }}>No jobs yet.</div>}
                      {pJobs.slice(0, 20).map(job => {
                        const meta = STATUS_META[job.status] || { label: job.status, color: "#888", bg: "#1a1a1a" };
                        return (
                          <div key={job._key} style={{ background: "#161616", border: "1px solid #1a1a1a", borderRadius: 8, padding: 12, marginBottom: 8 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: "#aaa" }}>{SERVICE_ICONS[job.service] || "🔧"} {job.service}</div>
                                <div style={{ fontSize: 11, color: "#555" }}>{job.customer} · {timeAgo(job.createdAt)}</div>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: meta.bg, color: meta.color }}>{meta.label}</div>
                                <div style={{ fontSize: 12, color: "#22c55e", marginTop: 4 }}>${getProviderPay(job).pay || 0}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Active providers */}
              {activeProviders.length === 0 && (
                <div style={{ background: "#1a1000", border: "1px solid #f59e0b", borderRadius: 10, padding: 15, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#f59e0b", marginBottom: 7 }}>⚠️ No providers loading?</div>
                  <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.6, marginBottom: 11 }}>
                    If you know you have providers, the secure proxy may not recognize you as admin. Run this check.
                  </div>
                  <button style={{ width: "100%", background: "#f59e0b", border: "none", color: "#000", padding: "11px", borderRadius: 8, fontWeight: 800, fontSize: 13, cursor: "pointer" }}
                    onClick={async () => {
                      try {
                        const r = await fetch(`${WORKER_URL}/whoami`, {
                          method: "POST", headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ idToken: getAuthToken() }),
                        });
                        const d = await r.json();
                        console.log("[whoami]", d);
                        alert(
                          `Token sent: ${d.tokenReceived}\n` +
                          `Token valid: ${d.tokenValid}\n` +
                          `Signed in as: ${d.emailFromToken || "—"}\n` +
                          `Admin email set to: ${d.adminEmailConfigured}\n` +
                          `Match: ${d.matches}\n\n${d.hint}`
                        );
                      } catch (e) {
                        alert("Check failed: " + e.message);
                      }
                    }}>
                    🔍 Run Admin Access Check
                  </button>
                </div>
              )}

              <div style={{ fontSize: 14, fontWeight: 800, color: WHITE, marginTop: 28, marginBottom: 6 }}>Active Providers ({activeProviders.length})</div>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 14 }}>Grouped by state and city</div>

              {/* Broken records from the signup outage */}
              {(() => {
                const broken = [...providers, ...customers, ...partners].filter(u => !u.role);
                if (broken.length === 0) return null;
                return (
                  <div style={{ background: "#1a0a0a", border: "2px solid #E63329", borderRadius: 11, padding: 15, marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 900, color: "#E63329", marginBottom: 6 }}>
                      ⚠️ {broken.length} account{broken.length !== 1 ? "s" : ""} missing a role
                    </div>
                    <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.6, marginBottom: 12 }}>
                      These signed up while account creation was broken. They can log in but the app won't recognize them. Assign the right role to fix.
                    </div>
                    {broken.map(u => (
                      <div key={u._key} style={{ background: "#0f0f0f", borderRadius: 8, padding: 11, marginBottom: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
                          {u.firstName || u.business || "Unknown"} {u.lastName || ""}
                        </div>
                        <div style={{ fontSize: 11.5, color: "#888", marginBottom: 9 }}>{u.email || "no email"} · {u.phone || "no phone"}</div>
                        <div style={{ display: "flex", gap: 6 }}>
                          {[["provider", "🛻 Provider", "#3b82f6"], ["customer", "🚗 Customer", "#E63329"], ["partner", "🏢 Business", "#22c55e"]].map(([role, lbl, col]) => (
                            <button key={role}
                              onClick={async () => {
                                if (!window.confirm(`Set ${u.email} as a ${role}?`)) return;
                                const patch = {
                                  role,
                                  status: role === "provider" ? "pending" : "approved",
                                  ...(role === "partner" ? { subRequired: true, subscribed: false, rate: PARTNER_RATE } : {}),
                                  repairedAt: Date.now(),
                                };
                                await dbPatch(`users/${u._key}`, patch);
                                alert(`✅ Fixed — ${u.email} is now a ${role}. Tell them to sign in again.`);
                                window.location.reload();
                              }}
                              style={{ flex: 1, background: "#1a1a1a", border: `1px solid ${col}`, color: col,
                                padding: "9px 4px", borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Onboarding status summary */}
              {activeProviders.length > 0 && (() => {
                const noBank = activeProviders.filter(p => !p.payoutsEnabled).length;
                const noArea = activeProviders.filter(p => !p.state || !p.city || !p.serviceRadius).length;
                const ready  = activeProviders.filter(p => p.payoutsEnabled && p.state && p.city && p.serviceRadius).length;
                return (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
                    {[
                      ["✅", "Ready", ready, "#22c55e"],
                      ["🏦", "No bank", noBank, noBank ? "#E63329" : "#333"],
                      ["📍", "No area", noArea, noArea ? "#f59e0b" : "#333"],
                    ].map(([ic, label, n, color]) => (
                      <div key={label} style={{ background: "#161616", border: `1px solid ${color}`, borderRadius: 10, padding: "12px 8px", textAlign: "center" }}>
                        <div style={{ fontSize: 15 }}>{ic}</div>
                        <div style={{ fontSize: 19, fontWeight: 900, color, marginTop: 3 }}>{n}</div>
                        <div style={{ fontSize: 9, letterSpacing: 1, color: "#666", fontWeight: 700, marginTop: 2 }}>{label.toUpperCase()}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Bulk-assign providers missing a service area */}
              {activeProviders.filter(p => !p.state || !p.city).length > 0 && (
                <div style={{ background: "#150a1f", border: "1px solid #a855f7", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: "#a855f7", marginBottom: 6 }}>
                    📍 {activeProviders.filter(p => !p.state || !p.city).length} provider(s) have no service area
                  </div>
                  <div style={{ fontSize: 11.5, color: "#999", lineHeight: 1.55, marginBottom: 11 }}>
                    Set them all to West Palm Beach, FL with a 50-mile radius. They can change it themselves in the app.
                  </div>
                  <button style={{ width: "100%", background: "#a855f7", border: "none", color: "#fff", padding: "11px", borderRadius: 8, fontWeight: 800, fontSize: 13, cursor: "pointer" }}
                    onClick={async () => {
                      const missing = activeProviders.filter(p => !p.state || !p.city);
                      if (!window.confirm(`Set ${missing.length} provider(s) to West Palm Beach, FL · 50 mi radius?`)) return;
                      for (const p of missing) {
                        await dbPatch(`users/${p._key}`, {
                          state: "FL",
                          city: p.city || "West Palm Beach",
                          serviceRadius: p.serviceRadius || "50",
                          areaSetByAdmin: true,
                          areaUpdatedAt: Date.now(),
                        });
                      }
                      setProviders(prev => prev.map(x =>
                        missing.find(m => m._key === x._key)
                          ? { ...x, state: "FL", city: x.city || "West Palm Beach", serviceRadius: x.serviceRadius || "50" }
                          : x
                      ));
                      alert(`✅ ${missing.length} provider(s) set to West Palm Beach, FL.`);
                    }}>
                    📍 Set All to West Palm Beach, FL (50 mi)
                  </button>
                </div>
              )}
              {activeProviders.length === 0 && <div style={{ color: "#444", fontSize: 13 }}>No active providers yet.</div>}

              {/* SEARCH */}
              {activeProviders.length > 0 && (
                <>
                  <input
                    style={{ ...c.input, marginBottom: 10 }}
                    placeholder="🔍 Search name, city, state, phone, email..."
                    value={provSearch}
                    onChange={e => setProvSearch(e.target.value)}
                  />

                  {/* State filter chips */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 10, letterSpacing: 2, color: "#666", fontWeight: 700 }}>FILTER BY STATE</span>
                    <button style={{ background: "none", border: "none", color: "#3b82f6", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                      onClick={() => {
                        const states = [...new Set(activeProviders.map(p => p.state || "Unassigned"))];
                        const anyOpen = states.some(st => !collapsedStates[st]);
                        setCollapsedStates(anyOpen ? Object.fromEntries(states.map(st => [st, true])) : {});
                      }}>
                      Collapse / expand all
                    </button>
                  </div>
                  {[...new Set(activeProviders.map(x => x.group).filter(Boolean))].length > 0 && (
                    <>
                      <div style={{ fontSize: 10, letterSpacing: 2, color: "#666", fontWeight: 700, marginBottom: 8 }}>FILTER BY GROUP</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                        <button onClick={() => setGroupFilter("")}
                          style={{ background: !groupFilter ? "#a855f7" : "#161616", border: `1px solid ${!groupFilter ? "#a855f7" : "#2a2a2a"}`, color: !groupFilter ? "#fff" : "#888", padding: "6px 12px", borderRadius: 14, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
                          All groups
                        </button>
                        {[...new Set(activeProviders.map(x => x.group).filter(Boolean))].sort().map(g => {
                          const n = activeProviders.filter(x => x.group === g).length;
                          return (
                            <button key={g} onClick={() => setGroupFilter(groupFilter === g ? "" : g)}
                              style={{ background: groupFilter === g ? "#a855f7" : "#161616", border: `1px solid ${groupFilter === g ? "#a855f7" : "#2a2a2a"}`, color: groupFilter === g ? "#fff" : "#888", padding: "6px 12px", borderRadius: 14, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
                              {g} ({n})
                            </button>
                          );
                        })}
                        {activeProviders.filter(x => !x.group).length > 0 && (
                          <button onClick={() => setGroupFilter(groupFilter === "__none" ? "" : "__none")}
                            style={{ background: groupFilter === "__none" ? "#a855f7" : "none", border: `1px dashed ${groupFilter === "__none" ? "#a855f7" : "#444"}`, color: groupFilter === "__none" ? "#fff" : "#666", padding: "6px 12px", borderRadius: 14, fontSize: 11.5, cursor: "pointer" }}>
                            No group ({activeProviders.filter(x => !x.group).length})
                          </button>
                        )}
                      </div>
                    </>
                  )}

                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                    <button onClick={() => setProvState("")}
                      style={{ background: !provState ? "#E63329" : "#161616", border: `1px solid ${!provState ? "#E63329" : "#2a2a2a"}`, color: !provState ? "#fff" : "#888", padding: "6px 12px", borderRadius: 14, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
                      All ({activeProviders.length})
                    </button>
                    {Object.entries(
                      activeProviders.reduce((acc, p) => {
                        const st = p.state || "Unassigned";
                        acc[st] = (acc[st] || 0) + 1;
                        return acc;
                      }, {})
                    ).sort((a, b) => b[1] - a[1]).map(([st, n]) => (
                      <button key={st} onClick={() => setProvState(provState === st ? "" : st)}
                        style={{ background: provState === st ? "#E63329" : "#161616", border: `1px solid ${provState === st ? "#E63329" : "#2a2a2a"}`, color: provState === st ? "#fff" : "#888", padding: "6px 12px", borderRadius: 14, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
                        {st === "Unassigned" ? "No state" : st} ({n})
                      </button>
                    ))}
                  </div>
                </>
              )}

              {(() => {
                const q = provSearch.trim().toLowerCase();
                const shown = activeProviders.filter(p => {
                  if (provState && (p.state || "Unassigned") !== provState) return false;
                  if (groupFilter === "__none" && p.group) return false;
                  if (groupFilter && groupFilter !== "__none" && p.group !== groupFilter) return false;
                  if (!q) return true;
                  return [
                    p.firstName, p.lastName, `${p.firstName} ${p.lastName}`,
                    p.city, p.state, STATE_NAME[p.state], p.phone, p.email, p.group,
                  ].some(v => (v || "").toLowerCase().includes(q));
                });

                if (shown.length === 0) {
                  return (
                    <div style={{ textAlign: "center", padding: "36px 0", color: "#555" }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                      <div style={{ fontSize: 13 }}>No providers match that search.</div>
                      <button style={{ background: "none", border: "1px solid #333", color: "#888", padding: "7px 14px", borderRadius: 6, fontSize: 12, cursor: "pointer", marginTop: 12 }}
                        onClick={() => { setProvSearch(""); setProvState(""); }}>
                        Clear filters
                      </button>
                    </div>
                  );
                }

                const byState = {};
                shown.forEach(p => {
                  const st = p.state || "Unassigned";
                  const ct = p.city || "Unassigned";
                  if (!byState[st]) byState[st] = {};
                  if (!byState[st][ct]) byState[st][ct] = [];
                  byState[st][ct].push(p);
                });
                const stateKeys = Object.keys(byState).sort((a, b) => {
                  if (a === "Unassigned") return 1;
                  if (b === "Unassigned") return -1;
                  return a.localeCompare(b);
                });

                return stateKeys.map(st => {
                  const cities = byState[st];
                  const stateCount = Object.values(cities).reduce((n, arr) => n + arr.length, 0);
                  return (
                    <div key={st} style={{ marginBottom: 20 }}>
                      <button
                        onClick={() => setCollapsedStates(prev => ({ ...prev, [st]: !prev[st] }))}
                        style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, background: "#1a0a0a", border: "1px solid #E63329", borderRadius: 8, padding: "10px 13px", marginBottom: 10, cursor: "pointer", textAlign: "left" }}>
                        <span style={{ fontSize: 14 }}>📍</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 900, color: "#fff", letterSpacing: 1 }}>
                            {st === "Unassigned" ? "NO STATE SET" : `${st} — ${STATE_NAME[st] || st}`}
                          </div>
                          <div style={{ fontSize: 10.5, color: "#888", marginTop: 2 }}>
                            {Object.keys(cities).length} cit{Object.keys(cities).length !== 1 ? "ies" : "y"}: {Object.keys(cities).sort().join(" · ")}
                          </div>
                        </div>
                        <span style={{ fontSize: 11, color: "#E63329", fontWeight: 700, flexShrink: 0 }}>
                          {stateCount}
                        </span>
                        <span style={{ color: "#666", fontSize: 12, flexShrink: 0 }}>
                          {collapsedStates[st] ? "▼" : "▲"}
                        </span>
                      </button>

                      {!collapsedStates[st] && Object.keys(cities).sort().map(ct => (
                        <div key={ct} style={{ marginBottom: 12, paddingLeft: 4 }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: "#f59e0b", letterSpacing: 1.5, marginBottom: 8, textTransform: "uppercase" }}>
                            🏙️ {ct} ({cities[ct].length})
                          </div>
                          {cities[ct].map(p => {
                            const providerName = `${p.firstName} ${p.lastName}`;
                const providerReviews = reviews.filter(r => r.techName === providerName);
                const avgRating = providerReviews.length > 0 ? (providerReviews.reduce((s, r) => s + r.stars, 0) / providerReviews.length).toFixed(1) : null;
                return (
                  <div key={p._key || p.email} style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 14, marginBottom: 10, cursor: "pointer" }}
                    onClick={() => setSelectedProvider(p)}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      {p.selfieUrl ? (
                        <img src={p.selfieUrl} alt={`${p.firstName} ${p.lastName}`}
                          style={{ width: 42, height: 42, borderRadius: "50%", objectFit: "cover", border: "2px solid #22c55e", flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 42, height: 42, background: "#E63329", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, color: "#fff", fontSize: 14, flexShrink: 0, border: "2px solid #444" }}>
                          {p.firstName?.[0]}{p.lastName?.[0]}
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: WHITE }}>{p.firstName} {p.lastName}</div>
                        <a href={`tel:${p.phone}`} style={{ fontSize: 12, color: "#E63329", display: "block" }}>{p.phone}</a>
                        <div style={{ fontSize: 11, color: "#666" }}>{p.email}</div>
                        {avgRating && <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 2 }}>{"⭐".repeat(Math.round(avgRating))} {avgRating} ({providerReviews.length} reviews)</div>}
                        {p.services && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                            {p.services.map(s => <span key={s} style={{ fontSize: 10, background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e", padding: "2px 6px", borderRadius: 8 }}>{SERVICE_LABELS[s] || s}</span>)}
                          </div>
                        )}
                        {p.group && (
                          <span style={{ display: "inline-block", fontSize: 10, background: "#1a0a25", border: "1px solid #a855f7", color: "#a855f7", padding: "2px 8px", borderRadius: 10, fontWeight: 700, marginTop: 5 }}>
                            👥 {p.group}
                          </span>
                        )}
                        <div style={{ fontSize: 10, marginTop: 5, color: p.payoutsEnabled ? "#22c55e" : "#f59e0b", fontWeight: 700 }}>
                          {p.payoutsEnabled
                            ? `🏦 Bank verified ••••${p.bankLast4 || "____"}`
                            : "⚠️ No bank connected — can't be paid"}
                        </div>
                        {p.serviceRadius && (
                          <div style={{ fontSize: 10, color: "#888", marginTop: 5 }}>
                            🎯 Services up to {p.serviceRadius} mi from {p.city}
                          </div>
                        )}
                        <div style={{ fontSize: 10, color: "#3b82f6", marginTop: 6 }}>Tap to view full profile →</div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 10, ...(p.online ? { color: "#22c55e", background: "#0a1f0a" } : { color: "#666", background: "#1a1a1a" }) }}>
                          {p.online ? "● ONLINE" : "○ OFFLINE"}
                        </div>
                        <button onClick={async () => {
                          if (!window.confirm(`Delete ${p.firstName} ${p.lastName}?`)) return;
                          await dbDelete(`users/${p._key}`);
                          setProviders(prev => prev.filter(x => x._key !== p._key));
                        }} style={{ background: "#2a0a0a", border: "1px solid #E63329", color: "#E63329", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
                        </div>
                      ))}
                    </div>
                  );
                });
              })()}
            </>
          );
        })()}

        {/* FLEET VIEW */}
        {view === "fleet" && (() => {
          const active = fleetAccts.filter(x => x.subscribed);
          const pending = fleetAccts.filter(x => !x.subscribed);
          const mrr = active.reduce((n, x) => n + (x.monthlyRate || fleetMonthly(x.vehicleCount)), 0);
          const totalVehicles = active.reduce((n, x) => n + (x.vehicleCount || 0), 0);
          const submitted = inspections.filter(x => x.status === "submitted");

          return (
            <>
              <div style={{ fontSize: 14, fontWeight: 800, color: WHITE, marginBottom: 4 }}>Fleet Maintenance</div>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 16 }}>
                {active.length} active · {totalVehicles} vehicles · ${mrr}/mo recurring
              </div>

              {fleetAccts.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#444" }}>
                  <div style={{ fontSize: 34, marginBottom: 10 }}>🚚</div>
                  <div style={{ fontSize: 13 }}>No fleet accounts yet.</div>
                  <div style={{ fontSize: 11.5, color: "#555", marginTop: 8 }}>They sign up at superfast-tires.web.app/fleet</div>
                </div>
              )}

              {pending.length > 0 && (
                <>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 4 }}>💳 NOT SUBSCRIBED</div>
                  <div style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>Signed up but haven't paid — worth a call.</div>
                  {pending.map(x => (
                    <div key={x._key} style={{ background: "#1a1400", border: "1px solid #f59e0b", borderRadius: 11, padding: 14, marginBottom: 10 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 800, color: WHITE }}>{x.business}</div>
                      <div style={{ fontSize: 11.5, color: "#777", marginBottom: 7 }}>
                        {x.vehicleCount} vehicles · ${x.monthlyRate || fleetMonthly(x.vehicleCount)}/mo · 📍 {x.city}, {x.state}
                      </div>
                      <a href={`tel:${x.phone}`} style={{ fontSize: 13, color: "#E63329", fontWeight: 700 }}>📞 {x.phone}</a>
                    </div>
                  ))}
                  <div style={{ height: 18 }} />
                </>
              )}

              {active.length > 0 && (
                <>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#14b8a6", fontWeight: 800, marginBottom: 10 }}>✅ ACTIVE ACCOUNTS</div>
                  {active.map(x => {
                    const theirs = submitted.filter(r => r.fleetId === x._key);
                    const last = theirs[0];
                    return (
                      <div key={x._key} style={{ background: "#161616", border: "1px solid #14b8a6", borderRadius: 11, padding: 15, marginBottom: 11 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 7 }}>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 900, color: WHITE }}>{x.business}</div>
                            <div style={{ fontSize: 11.5, color: "#888", marginTop: 2 }}>{x.businessType} · 📍 {x.city}, {x.state}</div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 17, fontWeight: 900, color: "#14b8a6" }}>${x.monthlyRate || fleetMonthly(x.vehicleCount)}</div>
                            <div style={{ fontSize: 10, color: "#666" }}>/month</div>
                          </div>
                        </div>
                        <div style={{ background: "#0f0f0f", borderRadius: 8, padding: 11, marginBottom: 10, fontSize: 12 }}>
                          <div style={{ color: "#ccc", marginBottom: 3 }}>🚚 {x.vehicleCount} vehicles · {x.locationCount || 1} location(s)</div>
                          <div style={{ color: "#999", marginBottom: 3 }}>{x.contactName}</div>
                          <a href={`tel:${x.phone}`} style={{ color: "#E63329", fontWeight: 700 }}>📞 {x.phone}</a>
                        </div>
                        <div style={{ fontSize: 11.5, color: last ? "#14b8a6" : "#f59e0b", fontWeight: 700 }}>
                          {last
                            ? `📋 Last inspected ${new Date(last.submittedAt || last.createdAt).toLocaleDateString()} · ${last.urgentCount || 0} urgent`
                            : "⚠️ Never inspected — schedule a visit"}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {submitted.length > 0 && (
                <>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#888", fontWeight: 800, margin: "22px 0 10px" }}>📋 RECENT INSPECTIONS</div>
                  {submitted.slice(0, 15).map(r => (
                    <div key={r._key} style={{ background: "#161616", border: "1px solid #222", borderRadius: 10, padding: 13, marginBottom: 9 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontSize: 13.5, fontWeight: 800, color: "#fff" }}>{r.fleetBusiness}</div>
                          <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                            {r.vehicleCount} vehicles · {r.techName} · {timeAgo(r.submittedAt || r.createdAt)}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          {r.urgentCount > 0 && (
                            <div style={{ fontSize: 11, color: "#E63329", fontWeight: 800 }}>{r.urgentCount} urgent</div>
                          )}
                          <div style={{ fontSize: 11, color: "#f59e0b" }}>{r.flaggedCount || 0} flagged</div>
                        </div>
                      </div>
                      {r.flaggedCount > 0 && (
                        <div style={{ background: "#1a1400", borderRadius: 7, padding: 9, marginTop: 9, fontSize: 11.5, color: "#f59e0b", lineHeight: 1.55 }}>
                          💰 {r.flaggedCount} quotable item{r.flaggedCount !== 1 ? "s" : ""} — call {r.fleetBusiness} and price them.
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </>
          );
        })()}

        {/* NETWORK VIEW */}
        {view === "network" && (() => {
          const open = netJobs.filter(x => !["completed", "failed"].includes(x.netStatus));
          const done = netJobs.filter(x => ["completed", "failed"].includes(x.netStatus));
          const grossFees = netJobs.filter(x => x.paidAt).length * NETWORK_FEE;

          const Card = ({ j }) => {
            const meta = NETWORK_STATUS[j.netStatus] || NETWORK_STATUS.posted;
            const q = netQuote[j._key] || {};
            return (
              <div style={{ background: "#161616", border: `1px solid ${meta.color}`, borderRadius: 11, padding: 15, marginBottom: 11 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 900, color: WHITE }}>{j.service}</div>
                    <div style={{ fontSize: 11.5, color: "#a855f7", fontWeight: 700, marginTop: 2 }}>
                      🔄 from {j.postedByBusiness}
                    </div>
                  </div>
                  <span style={{ fontSize: 9.5, fontWeight: 900, padding: "3px 9px", borderRadius: 10, background: meta.bg, color: meta.color, whiteSpace: "nowrap" }}>
                    {meta.label}
                  </span>
                </div>

                <div style={{ background: "#0f0f0f", borderRadius: 8, padding: 11, marginBottom: 11, fontSize: 12 }}>
                  <div style={{ color: "#ccc", marginBottom: 4 }}>👤 {j.customer}</div>
                  <a href={`tel:${j.phone}`} style={{ color: "#E63329", fontWeight: 700, display: "block", marginBottom: 4 }}>📞 {j.phone}</a>
                  <div style={{ color: "#999", marginBottom: 4 }}>📍 {j.address}</div>
                  {j.tireSize && <div style={{ color: "#f59e0b", fontWeight: 700, marginBottom: 4 }}>🔢 {j.tireSize} × {j.tireQty}</div>}
                  {j.vehicle && <div style={{ color: "#999", marginBottom: 4 }}>🚗 {j.vehicle}</div>}
                  <div style={{ color: j.urgency === "asap" ? "#E63329" : "#888", fontWeight: 700 }}>
                    {j.urgency === "asap" ? "⚡ ASAP" : "📅 Can wait"}
                  </div>
                  {j.note && <div style={{ color: "#888", marginTop: 7, lineHeight: 1.55, borderTop: "1px solid #1e1e1e", paddingTop: 7 }}>"{j.note}"</div>}
                </div>

                <div style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>
                  Posted by {j.postedByContact} · <a href={`tel:${j.postedByPhone}`} style={{ color: "#a855f7" }}>{j.postedByPhone}</a>
                </div>

                {/* STEP 1 — send a quote */}
                {j.netStatus === "posted" && (
                  <div style={{ background: "#1a1400", border: "1px solid #f59e0b", borderRadius: 9, padding: 13 }}>
                    <div style={{ fontSize: 11, letterSpacing: 1.5, color: "#f59e0b", fontWeight: 800, marginBottom: 9 }}>
                      💵 SOURCE A PARTNER, THEN QUOTE
                    </div>
                    <label style={c.label}>What the partner charges ($)</label>
                    <input style={{ ...c.input, marginBottom: 9 }} type="number" inputMode="decimal" placeholder="175"
                      value={q.price || ""} onChange={e => setNetQuote(p => ({ ...p, [j._key]: { ...p[j._key], price: e.target.value } }))} />
                    <label style={c.label}>Note for the shop (optional)</label>
                    <input style={{ ...c.input, marginBottom: 11 }} placeholder="e.g. 205/55R16 used, 8/32 tread"
                      value={q.note || ""} onChange={e => setNetQuote(p => ({ ...p, [j._key]: { ...p[j._key], note: e.target.value } }))} />

                    {q.price && parseFloat(q.price) > 0 && (
                      <div style={{ background: "#0f0f0f", borderRadius: 7, padding: 11, marginBottom: 11, fontSize: 12.5 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginBottom: 5 }}>
                          <span>Partner collects on arrival</span>
                          <span style={{ color: "#f59e0b", fontWeight: 700 }}>${parseFloat(q.price).toFixed(2)}</span>
                        </div>
                        <div style={{ fontSize: 10.5, color: "#666", marginBottom: 7, lineHeight: 1.45 }}>
                          Cash / Venmo / Zelle from the customer — never touches your account.
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", color: "#fff", fontWeight: 900, fontSize: 14, borderTop: "1px solid #222", paddingTop: 7 }}>
                          <span>Shop pays you</span><span style={{ color: "#22c55e" }}>${NETWORK_FEE}.00</span>
                        </div>
                      </div>
                    )}

                    <button style={{ width: "100%", background: "#f59e0b", border: "none", color: "#000", padding: "12px", borderRadius: 8, fontWeight: 900, fontSize: 13.5, cursor: "pointer", opacity: q.price ? 1 : 0.4 }}
                      disabled={!q.price || parseFloat(q.price) <= 0}
                      onClick={async () => {
                        const pp = parseFloat(q.price);
                        const patch = {
                          netStatus: "quoted", partnerPrice: pp,
                          dispatchFee: NETWORK_FEE, totalDue: NETWORK_FEE,
                          quoteNote: (q.note || "").trim() || null, quotedAt: Date.now(),
                        };
                        await dbPatch(`networkJobs/${j._key}`, patch);
                        setNetJobs(prev => prev.map(x => x._key === j._key ? { ...x, ...patch } : x));
                        setNetQuote(p => ({ ...p, [j._key]: {} }));
                        alert(`✅ Sent to ${j.postedByBusiness}.\n\nJob cost $${pp.toFixed(2)} (collected on arrival)\nThey pay you $${NETWORK_FEE} to dispatch.`);
                      }}>
                      💵 Send Quote
                    </button>
                  </div>
                )}

                {/* Waiting on payment */}
                {j.netStatus === "quoted" && (
                  <div style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 9, padding: 13, fontSize: 12.5, color: "#3b82f6", lineHeight: 1.6 }}>
                    ⏳ Waiting on {j.postedByBusiness} to pay the ${NETWORK_FEE} dispatch fee.
                    <div style={{ fontSize: 11.5, color: "#888", marginTop: 6 }}>
                      Job cost ${(j.partnerPrice || 0).toFixed(2)} — partner collects that on arrival
                    </div>
                    <button style={{ width: "100%", background: "none", border: "1px solid #666", color: "#888", padding: "9px", borderRadius: 7, fontSize: 11.5, cursor: "pointer", marginTop: 10 }}
                      onClick={async () => {
                        await dbPatch(`networkJobs/${j._key}`, { netStatus: "posted", partnerPrice: null, totalDue: null, quoteNote: null });
                        setNetJobs(prev => prev.map(x => x._key === j._key ? { ...x, netStatus: "posted", partnerPrice: null, totalDue: null } : x));
                      }}>
                      Change this quote
                    </button>
                  </div>
                )}

                {/* STEP 2 — dispatch to the partner */}
                {j.netStatus === "paid" && (
                  <div style={{ background: "#0a1f0a", border: "1px solid #22c55e", borderRadius: 9, padding: 13 }}>
                    <div style={{ fontSize: 11, letterSpacing: 1.5, color: "#22c55e", fontWeight: 800, marginBottom: 4 }}>
                      ✅ ${NETWORK_FEE} DISPATCH FEE PAID — SEND IT
                    </div>
                    <div style={{ fontSize: 11.5, color: "#4caf50", marginBottom: 11 }}>
                      Whoever runs it collects ${(j.partnerPrice || 0).toFixed(2)} from the customer on arrival.
                    </div>
                    {/* Who's running it — my provider or a partner shop */}
                    <div style={{ display: "flex", gap: 8, marginBottom: 11 }}>
                      {[["partner", "🏢 Partner Shop"], ["provider", "🛻 My Provider"]].map(([v, lbl]) => (
                        <button key={v}
                          onClick={() => setNetDispatch(p => ({ ...p, [j._key]: { ...p[j._key], kind: v, name: "", phone: "", uid: null } }))}
                          style={{ flex: 1, background: (netDispatch[j._key]?.kind || "partner") === v ? "#a855f7" : "#161616",
                            border: `1px solid ${(netDispatch[j._key]?.kind || "partner") === v ? "#a855f7" : "#2a2a2a"}`,
                            color: (netDispatch[j._key]?.kind || "partner") === v ? "#fff" : "#888",
                            padding: "11px", borderRadius: 8, fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
                          {lbl}
                        </button>
                      ))}
                    </div>

                    <input style={{ ...c.input, marginBottom: 8 }}
                      placeholder="🔍 Search by name, city, or state..."
                      value={netDispatch[j._key]?.search || ""}
                      onChange={e => setNetDispatch(p => ({ ...p, [j._key]: { ...p[j._key], search: e.target.value } }))} />

                    {(() => {
                      const kind = netDispatch[j._key]?.kind || "partner";
                      const q = (netDispatch[j._key]?.search || "").trim().toLowerCase();
                      const pool = kind === "provider"
                        ? providers.filter(x => x.status === "approved")
                        : partners.filter(x => x.status === "approved");
                      const matches = q
                        ? pool.filter(x => [
                            x.business, x.firstName, x.lastName, `${x.firstName} ${x.lastName}`,
                            x.city, x.state, STATE_NAME[x.state], x.phone, x.group,
                          ].some(v => (v || "").toLowerCase().includes(q)))
                        : pool;
                      const chosen = netDispatch[j._key]?.uid;

                      return (
                        <>
                          {q && (
                            <div style={{ fontSize: 11, color: "#666", marginBottom: 7 }}>
                              {matches.length} {kind === "provider" ? "provider" : "partner"}{matches.length !== 1 ? "s" : ""} match
                            </div>
                          )}
                          <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 10 }}>
                            {matches.length === 0 && (
                              <div style={{ textAlign: "center", padding: "18px 0", color: "#555", fontSize: 12 }}>
                                No {kind === "provider" ? "providers" : "partner shops"} match that search.
                              </div>
                            )}
                            {matches.map(x => {
                              const label = kind === "provider" ? `${x.firstName} ${x.lastName}` : x.business;
                              const on = chosen === x._key;
                              return (
                                <button key={x._key}
                                  onClick={() => setNetDispatch(p => ({ ...p, [j._key]: {
                                    ...p[j._key], uid: x._key, name: label, phone: x.phone || "",
                                  } }))}
                                  style={{ width: "100%", textAlign: "left",
                                    background: on ? "#150a1f" : "#141414",
                                    border: `1px solid ${on ? "#a855f7" : "#242424"}`,
                                    borderRadius: 9, padding: "11px 13px", marginBottom: 7, cursor: "pointer",
                                    display: "flex", alignItems: "center", gap: 11 }}>
                                  {kind === "provider" && x.selfieUrl ? (
                                    <img src={x.selfieUrl} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                                  ) : (
                                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: kind === "provider" ? "#E63329" : "#22c55e", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900, flexShrink: 0 }}>
                                      {kind === "provider" ? "🛻" : "🏢"}
                                    </div>
                                  )}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13.5, fontWeight: 800, color: on ? "#c9a0f7" : "#eee" }}>{label}</div>
                                    <div style={{ fontSize: 10.5, color: "#777", marginTop: 1 }}>
                                      {x.city ? `📍 ${x.city}, ${x.state || ""}` : "📍 No area set"}
                                      {kind === "provider" && x.serviceRadius ? ` · ${x.serviceRadius}mi` : ""}
                                    </div>
                                  </div>
                                  {kind === "provider" && (
                                    <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 9, background: x.online ? "#0a1f0a" : "#1a1a1a", color: x.online ? "#22c55e" : "#555", flexShrink: 0 }}>
                                      {x.online ? "● ON" : "○ OFF"}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>

                          {/* Or type someone not on the platform */}
                          <label style={c.label}>Or enter manually</label>
                          <input style={{ ...c.input, marginBottom: 8 }} placeholder="Company or person's name"
                            value={netDispatch[j._key]?.name || ""}
                            onChange={e => setNetDispatch(p => ({ ...p, [j._key]: { ...p[j._key], name: e.target.value, uid: null } }))} />
                          <input style={{ ...c.input, marginBottom: 11 }} type="tel" placeholder="(305) 000-0000"
                            value={netDispatch[j._key]?.phone || ""}
                            onChange={e => setNetDispatch(p => ({ ...p, [j._key]: { ...p[j._key], phone: e.target.value } }))} />
                        </>
                      );
                    })()}
                    <button style={{ width: "100%", background: "#a855f7", border: "none", color: "#fff", padding: "12px", borderRadius: 8, fontWeight: 900, fontSize: 13.5, cursor: "pointer", opacity: netDispatch[j._key]?.name ? 1 : 0.4 }}
                      disabled={!netDispatch[j._key]?.name}
                      onClick={async () => {
                        const d = netDispatch[j._key];
                        const patch = {
                          netStatus: "dispatched", partnerName: d.name.trim(),
                          partnerPhone: (d.phone || "").trim() || null,
                          partnerKind: d.kind || "partner",
                          partnerUid: d.uid || null,
                          dispatchedAt: Date.now(),
                        };
                        await dbPatch(`networkJobs/${j._key}`, patch);
                        setNetJobs(prev => prev.map(x => x._key === j._key ? { ...x, ...patch } : x));
                        setNetDispatch(p => ({ ...p, [j._key]: {} }));

                        // Push it to them if they're on the platform
                        if (d.uid) {
                          fetch(`${WORKER_URL}/notify-provider`, {
                            method: "POST", headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              providerId: d.uid,
                              title: `🔄 NETWORK JOB — ${j.service}`,
                              message: `${j.customer} · ${j.phone}\n📍 ${j.address}\n${j.tireSize ? `🔢 ${j.tireSize} × ${j.tireQty}\n` : ""}\n💵 COLLECT $${(j.partnerPrice || 0).toFixed(2)} ON ARRIVAL\nCash, Venmo or Zelle — in full BEFORE any work starts.\n\n${j.id}`,
                            }),
                          }).catch(() => {});
                        }
                        alert(`✅ Dispatched to ${d.name}.\n\nThey collect $${(j.partnerPrice || 0).toFixed(2)} in full before starting work.`);
                      }}>
                      🚐 Dispatch to Partner
                    </button>
                  </div>
                )}

                {/* STEP 3 — track it */}
                {["dispatched", "collected"].includes(j.netStatus) && (
                  <div style={{ background: "#150a1f", border: "1px solid #a855f7", borderRadius: 9, padding: 13 }}>
                    <div style={{ fontSize: 12.5, color: "#c9a0f7", fontWeight: 700, marginBottom: 4 }}>
                      🚐 {j.partnerName}
                      {j.partnerPhone && <a href={`tel:${j.partnerPhone}`} style={{ color: "#a855f7", marginLeft: 8 }}>{j.partnerPhone}</a>}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#888", marginBottom: 11 }}>
                      Collecting ${(j.partnerPrice || 0).toFixed(2)} on arrival
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {j.netStatus === "dispatched" && (
                        <button style={{ flex: 1, background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e", padding: "10px", borderRadius: 7, fontSize: 12, fontWeight: 800, cursor: "pointer" }}
                          onClick={async () => {
                            await dbPatch(`networkJobs/${j._key}`, { netStatus: "collected", collectedAt: Date.now() });
                            setNetJobs(prev => prev.map(x => x._key === j._key ? { ...x, netStatus: "collected" } : x));
                          }}>
                          💵 Cash Collected
                        </button>
                      )}
                      <button style={{ flex: 1, background: "#151515", border: "1px solid #444", color: "#aaa", padding: "10px", borderRadius: 7, fontSize: 12, fontWeight: 800, cursor: "pointer" }}
                        onClick={async () => {
                          await dbPatch(`networkJobs/${j._key}`, { netStatus: "completed", completedAt: Date.now() });
                          setNetJobs(prev => prev.map(x => x._key === j._key ? { ...x, netStatus: "completed" } : x));
                        }}>
                        ✅ Complete
                      </button>
                      <button style={{ flex: 1, background: "#1a0a0a", border: "1px solid #E63329", color: "#E63329", padding: "10px", borderRadius: 7, fontSize: 12, fontWeight: 800, cursor: "pointer" }}
                        onClick={async () => {
                          if (!window.confirm("Customer wouldn't pay? This flags the shop that posted it.")) return;
                          await dbPatch(`networkJobs/${j._key}`, { netStatus: "failed", failedAt: Date.now() });
                          setNetJobs(prev => prev.map(x => x._key === j._key ? { ...x, netStatus: "failed" } : x));
                        }}>
                        ⚠️ No Pay
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ fontSize: 11, color: "#555", marginTop: 10 }}>{j.id} · {timeAgo(j.createdAt)}</div>
              </div>
            );
          };

          return (
            <>
              <div style={{ fontSize: 14, fontWeight: 800, color: WHITE, marginBottom: 4 }}>Network Dispatch</div>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 16 }}>
                {open.length} active · ${grossFees} in dispatch fees earned
              </div>

              {netJobs.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#444" }}>
                  <div style={{ fontSize: 34, marginBottom: 10 }}>🔄</div>
                  <div style={{ fontSize: 13 }}>No overflow jobs yet.</div>
                  <div style={{ fontSize: 11.5, color: "#555", marginTop: 8, lineHeight: 1.6 }}>
                    Shops post jobs they can't run from their Overflow tab.<br />
                    You source a partner, quote it, and keep ${NETWORK_FEE}.
                  </div>
                </div>
              )}

              {open.length > 0 && open.map(j => <Card key={j._key} j={j} />)}
              {done.length > 0 && (
                <>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#555", fontWeight: 800, margin: "22px 0 10px" }}>CLOSED</div>
                  {done.slice(0, 20).map(j => <Card key={j._key} j={j} />)}
                </>
              )}
            </>
          );
        })()}

        {/* B2B PARTNERS VIEW */}
        {view === "partners" && (() => {
          // Only free industry partners need approval — payers qualify themselves
          const pend = partners.filter(x => x.status === "pending" && !x.subRequired);
          const awaitingSub = partners.filter(x => x.subRequired && !x.subscribed);
          const active = partners.filter(x => x.status === "approved" && (!x.subRequired || x.subscribed));
          const Card = ({ x, isPending }) => {
            const theirJobs = jobs.filter(jb => jb.partnerId === x._key);
            return (
              <div style={{ background: isPending ? "#1a1000" : "#161616", border: `1px solid ${isPending ? "#f59e0b" : "#222"}`, borderRadius: 11, padding: 15, marginBottom: 11 }}>
                <div style={{ fontSize: 15.5, fontWeight: 900, color: WHITE, marginBottom: 3 }}>{x.business}</div>
                <div style={{ fontSize: 11.5, color: "#777", marginBottom: 9 }}>{x.businessType} · 📍 {x.city}, {x.state}</div>

                <div style={{ background: "#0f0f0f", borderRadius: 8, padding: 11, marginBottom: 10 }}>
                  {[["Owner", x.owner], ["Contact", x.contactName], ["Address", `${x.address}, ${x.city}, ${x.state} ${x.zip || ""}`]].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12 }}>
                      <span style={{ color: "#666" }}>{k}</span>
                      <span style={{ color: "#ccc", textAlign: "right", maxWidth: "62%" }}>{v}</span>
                    </div>
                  ))}
                  <a href={`tel:${x.phone}`} style={{ fontSize: 13, color: "#E63329", fontWeight: 700, display: "block", marginTop: 6 }}>📞 {x.phone}</a>
                  <a href={`mailto:${x.email}`} style={{ fontSize: 11.5, color: "#3b82f6", display: "block", marginTop: 2 }}>📧 {x.email}</a>
                </div>

                {x.services?.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
                    {x.services.map(sv => {
                      const svc = PARTNER_SERVICES.find(z => z.id === sv);
                      return <span key={sv} style={{ fontSize: 10.5, background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e", padding: "3px 8px", borderRadius: 10 }}>{svc?.label || sv}</span>;
                    })}
                  </div>
                )}

                {x.notes && (
                  <div style={{ fontSize: 11.5, color: "#999", lineHeight: 1.55, background: "#0f0f0f", borderRadius: 7, padding: 10, marginBottom: 10 }}>"{x.notes}"</div>
                )}

                {!isPending && (
                  <>
                    <div style={{ display: "flex", gap: 14, fontSize: 12, marginBottom: 8, flexWrap: "wrap" }}>
                      <span style={{ color: "#aaa" }}>📋 {theirJobs.length} job{theirJobs.length !== 1 ? "s" : ""}</span>
                      <span style={{ color: "#22c55e", fontWeight: 700 }}>💰 ${theirJobs.filter(z => !z.quoteNeeded).length * PARTNER_RATE} billed</span>
                    </div>
                    <div style={{ fontSize: 11.5, marginBottom: 10, color: x.subRequired ? (x.subscribed ? "#22c55e" : "#f59e0b") : "#a855f7", fontWeight: 700 }}>
                      {x.subRequired
                        ? (x.subscribed ? `💳 Subscribed — $${PARTNER_SUB}/mo` : `⚠️ Not subscribed`)
                        : "🤝 Free industry partner"}
                    </div>
                  </>
                )}

                {isPending ? (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button style={{ flex: 1, background: "#22c55e", border: "none", color: "#000", padding: "11px", borderRadius: 7, fontWeight: 800, cursor: "pointer", fontSize: 13 }}
                      onClick={async () => {
                        await dbPatch(`users/${x._key}`, { status: "approved", approvedAt: Date.now() });
                        setPartners(prev => prev.map(z => z._key === x._key ? { ...z, status: "approved" } : z));
                        await fetch(`${WORKER_URL}/notify`, {
                          method: "POST", headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ title: "✅ Business Approved", message: `${x.business} can now dispatch jobs.\nCall them at ${x.phone} to let them know.` }),
                        }).catch(() => {});
                      }}>✅ Approve</button>
                    <button style={{ flex: 1, background: "none", border: "1px solid #E63329", color: "#E63329", padding: "11px", borderRadius: 7, fontWeight: 700, cursor: "pointer", fontSize: 13 }}
                      onClick={async () => {
                        if (!window.confirm(`Deny ${x.business}?`)) return;
                        await dbPatch(`users/${x._key}`, { status: "denied" });
                        setPartners(prev => prev.filter(z => z._key !== x._key));
                      }}>❌ Deny</button>
                  </div>
                ) : (
                  <button style={{ width: "100%", background: "#2a0a0a", border: "1px solid #E63329", color: "#E63329", padding: "8px", borderRadius: 7, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}
                    onClick={async () => {
                      if (!window.confirm(`Suspend ${x.business}? They won't be able to dispatch.`)) return;
                      await dbPatch(`users/${x._key}`, { status: "pending" });
                      setPartners(prev => prev.map(z => z._key === x._key ? { ...z, status: "pending" } : z));
                    }}>Suspend Account</button>
                )}
              </div>
            );
          };
          return (
            <>
              <div style={{ fontSize: 14, fontWeight: 800, color: WHITE, marginBottom: 4 }}>Business Dispatch Accounts</div>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 16 }}>
                {pend.length} awaiting approval · {awaitingSub.length} not subscribed · {active.length} active
              </div>
              {partners.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#444" }}>
                  <div style={{ fontSize: 34, marginBottom: 10 }}>🏢</div>
                  <div style={{ fontSize: 13 }}>No business accounts yet.</div>
                  <div style={{ fontSize: 11.5, color: "#555", marginTop: 8 }}>They apply at superfast-tires.web.app/partner</div>
                </div>
              )}
              {pend.length > 0 && (
                <>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 4 }}>⏳ AWAITING YOUR APPROVAL</div>
                  <div style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>Free industry partners — roadside, mobile tire, tire shops, towing</div>
                  {pend.map(x => <Card key={x._key} x={x} isPending />)}
                </>
              )}

              {awaitingSub.length > 0 && (
                <>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#3b82f6", fontWeight: 800, margin: "20px 0 4px" }}>💳 SIGNED UP — NOT SUBSCRIBED</div>
                  <div style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>
                    Auto-approved. They activate by paying ${PARTNER_SUB}/mo — no action needed from you.
                  </div>
                  {awaitingSub.map(x => (
                    <div key={x._key} style={{ background: "#0a1525", border: "1px solid #3b82f6", borderRadius: 11, padding: 14, marginBottom: 10 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 800, color: WHITE }}>{x.business}</div>
                      <div style={{ fontSize: 11.5, color: "#777", marginBottom: 8 }}>{x.businessType} · 📍 {x.city}, {x.state}</div>
                      <a href={`tel:${x.phone}`} style={{ fontSize: 13, color: "#E63329", fontWeight: 700, display: "block", marginBottom: 3 }}>📞 {x.phone}</a>
                      <div style={{ fontSize: 11, color: "#555", marginBottom: 10 }}>Signed up {timeAgo(x.createdAt)}</div>
                      <div style={{ fontSize: 11.5, color: "#3b82f6", lineHeight: 1.55 }}>
                        💡 Worth a call — they signed up but haven't subscribed yet.
                      </div>
                    </div>
                  ))}
                </>
              )}
              {active.length > 0 && (
                <>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#22c55e", fontWeight: 800, margin: "20px 0 10px" }}>✅ ACTIVE</div>
                  {active.map(x => <Card key={x._key} x={x} />)}
                </>
              )}
            </>
          );
        })()}

        {/* SPONSORS VIEW */}
        {view === "sponsors" && (() => {
          const openLeads = sponsors.filter(x => x.status !== "closed");
          const closed = sponsors.filter(x => x.status === "closed");
          const Card = ({ l }) => (
            <div style={{ background: "#161616", border: `1px solid ${l.status === "closed" ? "#222" : "#f59e0b"}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: l.status === "closed" ? "#888" : "#fff" }}>{l.business}</div>
                  <div style={{ fontSize: 11.5, color: "#666" }}>{l.type} · 📍 {l.city}</div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 10, background: "#1a1000", color: "#f59e0b", whiteSpace: "nowrap" }}>
                  {l.tier}
                </span>
              </div>
              <div style={{ background: "#0f0f0f", borderRadius: 8, padding: 11, marginBottom: 10 }}>
                <div style={{ fontSize: 12.5, color: "#ccc", marginBottom: 4 }}>👤 {l.contact}</div>
                <a href={`tel:${l.phone}`} style={{ fontSize: 13, color: "#E63329", fontWeight: 700, display: "block", marginBottom: 4 }}>📞 {l.phone}</a>
                {l.email && <a href={`mailto:${l.email}`} style={{ fontSize: 12, color: "#3b82f6", display: "block" }}>📧 {l.email}</a>}
                {l.notes && (
                  <div style={{ fontSize: 12, color: "#999", lineHeight: 1.55, marginTop: 8, paddingTop: 8, borderTop: "1px solid #1e1e1e" }}>
                    "{l.notes}"
                  </div>
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, color: "#555" }}>{timeAgo(l.createdAt)}</span>
                <div style={{ display: "flex", gap: 7 }}>
                  {l.status !== "closed" && (
                    <button style={{ background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e", padding: "5px 11px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                      onClick={async () => {
                        await dbPatch(`sponsorLeads/${l._key}`, { status: "closed", closedAt: Date.now() });
                        setSponsors(prev => prev.map(x => x._key === l._key ? { ...x, status: "closed" } : x));
                      }}>
                      ✅ Close
                    </button>
                  )}
                  <button style={{ background: "#2a0a0a", border: "1px solid #E63329", color: "#E63329", padding: "5px 11px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                    onClick={async () => {
                      if (!window.confirm(`Delete the lead from ${l.business}?`)) return;
                      await dbDelete(`sponsorLeads/${l._key}`);
                      setSponsors(prev => prev.filter(x => x._key !== l._key));
                    }}>
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          );
          return (
            <>
              <div style={{ fontSize: 14, fontWeight: 800, color: WHITE, marginBottom: 4 }}>Sponsor Inquiries</div>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 16 }}>{openLeads.length} open · {closed.length} closed</div>
              {sponsors.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#444" }}>
                  <div style={{ fontSize: 34, marginBottom: 10 }}>🏪</div>
                  <div style={{ fontSize: 13 }}>No sponsor inquiries yet.</div>
                </div>
              )}
              {openLeads.length > 0 && (
                <>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#f59e0b", fontWeight: 800, marginBottom: 10 }}>🟡 NEEDS FOLLOW-UP</div>
                  {openLeads.map(l => <Card key={l._key} l={l} />)}
                </>
              )}
              {closed.length > 0 && (
                <>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#555", fontWeight: 800, margin: "20px 0 10px" }}>✅ CLOSED</div>
                  {closed.slice(0, 20).map(l => <Card key={l._key} l={l} />)}
                </>
              )}
            </>
          );
        })()}

        {/* MESSAGES VIEW */}
        {view === "messages" && (() => {
          const open = messages.filter(m => !m.resolved);
          const done = messages.filter(m => m.resolved);

          const Thread = ({ m }) => {
            const thread = m.replies || [];
            const lastFrom = thread.length ? thread[thread.length - 1].from : "provider";
            const awaitingYou = !m.resolved && lastFrom === "provider";
            return (
              <div style={{ background: "#161616", border: `1px solid ${awaitingYou ? "#E63329" : m.resolved ? "#222" : "#3b82f6"}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: m.resolved ? "#888" : "#fff" }}>{m.providerName}</div>
                    <a href={`tel:${m.providerPhone}`} style={{ fontSize: 12, color: "#E63329" }}>{m.providerPhone}</a>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {m.jobId && (
                      <div style={{ fontSize: 11, background: "#0a1525", border: "1px solid #3b82f6", color: "#3b82f6", padding: "3px 9px", borderRadius: 12, fontWeight: 700, marginBottom: 4 }}>
                        Job {m.jobId}
                      </div>
                    )}
                    {awaitingYou && <div style={{ fontSize: 10, color: "#E63329", fontWeight: 800 }}>● AWAITING YOU</div>}
                  </div>
                </div>

                {/* Conversation */}
                <div style={{ background: "#0d0d0d", borderRadius: 8, padding: 10, marginBottom: 10 }}>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 9.5, color: "#666", fontWeight: 700, letterSpacing: 1, marginBottom: 3 }}>
                      {m.providerName?.split(" ")[0]?.toUpperCase()} · {timeAgo(m.createdAt)}
                    </div>
                    <div style={{ background: "#1a1a1a", borderRadius: 8, padding: "9px 11px", fontSize: 12.5, color: "#ddd", lineHeight: 1.55 }}>
                      {m.message}
                    </div>
                  </div>
                  {thread.map((r, i) => (
                    <div key={i} style={{ marginBottom: 8, textAlign: r.from === "admin" ? "right" : "left" }}>
                      <div style={{ fontSize: 9.5, color: "#666", fontWeight: 700, letterSpacing: 1, marginBottom: 3 }}>
                        {r.from === "admin" ? "YOU" : m.providerName?.split(" ")[0]?.toUpperCase()} · {timeAgo(r.at)}
                      </div>
                      <div style={{ display: "inline-block", maxWidth: "85%", textAlign: "left", background: r.from === "admin" ? "#0a2a1a" : "#1a1a1a", border: r.from === "admin" ? "1px solid #22c55e" : "none", borderRadius: 8, padding: "9px 11px", fontSize: 12.5, color: r.from === "admin" ? "#c8f5da" : "#ddd", lineHeight: 1.55 }}>
                        {r.text}
                      </div>
                    </div>
                  ))}
                </div>

                {!m.resolved && (
                  <>
                    <textarea
                      value={replyDrafts[m._key] || ""}
                      onChange={e => setReplyDrafts(prev => ({ ...prev, [m._key]: e.target.value }))}
                      placeholder="Type your reply..."
                      style={{ width: "100%", background: "#0f0f0f", border: "1px solid #2a2a2a", borderRadius: 8, padding: "10px 12px", color: "#fff", fontSize: 13, minHeight: 60, resize: "vertical", boxSizing: "border-box", marginBottom: 8, fontFamily: "inherit" }} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button style={{ flex: 2, background: "#3b82f6", border: "none", color: "#fff", padding: "10px", borderRadius: 7, fontSize: 12.5, fontWeight: 800, cursor: "pointer", opacity: (replyDrafts[m._key] || "").trim() ? 1 : 0.4 }}
                        disabled={!(replyDrafts[m._key] || "").trim()}
                        onClick={async () => {
                          const text = (replyDrafts[m._key] || "").trim();
                          if (!text) return;
                          const reply = { from: "admin", text, at: Date.now(), read: false };
                          const updated = [...(m.replies || []), reply];
                          await dbPatch(`providerMessages/${m._key}`, { replies: updated, lastReplyAt: Date.now(), unreadByProvider: true });
                          setMessages(prev => prev.map(x => x._key === m._key ? { ...x, replies: updated, unreadByProvider: true } : x));
                          setReplyDrafts(prev => ({ ...prev, [m._key]: "" }));
                        }}>
                        Send Reply →
                      </button>
                      <button style={{ flex: 1, background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e", padding: "10px", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                        onClick={async () => {
                          await dbPatch(`providerMessages/${m._key}`, { resolved: true, resolvedAt: Date.now(), unreadByProvider: true });
                          setMessages(prev => prev.map(x => x._key === m._key ? { ...x, resolved: true } : x));
                        }}>
                        ✅ Resolve
                      </button>
                    </div>
                  </>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, paddingTop: 10, borderTop: "1px solid #1e1e1e" }}>
                  <span style={{ fontSize: 11, color: "#555" }}>
                    {m.resolved ? `Resolved ${timeAgo(m.resolvedAt || m.createdAt)}` : timeAgo(m.createdAt)}
                  </span>
                  <div style={{ display: "flex", gap: 7 }}>
                    {m.resolved && (
                      <button style={{ background: "none", border: "1px solid #333", color: "#888", padding: "5px 11px", borderRadius: 6, fontSize: 11, cursor: "pointer" }}
                        onClick={async () => {
                          await dbPatch(`providerMessages/${m._key}`, { resolved: false });
                          setMessages(prev => prev.map(x => x._key === m._key ? { ...x, resolved: false } : x));
                        }}>
                        Reopen
                      </button>
                    )}
                    <button style={{ background: "#2a0a0a", border: "1px solid #E63329", color: "#E63329", padding: "5px 11px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                      onClick={async () => {
                        if (!window.confirm(`Delete this conversation with ${m.providerName}? This cannot be undone.`)) return;
                        await dbDelete(`providerMessages/${m._key}`);
                        setMessages(prev => prev.filter(x => x._key !== m._key));
                      }}>
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          };

          return (
            <>
              <div style={{ fontSize: 14, fontWeight: 800, color: WHITE, marginBottom: 4 }}>Provider Messages</div>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 16 }}>{open.length} open · {done.length} resolved</div>
              {messages.length === 0 && <div style={{ color: "#444", fontSize: 13, textAlign: "center", marginTop: 40 }}>No messages yet.</div>}
              {open.length > 0 && (
                <>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#3b82f6", fontWeight: 800, marginBottom: 10 }}>🔵 OPEN CONVERSATIONS</div>
                  {open.map(m => <Thread key={m._key} m={m} />)}
                </>
              )}
              {done.length > 0 && (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "20px 0 10px" }}>
                    <span style={{ fontSize: 11, letterSpacing: 2, color: "#555", fontWeight: 800 }}>✅ RESOLVED</span>
                    <button style={{ background: "none", border: "1px solid #E63329", color: "#E63329", padding: "5px 11px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                      onClick={async () => {
                        if (!window.confirm(`Delete all ${done.length} resolved conversation(s)? This cannot be undone.`)) return;
                        for (const m of done) {
                          await dbDelete(`providerMessages/${m._key}`);
                        }
                        setMessages(prev => prev.filter(x => !x.resolved));
                      }}>
                      🗑️ Clear all resolved
                    </button>
                  </div>
                  {done.slice(0, 20).map(m => <Thread key={m._key} m={m} />)}
                </>
              )}
            </>
          );
        })()}

        {/* CUSTOMERS VIEW */}
        {view === "customers" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: WHITE }}>Customers ({customers.length})</div>
              <button style={{ background: "#22c55e", border: "none", color: "#000", padding: "8px 14px", borderRadius: 6, fontWeight: 800, fontSize: 12, cursor: "pointer" }}
                onClick={() => {
                  const rows = [["Name","Email","Phone","Joined","Total Jobs","Total Spent"]];
                  customers.forEach(c => {
                    const cJobs = jobs.filter(j => j.customerId === c._key);
                    const spent = cJobs.reduce((s, j) => s + (j.price || 0), 0);
                    rows.push([
                      `${c.firstName} ${c.lastName}`,
                      c.email, c.phone || "",
                      new Date(c.createdAt).toLocaleDateString(),
                      cJobs.length, `$${spent.toFixed(2)}`
                    ]);
                  });
                  const csv = rows.map(r => r.join(",")).join("\n");
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = "customers.csv"; a.click();
                }}>
                📥 Export CSV
              </button>
            </div>
            <input style={{ ...c.input, marginBottom: 14 }} placeholder="🔍 Search customers..."
              onChange={e => {
                const q = e.target.value.toLowerCase();
                // filter inline
              }} />
            {customers.length === 0 && <div style={{ color: "#444", fontSize: 13 }}>No customers yet.</div>}
            {customers.map(cust => {
              const custJobs = jobs.filter(j => j.customerId === cust._key);
              const totalSpent = custJobs.reduce((s, j) => s + (j.price || 0), 0);
              const isBanned = cust.banned;
              return (
                <div key={cust._key} style={{ background: isBanned ? "#1a0a0a" : "#161616", border: `1px solid ${isBanned ? "#E63329" : "#222"}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: isBanned ? "#E63329" : WHITE }}>
                        {cust.firstName} {cust.lastName} {isBanned && "🚫"}
                      </div>
                      <a href={`tel:${cust.phone}`} style={{ fontSize: 12, color: "#E63329", display: "block" }}>{cust.phone}</a>
                      <div style={{ fontSize: 11, color: "#666" }}>{cust.email}</div>
                      <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 12 }}>
                        <span style={{ color: "#aaa" }}>📋 {custJobs.length} job{custJobs.length !== 1 ? "s" : ""}</span>
                        <span style={{ color: "#22c55e", fontWeight: 700 }}>💰 ${totalSpent.toFixed(2)} spent</span>
                      </div>
                      <div style={{ fontSize: 10, color: "#444", marginTop: 4 }}>
                        Joined {new Date(cust.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        const action = isBanned ? "Unban" : "Ban";
                        if (!window.confirm(`${action} ${cust.firstName} ${cust.lastName}?`)) return;
                        await dbPatch(`users/${cust._key}`, { banned: !isBanned });
                        setCustomers(prev => prev.map(x => x._key === cust._key ? { ...x, banned: !isBanned } : x));
                      }}
                      style={{ background: isBanned ? "#0a1f0a" : "#2a0a0a", border: `1px solid ${isBanned ? "#22c55e" : "#E63329"}`, color: isBanned ? "#22c55e" : "#E63329", borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                      {isBanned ? "✅ Unban" : "🚫 Ban"}
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* JOBS VIEW */}
        {view === "jobs" && (
          <>
            {/* Create Job */}
            <button style={{ ...c.btnPrimary, background: "#22c55e", color: "#000", fontWeight: 900, marginBottom: 16 }}
              onClick={() => setShowCreateJob(v => !v)}>
              {showCreateJob ? "✕ Cancel" : "➕ Create Job Manually"}
            </button>

            {/* Current week label */}
            <div style={{ fontSize: 11, letterSpacing: 2, color: "#E63329", fontWeight: 700, marginBottom: 12 }}>
              📅 PAY PERIOD — {thisWeekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} (Thu) to {new Date(thisWeekStartMs + 6 * 864e5).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} (Wed) · Paid Friday
            </div>

            {showCreateJob && (
              <div style={{ background: "#161616", border: "1px solid #22c55e", borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#22c55e", letterSpacing: 2, marginBottom: 14 }}>NEW JOB</div>
                <div style={{ marginBottom: 10 }}><label style={c.label}>Customer Name</label><input style={c.input} placeholder="John Smith" value={cjName} onChange={e => setCjName(e.target.value)} /></div>
                <div style={{ marginBottom: 10 }}><label style={c.label}>Phone</label><input style={c.input} type="tel" placeholder="(561) 000-0000" value={cjPhone} onChange={e => setCjPhone(e.target.value)} /></div>
                <div style={{ marginBottom: 10 }}>
                  <label style={c.label}>Address</label>
                  <AddressFields value={cjAddress} onChange={val => setCjAddress(val)} />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={c.label}>Service</label>
                  <select style={c.input} value={cjService} onChange={e => setCjService(e.target.value)}>
                    <option value="">Select service...</option>
                    <option>Tire Change</option>
                    <option>Jump Start</option>
                    <option>Fuel Delivery</option>
                    <option>Lockout Service</option>
                    <option>Tow Truck</option>
                    <option>Mobile Mechanic</option>
                    <option>Wheel Lock Removal</option>
                    <option>Battery Replacement</option>
                    <option>Rim Damage Repair</option>
                  </select>
                </div>
                <div style={{ marginBottom: 10 }}><label style={c.label}>Notes (optional)</label><input style={c.input} placeholder="Any details about the job..." value={cjNote} onChange={e => setCjNote(e.target.value)} /></div>
                <div style={{ marginBottom: 14 }}>
                  <label style={c.label}>Provider Pay ($)</label>
                  <input style={c.input} type="number" placeholder="e.g. 40" value={cjPay} onChange={e => setCjPay(e.target.value)} />
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    {["40","50"].map(amt => (
                      <button key={amt} style={{ flex: 1, background: cjPay === amt ? "#22c55e" : "#1a1a1a", border: `1px solid ${cjPay === amt ? "#22c55e" : "#2a2a2a"}`, color: cjPay === amt ? "#000" : "#666", padding: "8px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 700 }}
                        onClick={() => setCjPay(amt)}>
                        ${amt}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={c.label}>Payment Type</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["cash", "paid"].map(type => (
                      <button key={type} style={{ flex: 1, background: cjPayType === type ? "#f59e0b" : "#1a1a1a", border: `1px solid ${cjPayType === type ? "#f59e0b" : "#2a2a2a"}`, color: cjPayType === type ? "#000" : "#666", padding: "10px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 700 }}
                        onClick={() => setCjPayType(type)}>
                        {type === "cash" ? "💵 Cash from Customer" : "✅ Already Paid Online"}
                      </button>
                    ))}
                  </div>
                  {cjPayType === "cash" && (
                    <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 6, padding: "6px 10px", background: "#1a1000", borderRadius: 6 }}>
                      💵 Provider collects ${cjPay || "40"} cash directly from the customer
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "#666", marginBottom: 12 }}>💡 Manually created jobs are cash/phone jobs — no card payment required.</div>
                <button style={{ ...c.btnPrimary, background: "#22c55e", color: "#000" }} disabled={!cjName || !cjPhone || !cjAddress || !cjService || cjLoading}
                  onClick={async () => {
                    setCjLoading(true);
                    const newJob = {
                      id: genId(), service: cjService,
                      customer: cjName, phone: cjPhone, address: cjAddress,
                      note: cjNote, price: null, paid: cjPayType === "paid",
                      providerPay: cjPay ? parseFloat(cjPay) : null,
                      payType: cjPayType,
                      cashCollection: cjPayType === "cash",
                      status: "new", tech: null, techName: null,
                      manual: true, createdAt: Date.now(),
                    };
                    const key = await dbPush("jobs", newJob);
                    setJobs(prev => [{ ...newJob, _key: key }, ...prev]);
                    setCjName(""); setCjPhone(""); setCjAddress(""); setCjService(""); setCjNote(""); setCjPay(""); setCjPayType("cash");
                    setShowCreateJob(false);
                    setCjLoading(false);
                    await fetch(`${WORKER_URL}/notify`, {
                      method: "POST", headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ title: "📋 Manual Job Created", message: `${cjService} for ${cjName}\n📍 ${cjAddress}\n📞 ${cjPhone}` }),
                    }).catch(() => {});
                  }}>
                  {cjLoading ? "Creating..." : "Create & Add to Dispatch Board →"}
                </button>
              </div>
            )}

            {/* Search bar */}
            <input style={{ ...c.input, marginBottom: 12 }}
              placeholder="🔍 Search by name, phone, job ID..."
              value={search}
              onChange={e => setSearch(e.target.value)} />

            {/* Filter pills */}
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, marginBottom: 16 }}>
              {Object.entries(counts).map(([key, count]) => (
                <button key={key} style={{ flexShrink: 0, background: filter === key ? "#E63329" : "#1a1a1a", border: filter === key ? "1px solid #E63329" : "1px solid #2a2a2a", color: filter === key ? WHITE : "#666", padding: "6px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                  onClick={() => setFilter(key)}>
                  {key === "en_route" ? "En Route" : key === "on_scene" ? "On Scene" : key.charAt(0).toUpperCase() + key.slice(1)} {count > 0 ? `(${count})` : ""}
                </button>
              ))}
            </div>

            {loading && <div style={{ color: "#444", textAlign: "center", marginTop: 40 }}>Loading jobs...</div>}
            {!loading && filtered.length === 0 && (
              <div style={{ textAlign: "center", marginTop: 60, color: "#444" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                <div>No jobs in this category.</div>
              </div>
            )}

            {filtered.map(job => {
              const meta = STATUS_META[job.status] || { label: job.status, color: "#888", bg: "#1a1a1a" };
              return (
                <div key={job._key} style={{ background: "#161616", border: job.guest ? "1px solid #f59e0b" : "1px solid #222", borderRadius: 12, padding: 16, marginBottom: 12, cursor: "pointer" }}
                  onClick={() => { setSelectedJob(job); setEditingJob(false); }}>
                  {job.scheduled && job.scheduledLabel && (
                    <div style={{ display: "inline-block", background: "#1a1400", border: "1px solid #f59e0b", color: "#f59e0b", fontSize: 10.5, fontWeight: 900, letterSpacing: 0.8, padding: "3px 9px", borderRadius: 6, marginBottom: 9, marginRight: 6 }}>
                      📅 SCHEDULED — {job.scheduledLabel}
                    </div>
                  )}
                  {job.partnerId && (
                    <div style={{ display: "inline-block", background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e", fontSize: 10, fontWeight: 900, letterSpacing: 1.2, padding: "3px 9px", borderRadius: 6, marginBottom: 9 }}>
                      🏢 B2B — {job.partnerBusiness}{job.quoteNeeded ? " · QUOTE NEEDED" : ` · $${PARTNER_RATE}`}
                    </div>
                  )}
                  {job.guest && (
                    <div style={{ display: "inline-block", background: "#1a1000", border: "1px solid #f59e0b", color: "#f59e0b", fontSize: 10, fontWeight: 900, letterSpacing: 1.5, padding: "3px 9px", borderRadius: 6, marginBottom: 9 }}>
                      ⚡ GUEST — NON-MEMBER · NO ACCOUNT
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 22 }}>{SERVICE_ICONS[job.service] || "🔧"}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: job.guest ? "#f59e0b" : undefined }}>{job.customer}</div>
                      <div style={{ fontSize: 11, color: "#666" }}>{job.service}</div>
                    </div>
                    <div style={{ ...ad.statusPill, background: meta.bg, color: meta.color }}>{meta.label}</div>
                  </div>
                  <div style={{ fontSize: 12, color: "#555", marginBottom: 8, lineHeight: 1.4 }}>📍 {job.address}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 15, fontWeight: 900 }}>{job.price ? `$${job.price}` : "Quote"}</span>
                    <span style={{ fontSize: 11, color: "#444" }}>{timeAgo(job.createdAt)}</span>
                    {job.techName && <span style={{ fontSize: 11, color: "#888" }}>👤 {job.techName}</span>}
                  </div>
                  <div style={{ marginTop: 10, color: "#3b82f6", fontSize: 12, fontWeight: 600 }}>Tap to manage →</div>
                </div>
              );
            })}

            {/* PREVIOUS WEEKS ARCHIVE */}
            {Object.keys(archiveWeeks).length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 11, letterSpacing: 2, color: "#666", fontWeight: 700, marginBottom: 12 }}>📁 PREVIOUS WEEKS</div>
                {Object.entries(archiveWeeks).sort((a, b) => b[0].localeCompare(a[0])).map(([weekLabel, weekJobs]) => (
                  <ArchiveWeek key={weekLabel} weekLabel={weekLabel} weekJobs={weekJobs} onSelect={setSelectedJob} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* BOTTOM NAV */}
      <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 600, background: "#111", borderTop: "1px solid #222", display: "flex", zIndex: 150 }}>
        {[["jobs","📋","Jobs"],["stats","📊","Revenue"],["providers","👤","Providers"],["customers","👥","Customers"],["messages","💬","Messages"],["sponsors","🏪","Sponsors"],["partners","🏢","Business"],["network","🔄","Network"],["fleet","🚚","Fleet"]].map(([v, icon, label]) => (
          <button key={v} style={{ flex: 1, background: "none", border: "none", color: view === v ? "#E63329" : "#666", padding: "12px 0", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, position: "relative" }}
            onClick={() => setView(v)}>
            <span style={{ fontSize: 20 }}>{icon}</span>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>{label}</span>
            {v === "jobs" && counts.new > 0 && (
              <span style={{ position: "absolute", top: 8, right: "calc(50% - 18px)", background: "#E63329", color: WHITE, fontSize: 9, fontWeight: 800, borderRadius: 10, padding: "1px 5px" }}>{counts.new}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const RED = "#E63329", BLK = "#0a0a0a", DARK = "#111", CARD = "#161616", BORDER = "#222", GRAY = "#666", WHITE = "#fff";

const c = {
  root: { background: BLK, minHeight: "100vh", color: WHITE, maxWidth: 480, margin: "0 auto", fontFamily: "'Inter','Helvetica Neue',sans-serif" },
  header: { background: DARK, borderBottom: `3px solid ${RED}`, padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 },
  logo: { display: "flex", alignItems: "center", gap: 8 },
  flame: { fontSize: 20 },
  logoTop: { fontSize: 16, fontWeight: 900, letterSpacing: 2, color: WHITE, lineHeight: 1 },
  logoBot: { fontSize: 14, fontWeight: 900, letterSpacing: 8, color: RED, lineHeight: 1.4 },
  back: { background: "none", border: "1px solid #2a2a2a", color: GRAY, padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 13 },
  page: { padding: "24px 20px 100px" },
  hero: { marginBottom: 32 },
  eyebrow: { fontSize: 11, letterSpacing: 3, color: RED, fontWeight: 700, marginBottom: 10 },
  heroTitle: { fontSize: 38, fontWeight: 900, lineHeight: 1.1, margin: "0 0 12px", letterSpacing: -1 },
  red: { color: RED },
  heroSub: { fontSize: 15, color: GRAY, lineHeight: 1.6, margin: "0 0 16px" },
  badge: { display: "inline-block", background: "#1a0a0a", border: `1px solid ${RED}`, color: RED, padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600 },
  welcomeRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, fontSize: 13, color: "#aaa", background: "#1a1a1a", padding: "8px 14px", borderRadius: 8 },
  signOutLink: { color: RED, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  sectionLabel: { fontSize: 10, letterSpacing: 4, color: GRAY, fontWeight: 700, marginBottom: 14, marginTop: 4 },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 28 },
  card: { background: DARK, border: `1px solid #222`, borderRadius: 12, padding: 16, textAlign: "left", cursor: "pointer", color: WHITE },
  cardIcon: { fontSize: 28, marginBottom: 8 },
  cardName: { fontSize: 14, fontWeight: 800, marginBottom: 4 },
  cardDesc: { fontSize: 11, color: GRAY, lineHeight: 1.4, marginBottom: 10 },
  cardRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  cardPrice: { fontSize: 20, fontWeight: 900, color: WHITE },
  cardEta: { fontSize: 10, color: GRAY, background: "#2a2a2a", padding: "3px 8px", borderRadius: 10 },
  cardCta: { fontSize: 12, color: RED, fontWeight: 700 },
  trustRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  trustItem: { background: DARK, border: "1px solid #222", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: GRAY, display: "flex", alignItems: "center", gap: 6 },
  serviceHeader: { display: "flex", alignItems: "center", gap: 16, background: DARK, borderRadius: 12, padding: 16, marginBottom: 24, border: "1px solid #222" },
  serviceName: { fontSize: 18, fontWeight: 800 },
  servicePrice: { fontSize: 13, color: RED, fontWeight: 600, marginTop: 2 },
  fieldGroup: { marginBottom: 16 },
  label: { display: "block", fontSize: 11, letterSpacing: 2, color: GRAY, fontWeight: 700, marginBottom: 6 },
  input: { width: "100%", background: DARK, border: "1px solid #2a2a2a", borderRadius: 8, padding: "12px 14px", color: WHITE, fontSize: 15, boxSizing: "border-box" },
  spareRow: { display: "flex", flexDirection: "column", gap: 10 },
  spareBtn: { width: "100%", background: "#1a1a1a", border: "1px solid #2a2a2a", color: GRAY, fontSize: 14, fontWeight: 700, padding: "14px 16px", borderRadius: 8, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" },
  spareBtnActive: { background: "#0a1f0a", border: "1px solid #22c55e", color: "#22c55e" },
  spareBtnActiveRed: { background: "#1a0a0a", border: `1px solid ${RED}`, color: RED },
  sparePrice: { fontSize: 16, fontWeight: 900 },
  spareNote: { fontSize: 12, color: "#22c55e", marginTop: 8, padding: "8px 12px", background: "#0a1f0a", borderRadius: 6 },
  spareNoteRed: { fontSize: 12, color: "#aaa", marginTop: 8, padding: "8px 12px", background: "#1a1a1a", borderRadius: 6 },
  gpsBtn: { width: "100%", background: "#0d1f0d", border: "1px solid #2a5a2a", color: "#4caf50", fontSize: 14, fontWeight: 700, padding: "13px", borderRadius: 8, cursor: "pointer" },
  gpsError: { fontSize: 12, color: "#ff6b6b", marginTop: 8 },
  gpsSuccess: { fontSize: 12, color: "#4caf50", marginTop: 8 },
  btnPrimary: { width: "100%", background: RED, border: "none", color: WHITE, fontSize: 16, fontWeight: 800, padding: "16px", borderRadius: 10, cursor: "pointer", marginTop: 8, letterSpacing: 1 },
  btnSecondary: { width: "100%", background: "none", border: "1px solid #2a2a2a", color: GRAY, fontSize: 14, fontWeight: 600, padding: "13px", borderRadius: 10, cursor: "pointer", marginTop: 10 },
  confirmCard: { background: DARK, border: "1px solid #222", borderRadius: 14, padding: 20, marginBottom: 16 },
  confirmTitle: { fontSize: 10, letterSpacing: 3, color: GRAY, fontWeight: 700, marginBottom: 16 },
  confirmRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, fontSize: 14, color: WHITE, gap: 10 },
  divider: { borderTop: "1px solid #222", margin: "12px 0" },
  payNotice: { fontSize: 12, color: GRAY, textAlign: "center", marginBottom: 16, lineHeight: 1.5 },
  trackHero: { textAlign: "center", padding: "32px 0 24px" },
  trackTitle: { fontSize: 26, fontWeight: 900, marginBottom: 4 },
  trackTime: { fontSize: 52, fontWeight: 900, color: RED, lineHeight: 1 },
  trackCard: { background: DARK, border: "1px solid #222", borderRadius: 14, padding: 20, marginBottom: 20 },
  trackRow: { display: "flex", alignItems: "flex-start", gap: 14 },
  trackLabel: { fontSize: 10, letterSpacing: 2, color: GRAY, fontWeight: 700, marginBottom: 2 },
  statusBar: { display: "flex", justifyContent: "space-between", marginBottom: 20 },
  statusStep: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flex: 1 },
  statusDot: { width: 10, height: 10, borderRadius: "50%" },
  footer: { textAlign: "center", fontSize: 11, color: "#444", padding: "20px", borderTop: "1px solid #222" },
};

const au = {
  tabs: { display: "flex", background: "#1a1a1a", borderRadius: 10, padding: 4, marginBottom: 24, gap: 4 },
  tab: { flex: 1, background: "none", border: "none", color: "#666", fontSize: 14, fontWeight: 600, padding: "10px", borderRadius: 8, cursor: "pointer" },
  tabActive: { background: RED, color: WHITE },
  title: { fontSize: 24, fontWeight: 900, marginBottom: 6, color: WHITE },
  sub: { fontSize: 14, color: "#666", marginBottom: 24 },
  eyeBtn: { position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", cursor: "pointer", fontSize: 16 },
  link: { color: RED, cursor: "pointer", fontWeight: 600 },
  switchRow: { textAlign: "center", fontSize: 13, color: "#666", marginTop: 16 },
};

const pv = {
  jobCard: { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 16, marginBottom: 12, cursor: "pointer" },
  jobCardActive: { border: `1px solid ${RED}` },
  pill: { fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, letterSpacing: 0.5 },
  detailRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid #1a1a1a", fontSize: 13, gap: 10 },
  mapBtn: { display: "block", textAlign: "center", color: "#3b82f6", fontSize: 13, marginTop: 14, textDecoration: "none" },
};

const ad = {
  root: { display: "flex", minHeight: "100vh", background: BLK, color: WHITE, fontFamily: "'Inter','Helvetica Neue',sans-serif" },
  alert: { position: "fixed", top: 10, left: "50%", transform: "translateX(-50%)", background: RED, color: WHITE, padding: "10px 24px", borderRadius: 20, fontSize: 13, fontWeight: 700, zIndex: 999, boxShadow: "0 4px 20px rgba(230,51,41,0.5)" },
  sidebar: { width: 210, background: DARK, borderRight: `1px solid ${BORDER}`, display: "flex", flexDirection: "column", flexShrink: 0, minHeight: "100vh" },
  sideLogo: { padding: "18px 20px 14px", borderBottom: `1px solid ${BORDER}` },
  sideLogoTop: { fontSize: 18, fontWeight: 900, letterSpacing: 3, color: WHITE },
  sideLogoBot: { fontSize: 9, letterSpacing: 4, color: RED, fontWeight: 700 },
  sideSection: { fontSize: 9, letterSpacing: 3, color: "#444", fontWeight: 700, padding: "14px 20px 6px" },
  sideBtn: { display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", color: GRAY, padding: "9px 20px", cursor: "pointer", fontSize: 13, textAlign: "left", width: "100%" },
  sideBtnActive: { color: WHITE, background: "#1a1a1a", borderLeft: `2px solid ${RED}` },
  badge: { background: RED, color: WHITE, fontSize: 10, fontWeight: 800, borderRadius: 10, padding: "2px 7px" },
  sideCount: { background: "#1e1e1e", color: "#888", fontSize: 11, borderRadius: 8, padding: "2px 8px" },
  techRow: { display: "flex", alignItems: "center", gap: 8, padding: "7px 20px" },
  techDot: { width: 7, height: 7, borderRadius: "50%", background: "#22c55e", flexShrink: 0 },
  techName: { fontSize: 12, color: "#888" },
  main: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
  topbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 22px", borderBottom: `1px solid ${BORDER}`, background: DARK },
  topTitle: { fontSize: 15, fontWeight: 800 },
  topRight: { display: "flex", gap: 22 },
  topStat: { display: "flex", flexDirection: "column", alignItems: "flex-end" },
  topStatLabel: { fontSize: 9, letterSpacing: 2, color: GRAY },
  topStatVal: { fontSize: 17, fontWeight: 900 },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, padding: 20 },
  statCard: { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 18 },
  boardWrap: { display: "flex", flex: 1, overflow: "hidden" },
  jobList: { width: 320, borderRight: `1px solid ${BORDER}`, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10 },
  empty: { color: "#444", fontSize: 13, textAlign: "center", marginTop: 40 },
  jobCard: { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 10, padding: 14, cursor: "pointer" },
  jobCardActive: { border: `1px solid ${RED}` },
  jobCardTop: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
  jobName: { fontSize: 14, fontWeight: 700 },
  jobService: { fontSize: 11, color: GRAY },
  statusPill: { fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, letterSpacing: 0.5, flexShrink: 0 },
  jobAddr: { fontSize: 11, color: "#555", marginBottom: 8, lineHeight: 1.4 },
  jobMeta: { display: "flex", alignItems: "center", gap: 10 },
  jobPrice: { fontSize: 14, fontWeight: 800 },
  jobTime: { fontSize: 11, color: "#444", flex: 1 },
  jobTech: { fontSize: 11, color: "#888" },
  detail: { flex: 1, overflowY: "auto", padding: 22 },
  detailEmpty: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", opacity: 0.4 },
  detailHeader: { display: "flex", alignItems: "center", gap: 14, marginBottom: 20, padding: 16, background: CARD, borderRadius: 10, border: `1px solid ${BORDER}` },
  detailTitle: { fontSize: 18, fontWeight: 800 },
  detailSection: { fontSize: 9, letterSpacing: 3, color: "#444", fontWeight: 700, margin: "18px 0 10px" },
  detailRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "10px 0", borderBottom: `1px solid ${BORDER}`, fontSize: 13, gap: 12 },
  select: { width: "100%", background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 14px", color: WHITE, fontSize: 14, marginBottom: 10 },
  actionBtn: { width: "100%", background: RED, border: "none", color: WHITE, fontSize: 14, fontWeight: 800, padding: "13px", borderRadius: 8, cursor: "pointer", marginBottom: 10 },
  mapLink: { display: "block", textAlign: "center", color: "#3b82f6", fontSize: 13, marginTop: 10, textDecoration: "none" },
};
